// app/travel-guides/data/attractions.ts

import { main } from "framer-motion/client";
import { url } from "inspector/promises";

export interface Attraction {
  id: string;
  title: string;
  town: string;
  district: string;
  province: string;
  provinceId: string;
  rating: number;
  reviews: number;
  category: string;
  image: string;
  images: string[];
  shortDescription: string;
  fullDescription: string;
  whyVisit: string[];
 overview: string | {
    introduction?: string;
    points?: string[];
  };
  ticketsEntry: {
    residents: string;
    foreigners: string;
    children: string;
    notes: string[];
  };
  highlights: (string | {
  main: string;
  subPoints?: string[];
})[];
  openingHours?: {
    weekdays: string;
    weekends: string;
    holidays: string;
    notes: string;
  };
  visitingHours?: {
    daily: string | string[];
    bestTime: string | string[];
  };
  gettingThere?: {
    fromTown: string | string[] ;
    fromNearby?: string | string[];
    publicTransport?: string;
    parking?: string;
  };
  nearbyHotels?: string[];
  travelTips?: string[];
  didYouKnow?: string;
  nearby: string[];
  bestTimeToVisit?: string;
  duration?: string;
  accessibility?: string;
}
const DIGITAL_OCEAN_SPACES_CDN = "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";
export const attractions: Attraction[] = [
  //Central Province

  //Mathale district
  {
    id: "sigiriya-rock-fortress",
    title: "Sigiriya – The Majestic Rock Fortress of Sri Lanka",
    town: "Sigiriya",
    district: "Matale",
    province: "Central Province",
    provinceId: "central",
    rating: 4.8,
    reviews: 3456,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Sigiriya/sigiriya-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Sigiriya/sigiriya-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Sigiriya/sigiriya-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Sigiriya/sigiriya-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Sigiriya/sigiriya-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Sigiriya/sigiriya-5.jpg`,
    ],
    shortDescription:
      "✨ Climb Sigiriya – where history, architecture, and natural beauty converge to create one of Sri Lanka’s most awe-inspiring landmarks.",
    fullDescription:
      "Sigiriya, also known as Lion Rock, is an ancient rock fortress located in the central Matale District. This UNESCO World Heritage Site is one of the best-preserved examples of ancient urban planning. The site is dominated by a massive column of rock nearly 200 meters high.",
    whyVisit: [
      " UNESCO World Heritage Site – One of Sri Lanka's most iconic landmarks, renowned for its historical and architectural significance.",
      " Ancient Fortress – Built by King Kashyapa in the 5th century, combining engineering brilliance with artistic grandeur.",
      " Stunning Views – Panoramic vistas from the summit of the rock overlooking forests, gardens, and surrounding villages.",
      " Cultural & Historical Experience – Explore frescoes, mirror walls, water gardens, and archaeological ruins.",
      " Adventure & Exploration – A challenging yet rewarding climb through stairways and rock paths to the summit."
    ],
    overview:
      "Sigiriya, also known as the Lion Rock, is a massive rock fortress rising nearly 200 meters above the surrounding plains in the Central Province. The site features the remains of an ancient palace complex, elaborate gardens, frescoes of celestial maidens, and the iconic Lion's Gate. Sigiriya is celebrated for its unique combination of natural beauty, historical significance, and architectural ingenuity, making it a must-visit destination for travelers.",
    ticketsEntry: {
      residents: "LKR 1,500",
      foreigners: "USD 30 (~LKR 10,500)",
      children: "Reduced rates",
      notes: [
        " Prices may change; please check locally before your visit.",
        
      ]
    },
    highlights: [
      " Lion's Gate – The dramatic entrance to the summit, shaped like a lion's paws.",
      " Frescoes – Ancient paintings of celestial maidens preserved on the rock face.",
      " Water Gardens – Intricately designed gardens at the base of the rock, featuring pools, fountains, and symmetrical layouts.",
      "Panoramic Views – Stunning vistas of forests, villages, and surrounding plains from the top.",
      " Ancient Ruins – Remains of the royal palace, terraces, and defensive structures atop the rock."
    ],
    openingHours: {
      weekdays: "7:00 AM - 5:30 PM",
      weekends: "7:00 AM - 5:30 PM",
      holidays: "7:00 AM - 5:30 PM",
      notes: "Last entry at 5:00 PM. Early morning visits (before 8 AM) recommended to avoid crowds and heat."
    },
    visitingHours: {
      daily: "7:00 AM – 5:30 PM",
      bestTime: "Early morning for fewer crowds and cooler temperatures"
    },
    gettingThere: {
      fromTown: "From Kandy: ~90 km (~2.5 hours by car)",
      fromNearby: "From Dambulla: ~25 km (~45 minutes by car)",
      publicTransport: "Transport: Buses and tuk-tuks available from nearby towns",
      parking: "Available near the base entrance"
    },
    nearbyHotels: [
      "Heritance Kandalama – Luxury stay with views of Sigiriya Rock",
      "Mid-range hotels and guesthouses in Sigiriya village"
    ],
    travelTips: [
      " Comfortable shoes and light clothing recommended for the climb.",
      " Bring water, hat, sunscreen, and snacks",
      " Respect the site and avoid touching frescoes or defacing ruins..",
      "Early visits are ideal for cooler temperatures and better photography.",
      
    ],
    didYouKnow: "Sigiriya was originally a royal palace and fortress built in the 5th century by King Kashyapa and later abandoned. Its combination of natural rock formations, artistic frescoes, and advanced water gardens makes it a masterpiece of ancient urban planning..",
    nearby: [
      "Pidurangala Rock Temple (2 km)",
      "Dambulla Cave Temple (20 km)",
      "Minneriya National Park (25 km)",
      "Polonnaruwa Ancient City (70 km)",
      "Hiriwadunna Village (10 km)",
      "Kaudulla National Park (30 km)"
    ],
    bestTimeToVisit: "Early morning to avoid heat and crowds, or late afternoon for sunset views",
    duration: "3-4 hours",
    accessibility: "Challenging climb, not suitable for those with mobility issues"
  },
  {
    id: "sembuwatta-lake",
    title: "Sembuwatta Lake – Tranquil Highland Escape",
    town: "Matale",
    district: "Matale",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 1234,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/sembuwatta-lake/sembuwatta-1.jpg`,
    images: [
         `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/sembuwatta-lake/sembuwatta-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/sembuwatta-lake/sembuwatta-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/sembuwatta-lake/sembuwatta-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/sembuwatta-lake/sembuwatta-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/sembuwatta-lake/sembuwatta-5.jpg`,
    ],
    shortDescription:
      "✨ Visit Sembuwatta Lake, a serene destination combining relaxation, adventure, and breathtaking scenery.",
    fullDescription:
      "Sembuwatta Lake is a picturesque artificial lake nestled in the highlands near Matale, offering a peaceful retreat surrounded by mountains and tea plantations.",
    whyVisit: [
      "Scenic Beauty – Crystal-clear lake surrounded by lush greenery and rolling hills.",
      "Adventure & Recreation – Enjoy paddle boating, fishing, and leisurely walks around the lake.",
      "Photography Spot – Picturesque landscapes ideal for capturing reflections, sunsets, and wildlife.",
      "Peaceful Retreat – Quiet getaway from city life, perfect for relaxation and picnics.",
      "Unique Setting – Highland location with cool climate and panoramic views."
    ],
    overview:
      "Sembuwatta Lake is an artificial lake located in the central highlands of Sri Lanka near Matale. Surrounded by forests, tea plantations, and mountains, the lake is a serene spot for nature lovers and adventure seekers alike. Visitors can relax on the shores, explore walking trails, or engage in recreational activities while enjoying the scenic environment.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Prices may change; please check locally before your visit."
      ]
    },
    highlights: [
      "Paddle Boating – Enjoy calm waters and explore the lake at your own pace.",
      "Walking Trails – Scenic paths around the lake for peaceful strolls.",
      "Photography – Capture reflections, wildlife, and surrounding greenery.",
      "Wildlife & Nature – Spot birds, butterflies, and local flora along the banks.",
      "Panoramic Views – Elevated viewpoints offering stunning vistas of the surrounding highlands."
    ],
    openingHours: {
      weekdays: "6:00 AM - 6:00 PM",
      weekends: "6:00 AM - 6:00 PM",
      holidays: "6:00 AM - 6:00 PM",
      notes: "Best visited during daylight hours for safety and optimal views."
    },
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "Early morning or late afternoon for cooler temperatures and clear views"
    },
    gettingThere: {
      fromTown: ": ~25 km (~1 hour by car)",
      fromNearby: "From Riverston: ~20 km (~45 minutes by car)",
      publicTransport: "Private vehicles or tuk-tuks recommended; limited public transport options",
      parking: "Available at the lake entrance"
    },
    nearbyHotels: [
      "Eco-lodges and guesthouses in Matale and nearby villages",
      "Mid-range accommodations available in surrounding towns"
    ],
    travelTips: [
      " Comfortable shoes for walking along trails.",
      " Bring water, sunscreen, and a hat.",
      "Respect nature; avoid littering and disturbing wildlife.",
      " Early visits recommended for cooler temperatures and fewer visitors.",
     
    ],
    didYouKnow: " become a popular tourist and picnic spot, offering a peaceful escape amid the central highlands’ natural beauty.",
    nearby: [
      "Knuckles Mountain Range (30 km)",
      "Matale town (15 km)",
      "Riverston Peak (35 km)",
      "Aluvihare Rock Temple (20 km)"
    ],
    bestTimeToVisit: "Early morning or late afternoon for cooler temperatures and clear views",
    duration: "2-3 hours",
    accessibility: "Accessible terrain, suitable for most visitors"
  },
  {
    id: "riverston-peak",
    title: "Riverston – Nature's Hidden Gem in Sri Lanka",
    town: "Matale",
    district: "Matale",
    province: "Central Province",
    provinceId: "central",
    rating: 4.7,
    reviews: 987,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/riverston-peak/riverston-1.jpg`,
    images: [
         `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/riverston-peak/riverston-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/riverston-peak/riverston-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/riverston-peak/riverston-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/riverston-peak/riverston-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/riverston-peak/riverston-5.jpg`,
    ],
    shortDescription:
      "✨ Explore Riverston, a serene highland escape filled with adventure, natural beauty, and panoramic vistas.",
    fullDescription:
      "Riverston is a scenic mountain range in the Knuckles region, featuring dramatic landscapes, misty peaks, and diverse flora and fauna.",
    whyVisit: [
      "Scenic Beauty – Rolling hills, misty mountains, waterfalls, and vast tea plantations.",
      "Adventure & Hiking – Ideal for trekking, nature walks, and birdwatching.",
      "Photography Spot – Stunning landscapes, cloud forests, and panoramic viewpoints.",
      " Peaceful Retreat – Escape the crowds and enjoy the serenity of Sri Lanka's central highlands.",
      " Unique Flora & Fauna – Home to endemic plants, birds, and rare wildlife species."
    ],
    overview:
      "Riverston is a scenic area located in the central highlands of Sri Lanka, near the borders of Matale and Nuwara Eliya districts. Famous for its dramatic landscapes, it is a paradise for hikers, nature lovers, and photographers. Visitors can explore waterfalls, mountain trails, and viewpoints while immersing themselves in the cool climate and lush greenery of the region.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Prices may change; please check locally before your visit."
      ]
    },
    highlights: [
      " Mini World's End – Cliff edge offering breathtaking views of the surrounding valleys.",
      " Waterfalls – Explore small but picturesque waterfalls in the area.",
      " Trails & Hiking – Numerous trekking routes through forests, grasslands, and tea plantations.",
      " Photography – Misty hills, valleys, and panoramic landscapes ideal for capturing nature.",
      " Wildlife & Flora – Spot endemic birds, rare plants, and unique rock formations."
    ],
    openingHours: {
      weekdays: "6:00 AM - 6:00 PM",
      weekends: "6:00 AM - 6:00 PM",
      holidays: "6:00 AM - 6:00 PM",
      notes: "Visiting during daylight hours recommended for safety."
    },
    visitingHours: {
      daily: "6:00 AM – 5:00 PM",
      bestTime: "Early morning for clear skies and cooler temperatures"
    },
    gettingThere: {
      fromTown: " ~45 km (~1.5 hours by car)",
      fromNearby: "From Nuwara Eliya: ~60 km (~2 hours by car)",
      publicTransport: "Private vehicles or guided tours recommended; public transport options limited",
      parking: "L: Available at key viewpoints and trailheads"
    },
    nearbyHotels: [
      "Eco-lodges and guesthouses in Matale and nearby villages",
      "Mid-range accommodations available in Matale and Nuwara Eliya"
    ],
    travelTips: [
      " Comfortable hiking shoes and light clothing recommended.",
      " Bring water, snacks, hat, and sunscreen",
      " Respect nature; do not litter or disturb wildlife.",
      "Early visits are ideal for clearer views and fewer visitors",
      
    ],
    didYouKnow: "RRiverston is often called the “Little Paradise” of Sri Lanka’s central highlands due to its unique combination of hills, waterfalls, and misty landscapes, offering some of the most scenic trekking experiences in the country.",
    nearby: [
      "Knuckles Mountain Range (10 km)",
      "Sembuwatta Lake (35 km)",
      "Matale town (40 km)",
      "Illukkumbura village (15 km)"
    ],
    bestTimeToVisit: "Early morning or late afternoon for clear skies and cooler temperatures",
    duration: "3-4 hours",
    accessibility: "Challenging terrain, not suitable for those with mobility issues"
  },
  {
    id: "dambulla-cave-temple",
    title: "Dambulla Cave Temple – Ancient Rock Temple of Spiritual Heritage",
    town: "Dambulla",
    district: "Matale",
    province: "Central Province",
    provinceId: "central",
    rating: 4.8,
    reviews: 3456,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/dambulla-cave-temple/dambulla-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/dambulla-cave-temple/dambulla-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/dambulla-cave-temple/dambulla-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/dambulla-cave-temple/dambulla-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/dambulla-cave-temple/dambulla-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/dambulla-cave-temple/dambulla-5.jpg`,
    ],
    shortDescription:
    "✨ Explore Dambulla Cave Temple, where art, history, and spiritual devotion meet atop a scenic rock hill.",
    fullDescription:
      "The Dambulla Cave Temple complex is the largest and best-preserved cave temple in Sri Lanka, dating back to the 1st century BCE.",
    whyVisit: [
      "UNESCO World Heritage Site – One of Sri Lanka's most important and well-preserved cave temple complexes.",
      " Historical Significance – Dating back over 2,000 years, with connections to King Valagamba.",
      " Stunning Artwork – Features hundreds of Buddha statues, murals, and intricate cave paintings.",
      " Spiritual & Cultural Experience – A major pilgrimage site with a serene and contemplative atmosphere.",
      " Scenic Location – Perched on a rock hill offering panoramic views of the surrounding countryside."
    ],
    overview:
      "The Dambulla Cave Temple, also known as the Golden Temple of Dambulla, is a complex of five main caves filled with Buddhist statues and murals. The site was established in the 1st century BCE and has served as a place of meditation and worship for centuries. Visitors can explore the caves, admire the artistic and religious heritage, and enjoy scenic views of Dambulla and the surrounding plains.",
    ticketsEntry: {
      residents: "LKR 500",
      foreigners: "USD 10 (~LKR 3,500)",
      children: "Reduced rates",
      notes: [
        " Prices may change; please check locally before your visit."
      ]
    },
    highlights: [
      " Five Main Caves – Each cave contains Buddha statues, historical figures, and beautifully painted murals.",
      " Cave Art – Frescoes depicting the life of Buddha and various religious themes.",
      " Serene Environment – Peaceful atmosphere for meditation and reflection.",
      " Panoramic Views – Scenic vistas from the hilltop overlooking Dambulla town and countryside.",
      " Historical Significance – Important center of Buddhist worship and pilgrimage for over 2,000 years."
    ],
    openingHours: {
      weekdays: "7:00 AM - 7:00 PM",
      weekends: "7:00 AM - 7:00 PM",
      holidays: "7:00 AM - 7:00 PM",
      notes: "Best time to visit: Early morning for fewer crowds and cooler temperatures."
    },
    visitingHours: {
      daily: "7:00 AM – 7:00 PM",
      bestTime: "Early morning for fewer crowds and cooler temperatures"
    },
    gettingThere: {
      fromTown: "~5 minutes by car from Dambulla Town",
      fromNearby: "From Sigiriya: ~20 km (~30 minutes by car)",
      publicTransport: "Tuk-tuks and private vehicles recommended",
      parking: "Available at the temple entrance"
    },
    nearbyHotels: [
      "Amaya Lake Dambulla – Luxury stay nearby",
      " Mid-range hotels and guesthouses in Dambulla town"
    ],
    travelTips: [
      " Dress modestly; cover shoulders and knees.",
      " Remove footwear before entering sacred areas.",
      " Comfortable shoes recommended for climbing stairs to the caves.",
      "Bring water and sunscreen, especially in hot weather."
    ],
    didYouKnow: "The Dambulla Cave Temple complex contains over 150 Buddha statues and intricate murals covering more than 2,000 square meters, making it the largest and best-preserved cave temple complex in Sri Lanka.",
    nearby: [
      "Sigiriya Rock Fortress (20 km)",
      "Pidurangala Rock (22 km)",
      "Minneriya National Park (40 km)",
      "Polonnaruwa Ancient City (80 km)"
    ],
    bestTimeToVisit: "Early morning for fewer crowds and cooler temperatures",
    duration: "2-3 hours",
    accessibility: "Stairs to climb, moderate fitness required"
  },
  {
    id: "Aluvihare-Rock-Temple",
    title: "Aluvihare Rock Temple – Ancient Buddhist Heritage Site",
    town: "Dambulla",
    district: "Matale",
    province: "Central Province",
    provinceId: "central",
    rating: 4.8,
    reviews: 3456,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Aluvihare/Aluvihare-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Aluvihare/Aluvihare-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Aluvihare/Aluvihare-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Aluvihare/Aluvihare-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Aluvihare/Aluviharea-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Mathale/Aluvihare/Aluvihare-5.jpg`,
    ],
    shortDescription:
    "✨ Visit Aluvihare Rock Temple - a historic sanctuary blending spiritual devotion with ancient architecture atop a scenic rock formation.",
    fullDescription:
      "The Dambulla Cave Temple complex is the largest and best-preserved cave temple in Sri Lanka, dating back to the 1st century BCE.",
    whyVisit: [
      "Historical Significance – One of Sri Lanka’s oldest Buddhist temples, dating back over 2,000 years",
      " Cultural Importance – Site where the Pali Canon (Tripitaka) was first written down on ola leaves.",
      " Spiritual Experience – A serene temple complex carved into natural rock, offering meditation and reflection.",
      " Scenic Spot – Impressive rock formations, cave temples, and lush surroundings ideal for photography.",
      " Architectural Heritage – Unique combination of rock caves, statues, and traditional Buddhist art."
    ],
    overview:
      "The Aluvihare Rock Temple, located near Matale in Central Sri Lanka, is a historic temple carved into a large rock formation. Established in the 3rd century BCE, it is famous for being the place where Buddhist monks first documented the sacred Buddhist scriptures on ola leaves. Visitors can explore the cave temples, view ancient inscriptions, and experience the spiritual and historical significance of this sacred site.",
    ticketsEntry: {
      residents: "LKR 200",
      foreigners: "USD 2 (~LKR 500)",
      children: "LKR 100",
      notes: [
        " Prices may change; please check locally before your visit."
      ]
    },
    highlights: [
      "Cave Temples – Carved into rock, housing Buddha statues and ancient inscriptions. ",
      " Tripitaka Heritage – Recognized as the site where the Pali Canon was first transcribed.",
      " Peaceful Environment – Quiet, contemplative surroundings ideal for meditation..",
      " Photography – Capture rock formations, ancient statues, and scenic surroundings..",
      "  Historical Significance – A key site in the preservation of Buddhist teachings.."
    ],
    openingHours: {
      weekdays: "7:00 AM - 7:00 PM",
      weekends: "7:00 AM - 7:00 PM",
      holidays: "7:00 AM - 7:00 PM",
      notes: "Best time to visit: Early morning for fewer crowds and cooler temperatures."
    },
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: ": Early morning for fewer visitors and cooler temperatures"
    },
    gettingThere: {
      fromTown: "15 km (25 minutes by car)",
      fromNearby: "From Sigiriya: 20 km (30 minutes by car)",
      publicTransport: ": Tuk-tuks and private vehicles recommended",
      parking: "Available at the temple entrance"
    },
    nearbyHotels: [
      "Mid-range hotels and guesthouses available in Matale district",
      
    ],
    travelTips: [
      "Modest clothing recommended; cover shoulders and knees",
      "Remove footwear before entering sacred areas.",
      "Comfortable shoes for walking on rocky terrain.",
      "Bring water and sun protection during daytime visits.."
    ],
    didYouKnow: "Aluvihare Rock Temple is considered a cornerstone of Sri Lanka’s Buddhist heritage, as it preserved the oral traditions of the Pali Canon in written form for the first time in history.",
    nearby: [
      "Sigiriya Rock Fortress (20 km)",
      "Pidurangala Rock (22 km)",
      "Minneriya National Park (40 km)",
      "Polonnaruwa Ancient City (80 km)"
    ],
    bestTimeToVisit: "Early morning for fewer crowds and cooler temperatures",
    duration: "2-3 hours",
    accessibility: "Stairs to climb, moderate fitness required"
  },

  // kandy district
  {
    id: "Bahirawakanda-Buddha-Statue",
    title: "Bahirawakanda Buddha Statue – Towering Symbol of Peace",
    town: "Kandy",
    district: "Kandy",
    province: "Central Province",
    provinceId: "central",
    rating: 4.8,
    reviews: 3456,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Bahirawakandha/Bahirawakanda-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Bahirawakandha/Bahirawakanda-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Bahirawakandha/Bahirawakanda-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Bahirawakandha/Bahirawakanda-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Bahirawakandha/Bahirawakanda-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Bahirawakandha/Bahirawakanda-5.jpg`,
    ],
    shortDescription:
    "✨ Visit Bahirawakanda Buddha Statue - a peaceful sanctuary offering spiritual reflection and panoramic views of Kandy",
    fullDescription:
      "The Dambulla Cave Temple complex is the largest and best-preserved cave temple in Sri Lanka, dating back to the 1st century BCE.",
    whyVisit: [
      "Iconic Landmark – A 26-meter tall Buddha statue overlooking Kandy city",
      " Panoramic Views – Offers breathtaking views of Kandy, the lake, and surrounding hills",
      "Serene Environment – Peaceful temple grounds ideal for meditation and reflection.",
      "Cultural & Spiritual Experience – Observe devotees, rituals, and offerings at the site.",
      "Architectural Significance – Impressive statue with intricate design and vibrant surroundings."

      
    ],
    overview:
    "The Bahirawakanda Buddha Statue is perched on a hill in Kandy, making it visible from most parts of the city. Built in the late 20th century, the statue serves as both a religious site and a tourist attraction. Visitors can climb the hill or drive up to reach the temple, enjoy panoramic city views, and experience the serene atmosphere of this spiritual landmark",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        " "
      ]
    },
    highlights: [
      "Giant Buddha Statue – 26 meters tall, a prominent symbol of Buddhism in Kandy. ",
      " Scenic Views – Stunning vistas of Kandy city, Kandy Lake, and surrounding mountains.",
      " Temple Grounds – Peaceful gardens and meditation areas for quiet reflection.",
      " Photography – Capture the statue, cityscape, and panoramic horizons.",
      "  Spiritual Experience – Observe rituals and offerings performed by devotees."
    ],
    openingHours: {
      weekdays: "7:00 AM - 7:00 PM",
      weekends: "7:00 AM - 7:00 PM",
      holidays: "7:00 AM - 7:00 PM",
      notes: "Best time to visit: Early morning for fewer crowds and cooler temperatures."
    },
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: ": Early morning or late afternoon for cooler temperatures and clear views"
    },
    gettingThere: {
      fromTown: "4 km (~10–15 minutes by car)",
      fromNearby: "",
      publicTransport: " Tuk-tuks and private vehicles recommended",
      parking: "Available at the temple entrance"
    },
    nearbyHotels: [
      "Amaya Hills – Luxury stay ~10 minutes away",
      "Mid-range hotels and guesthouses available in Kandy city"
      
    ],
    travelTips: [
      "Modest clothing recommended; cover shoulders and knees",
      "Comfortable shoes for walking around temple grounds.",
      "Respect devotees and maintain silence during rituals.",
      "Early morning visits offer cooler temperatures and fewer crowds."
    ],
    didYouKnow: "The Bahirawakanda Buddha Statue was built to commemorate the teachings of Buddhism and to provide a serene vantage point over Kandy, making it a blend of spiritual devotion and scenic beauty.",
    nearby: [
      "Sigiriya Rock Fortress (20 km)",
      "Pidurangala Rock (22 km)",
      "Minneriya National Park (40 km)",
      "Polonnaruwa Ancient City (80 km)"
    ],
    bestTimeToVisit: "Early morning for fewer crowds and cooler temperatures",
    duration: "2-3 hours",
    accessibility: "Stairs to climb, moderate fitness required"
  },
  {
    id: "temple-of-tooth",
    title: " Temple of the Sacred Tooth Relic – Sacred Buddhist Sanctuary",
    town: "Kandy",
    district: "Kandy",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/temple-of-tooth/temple-of-tooth-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/temple-of-tooth/temple-of-tooth-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/temple-of-tooth/temple-of-tooth-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/temple-of-tooth/temple-of-tooth-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/temple-of-tooth/temple-of-tooth-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/temple-of-tooth/temple-of-tooth-5.jpg`,
    ],
    shortDescription:
      "Visit the Temple of the Sacred Tooth Relic where devotion, history, and architectural beauty converge in the heart of Kandy.",
    fullDescription:
      "Sri Dalada Maligawa, or the Temple of the Sacred Tooth Relic, is a Buddhist temple located in Kandy. It houses the relic of the tooth of the Buddha and is one of the most sacred places of worship in the Buddhist world.",
    whyVisit: [
      " Spiritual Significance – Houses the revered tooth relic of Lord Buddha, making it one of Sri Lanka’s most important Buddhist sites",
      " Historical & Cultural Heritage – A UNESCO World Heritage Site with centuries of history and tradition.",
      " Architectural Beauty – Ornate Kandyan-style architecture, golden roof, and intricate carvings.",
      " Religious Experience – Witness daily rituals, ceremonies, and the annual Esala Perahera festival.",
      " Serene Surroundings – Peaceful temple grounds and beautifully maintained gardens."
    ],
    overview:
    "Located in Kandy, the Temple of the Sacred Tooth Relic (Sri Dalada Maligawa) is a major pilgrimage destination for Buddhists worldwide. The temple complex preserves the sacred tooth relic of Lord Buddha, enshrined in a golden casket within the inner sanctum. Visitors can explore the temple halls, museum, and surroundings while experiencing the spiritual ambiance and historical significance of the site.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "USD 10",
      children: "Reduced rates",
      notes: [
        "•	Photography allowed in some areas; restricted near the relic.",
        "Prices may change; please check locally before your visit.",
      ]
    },
    highlights: [
      " Witness the sacred tooth relic's golden casket during puja ceremonies",
      " Experience traditional Kandyan dance performances in the evening",
      "Explore the temple museum with artifacts and historical treasures",
      " Admire the octagonal Paththirippuwa pavilion",
      " Visit during the Esala Perahera festival (July/August)",
      "Walk around the scenic Kandy Lake next to the temple",
      " See the audience hall with intricately painted ceilings",
      "Observe devotees making offerings of lotus flowers"
    ],
    openingHours: {
      weekdays: "5:30 AM - 8:00 PM",
      weekends: "5:30 AM - 8:00 PM",
      holidays: "5:30 AM - 8:00 PM",
      notes: "Puja ceremonies at 5:30 AM, 9:30 AM, and 6:30 PM daily. Arrive 30 minutes early during puja times as queues are long."
    },
    visitingHours: {
      daily: "5.30 AM – 8:00 PM",
      bestTime: ": Early morning for rituals or during the Esala Perahera festival (July–August)"
    },
    gettingThere: {
      fromTown: "Located in central Kandy city",
      fromNearby: "Kandy City Center: ~5 minutes by car",
      publicTransport: "Tuk-tuks and buses available",
      parking: "Available near temple entrance"
    },
    nearbyHotels: [
      "Amaya Hills – Luxury stay ~5 minutes aways",
      "Mid-range hotels and guesthouses available in Kandy city",
     
    ],
    travelTips: [
      " Dress modestly – cover shoulders and knees (sarongs available at entrance).",
      "Remove shoes before entering – bring socks for comfort.",
      " Visit during puja times (5:30 AM, 9:30 AM, 6:30 PM) for ceremonies.",
      "Respect temple rules and maintain silence during rituals."
    ],
    didYouKnow: "The Temple of the Sacred Tooth Relic has safeguarded the relic for over 2,000 years, and the tooth is considered a symbol of sovereignty and protection for Sri Lanka.",
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Kandy-Lake",
    title: "Kandy Lake – Tranquil Heart of the City",
    town: "Kandy",
    district: "Kandy",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kandy-Lake/Kandy-Lake-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kandy-Lake/Kandy-Lake-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kandy-Lake/Kandy-Lake-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kandy-Lake/Kandy-Lake-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kandy-Lake/Kandy-Lake-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kandy-Lake/Kandy-Lake-5.jpg`,
    ],
    shortDescription:
      "✨ Experience Kandy Lake - a serene urban oasis blending history, nature, and scenic beauty in the heart of Sri Lanka’s cultural capital",
    fullDescription:
      "Sri Dalada Maligawa, or the Temple of the Sacred Tooth Relic, is a Buddhist temple located in Kandy. It houses the relic of the tooth of the Buddha and is one of the most sacred places of worship in the Buddhist world.",
    whyVisit: [
      " Scenic Landmark – A man-made lake built in 1807, right in the heart of Kandy city.",
      " Relaxation & Leisure – Ideal for peaceful walks, jogging, or sitting by the water.",
      " Photography Spot – Picturesque views with the reflection of the Temple of the Sacred Tooth Relic and surrounding greenery.",
      "  Recreation – Enjoy a quiet stroll along the lake path or observe local birds and fish.",
      " Historical Significance – Built by King Sri Wickrama Rajasinghe, the last ruler of the Kingdom of Kandy."
    ],
    overview:
    "Kandy Lake, also called Bogambara Lake, is a serene artificial lake located next to the Temple of the Sacred Tooth Relic. It was constructed in 1807 by King Sri Wickrama Rajasinghe and has become an iconic symbol of Kandy. The lake is surrounded by a walking path, shaded by trees, and offers a tranquil escape amid the bustling city. Visitors can enjoy a leisurely walk, scenic photography, and peaceful moments by the water.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Prices may change; please check locally before your visit.",
      ]
    },
    highlights: [
      " Witness the sacred tooth relic's golden casket during puja ceremonies",
      " Experience traditional Kandyan dance performances in the evening",
      "Explore the temple museum with artifacts and historical treasures",
      " Admire the octagonal Paththirippuwa pavilion",
      " Visit during the Esala Perahera festival (July/August)",
      "Walk around the scenic Kandy Lake next to the temple",
      " See the audience hall with intricately painted ceilings",
      "Observe devotees making offerings of lotus flowers"
    ],
    openingHours: {
      weekdays: "5:30 AM - 8:00 PM",
      weekends: "5:30 AM - 8:00 PM",
      holidays: "5:30 AM - 8:00 PM",
      notes: "Puja ceremonies at 5:30 AM, 9:30 AM, and 6:30 PM daily. Arrive 30 minutes early during puja times as queues are long."
    },
    visitingHours: {
      daily: "5.00 AM – 7:00 PM",
      bestTime: ": Early morning or late afternoon for calm waters and pleasant lighting"
    },
    gettingThere: {
      fromTown: "",
      fromNearby: "Kandy City Center: ~5 minutes by foot or tuk-tuk",
      publicTransport: "Tuk-tuks and buses available",
      parking: "best accessed on foot from central Kandy"
    },
    nearbyHotels: [
      "The Kandy House – Luxury boutique stay",
      "Mid-range hotels and guesthouses available in central Kandy",
     
    ],
    travelTips: [
      "Comfortable walking shoes recommended.",
      "Bring water, sunscreen, and a hat.",
      "Respect the natural environment and avoid littering.",
      "Early visits offer quieter surroundings and better photography."
    ],
    didYouKnow: "Kandy Lake was originally constructed without concrete or cement, using natural clay and bunds. It remains a central recreational and cultural feature of the city.",
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Royal-Botanical-Gardens",
    title: " Royal Botanical Gardens – Paradise of Flora in Peradeniya",
    town: "Peradaniya",
    district: "Kandy",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Royal-Botanical-Gardens/botanical-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Royal-Botanical-Gardens/botanical-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Royal-Botanical-Gardens/botanical-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Royal-Botanical-Gardens/botanical-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Royal-Botanical-Gardens/botanical-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Royal-Botanical-Gardens/botanical-5.jpg`,
    ],
    shortDescription:
      "Visit the Temple of the Sacred Tooth Relic where devotion, history, and architectural beauty converge in the heart of Kandy.",
    fullDescription:
      "Sri Dalada Maligawa, or the Temple of the Sacred Tooth Relic, is a Buddhist temple located in Kandy. It houses the relic of the tooth of the Buddha and is one of the most sacred places of worship in the Buddhist world.",
    whyVisit: [
      "Extensive Plant Collections – Home to over 4,000 species of plants, including orchids, palms, medicinal plants, and towering trees.",
      "Photography Spot – Scenic landscapes, vibrant flowers, and picturesque avenues perfect for photos.",
      " Nature & Relaxation – Enjoy peaceful walks, shaded paths, and lush greenery.",
      " Educational Experience – Learn about Sri Lanka’s rich botanical diversity and conservation efforts.",
      " Historical Significance – Established in 1821 during British colonial times, blending history with horticulture."
    ],
    overview:
    "The Royal Botanical Gardens in Peradeniya, near Kandy, is one of Sri Lanka’s most famous gardens. Spanning 147 acres, it features centuries-old trees, exotic plant species, and beautifully landscaped gardens. The gardens are a perfect spot for nature lovers, families, and anyone seeking a serene environment to explore diverse flora.",
    ticketsEntry: {
      residents: "LKR 1,500",
      foreigners: "LKR 3,000 (~USD 10)",
      children: "•	Local Children/Students: LKR 750\n•	Foreign Children/Students: LKR 1,500 (~USD 5)",
      notes: [
        "Children under 5: Free",
        "Prices may change; please check locally before your visit.",
      ]
    },
    highlights: [
      " Orchid House – Showcases hundreds of exotic and native orchid species.",
      " Palm Avenue – A breathtaking avenue lined with majestic palm trees.",
      "Giant Trees – Includes enormous mahogany, rain trees, and other heritage trees.",
      " Medicinal Plants – Learn about plants used in traditional medicine",
      " Scenic Walks – Picturesque paths, ponds, and bridges for photography and leisure."
      
    ],
    openingHours: {
      weekdays: "5:30 AM - 8:00 PM",
      weekends: "5:30 AM - 8:00 PM",
      holidays: "5:30 AM - 8:00 PM",
      notes: "Puja ceremonies at 5:30 AM, 9:30 AM, and 6:30 PM daily. Arrive 30 minutes early during puja times as queues are long."
    },
    visitingHours: {
      daily: "7.30 AM – 6:00 PM",
      bestTime: "Early morning for cooler temperatures and fewer visitors"
    },
    gettingThere: {
      fromTown: "Located in central Kandy city",
      fromNearby: "Kandy City Center: ~4 km (~10 minutes by car",
      publicTransport: "Tuk-tuks and buses available",
      parking: ": Available at the garden entrancee"
    },
    nearbyHotels: [
      "Amaya Hills – Luxury stay ~15 minutes aways",
      "Mid-range hotels and guesthouses available in Kandy city",
     
    ],
    travelTips: [
      "Comfortable walking shoes recommended..",
      "Bring water, hat, and sunscreen",
      " Respect the plants; do not pick flowers or damage trees",
      "Ideal for morning visits to avoid heat and crowds"
    ],
    didYouKnow: "The Royal Botanical Gardens were initially developed as a cinnamon plantation before being converted into a botanical garden. It is famous for its collection of orchids, palm trees, and the “double coconut” (Lodoicea maldivica), one of the largest seeds in the world.",
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Udawattekele-Sanctuary",
    title: "Udawattekele Sanctuary – A Serene Urban Jungle in Kandy",
    town: "Kandy",
    district: "Kandy",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Udawattekele-Sanctuary/Udawattekele-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Udawattekele-Sanctuary/Udawattekele-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Udawattekele-Sanctuary/Udawattekele-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Udawattekele-Sanctuary/Udawattekele-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Udawattekele-Sanctuary/Udawattekele-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Udawattekele-Sanctuary/Udawattekele-5.jpg`,
    ],
    shortDescription:
      "Explore Udawattekele Sanctuary, a peaceful forest retreat blending history, biodiversity, and scenic beauty in the heart of Kandy",
    fullDescription:
      "Sri Dalada Maligawa, or the Temple of the Sacred Tooth Relic, is a Buddhist temple located in Kandy. It houses the relic of the tooth of the Buddha and is one of the most sacred places of worship in the Buddhist world.",
    whyVisit: [
      "Biodiversity Hotspot – A protected forest reserve home to monkeys, birds, butterflies, and native flora.",
      " Nature & Adventure – Ideal for walking trails, birdwatching, and exploring Kandy’s natural heritage",
      " Photography Spot – Lush greenery, scenic pathways, and wildlife provide excellent photo opportunities.",
      "Tranquil Escape – Peaceful retreat from the bustling city, perfect for relaxation and meditation.",
      " Historical Significance – Former royal forest reserve of the Kandyan Kingdom, rich in history and culture"
    ],
    overview:
    "Udawattekele Sanctuary is a 30-hectare forest reserve located on a hill behind the Temple of the Sacred Tooth Relic. Established as a royal forest in the 14th century, it preserves Kandy’s natural ecosystem and provides a refuge for wildlife. Visitors can explore its walking trails, shaded paths, and scenic viewpoints while enjoying the tranquility of this urban jungle",
    ticketsEntry: {
      residents: "LKR 200",
      foreigners: "LKR 500 (~USD 2)",
      children: "LKR 100",
      notes: [
        
        "Prices may change; please check locally before your visit.",
      ]
    },
    highlights: [
      " Witness the sacred tooth relic's golden casket during puja ceremonies",
      " Wildlife – Spot monkeys, birds, butterflies, and other native species",
      "Scenic Viewpoints – Panoramic views of Kandy city and Kandy Lake from higher paths.",
      "Photography – Capture lush greenery, wildlife, and serene forest landscapes",
      " Historical Ruins – Remnants of ancient royal structures within the sanctuary.",
      
    ],
    openingHours: {
      weekdays: "5:30 AM - 8:00 PM",
      weekends: "5:30 AM - 8:00 PM",
      holidays: "5:30 AM - 8:00 PM",
      notes: "Puja ceremonies at 5:30 AM, 9:30 AM, and 6:30 PM daily. Arrive 30 minutes early during puja times as queues are long."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "Early morning or late afternoon for cooler temperatures and wildlife activity"
    },
    gettingThere: {
      fromTown: "Located in central Kandy city",
      fromNearby: "Kandy City Center:  ~1 km (~5 minutes by car or 15-minute walk)r",
      publicTransport: "Tuk-tuks and walking access from central Kandy",
      parking: "Limited near entrancee"
    },
    nearbyHotels: [
      "Amaya Hills – Luxury stay ~5-10 minutes aways",
      "Mid-range hotels and guesthouses available in Kandy city",
     
    ],
    travelTips: [
      "Bring water, hat, and insect repellent",
      "Comfortable walking shoes recommended for forest trails.",
      "Respect wildlife; do not feed animals",
      "Ideal for morning visits to see active wildlife and enjoy cooler temperatures."
    ],
    didYouKnow: "Udawattekele Sanctuary was originally a royal forest reserve for the Kandyan kings and is now a protected sanctuary conserving the region’s native flora and fauna. ",
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Kande-Vihara-Temple",
    title: "Kande Vihara Temple – Majestic Buddhist Landmark",
    town: "Hataraliyadda",
    district: "Kandy",
    province: "Central-Province",
    provinceId: "central",
    rating: 4.7,
    reviews: 2891,
    category: "Religious & Cultural",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kande-Vihara-Temple/Kande-Vihara-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kande-Vihara-Temple/Kande-Vihara-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kande-Vihara-Temple/Kande-Vihara-Temple-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kande-Vihara-Temple/Kande-Vihara-Temple-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kande-Vihara-Temple/Kande-Vihara-Temple-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Kandy/Kande-Vihara-Temple/Kande-Vihara-Temple-5.jpg`,
    ],
    shortDescription:
      " Kande Vihara Temple is a must-visit for spiritual seekers, cultural enthusiasts, and photographers.Admire the towering Buddha, explore the peaceful temple complex, and enjoy panoramic hill views. ",
    fullDescription:
      "",
    whyVisit: [
    	"Iconic Giant Buddha Statue – Features one of Sri Lanka’s largest seated Buddha statues (approx. 48 meters tall).",
      "Historic Significance – Established in 1734, it’s an important site for Buddhist worship and pilgrimage.",
      "Cultural Insight – Observe traditional Buddhist rituals, offerings, and ceremonies.",
      "Scenic Surroundings – Located on a hillside with panoramic views of the hills and surrounding villages.",
      "Photography Opportunities – Capture the towering Buddha, intricate architecture, and panoramic vistas."

    ],
    overview:
    "Kande Vihara Temple, located in Hataraliyadda, Kandy District, is a major Buddhist temple and pilgrimage site. The temple is renowned for its gigantic seated Buddha statue, which dominates the landscape and can be seen from kilometers away. The temple complex combines religious, cultural, and scenic attractions, making it a popular destination for tourists and devotees alike.",

    ticketsEntry: {
      residents: "Free for visitors",
      foreigners: "Free for visitors",
      children: "Free for visitors",
      notes: [
            "Guided Tours: Optional; local guides available for LKR 500–1,000 (~USD 2–5)",
            "Photography: Allowed; respect worshippers during rituals"

      ]
    },
    highlights: [
      {
    main: "Giant Buddha Statue",
    subPoints: [
     "One of Sri Lanka’s tallest seated Buddha statues (~48 meters).",
      "Offers a sense of awe and spiritual serenity.",
    "Painted white and gold, standing out against the hillside backdrop."

    ]
  },
  {
    main: "Temple Complex & Surroundings",
    subPoints: [
      "Several smaller shrines, meditation halls, and statues within the temple grounds.",
      "Scenic hilltop views of Kandy and surrounding villages.",
      "Peaceful walking paths for reflection and exploration."


    ]
  },
  {
    main: "Photography & Exploration",
    subPoints: [
       "Capture wide-angle shots of the Buddha statue with panoramic vistas.",
        "Early morning or late afternoon visits provide softer lighting for photos.",
       
    ]
  },
  {
    main: "Cultural & Spiritual Significance",
    subPoints: [
      "Active temple with daily rituals and offerings.",
      "Learn about Buddhist practices, architecture, and Sri Lankan religious traditions."
    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning for peaceful experience and cooler temperatures; late afternoon for photography.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5–10 minutes, fare LKR 300–500",
        "By Tuk-Tuk: ~5–10 minutes, fare LKR 250–400"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      " By Car/Taxi: ~3.5 hours (120 km), cost USD 35–40 / LKR 12,000–14,000",

       "From Colombo Fort:",
      "By Car/Taxi: ~3.5–4 hours (120 km), fare USD 35–40 / LKR 12,000–14,000",
      "By Public Bus: Colombo–Kandy buses to Hataraliyadda; then short tuk-tuk to temple (~10–15 minutes)",
      "By Train: Colombo–Kandy train (~2.5–3 hours), then taxi or tuk-tuk to temple (~45 minutes)"
 

    ], 
    
    },  
    nearbyHotels: [
      "Kandy City Hotels (4★–5★) – ~45 minutes drive",
      "Local Guesthouses & Eco-Lodges – Available near Hataraliyadda and along Kandy outskirts",
      "Airbnb Options – Cozy homes with scenic views"


    ],
    travelTips: [
      "Dress modestly: cover shoulders and knees when entering temple areas.",
      "Remove footwear before entering shrines.",
      "Visit early morning for cooler weather and peaceful experience.",
      "Combine with a visit to Kandy city, Temple of the Tooth, or Royal Botanical Gardens.",
      "Carry water and sunscreen if visiting during midday."


    ],
    didYouKnow: "The Kande Vihara Buddha statue took several years to complete and is a prominent landmark visible from afar. The temple continues to be a major pilgrimage site and a symbol of Buddhist devotion in the central highlands of Sri Lanka.",
    nearby: [
      "",
      "-",
      
    ],
  },

  

  //NuwaraEliya District
{
    id: "Gregory-Lake",
    title: "Gregory Lake – The Heartbeat of Nuwara Eliya",
    town: "NuwaraEliya",
    district: "Nuwaraeliya",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Gregory Lake/Gregory Lake-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Gregory Lake/Gregory Lake-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Gregory Lake/Gregory Lake-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Gregory Lake/Gregory Lake-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Gregory Lake/Gregory Lake-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Gregory Lake/Gregory Lake-5.jpg`,
    ],
    shortDescription:
      "Explore Gregory Lake, a serene lakeside escape offering cool-climate views, boat rides, and relaxed outdoor activities amid the scenic charm of Nuwara Eliya.",
    fullDescription:
      " ",
    whyVisit: [
      "Nestled in the center of Nuwara Eliya, Gregory Lake is a serene escape where cool breezes meet the mist-covered hills. Visitors come here to enjoy peaceful boat rides, leisurely lakeside walks, and the calming sounds of water lapping against the shore. Whether it’s early morning fog, afternoon sun glinting on the water, or the soft glow of sunset, Gregory Lake is always picturesque.",
    ],
    overview:
    "Created during the British colonial period, Gregory Lake was originally designed as a recreational spot for British administrators. Today, it remains the heart of Nuwara Eliya, attracting tourists, locals, and families alike. The surrounding gardens and walking paths make it ideal for long strolls, while boats, paddle boards, and pony rides add fun for adventurous visitors. The lake is also surrounded by charming cafés and small eateries where you can enjoy tea or snacks while soaking in the view. Photography enthusiasts will love the reflections of the misty mountains and clouds on the calm waters, especially in the early morning or late afternoon.",
    ticketsEntry: {
      residents: "USD 2–3",
      foreigners: "USD 2–3",
      children: "",
      notes: [
        
        "Boating & activities: ~USD 5–10",
        "Prices may fluctuate at any time.",
      ]
    },
    highlights: [
      "Paddle boating and rowboats for a relaxing ride.",
      "Cycling and walking paths around the lake.",
      "Ponies and horse rides for families and kids.",
      "Lakeside cafés serving hot tea, snacks, and local treats.",
      "Stunning photography opportunities, especially at sunrise or sunset."

      
    ],
    gettingThere: {
      fromTown: "Located in the heart of Nuwara Eliya, easily accessible from most town hotels",
      publicTransport: "Tuk-tuk or Car: Short rides from most major hotels.",
      parking: "Limited near entrancee"
    },
    nearbyHotels: [
      "Grand Hotel",
      "Araliya Green Hills",
      "Jetwing St. Andrew’s"
     
    ],
    travelTips: [
      "Bring a light jacket or sweater, as mornings and evenings can be chilly.",
      "Comfortable walking shoes are recommended for the lakeside paths."

    ],
   
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Victoria-Park",
    title: "Victoria Park – A Floral Paradise in the Hills",
    town: "NuwaraEliya",
    district: "Nuwaraeliya",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Victoria Park/Victoria-Park-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Victoria Park/Victoria-Park-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Victoria Park/Victoria-Park-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Victoria Park/Victoria-Park-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Victoria Park/Victoria-Park-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Victoria Park/Victoria-Park-5.jpg`,
    ],
    shortDescription:
      "Explore Victoria Park, a beautifully landscaped urban park known for vibrant flower beds, rare birdlife, and a calm atmosphere in the heart of Nuwara Eliya.",
    fullDescription:
      " ",
    whyVisit: [
      "Victoria Park is the ideal place to enjoy a quiet walk surrounded by blooming flowers, tall trees, and well-maintained lawns. It’s perfect for birdwatching, casual picnics, and a relaxing afternoon away from the city buzz.",
    ],
    overview:
    "Built during the British colonial era, Victoria Park is one of Nuwara Eliya’s most charming green spaces. The park spans several acres and features flowerbeds, rose gardens, manicured lawns, and tall indigenous trees. During the April season, the park transforms into a riot of colors with flowering plants in full bloom. Visitors can spot several species of birds, making it a popular spot for nature lovers and photography enthusiasts. Benches and quiet corners make it a perfect location for reading or simply soaking in the cool highland air.",
    ticketsEntry: {
      residents: "USD 2–3",
      foreigners: "USD 2–3",
      children: "",
      notes: [
        "Prices may fluctuate at any time.",
      ]
    },
    highlights: [
      "Colorful flowerbeds and themed gardens.",
      "Birdwatching and photography opportunities.",
      "Calm walking paths ideal for relaxation.",
      "Seasonal blooms, especially during spring months.",
      "A quiet retreat in the middle of the bustling town."

    ],
    gettingThere: {
      fromTown: "Located in the city center, opposite the Nuwara Eliya Post Office",
      publicTransport: "Easily accessible by foot, tuk-tuk, or car.",
      parking: "Limited near entrancee"
    },
    nearbyHotels: [
      "Grand Hotel",
      "Hill Club",
      "The Blackpool"
     
    ],
    travelTips: [
      "Comfortable walking shoes and a light layer for the cooler highland air."

    ],
   
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Pedro-Tea-Estate",
    title: "Pedro Tea Estate – Journey Through Ceylon Tea",
    town: "NuwaraEliya",
    district: "Nuwaraeliya",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Pedro tea/Pedro-Tea-Estate-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Pedro tea/Pedro-Tea-Estate-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Pedro tea/Pedro-Tea-Estate-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Pedro tea/Pedro-Tea-Estate-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Pedro tea/Pedro-Tea-Estate-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Pedro tea/Pedro-Tea-Estate-5.jpg`,
    ],
    shortDescription:
      "Explore Pedro Tea Estate, a historic tea plantation offering lush hill-country views, factory tours, and insights into Sri Lanka’s world-famous Ceylon tea heritage.",
    fullDescription:
      " ",
    whyVisit: [
      "Walk through rolling hills blanketed with tea bushes, enjoy the fresh highland air, and discover how world-famous Ceylon tea is made.",
    ],
    overview:
    "Pedro Tea Estate is one of Nuwara Eliya’s most iconic tea plantations. The estate stretches across emerald hills, with perfectly pruned tea bushes creating a patchwork of green on the mountainsides. Visitors can take guided tours of the tea factory to see the entire process — from leaf plucking to drying, rolling, and packaging. The estate also offers tea tasting sessions, giving tourists a chance to savor the unique flavors of high-grown Ceylon tea. The combination of educational experiences, stunning landscapes, and refreshing climate makes Pedro Tea Estate a must-visit for nature lovers, tea enthusiasts, and photographers alike.",
    ticketsEntry: {
      residents: "USD 2–5",
      foreigners: "USD 2–5",
      children: "",
      notes: [
        "Prices may fluctuate at any time.",
      ]
    },
    highlights: [
      "Tea factory tour with explanations of production processes.",
      "Tea tasting of fresh, high-grown Ceylon teas.",
      "Photogenic rolling hills covered in tea plants.",
      "Opportunities to walk among workers plucking tea leaves.",
      "Refreshing highland air and scenic viewpoints."


    ],
    gettingThere: {
      fromTown: "Approximately 10 minutes from Nuwara Eliya town center.",
      publicTransport: "Easily accessible  tuk-tuk, or car.",
      parking: "Limited near entrancee"
    },
    nearbyHotels: [
      "Jetwing St. Andrew’s",
      "colonial bungalows",
      "local guesthouses."
     
    ],
    travelTips: [
      "Closed-toe shoes for walking in the plantation; a light jacket is recommended due to cool winds."

    ],
   
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
   {
    id: "Post-Office",
    title: "Nuwara Eliya Post Office – Colonial Landmark & Iconic Photo Spot",
    town: "NuwaraEliya",
    district: "Nuwaraeliya",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/post office/Post-Office-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/post office/Post-Office-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/post office/Post-Office-2.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/post office/Post-Office-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/post office/Post-Office-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/post office/Post-Office-5.jpg`,
    ],
    shortDescription:
      "Explore **Nuwara Eliya Post Office**, a charming colonial-era landmark famed for its Tudor-style architecture and a glimpse into Sri Lanka’s historic postal heritage.",
    fullDescription:
      " ",
    whyVisit: [
      "Step back in time with one of the most photographed colonial buildings in Sri Lanka.",
    ],
    overview:
    "Built during British colonial times, the Nuwara Eliya Post Office features striking Tudor-style red-brick architecture with white trim and a clock tower. Still a functioning post office, it’s a popular spot for tourists to send postcards or simply admire the old-world charm. The building is situated in the city center, next to Victoria Park, making it easy to combine visits with other nearby attractions.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Prices may fluctuate at any time.",
      ]
    },
    highlights: [
      "British-era Tudor architecture.",
      "Iconic photo opportunities.",
      "Nearby shops and cafés.",
      "A glimpse into colonial-era town planning."
    ],
    gettingThere: {
      fromTown: "Located in the city center,opposite Victoria Park",
      publicTransport: "Accessible by foot, tuk-tuk, or car.",
      parking: "Limited near entrancee"
    },
    nearbyHotels: [
      "Grand Hotel",
      "Hill Club",
      "The Blackpool"
     
    ],
    travelTips: [
      "No specific clothing requirements; casual attire is fine."

    ],
   
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Hakgala-Garden",
    title: "Hakgala Botanical Garden – Highland Floral Sanctuary",
    town: "NuwaraEliya",
    district: "Nuwaraeliya",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Hakgala Garden/Hakgala-Garden-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Hakgala Garden/Hakgala-Garden-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Hakgala Garden/Hakgala-Garden-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Hakgala Garden/Hakgala-Garden-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Hakgala Garden/Hakgala-Garden-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Hakgala Garden/Hakgala-Garden-5.jpg`,
    ],
    shortDescription:
      "Experience cool mountain air, colorful flowers, and the peaceful atmosphere of one of Sri Lanka’s largest botanical gardens",
    fullDescription:
      " ",
    whyVisit: [
      "Step back in time with one of the most photographed colonial buildings in Sri Lanka.",
    ],
    overview:
    "Hakgala Botanical Garden sits at the slopes of Hakgala Rock and covers a vast area with carefully arranged plant sections. It is famous for its roses, orchids, ferns, and indigenous highland flora. The garden is cooler and quieter than the town, making it ideal for a half-day escape into nature. Visitors can wander through winding paths, enjoy the views of misty valleys, and photograph exotic plants in bloom. The garden is especially stunning during the flowering season.",
    ticketsEntry: {
      residents: "USD 5–7",
      foreigners: "USD 5–7",
      children: "USD 5–7",
      notes: [
        "Prices may fluctuate at any time.",
      ]
    },
    highlights: [
      "Extensive rose and orchid gardens.",
      "Exotic highland plants and native flora.",
      "Misty mountain scenery and quiet walking paths.",
      "Photography opportunities and peaceful picnic spots."

    ],
    gettingThere: {
      fromTown: "About 20 minutes .",
      publicTransport: "by car or tuk-tuk 20 minutes.",
      parking: "Limited near entrancee"
    },
    nearbyHotels: [
      "Heritance Tea Factory",
      "mountain lodges",
      "boutique hotels"
     
    ],
    travelTips: [
      "Warm layers, comfortable shoes, and a light rain jacket recommended."

    ],
   
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Lover-Leap-Waterfall",
    title: "Lover’s Leap Waterfall – A Romantic Highland Legend",
    town: "NuwaraEliya",
    district: "Nuwaraeliya",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Lover Waterfall/Lover-Leap-Waterfall-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Lover Waterfall/Lover-Leap-Waterfall-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Lover Waterfall/Lover-Leap-Waterfall-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Lover Waterfall/Lover-Leap-Waterfall-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Lover Waterfall/Lover-Leap-Waterfall-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Lover Waterfall/Lover-Leap-Waterfall-5.jpg`,
    ],
    shortDescription:
      "Explore **Lover’s Leap Waterfall**, a picturesque cascade steeped in romantic legend, offering misty highland views and a tranquil escape near Nuwara Eliya.",
    fullDescription:
      " ",
    whyVisit: [
      "Dramatic waterfall, legendary tales, and sweeping highland views.",
    ],
    overview:
    "Lover’s Leap Waterfall is perched on the cliffs above Nuwara Eliya, offering panoramic views of the town and surrounding hills. According to local legend, the waterfall is named after two lovers who tragically leapt to their deaths — adding a romantic and mysterious allure. Visitors can hike a short trail to reach viewpoints, enjoy the cool mist, and capture photographs of cascading water against the lush green backdrop",
    ticketsEntry: {
      residents: "USD 5–7",
      foreigners: "USD 5–7",
      children: "USD 5–7",
      notes: [
        "Prices may fluctuate at any time.",
      ]
    },
    highlights: [
    "Scenic waterfall and cliff views.",
    "Short hiking trail through lush vegetation.",
    "Photogenic spot for sunrise or late afternoon.",
    "Local legend adds cultural and romantic appeal."


    ],
    gettingThere: {
      fromTown: "Accessible via a short hike from Pedro Tea Estate or by tuk-tuk to the base.",
      publicTransport: "",
      parking: ""
    },
    nearbyHotels: [
      "Jetwing St. Andrew’s",
      "Grand Hotel", 
      "guesthouses near tea estates."
     
    ],
    travelTips: [
      "Good grip shoes for the trail; light jacket recommended."

    ],
   
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },
  {
    id: "Single-Tree-Hill",
    title: "Single Tree Hill – Panoramic Viewpoint",
    town: "NuwaraEliya",
    district: "Nuwaraeliya",
    province: "Central Province",
    provinceId: "central",
    rating: 4.6,
    reviews: 4123,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Single Tree Hill/Single-Tree-Hill-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Single Tree Hill/Single-Tree-Hill-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Single Tree Hill/Single-Tree-Hill-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Single Tree Hill/Single-Tree-Hill-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Single Tree Hill/Single-Tree-Hill-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Central/Nuwara eliya/Single Tree Hill/Single-Tree-Hill-5.jpg`,
    ],
    shortDescription:
      "Explore **Single Tree Hill**, a popular viewpoint offering sweeping panoramic views of Nuwara Eliya, surrounding tea estates, and misty hill-country landscapes.",
    fullDescription:
      " ",
    whyVisit: [
      "The best vantage point for sunrise and sunset over Nuwara Eliya’s hills and valleys.",
    ],
    overview:
    "Single Tree Hill rises slightly above the town, offering sweeping panoramic views of Nuwara Eliya, its tea estates, lakes, and surrounding mountains. The area is calm and ideal for photography, quiet reflection, or enjoying a picnic. Early morning mist adds a magical touch to the landscape, while evenings are perfect for warm golden light over the hills.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        
      ]
    },
    highlights: [
    "Uninterrupted panoramic city and mountain views.",
    "Sunrise and sunset photography.",
    "Peaceful walking paths.",
    "Ideal for reflective and quiet experiences."

    ],
    gettingThere: {
      fromTown: "15–20 minute walk from town or short tuk-tuk ride",
      publicTransport: "",
      parking: ""
    },
    nearbyHotels: [
     "Grand Hotel",
    "Araliya Green Hills",
    "boutique stays near town."
     
    ],
    travelTips: [
      "Warm jacket or sweater; comfortable walking shoes."

    ],
   
    nearby: [
      "Kandy Lake (adjacent)",
      "Royal Botanical Gardens (6 km)",
      "Bahiravokanda Vihara Buddha Statue (2 km)",
      "Udawattakele Forest Reserve (1 km)",
      "Kandy View Point (4 km)",
      "Ceylon Tea Museum (5 km)"
    ],
    bestTimeToVisit: "Year-round; July/August for Esala Perahera festival",
    duration: "1-2 hours",
    accessibility: "Wheelchair accessible ramps available, some stairs"
  },


  // Eastern Province

  //ampara district
  {
    id: "Arugam-Bay",
    title: "Arugam Bay – Sri Lanka’s Surfing Paradise on the East Coast",
    town: "Arugam Bay",
    district: "Ampara",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Arugambay/arugambay-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Arugambay/arugambay-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Arugambay/arugambay-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Arugambay/arugambay-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Arugambay/arugambay-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Arugambay/arugambay-5.jpg`,
    ],
    shortDescription:
    "✨ Whether you’re chasing waves or serenity, Arugam Bay offers the ultimate coastal escape filled with adventure, beauty, and island soul.",
    fullDescription:
      "Galle Fort is a historical, archaeological and architectural heritage monument located in the Bay of Galle on the southwestern coast of Sri Lanka. The fort was first built by the Portuguese in 1588 and later extensively fortified by the Dutch during the 17th century.",
    whyVisit: [
      "World-Famous Surfing Spot – Ranked among the top 10 surf destinations in the world, ideal for both beginners and professionals.",
      " Breathtaking Sunrises – Watch the sun rise over the Indian Ocean, painting the sky with golden and pink hues.",
      "Chill Beach Vibes – A laid-back coastal town with rustic charm, cozy cafes, and friendly locals.",
      "Nature & Wildlife – Close to Kumana National Park, where elephants, leopards, and exotic birds roam freely.",
      "Local Cuisine – Savor fresh seafood and tropical fruit shakes in seaside restaurants"
    ],
    overview:
    "Located on Sri Lanka’s southeastern coast, Arugam Bay is a tropical haven known for its stunning waves, golden beaches, and relaxed atmosphere. Popular with surfers, backpackers, and nature lovers, it offers a perfect mix of adventure and tranquility. The crescent-shaped bay is surrounded by palm trees and small fishing villages, giving visitors a glimpse into authentic coastal life. Beyond surfing, the area offers yoga retreats, lagoon safaris, and scenic viewpoints such as Elephant Rock and Whiskey Point.",
    ticketsEntry: {
      residents: "Free to access the beach.",
      foreigners: "Free to access the beach.",
      children: "Free to access the beach.",
      notes: [
        "• Surfboard Rentals: LKR 2,000–4,000 (~USD 6–12) per day.",
        "• Surf Lessons: LKR 4,000–8,000 (~USD 12–25) per session",
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
      " Surfing & Water Sports – Ride the iconic waves at Main Point, Peanut Farm, and Baby Point.",
      " Wildlife Encounters – Spot elephants near Pottuvil Lagoon or on the way to Kumana National Park.",
      " Scenic Viewpoints – Climb Elephant Rock for panoramic ocean views",
      " Wellness & Relaxation – Join beachfront yoga sessions or enjoy Ayurvedic spa treatments.",
      " Photography & Culture – Capture fishing boats, coconut palms, and the simple rhythm of coastal life.",
      
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 7:00 PM",
      bestTime: "May to September (surfing season)",
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
    "From Colombo: ~8 hours by car (320 km)",
    "From Batticaloa: ~3 hours (140 km)",
    "Nearest Airport: Batticaloa or Mattala Rajapaksa International Airport"
    ], 
    publicTransport: "Buses from Colombo, Monaragala, and Pottuvil; tuk-tuks for local travel",
    parking: "Available near main beach areas and hotels"
    },  
    nearbyHotels: [
      " Jetwing Surf – Luxury eco-resort with oceanfront cabanas.",
      " The Bay Vista Hotel – Mid-range option with sea views and yoga classes.",
      " Surf N Sun Guesthouse – Budget-friendly and ideal for backpackers."
    ],
    travelTips: [
      " Bring sunscreen, sunglasses, and a hat.",
      " Dress modestly when walking through villages.",
      " Respect local fishing zones and wildlife.",
      " Early morning and late afternoon are ideal for surfing and photography.",
      
    ],
    didYouKnow: "Arugam Bay hosted International Surfing Competitions by the Association of Surfing Professionals (ASP), putting Sri Lanka on the global surfing map. The bay’s crescent shape naturally creates consistent right-hand point breaks—perfect for long rides and unforgettable memories.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Lahugala-National-Park",
    title: "Lahugala National Park – Hidden Wildlife Gem of the East",
    town: " Lahugala ",
    district: "Ampara",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Lahugala National Park/Lahugala-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Lahugala National Park/Lahugala-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Lahugala National Park/Lahugala-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Lahugala National Park/Lahugala-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Lahugala National Park/Lahugala-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Lahugala National Park/Lahugala-5.jpg`,

    ],
    shortDescription:
    "✨ Experience the peaceful rhythm of Sri Lanka’s wilderness at Lahugala National Park, where elephants roam freely and nature thrives in quiet harmony.",
    fullDescription:
      "Galle Fort is a historical, archaeological and architectural heritage monument located in the Bay of Galle on the southwestern coast of Sri Lanka. The fort was first built by the Portuguese in 1588 and later extensively fortified by the Dutch during the 17th century.",
    whyVisit: [
      "Elephant Paradise – Home to large herds of Sri Lankan elephants, especially seen during the dry season.",
      "Birdwatcher’s Delight – A sanctuary for endemic and migratory birds such as painted storks, peafowls, and pelicans.",
      "Untouched Wilderness – One of Sri Lanka’s smallest yet most scenic national parks, with open grasslands, lakes, and forested areas.",
      "Tranquil Escape – Peaceful and less crowded compared to other major national parks.",
      "Nature Photography – Ideal for capturing elephants grazing by the lakes and birds flying over wetlands."

    ],
    overview:
    "Situated about 16 km inland from Arugam Bay, Lahugala National Park is a serene wildlife haven that forms part of an important elephant corridor connecting to Yala East and Gal Oya National Parks. Despite its small size, it boasts incredible biodiversity, from majestic elephants and spotted deer to colorful bird species that thrive around its three main tanks: Lahugala, Kitulana, and Sengamuwa. The park’s tranquil environment, golden grasslands, and reflective waters create a magical landscape, especially at sunrise and sunset.",
    ticketsEntry: {
      residents: "LKR 150–500 per person.",
      foreigners: "LKR 1,500–3,000 (~USD 5–10) per person.",
      children: "",
      notes: [
       "Safari Jeep (Optional): LKR 6,000–10,000 (~USD 18–30) per vehicle, depending on duration.",
        " Prices may change; please check locally before your visit."

       
      ]
    },
    highlights: [
     "Elephant Herds – Best viewed around Lahugala Tank in the evening.",
      "Avian Life – Spot painted storks, purple herons, egrets, and lesser whistling ducks.",
      "Scenic Landscapes – Open plains surrounded by forests and shimmering lakes.",
     "Wildlife Photography – Perfect for natural light photography during golden hours.",
    "Nature Experience – Ideal for those seeking a calm, authentic safari away from crowds."

      
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "July to October – when elephant gatherings are most common.",
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
    "From Arugam Bay: ~30 minutes by car (16 km)",
    "From Monaragala: ~1 hour 15 minutes (60 km)",
    "Nearest Town: Pottuvil"

    ], 
    publicTransport: "tuk-tuks for local travel",
    parking: "Available near the park entrance"
    },  
    nearbyHotels: [
      " Jetwing Surf – Eco-luxury stay near Arugam Bay.",
      " Kottukal Beach House by Jetwing – Boutique stay close to the park",
      " Local Guesthouses – Affordable stays in Pottuvil and Panama area.."
    ],
    travelTips: [
      "Wear light, breathable clothing in neutral colors.",
      "Bring binoculars, water, and insect repellent.",
      "Maintain silence and avoid feeding animals.",
      "Visit early morning or late evening for best wildlife sightings"

      
    ],
    didYouKnow: "Lahugala National Park is part of an ancient elephant migration route and supports one of the highest densities of wild elephants in Sri Lanka. The nearby Magul Maha Viharaya, an ancient Buddhist temple within the park, adds a touch of cultural heritage to this natural sanctuary..",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Magul-Maha-Viharaya",
    title: "Magul Maha Viharaya – The Temple of the Great Royal Wedding",
    town: "Lahugala",
    district: "Ampara",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Mangul maha Viharaya/Magul-Viharaya-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Mangul maha Viharaya/Magul-Viharaya-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Mangul maha Viharaya/Magul-Viharaya-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Mangul maha Viharaya/Magul-Viharaya-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Mangul maha Viharaya/Magul-Viharaya-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Mangul maha Viharaya/Magul-Viharaya-5.jpg`,
    ],
    shortDescription:
      "Step into the ancient world at Magul Maha Viharaya, where royal love, faith, and the beauty of nature converge in timeless harmony",  
    fullDescription:
      "Galle Fort is a historical, archaeological and architectural heritage monument located in the Bay of Galle on the southwestern coast of Sri Lanka. The fort was first built by the Portuguese in 1588 and later extensively fortified by the Dutch during the 17th century.",
    whyVisit: [
      "Royal Legacy – Believed to be the site where King Kavan Tissa married Princess Viharamahadevi, an event that united two ancient kingdoms.",
      "Historic Buddhist Shrine – A sacred temple dating back over 2,000 years, reflecting the grandeur of ancient Ruhuna civilization.",
      "Peaceful Surroundings – Hidden in the forests near Lahugala, offering a serene, meditative atmosphere away from crowds.",
      "Architectural Beauty – Features ancient stupas, moonstones, guard stones, and inscriptions carved with remarkable craftsmanship.",
      "Cultural & Scenic Appeal – Perfect for those who love history, archaeology, and nature photography."

    ],
    overview:
    "Nestled beside Lahugala National Park in the Ampara District, Magul Maha Viharaya is a treasured archaeological site that combines royal history and Buddhist spirituality. Built by King Kavan Tissa around the 2nd century BCE, the temple commemorates his marriage to Princess Viharamahadevi, whose legendary journey and sacrifice symbolize courage and faith. The temple complex spans several acres, featuring stone terraces, image houses, and relics of ancient monasteries surrounded by tranquil greenery. With its peaceful ambiance and connection to both history and legend, it remains one of Sri Lanka’s most revered sacred sites.",
    ticketsEntry: {
      residents: "Free (Donations appreciated for temple upkeep).",
      foreigners: "Free (Donations appreciated for temple upkeep).",
      children: "Free",
      notes: [
        "Prices may change; please check locally before your visit"
       
      ]
    },
    highlights: [
    "Ancient Stupas & Image Houses – Explore beautifully preserved ruins and sacred relics.",
    "Royal Heritage Site – The legendary setting of Sri Lanka’s most famous royal marriage.",
    "Inscriptions & Carvings – Discover ancient Brahmi scripts and stone artistry.",
    "atural Beauty – Surrounded by forest and wildlife, creating a calm and spiritual aura.",
    "Photography & Exploration – Capture centuries-old ruins framed by lush greenery."
      
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 7:00 PM",
      bestTime: "May to September (surfing season)",
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
    "From Arugam Bay: ~30 minutes by car (20 km)",
    "From Pottuvil: ~20 minutes by car",
    "From Lahugala National Park: ~10 minutes drive"

    ], 
    publicTransport: "Buses and tuk-tuks for local travel",
    parking: "Available near the entrance."
    },  
    nearbyHotels: [
      " Jetwing Surf – Luxury eco-resort in Pottuvil.",
      " Kottukal Beach House by Jetwing – Boutique stay near Lahugala..",
      " Local Guesthouses – Budget-friendly options in Pottuvil and Panama."
    ],
    travelTips: [
    "Dress modestly – shoulders and knees covered.",
    "Remove shoes and hats before entering sacred areas.",
    "Bring water, sunscreen, and a hat.",
    "Be mindful of elephants that may wander nearby from the park."

      
    ],
    didYouKnow: "The temple’s name, “Magul Maha Viharaya,” means “The Great Wedding Temple.” Legend says the royal wedding of King Kavan Tissa and Princess Viharamahadevi was held here, and the temple’s stone platform marks the ceremonial site. Ancient chronicles also suggest that the temple was later expanded and renovated by several kings throughout Sri Lanka’s history.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Panama-Tank",
    title: "Panama Tank – A Serene Wetland Haven in the East",
    town: "Arugam Bay",
    district: "Ampara",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Panama Tank/panama-Tank-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Panama Tank/panama-Tank-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Panama Tank/panama-Tank-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Panama Tank/panama-Tank-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Panama Tank/panama-Tank-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Ampara/Panama Tank/panama-Tank-5.jpg`,
    ],
    shortDescription:
    "✨ Discover the peaceful charm of Panama Tank, where shimmering waters, village life, and wild beauty meet under Sri Lanka’s endless eastern skies.",
    fullDescription:
      "Galle Fort is a historical, archaeological and architectural heritage monument located in the Bay of Galle on the southwestern coast of Sri Lanka. The fort was first built by the Portuguese in 1588 and later extensively fortified by the Dutch during the 17th century.",
    whyVisit: [
      "Scenic Reservoir Views – A tranquil tank surrounded by lush greenery, paddy fields, and distant mountains.",
      "Wildlife Encounters – Elephants, water buffaloes, and migratory birds often gather along its shores.",
      "Cultural Significance – Vital to local farming communities and deeply connected to the village life of Panama",
      "Peaceful Ambiance – Ideal for quiet reflection, photography, and enjoying nature away from the crowds",
      "Sunrise & Sunset Beauty – The still waters mirror the sky’s colors, creating breathtaking reflections"

    ],
    overview:
    "Located just south of Arugam Bay, the Panama Tank (also known as Panama Wewa) is one of the most picturesque reservoirs in Sri Lanka’s Eastern Province. Built in ancient times for irrigation, the tank remains a lifeline for nearby paddy fields and wildlife. The calm waters, surrounded by forest and grasslands, attract elephants during the dry season and flocks of birds year-round. Visitors can enjoy a peaceful stroll along its banks or take in panoramic views from the dam at sunrise or sunset. Its proximity to Kudumbigala Monastery and Lahugala National Park makes it a great stop for nature lovers and photographers exploring the region.",
    ticketsEntry: {
      residents: " Free to visit and explore..",
      foreigners: " Free to visit and explore.",
      children: " Free to visit and explore.",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
      "Panoramic Views – Sweeping landscapes of water, greenery, and village life.",
    "Elephants & Birds – Spot herds cooling off and birds feeding along the shore.",
    "Rural Tranquility – Experience authentic Sri Lankan countryside charm.",
    "Photography – Excellent lighting and reflections, especially at dusk.",
    "Relaxation – A peaceful escape for meditation, picnics, or quiet moments by the water."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "Early morning or late afternoon for wildlife sightings and soft light.",
    },
   gettingThere: {
    fromTown: "Early morning or late afternoon for wildlife sightings and soft light.",
    fromNearby: [
    "From Lahugala National Park: ~35 minutes",
    "Nearest Town: Panama"

    ], 
    publicTransport: "",
    parking: ""
    },  
    nearbyHotels: [
      " Jetwing Surf – Luxury eco-resort in Pottuvil.",
      " Dee & Relax Inn Panama – Cozy local guesthouse near the tank",
      " The Blue Wave Hotel – Mid-range stay near Arugam Bay."
    ],
    travelTips: [
      " Bring binoculars for birdwatching.",
      " Wear light, breathable clothes and insect repellent.",
      "Avoid approaching wild elephants; observe from a distance",
      " Respect local communities and avoid littering near the water.",
      
    ],
    didYouKnow: "The Panama Tank is part of an ancient irrigation system dating back to Sri Lanka’s early hydraulic civilization. During the dry season, it becomes a vital watering point for elephants migrating between Lahugala and Kumana National Parks, offering visitors a rare opportunity to see wildlife in a calm, natural setting.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },

  // batticaloa District
  {
    id: "Batticaloa-Fort",
    title: "Batticaloa Fort – Echoes of Colonial Grandeur",
    town: "Batticaloa",
    district: "Batticaloa",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Batticlo Fort/Batticalo-Fort-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Batticlo Fort/Batticalo-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Batticlo Fort/Batticalo-Fort-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Batticlo Fort/Batticalo-Fort-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Batticlo Fort/Batticalo-Fort-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Batticlo Fort/Batticalo-Fort-5.jpg`,

    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
     "Historic Architecture – A 17th-century fort built by the Portuguese and later enhanced by the Dutch.",
      "Cultural Heritage – Learn about Sri Lanka’s colonial past.",
      "Scenic Vistas – Overlooks the lagoon with stunning water views."


    ],
    overview:
    "Built in 1628 by the Portuguese, the Batticaloa Fort stands as a silent witness to centuries of colonial history. Later occupied by the Dutch and British, this fort once served as a trading and military post guarding the eastern coast. Its weathered walls, stone bastions, and lagoon-side location give it both historical depth and quiet charm. Today, visitors can stroll through the ruins, admire ancient gateways, and enjoy sweeping views of the surrounding lagoon.",
    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
      "Panoramic Views – Sweeping landscapes of water, greenery, and village life.",
    "Elephants & Birds – Spot herds cooling off and birds feeding along the shore.",
    "Rural Tranquility – Experience authentic Sri Lankan countryside charm.",
    "Photography – Excellent lighting and reflections, especially at dusk.",
    "Relaxation – A peaceful escape for meditation, picnics, or quiet moments by the water."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: "Located in Batticaloa city center",
    fromNearby: [
    "",
    ], 
    publicTransport: "Easily accessible by tuk-tuk or foot",
    parking: ""
    },  
    nearbyHotels: [
      
    ],
    travelTips: [
      " Easily accessible by tuk-tuk or foot",
      "Visit early morning or evening for softer light.",
      "Wear comfortable shoes for exploring.",
      "Read local plaques for historical context."

      
    ],
    didYouKnow: "The fort was once surrounded entirely by water, connected to land only by a narrow causeway, making it nearly impregnable.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Batticaloa-Lagoon",
    title: "Batticaloa Lagoon – The Serene Heart of the East",
    town: "Batticaloa",
    district: "Batticaloa",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/BaticloLagon/Batticaloa-Fort-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/BaticloLagon/Batticaloa-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/BaticloLagon/Batticaloa-Fort-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/BaticloLagon/Batticaloa-Fort-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/BaticloLagon/Batticaloa-Fort-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/BaticloLagon/Batticaloa-Fort-5.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
     "Scenic Wonder – A tranquil lagoon fringed with mangroves and dotted with small islands.",
      "Boat Excursions – Sail through calm waters while spotting local fishermen at work.",
      "Nature & Wildlife – Home to herons, kingfishers, and migratory birds.",
      "Unforgettable Views – Reflections of palm trees and sunset hues across the glassy surface."


    ],
    overview:
    "Stretching over 50 km along the eastern coast, Batticaloa Lagoon is one of Sri Lanka’s largest and most picturesque lagoons. It is famous for its serene beauty, vibrant birdlife, and the communities that have thrived along its banks for centuries. A boat ride reveals the lagoon’s charm—floating islands, lush mangroves, and glimpses of daily life as local fishermen cast their nets at dawn. The still waters, often shimmering in gold at sunset, make it a paradise for photographers and peace seekers alike.",
    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
      "Guided Boat Tours – Explore mangrove forests and small fishing villages.",
    "Birdwatching – Spot pelicans, cormorants, and other migratory species.",
    "Scenic Photography – Perfect at sunrise or sunset for breathtaking reflections.",
    "Cultural Encounters – Learn about traditional fishing methods and lagoon life",
    ""

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: "~5 km (~10 minutes by car)",
    fromNearby: [
    "",
    ], 
    publicTransport: "Tuk-tuks and small boats available",
    parking: "Available near main jetties"
    },  
    nearbyHotels: [
    
    ],
    travelTips: [
      " Bring binoculars for birdwatching.",
      "Sunscreen and hats are essential.",
      "Respect local fishing communities.",
      

      
    ],
    didYouKnow: "Locals call Batticaloa “the land of the singing fish” at night, mysterious musical vibrations can sometimes be heard from the lagoon waters.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Kalkudah-Beach",
    title: "Kalkudah Beach – Serenity Beyond the Shore",
    town: "Kalkudah",
    district: "Batticaloa",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalkuda beach/Kalkudah-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalkuda beach/Kalkudah-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalkuda beach/Kalkudah-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalkuda beach/Kalkudah-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalkuda beach/Kalkudah-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalkuda beach/Kalkudah-Beach-5.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
     ".Peaceful Escape – Less crowded, ideal for quiet reflection.",
      "Golden Coastline – Unspoiled beach lined with whispering palms.",
      "Natural Beauty – Stunning sunrise views and clear horizons"


    ],
    overview:
    "Just a short drive from Pasikudah lies Kalkudah Beach, a tranquil paradise known for its untouched charm. The beach stretches for kilometers along Sri Lanka’s east coast, with soft sands and gently rolling waves that invite long walks and peaceful solitude.Once a bustling spot before the civil conflict, Kalkudah has returned as a hidden gem — a place where the world slows down. The calm sea makes it perfect for swimming or simply sitting by the water’s edge, listening to the rhythmic lap of waves and the rustle of coconut leaves.",
    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
      "Panoramic Views – Sweeping landscapes of water, greenery, and village life.",
    "Elephants & Birds – Spot herds cooling off and birds feeding along the shore.",
    "Rural Tranquility – Experience authentic Sri Lankan countryside charm.",
    "Photography – Excellent lighting and reflections, especially at dusk.",
    "Relaxation – A peaceful escape for meditation, picnics, or quiet moments by the water."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: "Located in Batticaloa city center",
    fromNearby: [
    "",
    ], 
    publicTransport: "Easily accessible by tuk-tuk or foot",
    parking: ""
    },  
    nearbyHotels: [
      
    ],
    travelTips: [
      " Easily accessible by tuk-tuk or foot",
      "Visit early morning or evening for softer light.",
      "Wear comfortable shoes for exploring.",
      "Read local plaques for historical context."

      
    ],
    didYouKnow: "The fort was once surrounded entirely by water, connected to land only by a narrow causeway, making it nearly impregnable.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Kallady-Bridge",
    title: "Kallady Bridge – Where Music Meets the Sea",
    town: "Kallady",
    district: "Batticaloa",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalladi Bridge/Kallady-Bridge-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalladi Bridge/Kallady-Bridge-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalladi Bridge/Kallady-Bridge-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalladi Bridge/Kallady-Bridge-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalladi Bridge/Kallady-Bridge-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Kalladi Bridge/Kallady-Bridge-5.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Cultural Icon – The gateway to Batticaloa’s famed singing lagoon.",
    "Scenic Views – Panoramic sunrise and sunset views over the water.",
    "Local Life – Watch fishermen casting nets as the light changes."



    ],
    overview:
    "Connecting Batticaloa city to the eastern villages, the Kallady Bridge is not only a vital link but also a place of wonder. Beneath its structure, in the still waters of the lagoon, lies one of Sri Lanka’s most enchanting mysteries, the phenomenon of the “singing fish.” Locals claim that on quiet nights, you can hear faint, melodic humming rising from the water.The bridge itself is a beautiful spot to watch the colors of dawn and dusk paint the sky. Fishermen’s boats drift lazily by, children play on the banks, and the gentle rhythm of daily life unfolds against the tranquil backdrop of the lagoon.",

    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
    "Golden Hour Views – Magical light for photographers.",
    "Local Culture – Observe daily fishing traditions.",
    "Legendary Soundscape – The site of the mysterious “singing fish"


    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: "~4 km (~10 minutes by car)",
    fromNearby: [
    "",
    ], 
    publicTransport: "Easily accessible by tuk-tuk or foot",
    parking: ""
    },  
    nearbyHotels: [
     
    ],
    travelTips: [
      "Visit at sunrise or sunset for the best atmosphere.",
      "If visiting at night, go with a local guide to experience the “singing fish.",
      "Be cautious of traffic when taking photos."


      
    ],
    didYouKnow: "In 1961, Sri Lanka Broadcasting Corporation recorded the mysterious underwater sounds near Kallady Bridge, a first-of-its-kind phenomenon still debated today",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Pasikudah-Beach",
    title: "Pasikudah Beach – The Crystal Lagoon of the East",
    town: "Pasikudah",
    district: "Batticaloa",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Pasikuda Beach/Pasikudah-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Pasikuda Beach/Pasikudah-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Pasikuda Beach/Pasikudah-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Pasikuda Beach/Pasikudah-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Pasikuda Beach/Pasikudah-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Batticalo/Pasikuda Beach/Pasikudah-Beach-1.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Shallow, Glassy Waters – Walk hundreds of meters into the sea safely.",
    "Family Paradise – Calm, warm water perfect for children and casual swimmers.",
     "Marine Adventure – Ideal for snorkeling and exploring coral reefs.",
    "Golden Horizon – Watch sunrise over the ocean and sunset behind palm trees."


    ],
    overview:
    "Renowned for its pristine beauty, Pasikudah Beach is one of Sri Lanka’s most alluring coastal jewels. The bay is protected by a natural coral reef, creating a vast shallow lagoon where the sea remains calm and clear year-round. The soft golden sands and gentle waves make it a favorite among families and travelers seeking tranquility.\n Lined with luxury resorts yet retaining its natural charm, Pasikudah offers both comfort and authenticity. Early risers can stroll along the endless shoreline as the morning sun sparkles on the water, while evenings are best spent watching the horizon fade into amber light",
    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
    "Safe Swimming – Calm, shallow water stretching far into the bay.",
    "Water Sports – Try jet skiing, kayaking, and windsurfing.",
    "Beach Walks – Long sandy stretches perfect for relaxation.",
    "Photography – Capture sunrise, reflections, and palm silhouettes."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: ": ~35 km (~1 hour by car)",
    fromNearby: [
    "",
    ], 
    publicTransport: "Easily accessible by tuk-tuk or foot",
    parking: ""
    },  
    nearbyHotels: [
     
    ],
    travelTips: [
    "Swimwear recommended; modest dress when leaving the beach.",
    "Carry sunscreen, towel, and bottled water.",
    "Early mornings are quietest and most serene."


      
    ],
    didYouKnow: "Pasikudah means “Green-Algae Bay”, named for the natural hue of its shallow sea when sunlight filters through the water.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  // Trincomalee District
  {
    id: "Fort-Frederick",
    title: "Fort Frederick – Historic Coastal Fortress",
    town: "Trincomalee",
    district: "Trincomalee",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Fort Frederick/Fort-Frederick-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Fort Frederick/Fort-Frederick-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Fort Frederick/Fort-Frederick-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Fort Frederick/Fort-Frederick-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Fort Frederick/Fort-Frederick-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Fort Frederick/Fort-Frederick-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Fort Frederick/Fort-Frederick-1.jpg`,

    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Colonial History – Built by the Portuguese and expanded by the Dutch and British.",
    "Photography – Stunning views of Trincomalee harbor and ocean.",
    "Historical Exploration – Old cannons, ramparts, and military architecture.",
    "Scenic Spot – Elevated location offering panoramic coastal views.",



    ],
    overview:
    "Fort Frederick, located near the entrance to Trincomalee harbor, is a colonial-era fort built in the 17th century. It played a strategic role during Portuguese, Dutch, and British rule and remains a key historical landmark.",
    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
    "Ramparts & Cannons – Historic military structures with ocean views.",
    "Panoramic Views – Scenic vistas of harbor and coastline.",
    "Historical Significance – Insight into Sri Lanka’s colonial past.",


    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "Open all day",
      bestTime: "",
    },
   gettingThere: {
    fromTown: ": ~35 km (~1 hour by car)",
    fromNearby: [
    "",
    ], 
    publicTransport: "Easily accessible by tuk-tuk or foot",
    parking: ""
    },  
    nearbyHotels: [
    "Jungle Beach by Uga Escapes – Luxury stay nearby",
    "Mid-range accommodations in Trincomalee city"

    ],
    travelTips: [
    "Comfortable shoes recommended for walking around ramparts.",
    "Bring water and sun protection.",
    "Respect the site; do not climb unsafe areas."


      
    ],
    didYouKnow: "Fort Frederick is still used by the Sri Lankan military, so some areas may be restricted to visitors.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  
  {
    id: "Koneswaram-Temple",
    title: "Koneswaram Temple – Ancient Hindu Shrine",
    town: "Trincomalee",
    district: "Trincomalee",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Koneshwaram Temple/Koneswaram-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Koneshwaram Temple/Koneswaram-Temple-1.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Koneshwaram Temple/Koneswaram-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Koneshwaram Temple/Koneswaram-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Koneshwaram Temple/Koneswaram-Temple-1.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Koneshwaram Temple/Koneswaram-Temple-1.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Sacred Pilgrimage Site – Dedicated to Lord Shiva, with centuries of spiritual history.",
  "Architectural Beauty – Ornate carvings, towers, and statues overlooking the sea.",
  "Photography – Stunning coastal views and temple architecture.",
  "Spiritual Experience – Observe rituals and cultural traditions.",
  "Scenic Location – Situated on Swami Rock, offering panoramic vistas."



    ],
    overview:
    "Koneswaram Temple, also called the “Temple of a Thousand Pillars,” is located in Trincomalee on Swami Rock. With origins dating back over 2,000 years, it is a key pilgrimage site and a masterpiece of Dravidian architecture.",
    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
    "Sacred Shrine – Main temple dedicated to Lord Shiva.",
    "Coastal Views – Overlooks the Bay of Bengal and Trincomalee harbor.", 
    "Cultural Significance – Center of Hindu worship and festivals.",
    "Photography – Capture architecture and scenic vistas."



    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 8:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: "2 km (~5 minutes by car)",
    fromNearby: [
    "",
    ], 
    publicTransport: "Easily accessible by tuk-tuk or foot",
    parking: ""
    },  
    nearbyHotels: [
    "Jungle Beach by Uga Escapes – Luxury stay nearby",
    "Mid-range guesthousesin Trincomalee city"

    ],
    travelTips: [
    "Dress modestly; cover shoulders and knees.",
    "Remove footwear before entering sacred areas.",
    "Respect rituals and ceremonies."


      
    ],
    didYouKnow: "Koneswaram Temple is often called the “Temple of a Thousand Pillars” and is linked to local legends of ancient kings and the epic Ramayana.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Marble-Beach",
    title: "Marble Beach – Secluded Coastal Beauty",
    town: "Trincomalee",
    district: "Trincomalee",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Marble beach/Marble-Beach-1.jpg`,
    images: [
    `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Marble beach/Marble-Beach-1.jpg`,
    `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Marble beach/Marble-Beach-1.jpg`,
    `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Marble beach/Marble-Beach-1.jpg`,
    `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Marble beach/Marble-Beach-1.jpg`,
    `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Marble beach/Marble-Beach-1.jpg`,
      
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Pristine Sandy Beach – White sand and clear waters ideal for relaxation",
    "Photography Spot – Beautiful shoreline and sunsets.",
    "Peaceful Retreat – Less crowded than main beaches, offering solitude",
    "Swimming & Sunbathing – Calm waters perfect for leisurely swims.",



    ],
    overview:
    "Marble Beach, located near Trincomalee, is a hidden gem known for its soft sands, turquoise waters, and serene atmosphere. It’s ideal for visitors seeking a quiet beach experience away from crowds.",
    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
    "Swimming – Calm waters safe for bathing.",
    " Sunbathing – Soft sand for relaxation.",
    "Photography – Capture scenic shoreline and sunsets.",
    "Quiet Escape – Ideal for peaceful retreats."


    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: "12 km (~25 minutes by car)",
    fromNearby: [
    "",
    ], 
    publicTransport: "Tuk-tuks or private vehicles recommended",
    parking: "Limited, near beach access"
    },  
    nearbyHotels: [
    "Jungle Beach by Uga Escapes – Luxury stay nearby",
    "Mid-range accommodations in Trincomalee city"

    ],
    travelTips: [
    "Swimwear recommended; cover up when leaving beach areas.",
    "Bring water and sun protection.",
    "Respect nature; avoid littering"


      
    ],
    didYouKnow: "Marble Beach is named for its smooth white sands that resemble polished marble under sunlight.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Nilaveli-Beach",
    title: "Nilaveli Beach – Pristine Coastal Paradise",
    town: "Nilaveli",
    district: "Trincomalee",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Nilaweli Beach/Nilaweli Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Nilaweli Beach/Nilaweli Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Nilaweli Beach/Nilaweli Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Nilaweli Beach/Nilaweli Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Nilaweli Beach/Nilaweli Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Nilaweli Beach/Nilaweli Beach-1.jpg`,
      
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "White Sandy Beach – Soft sands and crystal-clear waters perfect for relaxation.",
    "Swimming & Sunbathing – Calm waters ideal for families and leisurely swims.",
    "Photography – Stunning sunrises and ocean views.",
    "Water Activities – Snorkeling, diving, and boat trips nearby.",
    "Peaceful Atmosphere – Less crowded than other beaches, offering tranquility."



    ],
    overview:
    "Nilaveli Beach, located on the eastern coast of Sri Lanka near Trincomalee, is known for its long stretches of white sand, turquoise waters, and serene environment. It’s a favorite spot for beach lovers seeking calm and natural beauty.",
    ticketsEntry: {
      residents: " Free",
      foreigners: " Free",
      children: " Free",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
    "Calm Waters – Safe for swimming and water sports",
    "Sunbathing – Stretch out on soft, clean sand.",
    "Photography – Capture scenic seascapes and sunrises.",
    "Snorkeling & Diving – Explore nearby coral reefs and marine life."


    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "Early morning or late afternoon",
    },
   gettingThere: {
    fromTown: "15 km (~30 minutes by car)",
    fromNearby: [
    ], 
    publicTransport: "Tuk-tuks or buses available",
    parking: "Available near main beach areas"
    },  
    nearbyHotels: [
    "Jungle Beach by Uga Escapes – Luxury stay nearby",
    "Mid-range accommodations in Trincomalee city"

    ],
    travelTips: [
    "Swimwear recommended; cover up when leaving the beach",
    "Bring water, sunscreen, and a hat.",
    "Respect local customs and avoid littering."


      
    ],
    didYouKnow: "Nilaveli Beach is a nesting site for sea turtles and a starting point for trips to Pigeon Island.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Pigeon-Island",
    title: " Pigeon Island – Marine Life Paradise",
    town: "Trincomalee",
    district: "Trincomalee",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Pigeon island/Pigeon-Island-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Pigeon island/Pigeon-Island-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Pigeon island/Pigeon-Island-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Pigeon island/Pigeon-Island-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Pigeon island/Pigeon-Island-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Pigeon island/Pigeon-Island-1.jpg`,
      
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Snorkeling & Diving – Vibrant coral reefs and diverse marine species.",
    "Photography – Turquoise waters and colorful fish make stunning photos.",
    " Beaches – Small sandy coves for relaxation.",
    "Boat Tours – Easy access by boat from Nilaveli Beach.",
    "Conservation Area – Protected marine environment rich in biodiversity."



    ],
    overview:
    "Pigeon Island, located about 1 km off Nilaveli Beach, is one of Sri Lanka’s two coral islands. Known for snorkeling, diving, and its marine life, it offers an unforgettable experience for nature and adventure enthusiasts.",
    ticketsEntry: {
      residents: " •	Entry & Snorkeling: USD 15–20 (~LKR 5,500–7,500)",
      foreigners: " •	Entry & Snorkeling: USD 15–20 (~LKR 5,500–7,500)",
      children: " •	Entry & Snorkeling: USD 15–20 (~LKR 5,500–7,500)",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
    "Coral Reefs – Explore reefs teeming with tropical fish.",
    "Sandy Beaches – Small beaches for sunbathing and swimming.",
    "Marine Photography – Capture underwater life and reef formations.",
    "Boat Access – Short boat ride from Nilaveli Beach."


    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "7:00 AM – 4:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
    "Nilaveli Beach: ~10–15 minutes by boat",
    ], 
    publicTransport: "Easily accessible by tuk-tuk or foot",
    parking: ""
    },  
    nearbyHotels: [
    "Jungle Beach by Uga Escapes – Luxury stay near Nilaveli",
    "Mid-range guesthouses along the coast"

    ],
    travelTips: [
    "Snorkeling gear recommended; bring water and sunscreen.",
    "Respect coral reefs; do not touch or step on them",
    


      
    ],
    didYouKnow: "Pigeon Island is a protected marine sanctuary, home to over 85 species of coral and colorful tropical fish.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Uppuveli-Beach",
    title: "Uppuveli Beach – Popular Eastern Coastline",
    town: "Trincomalee",
    district: "Trincomalee",
    province: "Eastern Province",
    provinceId: "eastern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Uppaveli Beach/Uppaveli Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Uppaveli Beach/Uppaveli Beach-1.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Uppaveli Beach/Uppaveli Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Uppaveli Beach/Uppaveli Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Uppaveli Beach/Uppaveli Beach-1.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Uppaveli Beach/Uppaveli Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Eastern/Trincomalee/Uppaveli Beach/Uppaveli Beach-1.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Sandy Beach – Soft white sand and calm, clear waters.",
    "Swimming & Relaxation – Perfect for sunbathing and swimming.",
    " Photography – Stunning ocean views and sunsets.",
    "Water Sports – Snorkeling, diving, and boat trips available nearby.",
    "Vibrant Coastal Spot – Close to restaurants and resorts for convenience."



    ],
    overview:
    "Uppuveli Beach is a well-known beach near Trincomalee, offering a lively but serene environment. Visitors can enjoy swimming, sunbathing, and various water activities while appreciating the scenic eastern coastline.",
    ticketsEntry: {
      residents: " •	Entry & Snorkeling: USD 15–20 (~LKR 5,500–7,500)",
      foreigners: " •	Entry & Snorkeling: USD 15–20 (~LKR 5,500–7,500)",
      children: " •	Entry & Snorkeling: USD 15–20 (~LKR 5,500–7,500)",
      notes: [
        "Prices may change; please check locally before your visit",
       
      ]
    },
    highlights: [
    "Calm Waters – Safe for swimming.",
    "Sunbathing & Relaxation – Stretch out on soft sand.",
    "Snorkeling & Diving – Explore marine life in nearby waters.",
    "Photography – Capture sunrises, sunsets, and beachscapes."


    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "",
    },
   gettingThere: {
    fromTown: "From Trincomalee City: ~7 km (~15 minutes by car)",
    fromNearby: [
    
    ], 
    publicTransport: "Tuk-tuks and private vehicles available",
    parking: ": Available near main beach areas"
    },  
    nearbyHotels: [
    " Jungle Beach by Uga Escapes – Luxury stay nearby",
    "Mid-range accommodations along the coast"

    ],
    travelTips: [
    "Swimwear recommended; cover up when leaving the beach",
    "Bring water, sunscreen, and hat.",
    "Respect the environment; avoid littering.",
    


      
    ],
    didYouKnow: "Uppuveli Beach is one of the eastern coast’s most accessible beaches and is a gateway to snorkeling and diving excursions in the region.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },


  //North central Province
  //Anurdapura District
  {
    id: "Atamasthana",
    title: "Atamasthana – Anuradhapura",
    town: "Anuradhapura",
    district: "Anuradhapura",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Atamasthana/Atamasthana-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Atamasthana/Atamasthana-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Atamasthana/Atamasthana-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Atamasthana/Atamasthana-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Atamasthana/Atamasthana-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Atamasthana/Atamasthana-5.jpg`,
    ],
    shortDescription:
    "Atamasthana offers a profound journey into Sri Lanka’s Buddhist culture, history, and spiritual traditions, a must-visit for both pilgrims and cultural explorers.",
    fullDescription:
      "",
    whyVisit: [
    "Eight Sacred Sites – Considered the holiest Buddhist pilgrimage complex in Sri Lanka, encompassing eight ancient temples and shrines.",
    "Historical & Cultural Significance – Dating back over 2,000 years, integral to Sri Lanka’s Buddhist heritage.",
    " Peaceful Pilgrimage Experience – Serene surroundings among sacred Bodhi trees and ancient ruins.",
    "Photographer’s Delight – Stunning stupas, stone carvings, and lush gardens provide endless photo opportunities.",
    "Spiritual Insight – Experience meditation, rituals, and observe devotees performing traditional ceremonies."

    ],
    overview:{
    introduction:"Atamasthana, meaning “Eight Sacred Places,” is located in Anuradhapura, one of the world’s ancient cities. This complex includes eight highly revered Buddhist sites, each with its own history and spiritual significance:",
    points:[
    "Sri Maha Bodhi – The sacred fig tree grown from a cutting of the original Bodhi tree in India, over 2,300 years old.",
    "Ruwanwelisaya – A magnificent stupa built by King Dutugemunu, housing relics of the Buddha.",
    "Thuparamaya – The oldest stupa in Sri Lanka, marking the spot where the Buddha’s left collarbone relic is enshrined.",
    "Lovamahapaya (Brazen Palace) – Once a nine-story monastic building for monks, famous for its copper roofing.",
    "Abhayagiri Dagaba – A massive stupa and monastery complex, center for Mahayana Buddhism in ancient Anuradhapura.",
    "Jetavanaramaya – Among the tallest brick structures of the ancient world, a major center of monastic life.",
    "Mirisaveti Stupa – Built by King Dutugemunu, smaller than Ruwanwelisaya but historically significant.",
  	"Lankarama Dagaba – A stupa surrounded by a beautifully preserved brick wall, enshrining sacred relics."
    ]},
    ticketsEntry: {
      residents: " LKR 150–200",
      foreigners: " LKR 3,500 (~USD 10–11) — grants access to most Atamasthana sites",
      children: " ",
      notes: [
        "Guided Tours: Optional, ~LKR 1,500–2,500 (~USD 5–8) for historical insights",
        "Photography: Allowed; drones may require special permission",
        ": Prices may fluctuate or change at any time, so it’s recommended to check updated rates before your visit.",
       
      ]
    },
    highlights: [
    "Each site holds relics, inscriptions, or historical remains from the Anuradhapura period.",
    "	Pilgrims perform rituals and offerings, especially around Sri Maha Bodhi.",
    "	Capture golden stupas against the morning or sunset sky.",
    "	Ancient ruins, Bodhi trees, and stone carvings create stunning compositions."


    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "5:00 AM – 8:00 PM",
      bestTime: "Early morning (before 9:00 AM) for fewer crowds and serene ambiance",
    },
gettingThere: {
  fromTown: "By car: ~5–10 minutes (tuk-tuk fare ~LKR 300–500); by foot: walking possible if staying within the city center",
  fromNearby: [
    "By car from Colombo: ~4–5 hours (205 km) via A6 and A12 highways",
    "By train: Colombo–Anuradhapura (~4–5 hours) with local transport to the sites"
  ],
  publicTransport: "Local buses and three-wheelers available from Anuradhapura town center",
  parking: "Available near most temple sites, free of charge"
},
 
    nearbyHotels: [
    "Palm Garden Village Hotel – Mid-range hotel with cultural ambiance",
    "Heritage Hotel Anuradhapura – Comfortable stay near city attractions",
    "Anuradhapura City Hotel – Budget-friendly option close to Atamasthana",
    "•	Samadhi Retreat – Luxury eco-stay for spiritual travelers"

    ],
    travelTips: [
    "Dress Modestly: Cover shoulders and knees. Long pants or skirts and shirts with sleeves are required",
    "Footwear: Remove shoes and socks before entering stupas and sacred areas.",
    "Sun Protection: Carry water, a hat, and sunscreen; the sun can be intense midday.",
    "Respect Sacred Areas: Avoid loud noises and do not touch statues or relics.",
    "Guides: Hiring a local guide provides historical and spiritual context."
     
    ],
    didYouKnow: "The Sri Maha Bodhi tree is one of the oldest living trees with a recorded history and is protected by ancient laws. Atamasthana preserves over two millennia of Sri Lanka’s Buddhist and architectural heritage, making it a must-visit pilgrimage site.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "Early morning (6:00-9:00 AM) or late afternoon (4:00-6:00 PM) for comfortable temperatures and spiritual ambiance",
    duration: "Full day (6-8 hours) to visit all eight sacred sites; half day (3-4 hours) for main temples",
    accessibility: "Moderate walking required; uneven ancient pathways; most sites accessible to visitors with reasonable mobility"
  },
  {
    id: "Isurumuniya-Temple",
    title: "Isurumuniya Vihara – Anuradhapura",
    town: "Anuradhapura",
    district: "Anuradhapura",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Isurumuniya/Isurumuniya-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Isurumuniya/Isurumuniya-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Isurumuniya/Isurumuniya-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Isurumuniya/Isurumuniya-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Isurumuniya/Isurumuniya-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Isurumuniya/Isurumuniya-5.jpg`,
    ],
    shortDescription:
    "Isurumuniya Vihara offers history, art, and tranquility — perfect for culture enthusiasts, photographers, and spiritual travelers.",
    fullDescription:
      "",
    whyVisit: [
    "Ancient Buddhist Temple – Famous for its rock temple and intricate carvings dating back to the 3rd century BC",
    "Iconic Stone Sculptures – Home to the famous “Isurumuniya Lovers” and other unique carvings.",
     " Peaceful & Scenic – Nestled amidst lush gardens and rock outcrops, offering a tranquil atmosphere.",
    "Photography Opportunities – Perfect for capturing historical sculptures, rock carvings, and serene surroundings.",
    "Cultural Insight – Experience Buddhist rituals, meditation, and witness devotees offering prayers.",

    ],
    overview:{
    introduction:"Isurumuniya Vihara, located in Anuradhapura, is a historic Buddhist temple built during the reign of King Devanampiya Tissa (3rd century BC). The temple is carved into a rock outcrop and is renowned for its elaborate stone carvings, including elephants, floral motifs, and the famous “Isurumuniya Lovers” sculpture, a masterpiece of ancient Sri Lankan artistry."
    },
    ticketsEntry: {
      residents: " LKR 50–100",
      foreigners: "LKR 500 (~USD 1.50)",
      children: " ",
      notes: [
        "Guided Tours: Optional, ~LKR 1,000–1,500 (~USD 3–5)",
        "Photography: Allowed; drones may require special permission",
        "Note: Fees may fluctuate or change at any time, so check before visiting",
       
      ]
    },
    highlights: [
    "Isurumuniya Lovers: A finely carved sculpture of a couple in an intimate pose, considered a symbol of love and artistry.",
    "Elephant carvings and other intricate bas-reliefs depict Buddhist symbolism.",
    "Peaceful temple grounds surrounded by manicured gardens and ancient trees.",
    "Offers quiet meditation spots and scenic photo backdrops.",
    "Capture the intricate stone carvings in morning or late afternoon light.",
    "Gardens and rock formations provide natural framing for photos."


    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "Early morning for fewer crowds and soft light on sculptures",
    },
  gettingThere: {
  fromTown: "~5–10 minutes by tuk-tuk or car; fare LKR 300–500 ,Walking possible if staying nearby",
  fromNearby: [
    "By car from Colombo: ~4–5 hours (205 km) via A6 and A12 highways",
    "By train: Colombo–Anuradhapura (~4–5 hours), then local transport"
  ],
},
  nearbyHotels: [
    "Palm Garden Village Hotel – Mid-range hotel with cultural ambiance",
    "Heritage Hotel Anuradhapura – Comfortable stay near city attractions",
    "Anuradhapura City Hotel – Budget-friendly option close to Atamasthana",
    "Samadhi Retreat – Luxury eco-stay for spiritual travelers"

    ],
    travelTips: [
    "Dress modestly (shoulders and knees covered).",
    "Remove shoes before entering sacred areas.",
    "Bring water and sun protection.",
    "Early morning visits are best for photography and peaceful experience.",
    "Respect temple rituals and local devotees."
    
    ],
    didYouKnow: "The “Isurumuniya Lovers” sculpture is one of the most celebrated examples of ancient Sri Lankan art, carved directly into the rock and dating back over 2,000 years. The temple itself is a testament to the island’s rich Buddhist heritage and rock carving artistry.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
   {
    id: "Tissa-Wewa", 
    title: "Tissa Wewa – Ancient Reservoirs of Sri Lanka",
    town: "Anuradhapura",
    district: "Anuradhapura",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Tissa wewa/Tissa-Wewa-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Tissa wewa/Tissa-Wewa-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Tissa wewa/Tissa-Wewa-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Tissa wewa/Tissa-Wewa-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Tissa wewa/Tissa-Wewa-4.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Tissa wewa/Tissa-Wewa-5.jpg`,
    ],
    shortDescription:
    "Tissa Wewa provide a serene combination of history, nature, and photography opportunities, ideal for travelers seeking tranquility, culture, and birdwatching in Sri Lanka.",
    fullDescription:
      "",
    whyVisit: [
    "Historical Engineering Marvels – ancient reservoirs tank built during the Anuradhapura and Polonnaruwa periods, showcasing Sri Lanka’s advanced irrigation and water management systems.",
    "Birdwatcher’s Paradise – Ideal spots for spotting migratory birds, waterfowl, and native wildlife.",
     " Scenic Relaxation – Calm waters surrounded by lush greenery provide a peaceful retreat.",
    "Recreational Activities – Perfect for boating, nature walks, and photography.",
    "Photography Opportunities – Stunning reflections of sunrise and sunset over the water, with temple ruins or surrounding landscapes.",
    
    ],
    overview:{
    introduction:"Tissa Wewa, in Anuradhapura, is another historical tank constructed by King Kavan Tissa around the 3rd century BC. It supplies water to the surrounding area and is closely linked with the sacred city of Anuradhapura, making it both a functional and spiritual landmark.",
      
    },
    ticketsEntry: {
      residents: " Free (public areas)",
      foreigners: "Free (public areas)",
      children: " ",
      notes: [
        "Boat Rides: Approx. LKR 1,000–2,000 per ride (variable, may fluctuate)",
        "Photography: Allowed; no special permissions required",
        "Note: Fees may fluctuate or change at any time, so check before visiting",
       
      ]
    },
    highlights: [
    "Observe herons, kingfishers, cormorants, and other native birds.",
  	"Early morning or late afternoon offers the best wildlife activity and lighting for photography.",
    "Tissa Wewa: Paddle boats and lakeside walks provide tranquil experiences.",
    "Ancient embankments, sluices, and irrigation structures show advanced engineering techniques of early Sri Lankan kings.",
	  "played crucial roles in sustaining agriculture and settlements in their regions.",
    "Sunrise and sunset reflections on the water.",
    "Capture wildlife, lotus blooms, and nearby temples.",

    ],
    visitingHours: {
      daily: "5:00 AM – 6:00 PM",
      bestTime: "Early morning for wildlife activity or late afternoon for sunset photography",
    },
  gettingThere: {
  fromTown: "~5–10 minutes by tuk-tuk or car; fare LKR 300–500 ",
  
},
  nearbyHotels: [
    "Palm Garden Village Hotel – Mid-range hotel with cultural ambiance",
    "Heritage Hotel Anuradhapura – Comfortable stay near city attractions",
    "Anuradhapura City Hotel – Budget-friendly option close to Atamasthana",
    "Samadhi Retreat – Luxury eco-stay for spiritual travelers"

    ],
    travelTips: [
    "Wear comfortable shoes for lakeside walks or embankment trails.",
    "Bring sunscreen, hats, and water, especially during midday.",
    "Early morning visits are ideal for birdwatching.",
    "Respect local farmers’ and fishermen’s areas along the embankments.",
    "Light, breathable clothing recommended; modest attire suggested if visiting nearby temples."
    
    ],
    didYouKnow: "•	Tissa Wewa is linked with sacred Buddhist sites and was originally built to support the nearby ancient city, showcasing how water management and culture were intertwined.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Kuttam-pokuna", 
    title: "Kuttam Pokuna (Twin Ponds) – Anuradhapura",
    town: "Anuradhapura",
    district: "Anuradhapura",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Kuttam pokuna/Kuttam-pokuna-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Kuttam pokuna/Kuttam-pokuna-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Kuttam pokuna/Kuttam-pokuna-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Kuttam pokuna/Kuttam-pokuna-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Kuttam pokuna/Kuttam-pokuna-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Kuttam pokuna/Kuttam-pokuna-5.jpg`,
    ],
    shortDescription:
    "Kuttam Pokuna offers a glimpse into ancient monastic life, sophisticated engineering, and serene landscapes — perfect for history lovers, photographers, and culture enthusiasts.",
    fullDescription:
      "",
    whyVisit: [
  "Ancient Bathing Ponds – Marvel at the sophisticated twin ponds built during the Anuradhapura period for Buddhist monks.",
  "Architectural Excellence – Perfect example of ancient Sri Lankan hydraulic engineering and stone masonry.",
  "Peaceful Surroundings – Nestled near forested areas and monastic ruins, offering a serene experience.",
  "Photography Opportunities – Capture symmetrical stone structures, reflections in the water, and surrounding greenery.",
  "Cultural Insight – Learn about ancient monastic life and rituals associated with bathing and purification.",

    ],
    overview:{
    introduction:"Kuttam Pokuna, meaning “Twin Ponds,” are located in Anuradhapura, near the Abhayagiri Monastery. Built over 1,000 years ago, these ponds were used by Buddhist monks for bathing and ritual purification. The larger pond measures approximately 17m x 10m, while the smaller one is 12m x 5m. They feature stone steps, intricate drainage systems, and carved channels that demonstrate advanced engineering skills of ancient Sri Lanka.",
      
    },
    ticketsEntry: {
      residents: "LKR 50–100 for locals",
      foreigners: "LKR 500 (~USD 1.50)",
      children: " ",
      notes: [
        "Guided Tours: Optional, ~LKR 1,000–1,500 (~USD 3–5)",
        "Photography: Allowed; drones may require permission,",
        "Note: Prices may fluctuate or change at any time",
       
      ]
    },
    highlights: [
    "Twin ponds with sophisticated water filtration and overflow systems.",
    "	Stone steps and carved channels show detailed craftsmanship.",
    "Example of sustainable ancient water management.",
    "Located near forested monastery ruins.",
    "Quiet area for contemplation and photography.",
    "Symmetry of the ponds makes for striking photos.",
    "Morning light reflects beautifully on water, enhancing images.",
    "Surrounding trees and ruins provide natural frames."

    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: ": Early morning or late afternoon for soft light and fewer visitors",
    },
  gettingThere: {
  fromTown: "10 minutes by tuk-tuk or car; fare LKR 300–500,  Walking possible if staying nearby ",
  fromNearby: [
    "4–5 hours (205 km) via A6 and A12 highways",
    "Train: Colombo–Anuradhapura (~4–5 hours), then local transport"
  ]
  
},
  nearbyHotels: [
    "Palm Garden Village Hotel – Mid-range hotel with cultural ambiance",
    "Heritage Hotel Anuradhapura – Comfortable stay near city attractions",
    "Anuradhapura City Hotel – Budget-friendly option close to Atamasthana",
    "Samadhi Retreat – Luxury eco-stay for spiritual travelers"

    ],
    travelTips: [
    "Dress modestly (shoulders and knees covered).",
    "Wear comfortable walking shoes; stone steps may be slippery.", 
     "Bring water, hat, and sunscreen.",
    "Early morning visit recommended for peaceful exploration and photography.",
    "Respect surrounding ruins and avoid stepping on ancient structures."

      
    ],
    didYouKnow: "The Kuttam Pokuna demonstrate advanced hydraulic engineering, with carefully carved channels allowing water to flow and drain efficiently. These ponds are a rare surviving example of monastic bathing architecture from over a millennium ago.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Ridi-Vihara", 
    title: "Ridi Vihara (Silver Temple) – Anuradhapura",
    town: "Anuradhapura",
    district: "Anuradhapura",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Ridi Vihara/Ridi-Vihara-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Ridi Vihara/Ridi-Vihara-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Ridi Vihara/Ridi-Vihara-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Ridi Vihara/Ridi-Vihara-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Ridi Vihara/Ridi-Vihara-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Ridi Vihara/Ridi-Vihara-5.jpg`,
    ],
    shortDescription:
    "Ridi Vihara combines history, art, and tranquility, perfect for visitors interested in ancient Sri Lankan culture, architecture, and Buddhism.",
    fullDescription:
      "",
    whyVisit: [
     "Ancient Buddhist Temple – Known as the Silver Temple, built during the reign of King Dutugemunu in the 2nd century BC.",
    "Historical & Cultural Significance – Houses important Buddha statues and murals reflecting Sri Lanka’s ancient art and devotion.",
     "Peaceful & Scenic Surroundings – Nestled near natural landscapes and sacred city ruins.",
    "Photography Opportunities – Capture intricately carved statues, colorful murals, and temple architecture.",
    "Spiritual Experience – Observe traditional Buddhist rituals and offerings.",

    ],
    overview:{
    introduction:"Ridi Vihara, meaning “Silver Temple,” is located in Anuradhapura. The temple was built to honor a large silver treasure discovered during the construction of a monastery by King Dutugemunu. Ridi Vihara is renowned for its intricate stone and wooden carvings, ancient murals depicting the life of the Buddha, and beautifully preserved Buddha statues.",
      
    },
    ticketsEntry: {
      residents: "LKR 50–100 ",
      foreigners: "LKR 500 (~USD 1.50)",
      children: " ",
      notes: [
        "Guided Tours: Optional, ~LKR 1,000–1,500 (~USD 3–5)",
        "Photography: Allowed; drones may require permission,",
        "Note: Prices may fluctuate or change at any time",
       
      ]
    },
    highlights: [
"Elaborate stone carvings and wooden sculptures.",
"Murals depicting Buddhist stories and Jataka tales.",
"Houses several historic Buddha statues in varying postures.",
"Peaceful gardens and nearby forested areas.",
"Less crowded than other major temples, ideal for quiet reflection.",
"Capture murals and sculptures in soft morning light.",
"Architectural details and surrounding greenery provide excellent photo opportunities.",

    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: ": Early morning for peaceful exploration and photography",
    },
  gettingThere: {
  fromTown: "15 minutes by tuk-tuk or car; fare LKR 300–500",
  fromNearby: [
    "4–5 hours (205 km) via A6 and A12 highways",
    "Train: Colombo–Anuradhapura (~4–5 hours), then local transport"  
  ]
  
},
  nearbyHotels: [
    "Palm Garden Village Hotel – Mid-range hotel with cultural ambiance",
    "Heritage Hotel Anuradhapura – Comfortable stay near city attractions",
    "Anuradhapura City Hotel – Budget-friendly option close to Atamasthana",
    "Samadhi Retreat – Luxury eco-stay for spiritual travelers"

    ],
    travelTips: [
    " Dress modestly (cover shoulders and knees).",
    "Remove shoes before entering sacred areas.",
    "Carry water and sun protection.",
    "Respect temple rituals and devotees.",
    "Early morning visits are ideal for fewer crowds and better photography."

    ],
    didYouKnow: "Ridi Vihara got its name from the silver discovered during construction. The temple’s murals, carvings, and statues are some of the best-preserved examples of ancient Anuradhapura art, making it a culturally rich and spiritual site.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
    {
    id: "Samadhi-Buddha", 
    title: "Samadhi Buddha Statue – Anuradhapura",
    town: "Anuradhapura",
    district: "Anuradhapura",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Samadhi Buddha Statue/Samadhi-Buddha-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Samadhi Buddha Statue/Samadhi-Buddha-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Samadhi Buddha Statue/Samadhi-Buddha-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Samadhi Buddha Statue/Samadhi-Buddha-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Samadhi Buddha Statue/Samadhi-Buddha-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Anuradhapura/Samadhi Buddha Statue/Samadhi-Buddha-5.jpg`,
    ],
    shortDescription:
    "Samadhi Buddha Statue offers a blend of history, art, and tranquility — perfect for spiritual seekers, photographers, and culture enthusiasts.",
    fullDescription:
      "",
    whyVisit: [
    "Iconic Meditation Sculpture – Famous statue depicting the Buddha in deep meditation (Samadhi posture).",
    "Historical & Artistic Significance – Believed to date back to the 4th–5th century AD, showcasing ancient Sri Lankan craftsmanship.",
    "Peaceful & Serene Atmosphere – Set within the sacred Mihintale/Anuradhapura heritage area, ideal for reflection.",
    "Photography Opportunities – Capture the statue’s serene expression, surrounding ruins, and gardens.",
    "Spiritual Insight – Observe Buddhist rituals and the peaceful ambiance conducive to meditation."

    ],
    overview:{
    introduction:"The Samadhi Buddha Statue is located near the ancient city of Anuradhapura, housed in the Isurumuniya Vihara complex area. Carved from a single block of granite, the statue shows the Buddha seated cross-legged in the meditative posture, with eyes closed and hands in the Dhyana mudra. It is considered one of the finest examples of early Anuradhapura sculpture, reflecting the spiritual devotion and artistic excellence of the period.",
      
    },
    ticketsEntry: {
      residents: "LKR 50–100 ",
      foreigners: "LKR 500 (~USD 1.50)",
      children: " ",
      notes: [
        "Guided Tours: Optional, ~LKR 1,000–1,500 (~USD 3–5)",
        "Photography: Allowed; drones may require permission,",
        "Note: Prices may fluctuate or change at any time",
       
      ]
    },
    highlights: [
  "Carved from a single granite stone.",
  "Smooth, serene features capturing the essence of meditation.",
  "Represents the pinnacle of early Anuradhapura sculpture techniques.",
"Surrounded by ancient ruins and gardens.",
  "Provides a peaceful environment for meditation and reflection",
  "Soft morning or late afternoon light enhances the statue’s features.",
"Gardens and nearby ruins create natural photo frames"
    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: " Early morning for fewer crowds and peaceful atmosphere",
    },
  gettingThere: {
  fromTown: "5–10 minutes by tuk-tuk or car; fare LKR 300–500 ,Walking possible if staying nearby",
  fromNearby: [
    "4–5 hours (205 km) via A6 and A12 highways",
    "Train: Colombo–Anuradhapura (~4–5 hours), then local transport"  
  ]
  
},
  nearbyHotels: [
    "Palm Garden Village Hotel – Mid-range hotel with cultural ambiance",
    "Heritage Hotel Anuradhapura – Comfortable stay near city attractions",
    "Anuradhapura City Hotel – Budget-friendly option close to Atamasthana",
    "Samadhi Retreat – Luxury eco-stay for spiritual travelers"

    ],
    travelTips: [
    " Dress modestly (cover shoulders and knees).",
    "Remove shoes before entering sacred areas.",
    "Carry water and sun protection.",
    "Respect temple rituals and devotees.",
    "Early morning visits are ideal for fewer crowds and better photography."
      
    ],
    didYouKnow: "The Samadhi Buddha Statue is often regarded as a symbol of Sri Lanka’s ancient Buddhist art. Its calm expression and meditative posture inspire spiritual reflection, and it is a key highlight for visitors exploring the sacred city of Anuradhapura.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },

// Polonnaruwa District
{
    id: "Gal-Potha", 
    title: "Gal Potha – The Ancient Stone Books of Polonnaruwa",
    town: "Polonnaruwa",
    district: "Polonnaruwa",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Gal Potha/Gal-Potha-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Gal Potha/Gal-Potha-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Gal Potha/Gal-Potha-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Gal Potha/Gal-Potha-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Gal Potha/Gal-Potha-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Gal Potha/Gal-Potha-5.jpg`,
    ],
    shortDescription:
    "Explore the words of history etched in stone and connect with the lives, governance, and spirituality of ancient Sri Lanka.",
    fullDescription:
      "",
    whyVisit: [
    "Gal Potha, or “Stone Books,” offers a rare glimpse into the written history of medieval Sri Lanka. These stone inscriptions document royal decrees, donations, and religious guidelines from the Polonnaruwa period. For history enthusiasts and culture seekers, it’s a fascinating way to connect with the thoughts, governance, and daily life of people from centuries ago."
    ],
    overview:{
    introduction:"Located within the Polonnaruwa Archaeological Park, the Gal Potha consists of large stone slabs engraved with Sinhala script. These inscriptions, often protected by small shelters, reveal details about temples, land grants, taxes, and social regulations from the 12th–13th centuries. They are considered one of the most important epigraphical treasures of ancient Sri Lanka, offering insights into the administration and religious life of the time.",
      
    },
    ticketsEntry: {
      residents: "Archaeological Park ticket: LKR 50–200 ",
      foreigners: "Archaeological Park ticket: LKR 3,000 (~USD 9–10)",
      children: " ",
      notes: [
        "Guided tours optional (~LKR 2,000–3,000 / USD 6–10).",
        "Note: Prices may fluctuate or change at any time",
      ]
    },
    highlights: [
    "Historical Records – Ancient inscriptions detailing royal decrees, land grants, and religious activities.",
    "Cultural Significance – Provides insight into social, political, and religious life during the Polonnaruwa period.",
    "Preserved Stone Slabs – Well-maintained stones with clear carvings, protected under small shelters.",
    "Photography & Study – Great spot for historians, archaeologists, and those curious about ancient scripts.",
    "Serene Environment – Located among other archaeological ruins, offering a peaceful atmosphere for reflection.",

    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: " Early morning or late afternoon is best for fewer crowds and soft light.",
    },
  gettingThere: {
  fromTown: "Located within Polonnaruwa Archaeological Park, easily accessible by walking or cycling from central sites",
  
},
  nearbyHotels: [
    "Polonnaruwa Village Hotel (mid-range), Heritance Kandalama (luxury)",
    "The Lake Hotel (budget), and nearby guesthouses.",
    ],
    travelTips: [
   " Dress modestly if visiting nearby religious sites.",
    "Wear comfortable shoes as the area has uneven surfaces.",
    "Bring water, sunscreen, and a hat for sun protection."

      
    ],
    didYouKnow: "The inscriptions of Gal Potha are considered a treasure trove for understanding medieval Sri Lankan administration and religious practices. They provide some of the clearest examples of written records surviving from the Polonnaruwa era.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Parakrama-Samudra", 
    title: "Parakrama Samudra – The Giant Reservoir of Polonnaruwa",
    town: "Polonnaruwa",
    district: "Polonnaruwa",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Parakrama Samudra/Parakrama-Samudra-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Parakrama Samudra/Parakrama-Samudra-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Parakrama Samudra/Parakrama-Samudra-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Parakrama Samudra/Parakrama-Samudra-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Parakrama Samudra/Parakrama-Samudra-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Parakrama Samudra/Parakrama-Samudra-5.jpg`,
    ],
    shortDescription:
    "Experience the tranquility, history, and natural beauty of this monumental reservoir, reflecting both the landscape and the brilliance of ancient Polonnaruwa.",
    fullDescription:
      "",
    whyVisit: [
    "Parakrama Samudra is more than just a body of water, it’s a marvel of medieval engineering and a symbol of Sri Lanka’s advanced irrigation techniques. Stretching across vast fields, this “Sea of Parakrama” was created to support agriculture, settlements, and religious sites in the ancient capital. Today, it’s a serene spot for photography, peaceful walks, and appreciating the ingenuity of ancient Sri Lankan kings."
    ],
    overview:{
    introduction:"Built by King Parakramabahu I in the 12th century, Parakrama Samudra is a massive man-made reservoir that demonstrates the sophistication of Polonnaruwa’s hydraulic civilization. The lake reflects the surrounding greenery and ruins, creating scenic views perfect for travelers who enjoy history combined with nature. Many of the city’s temples, stupas, and settlements were supported by this reservoir, making it a lifeline of the ancient capital.",
      
    },
    ticketsEntry: {
      residents: "Free entry to the public. ",
      foreigners: "Free entry to the public.",
      children: " ",
      notes: [
        "Guided tours available upon request (~LKR 1,000–1,500 / USD 3–5).",
        "Note: Prices may fluctuate or change at any time",
      ]
    },
    highlights: [
    "Historical Significance – Built during the reign of King Parakramabahu I, showcasing medieval engineering brilliance.",
    "Vast Size – Covers over 2,500 hectares, earning the name “Sea of Parakrama.",
    "Scenic Views – Surrounded by greenery, ancient ruins, and distant temples, offering perfect spots for photography.",
    "Birdwatching – A haven for waterbirds and migratory species, ideal for nature lovers.",
    "Sunset & Sunrise – Golden light reflecting off the calm water provides stunning photographic opportunities."


    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: " Early morning or late afternoon is best for peaceful views and photography.",
    },
  gettingThere: {
  fromTown: "Located just outside Polonnaruwa’s archaeological park By car or tuk-tuk: 5–10 minutes from the city center.Walking or cycling around parts of the reservoir is also enjoyable.",
  
},
  nearbyHotels: [
   "Stay at Polonnaruwa Village Hotel or The Lake Hotel for easy access. Luxury stays like Heritance Kandalama offer panoramic views of the reservoir from afar."
    ],
    travelTips: [
    "Dress modestly if combining your visit with nearby temples.",
    "Comfortable shoes recommended for walking along embankments.",
    "Bring water, sunscreen, a hat, and binoculars if birdwatching."

      
    ],
    didYouKnow: "Parakrama Samudra was designed to irrigate over 100,000 acres of land and is still functional today, a testament to the ingenuity of Sri Lanka’s medieval engineers.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
{
    id: "Ancient-Buddha", 
    title: "Ancient Buddha Sculptures & Sacred Relics of Polonnaruwa",
    town: "Polonnaruwa",
    district: "Polonnaruwa",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Ancient Buddha/Ancient-Buddha-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Ancient Buddha/Ancient-Buddha-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Ancient Buddha/Ancient-Buddha-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Ancient Buddha/Ancient-Buddha-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Ancient Buddha/Ancient-Buddha-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Ancient Buddha/Ancient-Buddha-5.jpg`,
    ],
    shortDescription:
    "Experience ancient Buddha sculptures, sacred relics, and monastic ruins in a setting that combines history, culture, and natural beauty.",
    fullDescription:
      "",
    whyVisit: [
    "Step into a world of medieval Sri Lankan culture, where monumental Buddha statues, sacred stupas, and intricately carved temples tell stories of devotion and artistry. Explore serene monastic complexes, marvel at masterful stonework, and wander through lush surroundings alongside the Parakrama Samudra reservoir. This is a journey for history lovers, photographers, and anyone curious about the spiritual and architectural brilliance of Sri Lanka’s past."
    ],
    overview:{
    introduction:"Polonnaruwa, once Sri Lanka’s thriving capital, is now a UNESCO World Heritage site. Here, you can witness the majestic rock-carved Buddhas of Gal Vihara, the sacred relics housed in the Polonnaruwa Vatadage, and the ancient brick temples like Thuparama Gedige. Towering stupas like Rankoth Vehera, peaceful monastic ruins, and lotus-pillared pavilions all contribute to an unforgettable glimpse of medieval life, culture, and devotion.",
      
    },
    ticketsEntry: {
      residents: "Locals: LKR 50–200. ",
      foreigners: "LKR 3,000 (~USD 9–10)",
      children: " ",
      notes: [
        "Photography allowed; drone use may require permission.",
        "Note: Prices may fluctuate or change at any time",
      ]
    },
    highlights: [
    "Gal Vihara – Four massive granite-carved Buddhas in meditative postures, including reclining, standing, and seated figures.",
    "Polonnaruwa Vatadage – Circular relic house with intricate moonstones, stone pillars, and guard stones protecting sacred relics.",
    "Thuparama Gedige – Oldest brick temple blending Buddhist and Hindu architectural influences.",
    "Rankoth Vehera – Towering stupa housing sacred relics, symbolizing devotion and grandeur.",
    "Lankatilaka Image House – Elevated temple featuring a giant Buddha statue and detailed wall carvings.",
    "Hatadage – Small relic house containing multiple Buddha statues with intricate stonework.",
    "Nissanka Latha Mandapaya – Open pavilion with lotus-shaped stone pillars, used for meditation and offerings.",
    "Alahana Pirivena – Ruins of monks’ living quarters and meditation halls.",
    "Kiri Vehera – White stupa set amidst greenery, providing a serene and reflective atmosphere.",
    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "Early mornings are best for soft light, cooler temperatures, and fewer crowds.",
    },
  gettingThere: {
  fromTown: "Located just outside Polonnaruwa’s archaeological park By car or tuk-tuk: 5–10 minutes from the city center.Walking or cycling around parts of the reservoir is also enjoyable.",
  fromNearby: [
  "From Colombo: ~4–5 hours by car or train.",
  "From Sigiriya: ~1–1.5 hours by car."],
parking: "Within the park: Walking, cycling, or tuk-tuks are convenient..",
  },
  nearbyHotels: [
   "Polonnaruwa Village Hotel (mid-range)",
    "Heritance Kandalama (luxury)",
    "The Lake Hotel (budget)",
    "eco-lodges or guesthouses near archaeological sites."
    ],
    travelTips: [
    "Dress modestly (shoulders and knees covered).",
    "Comfortable shoes recommended; stone surfaces can be uneven.",
    "Bring water, sunscreen, and a hat.",
    "Hiring a local guide enhances historical and cultural understanding."

    ],
    didYouKnow: "Polonnaruwa’s medieval irrigation systems, including Parakrama Samudra, are still functional today. Its temples and statues remain some of the finest examples of Sinhalese art, offering an immersive journey through Sri Lanka’s spiritual and architectural heritage.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Vijayabahu-Museum", 
    title: "Vijayabahu Museum – Relics of Ancient Polonnaruwa",
    town: "Polonnaruwa",
    district: "Polonnaruwa",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Vijayabahu Museum/Vijayabahu-Museum-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Vijayabahu Museum/Vijayabahu-Museum-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Vijayabahu Museum/Vijayabahu-Museum-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Vijayabahu Museum/Vijayabahu-Museum-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Vijayabahu Museum/Vijayabahu-Museum-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Vijayabahu Museum/Vijayabahu-Museum-5.jpg`,
    ],
    shortDescription:
    "Step inside to see the artifacts and sculptures that reveal the grandeur, religion, and daily life of ancient Polonnaruwa.",
    fullDescription:
      "",
    whyVisit: [
    "Vijayabahu Museum is a must-visit for history enthusiasts eager to explore Polonnaruwa’s rich cultural heritage. It houses artifacts, sculptures, and tools from the medieval capital, offering a fascinating window into the daily life, religion, and artistry of Sri Lanka’s past."
    ],
    overview:{
    introduction:"Named after King Vijayabahu I, who liberated the country from Chola rule, the museum displays statues, stone inscriptions, coins, and pottery from the Polonnaruwa period. It provides context to the ruins scattered around the archaeological park, making the ancient city’s stories easier to understand for visitors. The museum is compact but packed with historical significance, making it perfect for a short, educational stop.",
      
    },
    ticketsEntry: {
      residents: ": LKR 50",
      foreigners: "LKR 500 (~USD 1.5–2)",
      children: " ",
      notes: [
        "Guided tours optional (~LKR 1,000 / USD 3)",
        "Note: Prices may fluctuate or change at any time",
      ]
    },
    highlights: [
    "Ancient Artifacts – Statues, sculptures, and tools from medieval Polonnaruwa.",
    "Historical Insights – Learn about governance, religion, and daily life in the 11th–13th centuries.",
    "Stone Inscriptions – Connect with royal decrees and cultural records preserved in stone.",
    "Cultural Preservation – Offers a closer look at items excavated from temples, stupas, and monasteries.",
    "Photography & Study – Ideal for historians, students, and curious travelers."

    ],
    visitingHours: {
      daily: "8:30 AM – 5:00 PM",
      bestTime: "Morning visits are quieter and best for photography",
    },
  gettingThere: {
  fromTown: "Located within Polonnaruwa Archaeological Park, near the main entrance and easy to reach by walking or tuk-tuk from other ruins.",
  fromNearby: [
 
  ],
parking: "",
  },
  nearbyHotels: [
      "Polonnaruwa Village Hotel",
       "The Lake Hotel",
       "Heritance Kandalama, and nearby guesthouses."
    ],
    travelTips: [
    "Dress modestly and wear comfortable shoes.",
    "Bring water and a hat, especially if combining with outdoor site visits.",
      
    ],
    didYouKnow: "King Vijayabahu I restored Polonnaruwa as a thriving capital after reclaiming the kingdom from Chola invaders, and this museum preserves many treasures from his era",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Watadage-Ruins", 
    title: "Watadage Ruins – The Circular Relic House of Polonnaruwa",
    town: "Polonnaruwa",
    district: "Polonnaruwa",
    province: "North-Central-Province",
    provinceId: "north-central",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Watadagaya/Watadagaya-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Watadagaya/Watadagaya-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Watadagaya/Watadagaya-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Watadagaya/Watadagaya-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Watadagaya/Watadagaya-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Central/Polonnaruwa/Watadagaya/Watadagaya-5.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "The Watadage stands as a testament to the architectural brilliance of ancient Sri Lanka. Its circular design, intricate carvings, and historical significance make it a must-visit for those interested in the island's rich Buddhist heritage."
    ],
    overview:{
    introduction:"Believed to have been constructed during the reign of King Parakramabahu I or King Nissanka Malla, the Watadage was designed to house and protect sacred Buddhist relics. The structure features two concentric circular platforms adorned with elaborate stone carvings, including moonstones, guardstones, and stone pillars. At the center of the upper platform sits a small stupa, surrounded by four seated Buddha statues facing the cardinal directions.",
      
    },
    ticketsEntry: {
      residents: "Approximately LKR 50–200",
      foreigners: "Approximately LKR 3,750 (~USD 10)",
      children: " ",
      notes: [
        "Inclusions: Entry to the Polonnaruwa Archaeological Park, which encompasses various historical sites, including the Watadage",
        "Note: Prices may fluctuate or change at any time",
      ]
    },
    highlights: [
    "Architectural Marvel: The circular design and layered platforms are unique to Sri Lankan architecture.",
    " Intricate Carvings: Detailed moonstones, guardstones, and stone pillars showcase the craftsmanship of the era.",
    "Sacred Relics: The structure was built to house and protect sacred Buddhist relics.",
    "Historical Significance: Offers insights into the religious practices and architectural advancements of ancient Sri Lanka."


    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "Early morning or late afternoon for cooler temperatures and fewer crowds.",
    },
  gettingThere: {
  fromTown: "",
  fromNearby: [
    "From the Main Entrance: The Watadage is located within the Polonnaruwa Archaeological Park. Upon entering through the main gate, proceed towards the Northern Quadrangle (Dalada Maluwa). The Watadage is situated nearby, easily accessible by foot.",
    "By Vehicle: If you're traveling by car or tuk-tuk, the Watadage is approximately a 5–10 minute drive from the main entrance, depending on traffic and the specific route taken."

  ],
parking: "",
  },
  nearbyHotels: [
    "EKHO Lake House: Located approximately 0.65 km from the Watadage, offering lakeside views and comfortable amenities.",
    "Man Guest Polonnaruwa: A highly rated guesthouse known for its hospitality and proximity to historical sites.",
  	"Geethani Tourist Home: A budget-friendly option with essential amenities, situated near the archaeological park.",
    "Radha Tourist Home: Offers affordable stays with easy access to the ancient city ruins.",
    "Deer Garden Polonnaruwa: Provides a tranquil environment with garden views, ideal for relaxation after a day of exploration."

    ],
    travelTips: [
    "Dress Modestly: As the Watadage is a religious site, ensure that shoulders and knees are covered.",
    "Footwear: Wear comfortable shoes suitable for walking on uneven stone surfaces.",
    "Essentials: Bring water, sunscreen, and a hat to protect against the sun.",
    "Guided Tours: Hiring a local guide can enhance your understanding of the site's history and significance."

    ],
    didYouKnow: "The Watadage is considered one of the finest examples of circular relic houses in Sri Lanka. Its preservation and architectural design reflect the advanced engineering and artistic skills of the Polonnaruwa period.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  // North western Province
  //Kurunagla District
    {
    id: "Kurunegala-Rock", 
    title: "Kurunegala Rock – The Elephant-Shaped Rock of History",
    town: "Kurunegala",
    district: "Kurunegala",
    province: "North-Western-Province",
    provinceId: "north-western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Kurunegala Rock/Kurunegala-Rock-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Kurunegala Rock/Kurunegala-Rock-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Kurunegala Rock/Kurunegala-Rock-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Kurunegala Rock/Kurunegala-Rock-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Kurunegala Rock/Kurunegala-Rock-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Kurunegala Rock/Kurunegala-Rock-5.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Kurunegala Rock, also known as Ethagala or “Elephant Rock,” is an iconic natural landmark rising above the city. Apart from offering panoramic views of Kurunegala, it holds historical significance with ancient Buddhist shrines and monasteries carved into its slopes. It’s perfect for travelers who enjoy light hiking, photography, and exploring cultural heritage sites in a serene setting."
    ],
    overview:{
    introduction:"The rock rises about 325 meters above sea level and has been a strategic location for centuries. Surrounding its base are the remains of ancient royal palaces and monasteries, while the summit provides breathtaking views of the city, coconut plantations, and distant hills. Several small shrines and meditation spots make it a spiritually significant site for locals and visitors alike.",
      
    },
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Donations welcome at shrines",
        "Note: Prices may fluctuate or change at any time.",
      ]
    },
    highlights: [
      "Iconic Shape – The rock resembles a giant elephant, earning it the name Ethagala.",
      "Panoramic Views – Stunning vistas of Kurunegala city, farmland, and surrounding hills from the summit.",
      "Ancient Monasteries – Historical Buddhist shrines and meditation spots dotting the rock.",
      "Hiking & Adventure – A moderate climb with stairs leading to the top, suitable for casual trekkers.",
      "Photography & Nature – Ideal for sunrise or sunset photography and capturing cityscapes from above"
    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "•	Early morning visits recommended for cooler temperatures, fewer crowds, and best light for photography.",
    },
  gettingThere: {
  fromTown: "•	From Kurunegala City Center: The base of the rock is easily accessible by tuk-tuk, taxi, or a 10–15 minute walk.",
  fromNearby: [
   "•	From Colombo: ~4–5 hours by car (190 km)"
  ],
parking: "Available near the base for cars and tuk-tuks.",
  },
  nearbyHotels: [
    "•	Rock Side Hotel Kurunegala – Comfortable stay close to the rock with city views.",
    "•	Mango Villa Hotel – Budget-friendly and centrally located.",
    "•	Ramada by Wyndham Kurunegala – Mid-range hotel with modern amenities.",
    "•	Villa Kurunegala – Guesthouse option close to the city and rock.",
    "•	Nearby guesthouses – Multiple small lodges within walking distance of city attractions."


    ],
    travelTips: [
      "Wear comfortable shoes for climbing the stairs.",
      "Dress modestly when visiting shrines on the rock.",
      "Carry water and sunscreen; the climb can be sunny and warm.",
      "Early morning is best for fewer crowds and cooler temperatures."

    ],
    didYouKnow: "Kurunegala Rock has served as a natural fortress and lookout point throughout history. Its summit not only offers magnificent views but also provides insight into the city’s strategic and cultural significance in medieval Sri Lanka.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Panduwasnuwara", 
    title: " Panduwasnuwara – Ancient Capital Ruins of Sri Lanka",
    town: "Panduwasnuwara",
    district: "Kurunegala",
    province: "North-Western-Province",
    provinceId: "north-western",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Panduwasnuwara/panduwasnuwara-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Panduwasnuwara/panduwasnuwara-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Panduwasnuwara/panduwasnuwara-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Panduwasnuwara/panduwasnuwara-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Panduwasnuwara/panduwasnuwara-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Panduwasnuwara/panduwasnuwara-5.jpg`,
    ],
    shortDescription:
    "Walk through ancient palace ruins, explore sacred shrines, and uncover the history of one of Sri Lanka’s former capitals.",
    fullDescription:
      "",
    whyVisit: [
    "Panduwasnuwara offers a glimpse into Sri Lanka’s medieval history as a former capital city. Visitors can explore ancient palace ruins, monasteries, and water management systems, making it an excellent destination for history buffs, architecture enthusiasts, and cultural travelers."
    ],
    overview:{
    introduction:"Panduwasnuwara was the capital of the North Western Province during the 12th century. The site includes remnants of the royal palace, temples, Buddhist shrines, and ancient irrigation tanks that supported the settlement. Its compact layout allows visitors to wander through centuries-old ruins while appreciating the ingenuity of medieval urban planning.",
      
    },
    ticketsEntry: {
      residents: "LKR 50–200",
      foreigners: "LKR 1,500–2,000 (~USD 5–6)",
      children: "",
      notes: [
        "•	Guided tours available (~LKR 1,500 / USD 5)",
        "Note: Prices may fluctuate or change at any time.",
      ]
    },
    highlights: [
      "Royal Palace Ruins – Remnants of the king’s residence, with stone foundations and columns.",
      "Buddhist Monasteries & Shrines – Small temples and image houses scattered around the site.",
      "Ancient Irrigation Tanks – Historical water management system still visible today.",
      "Cultural Significance – Offers insight into the political and religious life of medieval Sri Lanka.",
      "Photography & Exploration – Quiet, scenic site ideal for capturing ruins against natural backdrops.",

    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "•	Morning visits recommended for cooler weather and fewer crowds.",
    },
  gettingThere: {
  fromTown: "•	From Kurunegala: ~1 hour by car or tuk-tuk (approx. 30 km)",
  fromNearby: [
   "•	From Colombo: ~3.5–4 hours by car (~140 km)"
  ],
parking: " Available near the entrance; easy walking access to main ruins.",
  },
  nearbyHotels: [
    "•	Kurunegala City Hotels: Rock Side Hotel, Mango Villa, Villa Kurunegala (~30–45 min drive)",
    "•	Budget Guesthouses: Available in nearby towns for shorter stays.",
    "•	Heritage Lodges: Eco-friendly or boutique lodges in the surrounding region."

    ],
    travelTips: [
      
    "Wear comfortable shoes for walking across uneven surfaces.",
    "Dress modestly when visiting shrines.",
    "Bring water, a hat, and sunscreen.",
    "Hiring a local guide enhances understanding of historical and religious significance."
      
    ],
    didYouKnow: "Panduwasnuwara was once a strategic administrative and military center and played a key role in the North Western Province’s governance during the medieval era.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Ridi-Viharaya", 
    title: "Ridi Viharaya – The Silver Temple of Kurunegala",
    town: "Kurunegala",
    district: "Kurunegala",
    province: "North-Western-Province",
    provinceId: "north-western",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Ridi Viharaya/Ridi-Viharaya-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Ridi Viharaya/Ridi-Viharaya-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Ridi Viharaya/Ridi-Viharaya-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Ridi Viharaya/Ridi-Viharaya-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Ridi Viharaya/Ridi-Viharaya-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Ridi Viharaya/Ridi-Viharaya-5.jpg`,
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
    "Ridi Viharaya, meaning “Silver Temple,” is a vibrant Buddhist temple complex famous for its historical significance, intricate architecture, and serene atmosphere. It is an essential stop for travelers interested in Sri Lankan religious heritage, art, and colorful temple rituals."
    ],
    overview:{
    introduction:"Ridi Viharaya was built in the 2nd century BC after a silver treasure was discovered at the site during King Dutugemunu’s reign. The temple complex features beautifully painted murals, statues of Buddha, and a mix of Kandyan and traditional Sinhalese architectural elements. Visitors can explore meditation halls, image houses, and a peaceful courtyard, all reflecting centuries of devotion and craftsmanship.",
      
    },
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "•	Donations welcome",
        "Note: Prices may fluctuate or change at any time.",
      ]
    },
    highlights: [
    "Historic Significance – Built following the discovery of a silver treasure; connected to King Dutugemunu.",
    "Colorful Murals & Statues – Walls and ceilings adorned with intricate paintings and Buddha statues.",
    "Sacred Courtyards – Quiet spaces for reflection and meditation.",
    "Architectural Charm – Combines Kandyan and traditional Sinhalese styles.",
    "Photography Opportunities – Perfect for capturing cultural and religious artistry."

    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "•	Early morning is ideal for a peaceful visit and best lighting for photos",
    },
  gettingThere: {
  fromTown: "•	From Kurunegala City Center: ~10–15 minutes by tuk-tuk or taxi",
  fromNearby: [
   "•	From Colombo: ~3–4 hours by car (approx. 140 km)"
  ],
parking: " Available near the temple entrance.",
  },
  nearbyHotels: [
    "•	Rock Side Hotel Kurunegala – Comfortable stay close to city attractions. ", 
    "•	Mango Villa Hotel – Budget-friendly option within the city.",
    "•	Ramada by Wyndham Kurunegala – Mid-range hotel with modern amenities.",
    "•	Villa Kurunegala – Guesthouse option near the city center.",
    "•	Other guesthouses – Multiple small lodges offering convenient access to Kurunegala temples."

    ],
    travelTips: [
      
    	"Dress modestly: cover shoulders and knees.",
      "Remove shoes before entering temple buildings.",
      "Carry water, especially during hot days.",
      "Respect rituals and quiet areas for meditation."
    ],
    didYouKnow: "Ridi Viharaya’s name comes from the discovery of a silver ore treasure at the site, which was then used to fund its construction. Its murals and statues offer a glimpse into the artistry and religious devotion of ancient Sri Lanka.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
   {
    id: "Yapahuwa-Rock Fortress", 
    title: "Ridi Viharaya – The Silver Temple of Kurunegala",
    town: "Yapahuwa",
    district: "Kurunegala",
    province: "North-Western-Province",
    provinceId: "north-western",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Yapahuwa/Yapahuwa-Rock Fortress-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Yapahuwa/Yapahuwa-Rock Fortress-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Yapahuwa/Yapahuwa-Rock Fortress-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Yapahuwa/Yapahuwa-Rock Fortress-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Yapahuwa/Yapahuwa-Rock Fortress-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Kurunegala/Yapahuwa/Yapahuwa-Rock Fortress-5.jpg`,
    ],
    shortDescription:
    "Climb the historic rock fortress, admire intricate carvings, and enjoy breathtaking views from one of Sri Lanka’s medieval capitals.",
    fullDescription:
      "",
    whyVisit: [
    "Yapahuwa Rock Fortress is a striking historical site that combines natural beauty with medieval architecture. Once a royal capital in the 13th century, it offers visitors a fascinating glimpse into Sri Lanka’s military history, strategic engineering, and Buddhist heritage. Perfect for history enthusiasts, photographers, and adventurous travelers alike."
    ],
    overview:{
    introduction:"Perched atop a massive granite rock, Yapahuwa was the second capital of Sri Lanka after Polonnaruwa. The fortress was constructed to protect the kingdom from invasions and features a grand stairway leading to the royal palace, intricate stone carvings, and remnants of Buddhist shrines. The site also houses a sacred tooth relic of the Buddha for a brief period in history. The combination of rugged rock, ancient walls, and panoramic views makes Yapahuwa a visually stunning destination.",
      
    },
    ticketsEntry: {
      residents: "LKR 50–200",
      foreigners: "LKR 3,000 (~USD 9–10)",
      children: "",
      notes: [
        "•	Guided tours optional (~LKR 2,000–3,000 / USD 6–10)",
        "Note: Prices may fluctuate or change at any time.",
      ]
    },
    highlights: [
    "Grand Stairway – A monumental stone staircase adorned with carvings of lions and floral motifs.",
    "Royal Palace Ruins – Remnants of the king’s residence and administrative buildings.",
    "Buddhist Shrines – Ancient temples and stupas situated atop the rock.",
    "Historical Significance – Served as a fortress and temporary home to the sacred tooth relic.",
    "Panoramic Views – Stunning vistas of surrounding villages, forests, and farmland."
    ],
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "•	Early morning is best for cooler weather, fewer crowds, and optimal lighting for photography.",
    },
  gettingThere: {
  fromTown: "1 hour by car or tuk-tuk",
  fromNearby: [
   "•	From Colombo: ~4–5 hours by car (160 km)"
  ],
parking: " Available near the entrance; short walk to the base of the rock.",
  },
  nearbyHotels: [
    "•	Yapahuwa Heritage Hotel – Comfortable stay near the site with traditional charm.",
    "•	Kurunegala City Hotels – Options like Rock Side Hotel, Mango Villa, or Villa Kurunegala (~30–45 min drive).",
    "•	Budget Guesthouses – Several small lodges available in nearby towns."

    ],
    travelTips: [
      
    	"Wear sturdy, comfortable shoes for climbing the steep steps.",
      "Dress modestly when visiting shrines (shoulders and knees covered).",
      "Carry water, sunscreen, and a hat.",
      "Respect sacred areas and avoid loud noises near shrines."
    ],
    didYouKnow: "Yapahuwa was built in the 13th century as a defensive stronghold and briefly housed the sacred tooth relic of the Buddha, making it a site of immense historical and religious significance.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },

  // Puttalam District
{
    id: "Kalpitiya-Beach", 
    title: "Kalpitiya Beach – Pristine Coastal Escape",
    town: "Kalpitiya",
    district: "Puttalam",
    province: "North-Western-Province",
    provinceId: "north-western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Kalpitiya Beach/Kalpitiya-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Kalpitiya Beach/Kalpitiya-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Kalpitiya Beach/Kalpitiya-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Kalpitiya Beach/Kalpitiya-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Kalpitiya Beach/Kalpitiya-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Kalpitiya Beach/Kalpitiya-Beach-5.jpg`,
    ],
    shortDescription:
    "Relax on untouched beaches, explore lagoons, and experience the vibrant marine life of Kalpitiya.",
    fullDescription:
      "",
    whyVisit: [
    "Kalpitiya Beach is a serene coastal destination known for its unspoiled beauty, calm waters, and abundant marine life. Ideal for relaxation, water sports, and nature lovers, it offers a quieter alternative to Sri Lanka’s more crowded beaches."
    ],
    overview:{
    introduction:"Located on the northwestern coast of Sri Lanka, Kalpitiya is famous for its long sandy beaches, lagoons, and coral reefs. The area is a hotspot for dolphin and whale watching, kite surfing, and eco-tourism. Kalpitiya’s unique ecosystem includes mangroves, sandbars, and lagoons, making it a favorite for adventure seekers and wildlife enthusiasts alike.",
      
    },
    ticketsEntry: {
      residents: "Free entry to public beaches",
      foreigners: "Free entry to public beaches",
      children: "",
      notes: [
        "•	Dolphin or whale-watching tours: LKR 5,000–15,000 (~USD 15–45) per boat( depending on season and operator)",
        "Note: Prices may fluctuate or change at any time.",
      ]
    },
    highlights: [
      "Pristine Beaches – Long stretches of soft sand and clear, calm waters.",
      "Dolphin & Whale Watching – Excursions available to spot marine wildlife.",
      "Kite Surfing – Ideal windy conditions attract water sports enthusiasts.",
      "Scenic Lagoons & Mangroves – Perfect for eco-tours and kayaking.",
      "Peaceful Retreat – Less crowded than popular tourist beaches, offering a tranquil escape."

    ],
    visitingHours: {
      daily: "Open 24 hours",
      bestTime: ": Early morning or late afternoon for wildlife sightings and cooler temperatures",
    },
  gettingThere: {
  fromTown: "",
  fromNearby: [
   "From Colombo: ~4–5 hours by car (approx. 190 km)",
    "From Negombo: ~2.5–3 hours by car (~120 km)"

  ],
parking: " Available at main beach access points; boat tours depart from Kalpitiya Lagoon",
  },
  nearbyHotels: [
    "•	Bar Reef Resort – Luxury beachfront accommodations with eco-friendly amenities.",
    "•	Aqua Eco Lodges – Boutique lodges near lagoons for nature lovers.",
    "•	Kalpitiya Beach Bungalows – Budget-friendly stays right on the beach.",
    "•	Jetwing Lighthouse Kalpitiya – Mid-range option with modern facilities.",
    "•	Local Guesthouses – Several small lodges and homestays within walking distance of the beach."
    ],
    travelTips: [  
    "Swim in designated safe areas and follow local guidance.",
    "Wear light, breathable clothing; bring swimwear, hats, and sunscreen.",
    "Morning or late afternoon is best for wildlife tours and photography.",
    "Respect local communities and natural habitats, especially mangroves and coral reefs."

    ],
    didYouKnow: "Kalpitiya is one of the best locations in Sri Lanka for dolphin watching, with sightings of bottlenose and spinner dolphins almost year-round. It’s also a prime location for observing migratory birds along its lagoons.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Munneswaram-Temple",
    title: "Munneswaram Temple – Sacred Sanctuary of Devotion and Heritage",
    town: "Chilaw",
    district: "Puttalam",
    province: "North-Western-Province",
    provinceId: "north-western",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Munneswaram Temple/Munneswaram-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Munneswaram Temple/Munneswaram-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Munneswaram Temple/Munneswaram-Temple-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Munneswaram Temple/Munneswaram-Temple-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Munneswaram Temple/Munneswaram-Temple-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Munneswaram Temple/Munneswaram-Temple-5.jpg`,
    ],
    shortDescription: "Experience divine serenity, witness centuries-old rituals, and feel the harmony of faith at Munneswaram Temple, where legend, culture, and spirituality intertwine.",
    fullDescription: "",
    whyVisit: [
      "Ancient Hindu Shrine – One of the oldest and most revered Hindu temples in Sri Lanka.",
      " Spiritual Experience – A center of worship for both Hindus and Buddhists, known for its peaceful aura.",
      "Vibrant Festivals – Famous for the annual Munneswaram Festival (Thiruvila) that attracts thousands of devotees.",
      "Architectural Beauty – Intricate carvings, colorful gopurams (tower gates), and traditional Dravidian-style architecture.",
      "Cultural Harmony – Symbol of religious coexistence, with nearby Buddhist and Catholic shrines."
    ],
    overview: {
      introduction: "Located in Chilaw, about 80 km north of Colombo, Munneswaram Temple is dedicated to Lord Shiva and is believed to date back over a thousand years. It forms one of the five ancient Ishwarams (Hindu temples dedicated to Shiva) in Sri Lanka. The temple complex includes multiple shrines, including those for Lord Ganesha, Kali, and Ayyanayake, surrounded by a serene atmosphere and vibrant rituals. The temple holds deep spiritual significance and is a vital stop for pilgrims and culture seekers alike.",
    },
    ticketsEntry: {
      residents: "Free (donations appreciated)",
      foreigners: "Free (donations appreciated)",
      children: "",
      notes: [
        "Festival Entry: Free, though offerings can be made during rituals",
        "Note: Donations and special puja fees may vary depending on occasion."
      ]
    },
    highlights: [
      "Main Shiva Devalaya – Central shrine dedicated to Lord Shiva, adorned with lamps and intricate carvings.",
      "Kali Amman Temple – Known for powerful rituals and annual ceremonies.",
      "Cultural Diversity – Surrounded by Buddhist and Christian places of worship, showcasing harmony.",
      " Thiruvila Festival – A 10-day celebration (August/September) with processions, music, and traditional dances.",
      "Architectural Marvel – Vibrant sculptures and gopurams painted in vivid colors reflecting South Indian artistry."
    ],
    visitingHours: {
      daily: "6:00 AM – 8:00 PM",
      bestTime: "Early morning or evening puja times for the most spiritual atmosphere,           •	Festival Season: August–September (Thiruvila) – Expect large crowds and colorful celebrations.",
    },
    gettingThere: {
      fromTown: "",
      fromNearby: [
        "From Colombo: 2.5 hours by car (~80 km via Chilaw Road)",
        "From Negombo: 1.5 hours (~60 km)"
      ],
      publicTransport: "Regular buses and trains run to Puttalam; tuk-tuks available locally for short rides.",
      parking: "Available near main viewing points and boat docks."
    },

    nearbyHotels: [
      "•	Anantaya Resort & Spa Chilaw – Luxury beachfront resort ~10 min away.",
      "•	Coco Village Hotel Chilaw – Comfortable mid-range stay near the lagoon.",
      "•	Athena Resort – Budget-friendly option within easy reach of the temple.",
      "•	Local Guesthouses – Homestay-style accommodations available in Chilaw town."
    ],
    travelTips: [
      "Dress modestly (shoulders and knees covered); remove footwear before entering shrines. ",
      "Avoid loud conversations or photography during prayers.",
      "Carry offerings like flowers or fruits if participating in puja.",
      "Visit early to avoid crowds, especially during festivals.",
      "Respect temple customs regardless of religion or background."
    ],
    didYouKnow: "According to legend, Lord Rama prayed at Munneswaram to seek forgiveness from Lord Shiva after defeating King Ravana of Lanka. This story connects the temple deeply to the Ramayana Trail of Sri Lanka",
    nearby: [
      "-",
      "-",
    ],
    openingHours: {
      weekdays: "",
      weekends: "",
      holidays: "",
      notes: ""
    },
    bestTimeToVisit: "",
    duration: "",
    accessibility: ""
  },
 {
    id: "Puttalam-Lagoon",
    title: "Puttalam Lagoon – Tranquil Waters of Nature and Heritage",
    town: "Puttalam",
    district: "Puttalam",
    province: "North-Western-Province",
    provinceId: "north-western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Puttalam Lagoon/Puttalam-Lagoon-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Puttalam Lagoon/Puttalam-Lagoon-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Puttalam Lagoon/Puttalam-Lagoon-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Puttalam Lagoon/Puttalam-Lagoon-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Puttalam Lagoon/Puttalam-Lagoon-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Puttalam Lagoon/Puttalam-Lagoon-5.jpg`,
    ],
    shortDescription:
    "Lose yourself in the calm rhythm of Puttalam Lagoon, where tranquil waters, wildlife, and village life blend into pure serenity.",
    fullDescription:
      "",
    whyVisit: [
     "Scenic Beauty – One of Sri Lanka’s largest coastal lagoons, where calm waters meet golden skies.",
     "Birdwatcher’s Paradise – Home to flamingos, pelicans, herons, and migratory birds.",
    "Lagoon Adventures – Take a serene boat ride through mangroves and fishing villages.",
    " Magical Sunsets – The lagoon’s mirror-like surface reflects breathtaking evening hues.",
    " Local Life – Watch traditional fishing and salt harvesting, reflecting the heart of rural Sri Lanka."

    ],
    overview:
    "Stretching along Sri Lanka’s northwestern coast, Puttalam Lagoon is a vast and peaceful expanse of water bordered by mangroves, coconut palms, and small islands. It plays a vital role in the region’s ecosystem, supporting fishermen and salt producers while attracting nature lovers and eco-travelers. The lagoon connects to the Dutch Bay and Mundal Lagoon, forming a rich wetland network. Whether you’re birdwatching, cruising on a boat, or enjoying the coastal breeze, Puttalam Lagoon offers a calm and authentic experience away from the crowds.",
    ticketsEntry: {
      residents: "Free (boat rides are paid separately)",
      foreigners: "Free (boat rides are paid separately)",
      children: "Free (boat rides are paid separately)",
      notes: [
        "Boat Ride: Approx. USD 15–35 per person depending on duration and operator",
        "Prices can fluctuate based on season and demand.",
       
      ]
    },
    highlights: [
     "Lagoon Boat Safari – Explore winding waterways surrounded by lush mangroves.",
     "Flamingo Season – Spot flocks of pink flamingos during the migratory months (Dec–Mar).",
    "Mangrove Islands – Visit small islands like Karaitivu, home to diverse flora and fauna.",
    "Salt Pans & Fishing Villages – Observe locals harvesting salt and fishing with traditional nets.",
    "Sunset Views – Best enjoyed near Kalpitiya, where the lagoon meets the sea."

      
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6.30 PM",
      bestTime: "Early morning for birdwatching, or evening for sunset views.",
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
      "From Colombo: ~3 hours (130 km) via the A3 Highway",
      "From Negombo: ~2.5 hours (110 km)"

    ], 
    publicTransport: "Regular buses and trains run to Puttalam; tuk-tuks available locally for short rides.",
    parking: "Available near main viewing points and boat docks."
    },  
    nearbyHotels: [
      "Bar Reef Resort – Eco-luxury villas near Kalpitiya, perfect for relaxation",
     " Elements Beach & Nature Resort – Combines lagoon and ocean views.",
     "Udekki – Stylish stay offering kitesurfing and lagoon tours.",
    "Villa Setha Wadi – Boutique retreat overlooking the lagoon’s calm waters.",
    "Local Guesthouses – Affordable options with authentic village hospitality."

    ],
    travelTips: [
    "Lightweight, comfortable clothing and a hat to protect from the sun.",
    "Carry binoculars and a camera for birdwatching.",
    "Bring insect repellent for evening visits.",
    "Respect the quiet atmosphere of fishing villages.",
    "Boat tours depend on tides. Check schedules in advance."

    ],
    didYouKnow: "Puttalam Lagoon is part of one of Sri Lanka’s largest mangrove ecosystems and plays a key role in protecting the coastline from erosion. It’s also near Sri Lanka’s main salt production sites, supplying much of the island’s salt!",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Wilpattu-National-Park",
    title: "Wilpattu National Park – The Land of Lakes and Leopards",
    town: "Puttalam",
    district: "Puttalam",
    province: "North-Western-Province",
    provinceId: "north-western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Wilpattu/Wilpattu-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Wilpattu/Wilpattu-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Wilpattu/Wilpattu-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Wilpattu/Wilpattu-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Wilpattu/Wilpattu-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/North Western/Puttalam/Wilpattu/Wilpattu-5.jpg`,
    ],
    shortDescription:
    "Experience Sri Lanka’s wild heart where leopards roam free, elephants wander through ancient forests, and tranquil lakes mirror the sky.",
    fullDescription:
      "",
    whyVisit: [
    "Leopard Territory – Home to one of the highest leopard densities in Sri Lanka.",
     "Birdwatcher’s Paradise – Spot eagles, owls, peacocks, and migratory birds.",
    "Elephant Encounters – Witness herds of majestic elephants in their natural habitat.",
    "Scenic Villus (Natural Lakes) – The park’s unique feature, beautiful water-filled depressions surrounded by forest.",
    "Tranquil Safaris – Vast, less crowded wilderness for an authentic safari experience.",


    ],
    overview:
    "Wilpattu National Park, meaning “Land of Lakes,” is Sri Lanka’s largest and one of its oldest national parks, spanning over 1,300 square kilometers. Renowned for its natural “villus” (lakes), the park’s diverse ecosystem features dense scrub jungles, grasslands, and shimmering lakes that attract wildlife all year round. Located in the northwest between Puttalam and Anuradhapura, Wilpattu is ideal for travelers seeking a quiet, immersive experience in nature, especially those hoping to glimpse leopards in the wild.",
    ticketsEntry: {
      residents: "LKR 150–500",
      foreigners: "LKR 10,000–12,000 (~USD 30–35) per person",
      children: "",
      notes: [
        "Safari Jeep Hire: LKR 8,000–12,000 (~USD 25–35) per half-day trip",
        "Prices may fluctuate or change at any time",
       
      ]
    },
    highlights: [
      "Leopard & Sloth Bear Sightings – The park’s signature attractions.",
      "Birdlife Galore – Over 200 bird species, including painted storks, kingfishers, and eagles.",
      "Scenic Lakes – 60+ natural lakes (“villus”) providing excellent photography opportunities.",
      "Elephants & Deer – Regular sightings near water bodies during early morning safaris.",
      "Archaeological Interest – Ancient ruins and folklore sites scattered within the park.",

      
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6.00 AM – 6.00 PM",
      bestTime: "February – October (dry season, easier wildlife sightings)",
    },
   gettingThere: {
    fromTown: "1.5 hours (~60 km)",
    fromNearby: [
      "From Colombo: ~4–5 hours by car (~180 km)",
      "From Anuradhapura: ~1 hour (~45 km)",
      "Nearest Entrance: Hunuwilagama Gate"

    ], 
    publicTransport: "Private car, taxi, or pre-booked safari jeep (can pick up from nearby towns).",
    
    },  
    nearbyHotels: [
    "Wilpattu Safari Camp – Luxury tents with guided tours.",
    "Leopard Trails Wilpattu – Boutique eco-luxury glamping experience.",
    "Big Game Camp Wilpattu – Mid-range tented lodges near the park entrance.",
    "Anuradhapura & Puttalam Hotels – Wide range of stays from budget to luxury (30–60 min away)."


    ],
    travelTips: [
    "Wear neutral-colored, lightweight clothing (avoid bright colors).",
    "Bring hats, sunscreen, and insect repellent. ",     
    "Keep quiet during safaris to avoid disturbing animals.",
    "Binoculars and zoom lenses are a must for wildlife photography.",
    "Carry enough water and light snacks. Facilities inside are limited."


    ],
    didYouKnow: "Wilpattu was declared a sanctuary in 1905 and later a national park in 1938. Its unique feature, the interconnected lakes, creates a rich ecosystem that supports over 30 species of mammals and countless birds.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },

  // Nothern Province
  //Jaffna district
{
    id: "Casuarina-Beach",
    title: "Casuarina Beach – Calm Waters and Tranquil Shores",
    town: "Jaffna",
    district: "Jaffna",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Casuarina Beach/Casuarina-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Casuarina Beach/Casuarina-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Casuarina Beach/Casuarina-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Casuarina Beach/Casuarina-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Casuarina Beach/Casuarina-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Casuarina Beach/Casuarina-Beach-5.jpg`,
    ],
    shortDescription:
    "Relax, swim, and watch the sunset over Casuarina Beach, a serene northern retreat where nature and tranquility meet.",
    fullDescription:
      "",
    whyVisit: [
    "Pristine White Sands – Soft, golden sands stretching along the northern coast.",
    "Clear, Calm Waters – Ideal for swimming, snorkeling, and family-friendly beach activities.",
    "Scenic Coastal Views – Lined with Casuarina trees providing natural shade and perfect spots for relaxation.",
    "Peaceful Escape – Less crowded than southern beaches, offering serenity and privacy.",
    "Sunset Photography – Stunning sunsets over the Indian Ocean."


    ],
    overview:
    "Located about 12 km from Jaffna city, Casuarina Beach is named after the Casuarina trees that line the shore. It is the northernmost beach in Sri Lanka that is popular among both locals and tourists seeking a quiet getaway. The beach is known for its clean sands, gentle waves, and relaxed atmosphere, making it perfect for swimming, picnics, and casual strolls. It is also an excellent base for exploring nearby islands and lagoon areas.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "",
      notes: [
        "Boat Rides: Optional, USD 10–20 per person (prices may fluctuate)",
       
      ]
    },
    highlights: [
      "Casuarina Tree Lined Beach – Provides shade and a picturesque coastal backdrop.",
      "Swimming & Snorkeling – Safe waters suitable for beginners and families.",
      "Nearby Islands – Boat trips available to explore smaller islands and coastal lagoons.",
      "Sunset Views – Best enjoyed in the late afternoon with minimal crowds.",
      "Fishing & Local Culture – Observe traditional fishing methods along the shore.",
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "Open 24 hours",
      bestTime: "Early morning for solitude, late afternoon for sunset views",
    },
   gettingThere: {
    fromTown: "20 minutes by car (12 km)",
    fromNearby: [
      "From Jaffna Fort: ~15 minutes by tuk-tuk or car",

    ], 
    publicTransport: "Buses to Casuarina Beach are available from Jaffna city; tuk-tuks can take you directly from the city center",
    
    },  
    nearbyHotels: [
      "Jetwing Jaffna – Modern comfort within 15 minutes",
      "Tilko City Hotel – City-based hotel convenient for day trips to the beach",
      "Fox Jaffna by Fox Resorts – Peaceful boutique stay near the coast",
      "Local Guesthouses – Affordable homestays near the beach",

    ],
    travelTips: [
        "Light, breathable clothing and swimwear recommended (cover up when leaving beach areas).",
        "Sunscreen, hats, and water are essential.",
        "Bring a towel or mat for sitting on the sand.",
        "Respect local customs and avoid littering.",
        "Visit early to enjoy calm waters and minimal crowds.",

    ],
    didYouKnow: "Casuarina Beach gets its name from the rows of Casuarina trees that protect the coast from strong winds and provide natural beauty. Its calm waters make it a favorite spot for families and birdwatchers, especially during migratory seasons.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
{
    id: "Jaffna-Fort",
    title: "Jaffna Fort – Echoes of Colonial Power and Northern Resilience",
    town: "Jaffna",
    district: "Jaffna",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Jaffna Fort/Jaffna-Fort-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Jaffna Fort/Jaffna-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Jaffna Fort/Jaffna-Fort-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Jaffna Fort/Jaffna-Fort-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Jaffna Fort/Jaffna-Fort-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Jaffna Fort/Jaffna-Fort-5.jpg`,
    ],
    shortDescription:
    "Step into the living history of Jaffna Fort, where echoes of empires meet the calm of the northern winds, and every stone tells a tale of endurance and pride.",
    fullDescription:
      "",
    whyVisit: [
        "Historic Landmark – One of the largest and best-preserved colonial forts in Sri Lanka.",
        "Dutch Architecture – A grand example of 17th-century Dutch military design.",
        "Cultural Legacy – Symbol of Jaffna’s layered history, from Portuguese to Dutch to British rule.",
        "Scenic Views – Overlooks the Jaffna Lagoon with breathtaking sunset vistas.",
        "Archaeological Importance – Ongoing restoration reveals stories of trade, war, and heritage.",


    ],
    overview:
    "Located in the heart of Jaffna city, this star-shaped stone fortress was originally built by the Portuguese in 1618 and later expanded by the Dutch after capturing it in 1658. The British took control in the late 18th century, adding their own touches. Despite war-time damages, the fort remains a proud symbol of resilience and northern heritage. Its thick ramparts, arched gates, and serene moat make it a captivating site for history enthusiasts and photographers alike.",
    ticketsEntry: {
      residents: "LKR 100–200",
      foreigners: "USD 3–5 for foreigners (may fluctuate based on season)",
      children: "",
      notes: [
        "Prices can change depending on restoration status and local authority management.",
       
      ]
    },
    highlights: [
      "Massive Ramparts & Bastions – Walk along the high stone walls once used to defend the fort.",
      "Old Dutch Church Ruins – Intricate remnants of colonial worship and architecture.",
      "Main Gate & Drawbridge – The original entrance still stands, framed by stone carvings.",
      "Viewpoints Over Jaffna Lagoon – Ideal for panoramic photos, especially at dusk.",
      "Archaeological Museum Nearby – Houses artifacts from the Dutch and Chola periods.",
      "Peaceful Ambience – A serene place to reflect on history amid ocean breezes."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "8:00 AM – 6:00 PM",
      bestTime: "Early morning or late afternoon for pleasant temperatures and best light.",
    },
   gettingThere: {
    fromTown: "5 minutes by tuk-tuk (1 km).",
    fromNearby: [
      "From Colombo: ~7–8 hours by road (~400 km) or train (Colombo–Jaffna line).",

    ], 
    publicTransport: "Buses and trains operate daily; tuk-tuks and bicycles are popular locally.",
    parking:"Available near the fort entrance.",
    
    },  
    nearbyHotels: [
      "Jetwing Jaffna – Modern comfort in the city center, 5 minutes from the fort.",
      "Tilko City Hotel – Modern comfort in the city center, 5 minutes from the fort.",
      "Fox Jaffna by Fox Resorts – Colonial-style charm with serene gardens.",
      "Green Grass Hotel & Restaurant – Affordable and close to the Jaffna railway station.",

    ],
    travelTips: [
        "Wear light, breathable clothes — it can get quite hot during the day.",
        "Bring a hat, sunscreen, and plenty of water.",
        "Comfortable walking shoes are recommended for exploring ramparts.",
        "Visit near sunset for golden light over the lagoon.",
        "Respect restoration zones and local regulations when taking photos.",

    ],
    didYouKnow: "Jaffna Fort was once surrounded by a thriving colonial town with markets, churches, and administrative buildings. During the civil conflict, it was a key military stronghold. Today, it stands as a symbol of peace and revival.",
    nearby: [
      "-",
      "-",
      
    ],
    bestTimeToVisit: "November to April (dry season)",
    duration: "2-3 hours (half day with museums)",
    accessibility: "Wheelchair accessible in most areas, some cobblestone streets"
  },
  {
    id: "Keerimalai-Springs",
    title: "Keerimalai Springs – Natural Healing Waters by the Sea",
    town: "Jaffna",
    district: "Jaffna",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Keerimalai Springs/Keerimalai-Springs-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Keerimalai Springs/Keerimalai-Springs-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Keerimalai Springs/Keerimalai-Springs-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Keerimalai Springs/Keerimalai-Springs-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Keerimalai Springs/Keerimalai-Springs-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Keerimalai Springs/Keerimalai-Springs-5.jpg`,
    ],
    shortDescription:
    "Dip into the cool, rejuvenating waters of Keerimalai Springs, a perfect blend of nature, tradition, and tranquility on Sri Lanka’s northern coast.",
    fullDescription:
      "",
    whyVisit: [
          "Sacred Freshwater Springs – Famous for their mineral-rich waters believed to have healing properties.",
          "Coastal Beauty – Set against the backdrop of the Indian Ocean with rocky shores and gentle waves.",
          "Spiritual Significance – Associated with local legends and rituals; pilgrims often bathe here for purification.",
          "Scenic Spot – Ideal for photography with turquoise waters, palm trees, and surrounding greenery.",
          "Relaxation & Recreation – A serene spot to unwind, swim, and enjoy nature away from crowds."


    ],
    overview:
    "Located near Jaffna city on the northern coast, Keerimalai Springs are freshwater springs that flow directly into the sea. The site is considered sacred by both locals and pilgrims, who believe that bathing in these waters can cleanse the body and spirit. The area offers a unique combination of natural beauty, spiritual heritage, and a peaceful seaside ambiance, making it a must-visit destination in the Jaffna peninsula.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "",
      notes: [
        "No formal facilities; donations may be appreciated for maintenance.",
       
      ]
    },
    highlights: [
      "Natural Springs – Cool, mineral-rich freshwater that mixes with the sea at the shoreline.",
      "Oceanfront Views – Stunning vistas of the Indian Ocean meeting the springs.",
      "Tranquil Environment – Less crowded, offering a relaxing retreat.",
      "Local Legends – Believed to have purifying and healing qualities according to folklore.",
      "Photography & Nature Walks – Scenic paths along the rocky coast and lush greenery."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "6:00 AM – 6:00 PM",
      bestTime: "Early morning for calm waters and fewer visitors",
    },
   gettingThere: {
    fromTown: "35 minutes by car (22 km)",
    fromNearby: [
      "From Nagadeepa Viharaya: ~1 hour by car",

    ], 
    publicTransport: "Buses to Keerimalai from Jaffna city; tuk-tuks available for local transfers",
    parking:"Available near the spring entrance",
    
    },  
    nearbyHotels: [
      "Jetwing Jaffna – Luxury stay ~35 min away.",
      "Tilko City Hotel – Mid-range accommodation in Jaffna city",
      "Local Guesthouses – Budget-friendly homestays in nearby towns.",
    ],
    travelTips: [
          "Swimwear recommended for bathing in the springs; cover up when leaving the area.",
          "Bring a towel, sunscreen, and water shoes for rocky areas.",
          "Respect local customs and avoid littering.",
          "Early morning visits are ideal to enjoy the serene atmosphere.",
          "Do not disturb pilgrims performing rituals; observe respectfully"


    ],
    didYouKnow: "Keerimalai Springs have been considered sacred for centuries, with legends linking them to divine purification. The mixing of freshwater and seawater creates a unique ecosystem and a scenic bathing experience",
    nearby: [
      "-",
      "-",
      
    ],
    
  },
  {
    id: "Nagadeepa-Viharaya",
    title: "Nagadeepa Viharaya – Sacred Island of Faith and Legend",
    town: "Nagadeepa island",
    district: "Jaffna",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Religous",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nagadeepa Viharaya/Nagadeepa-Viharaya-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nagadeepa Viharaya/Nagadeepa-Viharaya-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nagadeepa Viharaya/Nagadeepa-Viharaya-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nagadeepa Viharaya/Nagadeepa-Viharaya-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nagadeepa Viharaya/Nagadeepa-Viharaya-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nagadeepa Viharaya/Nagadeepa-Viharaya-5.jpg`,
    ],
    shortDescription:
    "Step onto Nainativu Island, feel the calm waters around you, and experience centuries of devotion at Nagadeepa Viharaya, a sacred northern treasure of Sri Lanka.",
    fullDescription:
      "",
    whyVisit: [
          "Ancient Buddhist Shrine – One of Sri Lanka’s most sacred Buddhist temples, believed to date back over 2,000 years.",
          "Spiritual Experience – Offers a serene environment for meditation and reflection.",
          "Island Retreat – Located on Nainativu Island, surrounded by calm waters and natural beauty.",
          "Historic Significance – Connected to the Ramayana legend, where Lord Rama is said to have worshipped Buddha.",
          "Architectural Beauty – Traditional Sri Lankan temple design with colorful murals and statues"
    ],
    overview:
    "Nagadeepa Viharaya sits on Nainativu Island in northern Sri Lanka and is one of the country’s most important pilgrimage sites. According to legend, after defeating King Ravana, Lord Rama prayed at this site, marking it as a place of divine significance. The temple is a focal point for Buddhist devotees and features a main shrine, sacred Bodhi tree, and beautifully decorated halls. The tranquil surroundings of the island add to its spiritual and scenic charm, making it a unique combination of culture, history, and nature.",
    ticketsEntry: {
      residents: "Free (donations appreciated)",
      foreigners: "Free (donations appreciated)",
      children: "",
      notes: [
        "Boat Transfer: Approx. USD 5–10 per person (from Kayts or Delft ferry points)",
        "Note: Prices can fluctuate based on season and transport availability.",
       
      ]
    },
    highlights: [
      "Natural Springs – Cool, mineral-rich freshwater that mixes with the sea at the shoreline.",
      "Oceanfront Views – Stunning vistas of the Indian Ocean meeting the springs.",
      "Tranquil Environment – Less crowded, offering a relaxing retreat.",
      "Local Legends – Believed to have purifying and healing qualities according to folklore.",
      "Photography & Nature Walks – Scenic paths along the rocky coast and lush greenery."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "5:00 AM – 6:00 PM",
      bestTime: "Early morning for a peaceful spiritual experience",
    },
   gettingThere: {
    fromTown: "1.5 hours by car to Kayts jetty, then a 15–20 min ferry to Nainativu Island",
    fromNearby: [
      "From Colombo: ~8–9 hours by road (~410 km) or train + local transport",

    ], 
    publicTransport: "Buses to Kayts jetty from Jaffna; ferries run regularly to Nainativu Island",
    parking:"Available near Kayts jetty",
    
    },  
    nearbyHotels: [
      "Jetwing Jaffna – Luxury stay ~1.5 hours away",
      "Tilko City Hotel – Comfortable mid-range hotel in Jaffna city",
      "Local Guesthouses – Homestay options in Kayts and Jaffna town for budget travelers",
    ],
    travelTips: [
          "Dress modestly (shoulders and knees covered) and remove footwear before entering shrines.",
          "Carry offerings such as flowers or incense if participating in rituals.",
          "Respect temple customs and photography rules.",
          "Bring water, sunscreen, and a hat for ferry rides and island exploration.",
          "Visit early to avoid crowds, especially during religious festivals."



    ],
    didYouKnow: "Nagadeepa Viharaya is one of two famous temples on Nainativu Island (the other is the Nagapooshani Amman Hindu Temple). According to legend, the Buddha visited this island during his third visit to Sri Lanka, blessing it as a site of sanctity.",
    nearby: [
      "-",
      "-",
      
    ],
   
  },
  {
    id: "Nallur-Temple",
    title: "Nallur Kandaswamy Temple – The Heart of Jaffna’s Devotion",
    town: "Jaffna",
    district: "Jaffna",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Religous",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nallur Temple/Nallur-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nallur Temple/Nallur-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nallur Temple/Nallur-Temple-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nallur Temple/Nallur-Temple-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nallur Temple/Nallur-Temple-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Jaffna/Nallur Temple/Nallur-Temple-5.jpg`,
    ],
    shortDescription:
    "Immerse yourself in devotion, color, and tradition at Nallur Kandaswamy Temple  where faith, history, and culture come alive.",
    fullDescription:
      "",
    whyVisit: [
          "Spiritual Hub – One of the most important Hindu temples in northern Sri Lanka, dedicated to Lord Murugan.",
          "Vibrant Festivals – Famous for the annual Nallur Festival (Nallur Thiruvizha), drawing thousands of devotees and tourists.",
          "Architectural Splendor – Stunning Dravidian-style gopurams (tower gates), colorful sculptures, and intricately carved pillars.",
          "Cultural Experience – Experience traditional rituals, music, and processions that have been observed for centuries.",
          "Photography & Exploration – Beautifully landscaped temple grounds, sacred ponds, and ceremonial halls ideal for photos."

    ],
    overview:
    "Situated in the heart of Jaffna city, Nallur Kandaswamy Temple is dedicated to Lord Murugan, the Hindu god of war and victory. The temple was originally built in 948 AD and rebuilt in the 18th century after periods of destruction. Its massive gopurams, grand halls, and sacred tanks make it a focal point of Jaffna’s religious and cultural life. The temple complex reflects centuries of devotion, art, and tradition, attracting pilgrims and visitors from around the world.",
    ticketsEntry: {
      residents: "Free (donations appreciated)",
      foreigners: "Free (donations appreciated)",
      children: "",
      notes: [
        "Special Ceremonies & Poojas: Optional donations may apply",
        "Note: Prices and donation suggestions can fluctuate based on events and festivals.",
       
      ]
    },
    highlights: [
      "Main Sanctum – Houses the idol of Lord Murugan, adorned with vibrant flowers and ceremonial decorations.",
      "Temple Drums & Rituals – Experience traditional drumming and offerings during daily pujas.",
      "Sacred Tanks – Temple ponds where devotees perform purification rituals.",
      "Annual Nallur Festival – 25-day celebration with chariot processions, music, and cultural performances (August–September).",
      "Cultural Significance – Acts as a center for learning, music, and community gatherings over centuries."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily: "5:00 AM – 12:00 PM  /  4:00 PM – 8:00 PM",
      bestTime: [
        "Early morning or during evening rituals for the most spiritual experience",
        "Festival Season: August–September (Nallur Thiruvizha) – Expect large crowds and vibrant celebrations.",
      ]
    },
   gettingThere: {
    fromTown: "5–10 minutes by tuk-tuk (2 km)",
    fromNearby: [
      "From Colombo: ~7–8 hours by road (~400 km) or train (Colombo–Jaffna line)",

    ], 
    publicTransport: "Frequent buses and trains to Jaffna city tuk-tuks available locally",
    parking:"Available near the temple entrance",
    
    },  
    nearbyHotels: [
      "Jetwing Jaffna – Modern comfort within 10 minutes of the temple",
      "Tilko City Hotel – Overlooks the city, convenient for exploring nearby temples",
      "Fox Jaffna by Fox Resorts – Boutique hotel with peaceful surroundings",
      "Green Grass Hotel & Restaurant – Affordable accommodation near the city center",
    ],
    travelTips: [
          "Dress modestly (shoulders and knees covered) and remove footwear before entering shrines.",
          "Carry offerings such as flowers or fruits if participating in pujas.",
          "Keep noise to a minimum during prayers.",
          "Visit early in the day to avoid crowds, especially during festivals.",
          "Respect temple customs and photography rules."

    ],
    didYouKnow: "The Nallur Kandaswamy Temple has been a spiritual center for over a thousand years and is considered the primary Murugan temple in northern Sri Lanka. Its annual festival, with grand chariot processions, has been celebrated continuously for centuries, showcasing Jaffna’s vibrant cultural heritage.",
    nearby: [
      "-",
      "-",
      
    ],
    
  },
  // Kilinochchi District
  {
    id: "Elephant-Pass",
    title: "Elephant Pass – Gateway Between Land and Lagoon",
    town: "Kilinochchi",
    district: "Kilinochchi",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Elephant Pass/Elephant-Pass-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Elephant Pass/Elephant-Pass-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Elephant Pass/Elephant-Pass-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Elephant Pass/Elephant-Pass-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Elephant Pass/Elephant-Pass-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Elephant Pass/Elephant-Pass-5.jpg`,
    ],
    shortDescription:
    "Experience the serene landscapes and historical significance of Elephant Pass, where land, water, and history converge in Sri Lanka’s northern region.",
    fullDescription:
      "",
    whyVisit: [
          "Historic Strategic Site – Known for its military and historical significance, connecting the Jaffna Peninsula to the mainland.",
          "Scenic Coastal Views – Offers breathtaking vistas of the Jaffna Lagoon and surrounding waterways.",
          "Nature & Wildlife – The area around the pass is home to diverse flora and fauna, including bird species along the lagoon.",
          "Photography Spot – Ideal for capturing panoramic landscapes of water, land, and sky.",
          "Outdoor Activities – Enjoy fishing, boating, and peaceful walks along the shoreline."
    ],
    overview:
    "Located at the narrow strip connecting the Jaffna Peninsula with mainland Sri Lanka, Elephant Pass has long been a critical passage for travel, trade, and military strategy. Today, it stands as a place where history and nature meet. Visitors can enjoy serene lagoon views, watch local fishermen at work, and explore the surrounding natural habitats. Its unique position between land and water makes it a distinctive destination for travelers.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "Minimal facilities; bring your own refreshments and essentials.",
      ]
    },
    highlights: [
      "Lagoon Views – Stunning vistas of the Jaffna Lagoon and adjacent landscapes.",
      "Nature Walks – Bunds and trails along the water provide peaceful strolls.",
      "Historical Significance – Known for its role in Sri Lanka’s military history and regional connectivity.",
      "Birdwatching – Spot local and migratory birds near the water’s edge.",
      "Photography – Capture panoramic shots of water, sand, and wildlife.",

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning or late afternoon for cooler weather and optimal photography",
      ]
    },
   gettingThere: {
    fromTown: "30 minutes by car",
    fromNearby: [
      "From Jaffna City -1 hour by car (55 km)",

    ], 
    publicTransport: "Buses and tuk-tuks available from Jaffna and surrounding towns",
    parking:"Limited; best to arrive early for easy access",
    
    },  
    nearbyHotels: [
      "Tilko City Hotel – Mid-range accommodation in Jaffna city",
      "Jetwing Jaffna – Luxury stay ~1 hour away",
      " Local Guesthouses – Budget-friendly homestays in nearby villages",
      
    ],
    travelTips: [
          "Comfortable walking shoes recommended for trails and uneven terrain.",
          "Bring water, sunscreen, and a hat for sun protection.",
          "Respect the local environment and avoid littering.",
          "Early visits are best to enjoy the peaceful lagoon scenery.",
          "Binoculars helpful for birdwatching.",

    ],
    didYouKnow: "Elephant Pass has historically been a crucial military and transportation link, controlling access to the Jaffna Peninsula. Today, it offers visitors a unique combination of history, natural beauty, and tranquil waters.",
    nearby: [
      "-",
      "-",
      
    ],
   
  },
  {
    id: "Iranamadu-Tank",
    title: "Iranamadu Tank – Serene Reservoir Amidst Nature",
    town: "Kilinochchi",
    district: "Kilinochchi",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Iranamadu Tank/Iranamadu-Tank-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Iranamadu Tank/Iranamadu-Tank-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Iranamadu Tank/Iranamadu-Tank-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Iranamadu Tank/Iranamadu-Tank-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Iranamadu Tank/Iranamadu-Tank-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Kilinochchi/Iranamadu Tank/Iranamadu-Tank-5.jpg`,
    ],
    shortDescription:
    "Immerse yourself in the tranquil beauty of Iranamadu Tank, a perfect blend of nature, history, and serenity in Sri Lanka’s northern plains.",
    fullDescription:
      "",
    whyVisit: [
          "Historic Irrigation Marvel – One of Sri Lanka’s largest and oldest tanks, supporting agriculture and local livelihoods.",
          "Natural Beauty – Surrounded by lush paddy fields, greenery, and distant hills, offering a peaceful rural landscape.",
          "Wildlife Spotting – A haven for birds, including migratory species, making it ideal for birdwatchers and nature lovers.",
          "Scenic Views – Wide stretches of water reflecting the sky, creating perfect photography opportunities.",
          "Relaxation & Recreation – Enjoy tranquil walks along the bund, fishing, or simply unwinding by the water.",

    ],
    overview:
    "Located in the northern part of Sri Lanka near Kilinochchi, Iranamadu Tank is a massive irrigation reservoir built centuries ago. It plays a crucial role in supporting the local agricultural economy while offering visitors a serene escape into nature. The tank is surrounded by verdant fields and is especially beautiful during sunrise and sunset, when the calm waters mirror the sky.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "No formal facilities; visitors are advised to carry water and snacks.",
      ]
    },
    highlights: [
      "Expansive Reservoir – Large, peaceful water body ideal for reflection and relaxation.",
      "Lush Surroundings – Paddy fields, trees, and natural greenery create a scenic environment.",
      "Birdwatching – Home to various local and migratory birds, especially during early mornings.",
      "Nature Walks – Bund pathways allow leisurely strolls with panoramic views.",
      "Photography – Stunning landscape shots with water, sky, and greenery.",


    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning or late afternoon for calm waters and wildlife sightings",
      ]
    },
   gettingThere: {
    fromTown: "20 minutes by car",
    fromNearby: [
      "From Jaffna City: ~1.5 hours by car (85 km)",

    ], 
    publicTransport: "Limited; tuk-tuks and private transport recommended",
    parking:"Available near main access points",
    
    },  
    nearbyHotels: [
      "Local Guesthouses – Budget-friendly stays in Kilinochchi or nearby villages",
      "Tilko City Hotel Jaffna – Mid-range accommodation ~1.5 hours away",
      " Jetwing Jaffna – Luxury stay ~1.5 hours away",
      
    ],
    travelTips: [
          "Comfortable shoes recommended for walking along the bund.",
          "Bring water, sunscreen, and a hat for sun protection.",
          "Respect local farmers and avoid entering cultivated fields.",
          "Binoculars helpful for birdwatching.",
          "Visit during early morning or late afternoon for the best scenery."

    ],
    didYouKnow: "Iranamadu Tank has been a lifeline for the region for centuries, providing water for irrigation and supporting local communities. Its vast waters and natural surroundings make it a peaceful retreat away from city life.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
// Mannar District
 {
    id: "Adam’s-Bridge",
    title: "Adam’s Bridge – Also Called Rama’s Bridge – Mythical Chain of Islands and Sandbanks",
    town: "Mannar",
    district: "Mannar",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Adam’s Bridge/Adam’s-Bridge-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Adam’s Bridge/Adam’s-Bridge-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Adam’s Bridge/Adam’s-Bridge-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Adam’s Bridge/Adam’s-Bridge-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Adam’s Bridge/Adam’s-Bridge-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Adam’s Bridge/Adam’s-Bridge-5.jpg`,
    ],
    shortDescription:
    "Discover Adam’s Bridge (also called Rama’s Bridge) where mythology, nature, and serene coastal landscapes converge on Sri Lanka’s northern frontier.",
    fullDescription:
      "",
    whyVisit: [
          "Natural & Geological Wonder – A chain of limestone shoals connecting Sri Lanka to India.",
          "Mythical Significance – Also called Rama’s Bridge, it is associated with the Ramayana epic and believed to have been built by Lord Rama’s army.",
          "Scenic Coastal Views – Stunning vistas of shallow turquoise waters, sandbanks, and surrounding seas.",
          "Photography Spot – Ideal for aerial, drone, or coastal photography.",
          "Exploration & Nature – Observe marine life, wading birds, and unique shallow water formations."

    ],
    overview:
    "Adam’s Bridge, also known as Rama’s Bridge, stretches from the northwestern coast of Sri Lanka to southern India. Composed of a series of shoals, sandbanks, and small islands, this natural formation is both a geological marvel and a site of cultural and religious significance. Though largely submerged, the shallow waters are visible from satellite images and nearby coastal points. The area is serene and less frequented, offering visitors a glimpse into a unique natural and historical phenomenon.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "No formal facilities; access is mostly from coastal observation points.",
      ]
    },
    highlights: [
      "Shallow Sandbanks – Natural limestone shoals connecting land masses.",
      "Mythical & Cultural Significance – Revered in local legends and the Ramayana epic.",
      "Coastal & Marine Life – Habitat for wading birds and small marine species.",
      "Photography – Stunning vistas, especially from aerial viewpoints or nearby shores.",
      "Tranquil Observation – Explore nearby coastal areas and enjoy peaceful views."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning or late afternoon for calm waters and ideal photography",
      ]
    },
   gettingThere: {
    fromTown: "30 minutes by car to coastal observation points",
    fromNearby: [
      "From Jaffna City: ~2.5–3 hours by car (140 km)",

    ], 
    publicTransport: "Limited; private transport recommended",
    parking:"Available near coastal access points",
    
    },  
    nearbyHotels: [
      "Local Guesthouses – Budget-friendly stays in Mannar town",
      "Tilko City Hotel Jaffna – Mid-range accommodation ~2.5 hours away",
      " Jetwing Jaffna – Luxury stay ~2.5 hours away",
      
    ],
    travelTips: [
          "Comfortable walking shoes recommended for coastal exploration.",
          "Bring sunscreen, a hat, and water.",
          "Avoid venturing into unsafe shallow waters or sandbanks.",
          "Binoculars helpful for bird and wildlife observation.",
          "Respect local customs and environmental conservation."
    ],
    didYouKnow: "Adam’s Bridge, also called Rama’s Bridge, is believed to have been naturally formed over thousands of years, yet legend says it was used by Lord Rama to reach Lanka. It remains a symbol of both natural wonder and cultural heritage.",
    nearby: [
      "-",
      "-",
      
    ],
    
  },
  {
    id: "Baobab-Tree",
    title: "Baobab Tree – Ancient Giants of Mannar",
    town: "Mannar",
    district: "Mannar",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Baobab Tree/Baobab-Tree-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Baobab Tree/Baobab-Tree-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Baobab Tree/Baobab-Tree-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Baobab Tree/Baobab-Tree-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Baobab Tree/Baobab-Tree-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Baobab Tree/Baobab-Tree-5.jpg`,
    ],
    shortDescription:
    "Marvel at the ancient baobabs of Mannar, towering natural monuments of resilience, beauty, and mystery in Sri Lanka’s northwestern region.",
    fullDescription:
      "",
    whyVisit: [
        "Natural Wonder – Home to some of the largest and oldest baobab trees in Sri Lanka.",
        "Unique Flora – Iconic “upside-down” trees with massive trunks and sparse branches.",
        "Historical & Cultural Significance – Locally cherished and surrounded by folklore and legends.",
        "Photography Spot – Perfect for capturing striking natural silhouettes, especially at sunrise or sunset.",
        "Tranquil Exploration – A serene place to walk, reflect, and appreciate nature’s marvels."
    ],
    overview:
    "Located in the Mannar district, the baobab trees are believed to have been brought from Africa centuries ago. Their enormous trunks and unusual shapes make them a natural attraction for visitors and photographers. These trees are often regarded as symbols of longevity and resilience. The surrounding area, with its dry landscapes and open skies, creates a dramatic setting for these ancient giants.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "No formal facilities; visitors should carry water and essentials.",
      ]
    },
    highlights: [
      "Majestic Baobabs – Massive trunks with unique, sculptural shapes.",
      "Folklore & Legends – Local stories celebrate the tree’s longevity and mystical aura.",
      "Natural Surroundings – Sparse, dry landscapes that enhance the visual impact of the trees.",
      "Photography – Ideal for dramatic sunrise, sunset, and silhouette shots.",
      "Nature Walks – Explore the area on foot and enjoy the peaceful environment."

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning or late afternoon for cooler weather and the best lighting for photos",
      ]
    },
   gettingThere: {
    fromTown: "20 minutes by car",
    fromNearby: [
      "From Jaffna City: ~3 hours by car (150 km)",

    ], 
    publicTransport: "Limited; tuk-tuks or private transport recommended",
    parking:"Available near main baobab tree sites",
    
    },  
    nearbyHotels: [
      "Local Guesthouses – Budget-friendly accommodation in Mannar town",
      "Tilko City Hotel Jaffna – Mid-range stay ~3 hours away",
      " Jetwing Jaffna – Luxury stay ~3 hours away",
      
    ],
    travelTips: [
          "Comfortable shoes recommended for walking around the trees.",
          "Bring water, sunscreen, and a hat for sun protection.",
          "Avoid damaging or carving into the trees; respect the natural heritage.",
          "Best visited in early morning or late afternoon for cooler temperatures and striking photography."

    ],
    didYouKnow: "Some baobab trees in Mannar are over 300 years old, making them living witnesses of history. Their enormous trunks can store thousands of liters of water, helping them survive in the arid climate.",
    nearby: [
      "-",
      "-",
      
    ],
  
  },
  {
    id: "Mannar-Fort",
    title: "Mannar Fort – Colonial Heritage by the Sea",
    town: "Mannar",
    district: "Mannar",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Mannar Fort/Mannar-Fort-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Mannar Fort/Mannar-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Mannar Fort/Mannar-Fort-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Mannar Fort/Mannar-Fort-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Mannar Fort/Mannar-Fort-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Mannar Fort/Mannar-Fort-5.jpg`,
    ],
    shortDescription:
    "Step back in time and explore Mannar Fort, a blend of history, architecture, and coastal charm on Sri Lanka’s northwestern coast.",
    fullDescription:
      "",
    whyVisit: [
        "Historical Landmark – A well-preserved 16th-century Portuguese fort later modified by the Dutch.",
        "Coastal Views – Located near the coast, offering scenic vistas of the Gulf of Mannar.",
        "Architectural Interest – Star-shaped fort with bastions, ramparts, and a moat showcasing colonial military design.",
        "Photography Spot – Ideal for capturing historic walls, cannons, and surrounding seascapes.",
        "Exploration & Leisure – Walk along the fort walls and enjoy the tranquil surroundings."

    ],
    overview:
    "Mannar Fort, built by the Portuguese in 1560 and later strengthened by the Dutch, is situated in Mannar town on Sri Lanka’s northwestern coast. The fort played a strategic role in controlling trade routes and coastal access. Today, it stands as a testament to colonial architecture and history, attracting visitors interested in heritage, history, and coastal scenery. The nearby town and fishing harbor add to the cultural charm of the area.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "Minimal facilities; visitors should carry water and essentials",
      ]
    },
    highlights: [
      "Colonial Architecture – Star-shaped fort with bastions, stone walls, and a moat.",
      "Ocean & Harbor Views – Overlooks the Gulf of Mannar and the local fishing harbor.",
      "Historical Significance – Portuguese and Dutch influences visible in design and construction.",
      "Walkable Fort – Explore ramparts, bastions, and interior pathways.",
      "Photography – Capture scenic shots of fort walls, cannons, and coastal views.",

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning or late afternoon for soft lighting and fewer crowds",
      ]
    },
   gettingThere: {
    fromTown: "Easily accessible within minutes",
    fromNearby: [
      "From Jaffna City: 3 hours by car (150 km)",

    ], 
    publicTransport: "Buses and tuk-tuks available from nearby towns",
    parking:"Limited, near the fort entrance",
    
    },  
    nearbyHotels: [
      "Local Guesthouses – Budget-friendly accommodation in Mannar town",
      "Tilko City Hotel Jaffna – Mid-range stay ~3 hours away",
      " Jetwing Jaffna – Luxury stay ~3 hours away",
      
    ],
    travelTips: [
        "Comfortable shoes recommended for walking on uneven fort surfaces.",
        "Bring water, sunscreen, and a hat for sun protection.",
        "Respect the historic structure; avoid climbing unsafe areas.",
        "Visit during early morning or late afternoon for pleasant weather and photography."

    ],
    didYouKnow: "Mannar Fort was crucial for controlling trade and maritime routes in the 16th and 17th centuries. Its star-shaped design is a classic example of European military architecture adapted to tropical coastal conditions.",
    nearby: [
      "-",
      "-",
      
    ],
   
  },
  {
    id: "Thiruketheeswaram-Temple",
    title: "Thiruketheeswaram Temple – Ancient Hindu Sanctuary of Devotion",
    town: "Mannar",
    district: "Mannar",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Histrocial",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Thiruketheeswaram Temple/Thiruketheeswaram-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Thiruketheeswaram Temple/Thiruketheeswaram-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Thiruketheeswaram Temple/Thiruketheeswaram-Temple-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Thiruketheeswaram Temple/Thiruketheeswaram-Temple-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Thiruketheeswaram Temple/Thiruketheeswaram-Temple-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mannar/Thiruketheeswaram Temple/Thiruketheeswaram-Temple-5.jpg`,
    ],
    shortDescription:
    "Experience the spiritual aura and historic grandeur of Thiruketheeswaram Temple, a sacred sanctuary where devotion, culture, and history intertwine in Sri Lanka’s Mannar district.",
    fullDescription:
      "",
    whyVisit: [
        "Historic Spiritual Site – One of the oldest Shiva temples in Sri Lanka, dating back over 2,000 years.",
        "Religious Significance – A major pilgrimage site revered for its connection to Lord Shiva and ancient legends.",
        "Peaceful Surroundings – Set amidst lush greenery and calm rural landscapes, offering serenity and reflection.",
        "Photography Spot – Stunning temple architecture, colorful gopurams, and sacred sculptures.",
        "Cultural Exploration – Witness traditional rituals, festivals, and devotional practices."

    ],
    overview:
    "Located near Mannar town, Thiruketheeswaram Temple is an important Hindu shrine dedicated to Lord Shiva. The temple is believed to have been established in ancient times and has been a center of devotion through centuries. It is renowned for its impressive architecture, intricate carvings, and vibrant festivals, which draw pilgrims from across the country and beyond. The temple complex provides a peaceful environment for spiritual reflection and cultural appreciation.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "Visitors should dress modestly and respect temple customs",
      ]
    },
    highlights: [
      "Ancient Architecture – Intricate stone carvings, gopurams, and sanctum sanctorum reflecting traditional Dravidian style.",
      "Religious & Cultural Importance – Revered as a sacred site with deep historical roots.",
      "Serene Environment – Peaceful temple grounds surrounded by greenery and natural beauty.",
      "Festivals & Rituals – Experience colorful Hindu festivals and daily poojas.",
      "Photography – Capture the temple’s vibrant details, sculptures, and sacred spaces.",

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "5:00 AM – 8:00 PM",
      bestTime: [
        "Early morning for rituals or festival days for cultural immersion",
      ]
    },
   gettingThere: {
    fromTown: "15 minutes by car",
    fromNearby: [
      "From Jaffna City: 3 hours by car (170 km)",

    ], 
    publicTransport: "Tuk-tuks and private vehicles recommended for easy access",
    parking:"Available near the temple entrance",
    
    },  
    nearbyHotels: [
      "Local Guesthouses – Budget-friendly accommodation in Mannar town",
      "Tilko City Hotel Jaffna – Mid-range stay ~3 hours away",
      "Jetwing Jaffna – Luxury accommodation ~3 hours away",
      
    ],
    travelTips: [
        "Modest clothing recommended; cover shoulders and knees.",
        "Remove footwear before entering sacred areas.",
        "Photography allowed in certain areas; observe respectfully.",
        "Early morning visits provide a serene experience and fewer crowds.",
        "Participate or observe rituals quietly without disturbing devotees."

    ],
    didYouKnow: "Thiruketheeswaram Temple is considered one of the five ancient Shiva temples in Sri Lanka and has played a central role in preserving Tamil Hindu culture and traditions for over two millennia.",
    nearby: [
      "-",
      "-",
      
    ],
   
  },

// Mulativ district
{
    id: "Mullaitivu-Beach",
    title: "Mullaitivu Beach – Pristine Shores of Northern Sri Lanka",
    town: "Mullaitivu",
    district: "Mullaitivu",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Mullaitivu Beach/Mullaitivu-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Mullaitivu Beach/Mullaitivu-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Mullaitivu Beach/Mullaitivu-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Mullaitivu Beach/Mullaitivu-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Mullaitivu Beach/Mullaitivu-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Mullaitivu Beach/Mullaitivu-Beach-5.jpg`,
    ],
    shortDescription:
    "Escape to Mullaitivu Beach, where golden sands, calm waters, and peaceful seclusion create a serene northern coastal retreat.",
    fullDescription:
      "",
    whyVisit: [
        "Untouched Coastal Beauty – Long stretches of golden sand with clear blue waters.",
        "Peaceful & Secluded – Less crowded, offering tranquility away from busy tourist spots.",
        "Photography Spot – Stunning sunrises, sunsets, and scenic coastal landscapes.",
        "Relaxation & Recreation – Ideal for swimming, beach walks, and quiet reflection.",
        "Nature & Wildlife – Opportunity to observe coastal birds and marine life in calm waters."

    ],
    overview:
    "Mullaitivu Beach, located in the northern province of Sri Lanka, is known for its serene and largely untouched environment. The beach stretches along the coast, providing visitors with a peaceful escape amidst natural beauty. With its gentle waves, golden sands, and quiet surroundings, Mullaitivu Beach is perfect for relaxation, photography, and enjoying the coastal landscape far from crowds.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "No formal facilities; carry water, snacks, and sun protection.",
      ]
    },
    highlights: [
      "Pristine Sandy Shore – Clean, wide beach ideal for walking and relaxation.",
      "Calm Coastal Waters – Safe areas for swimming and wading.",
      "Scenic Sunrises & Sunsets – Perfect for photography enthusiasts.",
      "Natural Surroundings – Lush greenery near the shoreline and opportunities for birdwatching.",
      "Leisurely Walks – Explore the peaceful beach and nearby coastal paths.",
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning or late afternoon for calm waters and pleasant temperatures",
      ]
    },
   gettingThere: {
    fromTown: "15 minutes by car",
    fromNearby: [
      "From Jaffna City: ~3.5–4 hours by car (170 km)",

    ], 
    publicTransport: "Limited; private transport or tuk-tuks recommended",
    parking:"Available near main beach access points",
    
    },  
    nearbyHotels: [
      "Local Guesthouses – Budget-friendly stays in Mullaitivu town",
      "Tilko City Hotel Jaffna – Mid-range accommodation ~3.5–4 hours away",
      "Jetwing Jaffna – Luxury stay ~4 hours away",
      
    ],
    travelTips: [
       "Swimwear recommended; cover up when leaving the beach area.",
       "Bring sunscreen, hat, and water.",
       "Avoid littering and respect the natural environment.",
       "Visit in early morning or late afternoon for the best experience and lighting.",
       "Keep an eye on tides and local safety advice while swimming."

    ],
    didYouKnow: "Mullaitivu Beach remains one of the less explored beaches in northern Sri Lanka, offering an unspoiled coastal experience. Its quiet shores make it a perfect spot for solitude, reflection, and nature appreciation.",
    nearby: [
      "-",
      "-",
      
    ],
   
  },
  {
    id: "Nanthikadal-Lagoon",
    title: "Nanthikadal Lagoon – Serene Waters of Jaffna",
    town: "Mullaitivu",
    district: "Mullaitivu",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Nandikhadhal/Nanthikadal-Lagoon-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Nandikhadhal/Nanthikadal-Lagoon-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Nandikhadhal/Nanthikadal-Lagoon-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Nandikhadhal/Nanthikadal-Lagoon-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Nandikhadhal/Nanthikadal-Lagoon-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Mulativ/Nandikhadhal/Nanthikadal-Lagoon-5.jpg`,
    ],
    shortDescription:
    "Discover the peaceful beauty of Nanthikadal Lagoon, where calm waters, wildlife, and serene landscapes create a perfect northern Sri Lankan retreat.",
    fullDescription:
      "",
    whyVisit: [
        "Scenic Lagoon – Calm waters surrounded by lush greenery and rural landscapes.",
        "Historical Significance – Notable in Sri Lanka’s history and local culture.",
        "Leisure & Recreation – Ideal for boating, birdwatching, and peaceful walks along the shore.",
        "Photography Spot – Stunning reflections on tranquil waters, especially at sunrise or sunset.",
        "Nature & Wildlife – Home to diverse bird species and aquatic life, offering nature enthusiasts a serene experience.",

    ],
    overview:
    "Nanthikadal Lagoon, located near the town of Mullaitivu in northern Sri Lanka, is a picturesque water body known for its calm environment and natural beauty. The lagoon is surrounded by farmland and mangroves, making it a peaceful spot for nature lovers and photographers. Its waters and surroundings provide an ideal escape from urban life while offering insight into local ecosystems and rural life.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "No formal facilities; carry water, snacks, and sun protection.",
      ]
    },
    highlights: [
      "Calm Waters – Perfect for reflection, photography, and leisure boating.",
      "Natural Surroundings – Paddy fields, mangroves, and greenery create a serene landscape.",
      "Birdwatching – Spot local and migratory birds along the lagoon’s edges.",
      "Scenic Photography – Ideal for capturing the tranquil beauty of water and nature.",
      "Relaxing Walks – Explore paths along the lagoon’s banks for peaceful strolls.",

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning or late afternoon for calm waters and optimal lighting",
      ]
    },
   gettingThere: {
    fromTown: "15 minutes by car",
    fromNearby: [
      "From Jaffna City: ~2.5–3 hours by car (130 km)",

    ], 
    publicTransport: "Limited; tuk-tuks or private transport recommended",
    parking:"Available near main access points",
    
    },  
    nearbyHotels: [
      "Local Guesthouses – Budget-friendly stays in Mullaitivu town",
      "Tilko City Hotel Jaffna – Mid-range accommodation ~2.5–3 hours away",
      "Jetwing Jaffna – Luxury stay ~2.5–3  hours away",
      
    ],
    travelTips: [
      "Comfortable walking shoes recommended for exploring the lagoon banks.",
      "Bring water, sunscreen, and a hat for sun protection.",
      "Respect the natural environment; avoid littering.",
      "Binoculars useful for birdwatching.",
      "Visit during early morning or late afternoon for a peaceful experience and best photographs.",


    ],
    didYouKnow: "Nanthikadal Lagoon is an important ecological site in northern Sri Lanka, supporting diverse birdlife and aquatic species. Its tranquil waters and scenic surroundings make it a hidden gem for nature enthusiasts.",
    nearby: [
      "-",
      "-",
      
    ],
   
  },
//Vavuniya district
  {
    id: "Vavuniya-Museum",
    title: "Vavuniya Museum – Gateway to History and Heritage",
    town: "Vavuniya",
    district: "Vavuniya",
    province: "North-Province",
    provinceId: "northern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Vavuniya/Vavuniya Museum/Vavuniya-Museum-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Vavuniya/Vavuniya Museum/Vavuniya-Museum-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Vavuniya/Vavuniya Museum/Vavuniya-Museum-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Vavuniya/Vavuniya Museum/Vavuniya-Museum-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Vavuniya/Vavuniya Museum/Vavuniya-Museum-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Northern/Vavuniya/Vavuniya Museum/Vavuniya-Museum-5.jpg`,
    ],
    shortDescription:
    "Discover the rich heritage of Vavuniya at the Vavuniya Museum, where history, culture, and education come alive.",
    fullDescription:
      "",
    whyVisit: [
      "Local History – Explore artifacts, exhibits, and relics that showcase the cultural and historical heritage of Vavuniya and northern Sri Lanka.",
      "Educational Experience – Learn about archaeology, ancient settlements, and regional traditions.",
      "Peaceful Environment – Small, well-maintained museum offering a calm space for reflection and learning.",
      "Photography Spot – Ideal for capturing historical artifacts and traditional displays.",
      "Cultural Exploration – Understand local crafts, tools, and heritage through curated exhibits.",

    ],
    overview:
    "Located in the heart of Vavuniya town, the Vavuniya Museum offers visitors a glimpse into the rich history and cultural traditions of the northern region. The museum houses a variety of artifacts, including pottery, ancient tools, traditional clothing, and archaeological finds. It provides an educational and cultural experience for locals, tourists, and students interested in the region’s past.",
    ticketsEntry: {
      residents: "Free or nominal fee ",
      foreigners: "Free or nominal fee",
      children: "",
      notes: [
          "Photography may be restricted for certain exhibits; check with museum staff",
      ]
    },
    highlights: [
      "Historical Artifacts – Items reflecting the lifestyle, culture, and history of northern Sri Lanka.",
      "Archaeological Exhibits – Stone tools, pottery, and relics from ancient settlements.",
      "Cultural Insights – Learn about traditional crafts, rituals, and regional heritage.",
      "Photography – Capture unique displays and intricate historical items.",
      "Calm Exploration – Small museum layout makes for a relaxed and focused visit.",

    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "8:00 AM – 5:00 PM",
      bestTime: [
        "Morning hours for fewer visitors and a quiet experience",
      ]
    },
   gettingThere: {
    fromTown: "5–10 minutes by car or tuk-tuk",
    fromNearby: [
      "From Jaffna City: ~3.5–4 hours by car (160 km)",

    ], 
    publicTransport: "Local buses and tuk-tuks available",
    parking:"Available near the museum",
    
    },  
    nearbyHotels: [
      "Local Guesthouses – Budget-friendly stays in Vavuniya",
      "Vavuniya City Hotel – Mid-range accommodation in town",
      "Tilko City Hotel Jaffna – Luxury stay ~3.5 hours away",
      
    ],
    travelTips: [
      "Comfortable shoes recommended for walking inside and around the museum.",
      "Carry water and a notebook for notes if interested in research.",
      "Respect exhibits and avoid touching artifacts unless permitted.",
      "Visit in the morning for a calm and focused experience.",
      "Ask staff for guidance on the most interesting exhibits."

    ],
    didYouKnow: "Vavuniya Museum preserves artifacts that highlight the ancient settlements, cultural evolution, and historical lifestyle of northern Sri Lanka, offering a unique window into the region’s past.",
    nearby: [
      "-",
      "-",
      
    ],
   
  },

  //Sabaragamuwa Procince
  //kegalle district
   {
    id: "Bible-Rock",
    title: "Bible Rock (Bathalegala) – Iconic Rock Formation & Hike",
    town: "Kegalle",
    district: "Kegalle",
    province: "Sabaragamuwa-Province",
    provinceId: "sabaragamuwa",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Bible Rock/Bible-Rock-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Bible Rock/Bible-Rock-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Bible Rock/Bible-Rock-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Bible Rock/Bible-Rock-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Bible Rock/Bible-Rock-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Bible Rock/Bible-Rock-5.jpg`,
    ],
    shortDescription:
    "Bible Rock (Bathalegala) is perfect for hikers, photographers, and nature lovers.Enjoy the moderate trek, panoramic views, and peaceful escape into Sri Lanka’s scenic countryside.",
    fullDescription:
      "",
    whyVisit: [
      "Iconic Landmark – Unique flat-topped rock resembling a Bible, a striking feature in Sri Lanka’s landscape.",
      "Hiking Adventure – Moderate trek with panoramic views of surrounding valleys, tea estates, and villages.",
      "Photography Heaven – Capture sunrise, sunset, and wide-angle shots of the rock and lush surroundings.",
      "Scenic Beauty – Surrounded by rolling hills, tea plantations, and rural landscapes.",
      "Peaceful Escape – Less crowded than other popular hikes, offering serenity and nature immersion.",

    ],
    overview:
    "Bible Rock (Bathalegala) is located near Kurunegala / Kegalle, in Sri Lanka’s Sabaragamuwa Province. The name “Bible Rock” comes from its resembling an open book or Bible when viewed from a distance. Rising approximately 230 meters above the surrounding plains, it’s a favorite spot for hikers, photographers, and nature enthusiasts.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "",
      notes: [
          "Guided Hikes: Optional; local guides charge ~LKR 1,500–2,500 (~USD 5–8)",
          "Photography: Allowed; best lighting in early morning or late afternoon"
      ]
    },
    highlights: [
      {
        main:"Rock Formation & Summit Views",
        subPoints: [
      "topped rock with 360° panoramic views of tea estates, villages, and nearby mountains",
      "Ideal spot for sunrise or sunset photography."
    ]},
    {
    main:"Hiking Trail",
    subPoints:[
      "Moderate trek (~1.5–2 hours round trip)",
      " Trails pass through lush forests, paddy fields, and small streams",
      "Well-marked paths, but hiring a local guide is recommended for first-time visitors."

    ]},
    {
      main:"Photography & Nature",
      subPoints:[
        "Capture unique rock formation from base and summit",
        "Wildlife spotting: birds, butterflies, and small mammals along the trail."

      ]},
      {
        main:"Nearby Attractions",
        subPoints:[

        ]
      }
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Morning for cooler temperatures and fewer crowds; dry season (December–March) preferred",
      ]
    },
   gettingThere: {
    fromTown: "5–10 minutes by car or tuk-tuk",
    fromNearby: [
      "From Colombo Fort:",
        "🚗 By Car/Taxi: ~3–3.5 hours (110 km), fare USD 25–30 / LKR 8,000–10,000",
        "🚌 By Public Bus: Colombo–Kurunegala/Kegalle buses, then tuk-tuk to trailhead (~15–20 min)",
        "🚆 By Train: Colombo–Kurunegala or Colombo–Peradeniya line, then taxi/tuk-tuk (~15–20 min)",
        "From Bandaranaike International Airport (BIA):- By Car/Taxi: ~3–3.5 hours (110 km), fare USD 25–30 / LKR 8,000–10,000",


    ], 
    
    },  
    nearbyHotels: [
      "Budget Guesthouses: Kurunegala and Kegalle town (~30 min drive).",
      "Mid-Range Hotels: Scenic stays near tea plantations.",
      "Eco-Lodges & Homestays: Immersed in nature near hiking trails."
    ],
    travelTips: [
      "Wear sturdy hiking shoes and light clothing.",
      "Carry water, snacks, and sunscreen.",
      "Start early morning to avoid midday heat.",
      "Respect nature and local communities; avoid littering.",
      "Combine with Knuckles Mountain hikes or Pinnawala Elephant Orphanage for a full-day itinerary."
    ],
    didYouKnow: "Bible Rock (Bathalegala) is not only a photogenic landmark but also a site of local folklore and history. Its flat summit was historically used as a lookout, and today it’s a popular destination for hikers and adventure tourists.",
    nearby: [
      "-",
      "-",
      
    ],
  },
  {
    id: "Dedugala-Falls",
    title: "Dedugala Falls – Hidden Waterfall Gem",
    town: "Kegalle",
    district: "Kegalle",
    province: "Sabaragamuwa-Province",
    provinceId: "sabaragamuwa",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Dedugala Falls/Dedugala-Falls-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Dedugala Falls/Dedugala-Falls-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Dedugala Falls/Dedugala-Falls-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Dedugala Falls/Dedugala-Falls-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Dedugala Falls/Dedugala-Falls-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Dedugala Falls/Dedugala-Falls-5.jpg`,
    ],
    shortDescription:
    "Dedugala Falls is perfect for nature lovers, photographers, and those seeking a tranquil escape.Enjoy the serene waterfall, scenic hike, and peaceful forest surroundings.",
    fullDescription:
      "",
    whyVisit: [
      "Secluded Natural Beauty – Less crowded than major waterfalls, offering a tranquil escape.",
      "Scenic Surroundings – Surrounded by lush forests, tea estates, and tropical vegetation.",
      "Refreshing Swim Spot – Natural pools at the base ideal for cooling off.",
      "Photography & Nature – Perfect for landscape photography and spotting local wildlife.",
      "Adventure Trek – Short hike through forest trails adds a touch of adventure"


    ],
    overview:
    "Dedugala Falls, located near Ratnapura in Sabaragamuwa Province, is a picturesque waterfall known for its serene environment and lush surroundings. Though smaller than Bopath Ella, Dedugala provides a more private and immersive nature experience, making it perfect for visitors seeking peace and natural beauty.",
    ticketsEntry: {
      residents: "LKR 100–200 (~USD 0.50–1) per person",
      foreigners: "LKR 100–200 (~USD 0.50–1) per person",
      children: "",
      notes: [
          "Guided Hikes: Optional; local guides ~LKR 1,000–2,000 (~USD 3–6)",
          "Photography: Allowed; ideal for nature and landscape photography"
      ]
    },
    highlights: [
      {
        main:"Waterfall & Natural Pools",
        subPoints:[
            "Moderate-height waterfall with clear water cascading into a natural pool",
            "Ideal for relaxation, wading, or a quick dip"
        ]},
      {
        main:"Short Hiking Trail",
        subPoints:[
          "Accessible via a short 15–30 minute hike through tropical vegetation",
          "Trails pass small streams, rocks, and scenic viewpoints."
        ]},
      {
      main:"Photography Opportunities",
      subPoints:[
          "Capture the waterfall, surrounding jungle, and reflection pools",
          "Morning or late afternoon provides the best natural light."
      ]},
      {
        main:"Nearby Attractions",
        subPoints:[
            "Bopath Ella Falls",
            "Ratnapura Gem Mines",
            "Sinharaja Forest Reserve"
        ]}
    ],
    openingHours: {
      weekdays: "Open 24/7 (fort area)",
      weekends: "Open 24/7 (fort area)",
      holidays: "Open 24/7 (fort area)",
      notes: "Museums typically open 9:00 AM - 5:00 PM. Individual shop and restaurant hours vary."
    },
    visitingHours: {
      daily:
        "7:00 AM – 5:00 PM",
      bestTime: [
        "Morning or late afternoon; avoid monsoon months (May–September) for safer trails",
      ]
    },
   gettingThere: {
    fromTown: "5–10 minutes by car or tuk-tuk",
    fromNearby: [
      "From Colombo Fort:",
        "🚗 By Car/Taxi: ~3.5–4 hours (120 km), fare USD 35–40 / LKR 12,000–14,000",
        "🚌 By Public Bus: Colombo–Ratnapura buses; then tuk-tuk or taxi to trailhead (~15–20 min)",
        "🚆 By Train: Colombo–Ratnapura train (~5 hours), then local transport to the falls",

        "From Bandaranaike International Airport (BIA):- By Car/Taxi: ~3.5 hours (120 km), fare USD 35–40 / LKR 12,000–14,000",


    ], 
    
    },  
    nearbyHotels: [
      "Budget Guesthouses: Ratnapura town (~15–20 min drive).",
      "Mid-Range Hotels: Comfortable stays near main roads to waterfalls. ",
      "Eco-Lodges & Homestays: Immersed in nature near Dedugala trail."

      
    ],
    travelTips: [
      "Wear sturdy shoes for forest trails and wet rocks.",
      "Carry water, snacks, and sun protection.",
      "Respect the natural environment; avoid littering.",
      "Combine with Bopath Ella, Ratnapura Gem Mines, or Sinharaja Forest Reserve for a full-day itinerary.",
      "Check weather conditions; trails can be slippery after rain."
    ],
    didYouKnow: "Dedugala Falls is lesser-known than Bopath Ella, which means you can enjoy a more peaceful and private experience. Its natural pools are popular with locals and tourists seeking a quiet retreat in the jungle.",
    nearby: [
      "-",
      "-",
      
    ],
  }, 
  {
    id: "Pinnawala",
    title: "Pinnawala Elephant Orphanage- Sri Lanka’s Gentle Giants ",
    town: "Pinnawala",
    district: "Kegalle",
    province: "Sabaragamuwa-Province",
    provinceId: "sabaragamuwa",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Pinnawala/Pinnawala-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Pinnawala/Pinnawala-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Pinnawala/Pinnawala-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Pinnawala/Pinnawala-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Pinnawala/Pinnawala-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/kegalle/Pinnawala/Pinnawala-5.jpg`,
    ],
    shortDescription:
    "Pinnawala Elephant Orphanage is a must-visit for animal lovers, families, and photographers.Experience the charm of Sri Lanka’s gentle giants, witness feeding and bathing rituals, and enjoy a unique wildlife encounter. ",
    fullDescription:
      "",
    whyVisit: [
      "Close-Up Elephant Encounters – Observe and photograph elephants up close, including babies.",
      "Feeding & Bathing Sessions – Watch elephants being fed and bathing in the Maha Oya river.",
    	"Conservation & Education – Learn about elephant rescue, care, and conservation efforts.",
      "Family-Friendly Attraction – Suitable for adults, children, and animal lovers.",
      "Scenic Riverside Location – Set amidst lush greenery with a flowing river providing a picturesque backdrop."

    ],
    overview:
    "Pinnawala Elephant Orphanage, located in Pinnawala, Sabaragamuwa Province, was established in 1975 to care for abandoned and injured elephants. It is now one of the most popular wildlife attractions in Sri Lanka, housing over 80 elephants, including adults and calves. Visitors can watch elephants interact naturally, learn about their care, and witness feeding and bathing rituals.",
    ticketsEntry: {
      residents: " LKR 250",
      foreigners: "LKR 5,000 (~USD 15) ",
      children: "LKR 2,500 (~USD 7.50) ",
      notes: [
          "Guided Tours: Optional; LKR 1,500–2,500 (~USD 5–8) per guide",
          "Photography: Allowed; bring a zoom lens for close-ups"
      ]
    },
    highlights: [
      {
        main:"Elephant Feeding & Interaction",
        subPoints:[
          "Elephants fed 3 times daily; visitors can observe or sometimes assist",
          "Baby elephants receive special care and milk feedings."
        ]},
        {
          main:"Elephant Bathing in Maha Oya",
          subPoints:[
            "Daily ritual where elephants are taken to the river for a refreshing bath.",
            "Great for photography and observing social behaviors of elephants."
        ]},
        {
          main:"Scenic Location",
          subPoints:[
            "The orphanage is set along the Maha Oya river, surrounded by lush greenery.",
            "Offers a peaceful environment for walks along the riverbanks."
        ]},
        {
          main:"Photography Opportunities",
          subPoints:[
            "Capture elephants during feeding, bathing, and playtime.",
            "Sunrise and morning visits provide soft natural lighting for photos."
          ]
        },
        {
          main:"Educational & Conservation Focus",
          subPoints:[
            "Learn about the plight of orphaned and rescued elephants in Sri Lanka.",
            "Understand conservation efforts and ethical care practices."
          ]
        }
    ],
    
    visitingHours: {
      daily:
        "8:30 AM – 5:00 PM",
      bestTime: [
        "Morning for feeding and bathing sessions; fewer crowds mid-week.",
      ]
    },
   gettingThere: {
    fromTown: "5–10 minutes by car or tuk-tuk",
    fromNearby: [
      "From Colombo Fort:",
        "🚗 By Car/Taxi: ~2.5–3 hours (90 km), fare USD 25–30 / LKR 8,000–10,000",
        "🚌 By Public Bus: Colombo–Pinnawala buses available; then a short walk or tuk-tuk to orphanage",
        "🚆 By Train: Colombo–Kegalle or Colombo–Peradeniya train, then tuk-tuk or taxi to Pinnawala (~30–45 min).",

        "From Bandaranaike International Airport (BIA):- By Car/Taxi:~2.5–3 hours (90 km), fare USD 25–30 / LKR 8,000–10,000",

    ], 
    
    },  
    nearbyHotels: [
      "Budget Guesthouses:Pinnawala town and surrounding villages ",
      "Mid-Range Hotels: Kegalle and Rambukkana (~20–30 min drive).",
      "Luxury Options: Hotels in Kurunegala or Colombo (~2–3 hours away)."
      
    ],
    travelTips: [
      "Visit early morning for elephant feeding and bathing sessions.",
      "Wear comfortable walking shoes and light clothing.",
      "Carry water, sunscreen, and a hat for sun protection.",
      "Respect elephants; avoid sudden movements or loud noises.",
      "Combine with Kegalle or Rambukkana sightseeing for a day trip."

    ],
    didYouKnow: "Pinnawala Elephant Orphanage is home to the largest herd of captive elephants in Sri Lanka. Many elephants were rescued from abandonment, injury, or conflict, and the orphanage has become a center for elephant conservation and public awareness.",
    nearby: [
      "-",
      "-",
      
    ],
  },
  
  //Rathanpura district
  {
    id: "Adams-Peak",
    title: "Adam’s Peak (Sri Pada) – Sacred Mountain & Pilgrimage Site",
    town: "Nallathanniya",
    district: "Rathnapura",
    province: "Sabaragamuwa-Province",
    provinceId: "sabaragamuwa",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Adam Peak/Adam’s-Peak-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Adam Peak/Adam’s-Peak-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Adam Peak/Adam’s-Peak-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Adam Peak/Adam’s-Peak-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Adam Peak/Adam’s-Peak-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Adam Peak/Adam’s-Peak-5.jpg`,
    ],
    shortDescription:
    "Adam’s Peak (Sri Pada) is a must-visit for pilgrims, hikers, and nature lovers.Experience the climb, witness sunrise above the clouds, and feel the spiritual energy of one of Sri Lanka’s most iconic mountains. ",
    fullDescription:
      "",
    whyVisit: [
      "Spiritual Significance – Famous for the “Sri Pada” footprint, sacred to Buddhists, Hindus, Christians, and Muslims alike.",
      "Pilgrimage Destination – Attracts thousands of devotees yearly, especially during the pilgrimage season (December–May).",
      "Stunning Sunrises – Witness breathtaking sunrise views above the clouds from the summit.",
      "Scenic Hiking Trails – Lush forests, waterfalls, and misty mountains along well-marked trails.",
      "Cultural Experience – Interact with local pilgrims, climb along decorated paths, and observe traditional rituals."

    ],
    overview:
    "Adam’s Peak (Sri Pada) is a 2,243-meter (7,359 ft) tall mountain in central Sri Lanka, known for its sacred footprint at the summit. The mountain is a multi-religious pilgrimage site: Buddhists revere it as Buddha’s footprint, Hindus as Shiva’s, Muslims as Adam’s, and Christians as St. Thomas’s. The climb is an iconic experience, combining spirituality, natural beauty, and adventure.",
    ticketsEntry: {
      residents: "  Free",
      foreigners: " Free",
      children: " Free",
      notes: [
          "Pilgrimage Season: December–May (highest footfall)",
          "Guided Hikes: Optional; LKR 2,000–5,000 (~USD 6–15) per guide for groups",
          "Photography: Allowed; respect pilgrims during rituals"
      ]
    },
    highlights: [
     {
      main:"Sacred Footprint (Sri Pada)",
      subPoints:[
        "Ancient rock formation believed to be the footprint of Buddha, Shiva, Adam, or St. Thomas.",
        "Pilgrims offer flowers and light lamps during rituals.",
        "Summit is the spiritual climax of the climb."
      ]
     },
     {
      main:"Scenic Hike & Nature Trails",
      subPoints:[
          "Trails lined with dense forests, streams, and waterfalls.",
          "Steps numbered along the way with resting spots and small shops",
          "Wildlife includes monkeys, birds, and tropical flora"
      ]
     },
     {
      main:"Sunrise & Photography",
      subPoints:[
        "Best seen at summit early morning for a spectacular sunrise above the clouds.",
        "Misty valleys, terraced hills, and golden sky make incredible photos."
      ]
     },
     {
      main:"Adventure & Pilgrimage Experience",
      subPoints:[
          "Trails vary in difficulty; average climb takes 4–6 hours.",
          "Night climbs popular during pilgrimage season to reach summit by sunrise.",
          "Experience local culture and religious devotion along the route."
      ]
     }
      
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours, but summit climbs typically start 10 PM–2 AM for sunrise viewing.",
      bestTime: [
        "December–May for dry weather and pilgrimage season. Avoid monsoon months (June–September).",
      ]
    },
   gettingThere: {
    fromTown: "5–10 minutes by car or tuk-tuk",
    fromNearby: [
      "From Colombo Fort:",
        "🚗 By Car/Taxi: 5–6 hours (200 km), fare USD 60–70 / LKR 20,000–25,000",
        "🚌 By Public Bus: Colombo–Hatton/Balakot bus, then tuk-tuk or local transport to Nallathanniya or Dalhousie trailhead (~15–30 minutes)",
        "🚆 By Train: Colombo–Hatton train (~5 hours), then tuk-tuk to trailhead (~30 min)",

        "From Bandaranaike International Airport (BIA):- By Car/Taxi:~5.5–6.5 hours (210 km), cost USD 60–70 / LKR 20,000–25,000",

    ], 
    
    },  
    nearbyHotels: [
      "Trailhead Guesthouses: Budget-friendly stays at Nallathanniya or Dalhousie",
      "Hatton & Ratnapura Hotels (3★–4★) – 30–60 minutes drive from trailhead",
      "Eco-Lodges & Homestays – Small accommodations along trail and nearby villages."
      
    ],
    travelTips: [
      "Start your climb at night to reach summit for sunrise.",
      "Wear sturdy hiking shoes and bring warm clothes, as summit can be cold.",
      "Carry water, snacks, and a flashlight/headlamp.",
      "Respect pilgrims and avoid loud noises along the trail.",
      "Check weather conditions; trails can be slippery during rain.",
      "Use the Dalhousie trail for a shorter climb, Nallathanniya for the classic experience."


    ],
    didYouKnow: "Adam’s Peak is one of Sri Lanka’s most sacred mountains, drawing hundreds of thousands of pilgrims annually. Its footprint relic, misty forest trails, and sunrise views make it both a spiritual and adventure destination.",
    nearby: [
      "-",
      "-",
      
    ],
  },
  {
    id: "Bopath-Ella",
    title: "Bopath Ella Falls – Stunning Natural Waterfall",
    town: "Rathnapura",
    district: "Rathnapura",
    province: "Sabaragamuwa-Province",
    provinceId: "sabaragamuwa",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Bopath Ella/Bopath-Ella-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Bopath Ella/Bopath-Ella-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Bopath Ella/Bopath-Ella-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Bopath Ella/Bopath-Ella-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Bopath Ella/Bopath-Ella-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Bopath Ella/Bopath-Ella-5.jpg`,
    ],
    shortDescription:
    "Bopath Ella Falls is perfect for nature lovers, photographers, and those seeking a tranquil retreat.Admire the heart-shaped cascade, explore surrounding trails, and enjoy a refreshing escape near Ratnapura.",
    fullDescription:
      "",
    whyVisit: [
      "Iconic Heart-Shaped Waterfall – Unique heart shape visible from certain angles, perfect for photography.",
      "Scenic Beauty – Surrounded by lush greenery, tea estates, and tropical forests.",
      "Relaxing Atmosphere – Peaceful environment for picnics and nature walks.",
      "Easy Accessibility – Located close to Ratnapura town, making it convenient for a day trip.",
      "Wildlife & Nature – Opportunities to spot birds, butterflies, and tropical flora."

    ],
    overview:
    "Bopath Ella Falls, located near Ratnapura in Sri Lanka’s Sabaragamuwa Province, is a popular waterfall named after its heart-shaped appearance (“Bopath” means Bo leaf). The waterfall plunges from a height of approximately 30 meters into a natural pool, attracting tourists, photographers, and nature lovers. It’s ideal for short hikes, picnics, and scenic photography",
    ticketsEntry: {
      residents: "LKR 150–250",
      foreigners: " LKR 150–250 (~USD 0.50–1) ",
      children: " ",
      notes: [
          "Parking: Available near the falls (small fee may apply)",
          "Photography: Allowed; great for both casual and professional shots"
      ]
    },
    highlights: [
      {
      main:"Waterfall & Scenic Views",
      subPoints:[
        "30-meter cascade forming a natural pool at the base.",
        "Heart-shaped appearance visible from certain viewpoints.",
        "Surrounding landscape includes paddy fields, tea estates, and forested hills.",
      ]
     },
     {
      main:"Photography Opportunities",
      subPoints:[
          "Best light in the morning or late afternoon.",
          "Capture the waterfall, surrounding greenery, and reflections in the pool."
      ]
     },
     {
      main:"Nature & Leisure",
      subPoints:[
          "Walk along trails surrounding the waterfall.",
          "Picnic spots and shaded areas near the stream.",
          "Spot local birds, butterflies, and tropical flora"
      ]
     },
     {
      main:"Nearby Attractions",
      subPoints:[
        "Ratnapura Gem Mines – Learn about Sri Lanka’s gemstones.",
        "Sinharaja Forest Reserve – Tropical rainforest for hiking and wildlife.",
        "Local Villages – Experience rural Sri Lankan culture."
      ]
     }
      
    ],
    
    visitingHours: {
      daily:
        "7:00 AM – 6:00 PM",
      bestTime: [
        "Morning for calm waters and soft sunlight; avoid heavy rain months (May–September).",
      ]
    },
   gettingThere: {
    fromTown: "20-25 minutes by car or tuk-tuk",
    fromNearby: [
      "From Colombo Fort:",
        "🚗 By Car/Taxi: 3.5–4 hours (120 km), fare USD 35–40 / LKR 12,000–14,000",
        "🚌 By Public Bus:Colombo–Ratnapura buses; short tuk-tuk ride to the falls (~10–15 min)",
        "🚆 By Train: Colombo–Ratnapura train (~5 hours), then tuk-tuk to Bopath Ella (~15 min)",

        "From Bandaranaike International Airport (BIA):- By Car/Taxi:~3.5 hours (120 km), cost USD 35–40 / LKR 12,000–14,0000",

    ], 
    
    },  
    nearbyHotels: [
      "Ratnapura Guesthouses & Hotels – Budget and mid-range accommodations within 15–20 minutes.",
      "Eco-Lodges & Homestays – Stay closer to nature near the falls.",
      "Luxury Hotels – Options in Ratnapura town or nearby towns like Balangoda."
      
    ],
    travelTips: [
      "Wear comfortable shoes for walking around trails and slippery rocks.",
      "Bring water, snacks, and sun protection.",
      "Visit early morning or late afternoon for best lighting and fewer crowds.",
      "Respect nature; avoid littering near the falls.",
      "Combine with Ratnapura Gem Mines or Sinharaja Forest Reserve for a full-day itinerary."
    ],
    didYouKnow: "Bopath Ella Falls is famous not just for its heart-shaped appearance, but also for the lush tropical ecosystem surrounding it. Its proximity to Ratnapura makes it an accessible and picturesque escape into nature.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Gem-Mines",
    title: "Ratnapura Gem Mines – Sri Lanka’s Gemstone Heartland",
    town: "Rathnapura",
    district: "Rathnapura",
    province: "Sabaragamuwa-Province",
    provinceId: "sabaragamuwa",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Gem Mines/Gem-Mines-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Gem Mines/Gem-Mines-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Gem Mines/Gem-Mines-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Gem Mines/Gem-Mines-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Gem Mines/Gem-Mines-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Gem Mines/Gem-Mines-5.jpg`,
    ],
    shortDescription:
    "Ratnapura’s gem mines offer a unique blend of cultural heritage, natural beauty, and geological wonder.Explore the depths of Sri Lanka’s gemstone industry and experience the vibrant traditions of the “City of Gems.”",
    fullDescription:
      "",
    whyVisit: [
    "World-Class Gem Deposits – Home to some of the world’s finest sapphires, rubies, topaz, and moonstones.",
    "Traditional Mining Culture – Experience hand-dug pits, bamboo sieves, and manual washing techniques.",
    "Authentic Local Experience – Interact with miners and shop for ethically sourced gems.",
    "Educational Insight – Learn the geology, history, and craftsmanship behind Sri Lanka’s gem industry.",
    "Scenic Beauty – Explore lush landscapes, waterfalls, and tea plantations surrounding the mines."


    ],
    overview:
    "Ratnapura, located approximately 100 km southeast of Colombo, is renowned for its rich deposits of precious and semi-precious gemstones. The city’s name, meaning “City of Gems,” reflects its long-standing association with gem mining. Visitors can explore traditional mining methods, visit gem treatment centers, and purchase certified gemstones.",
    ticketsEntry: {
      residents: "LKR 500–1,000 (~USD 2–5) ",
      foreigners: "LKR 500–1,000 (~USD 2–5)  ",
      children: " ",
      notes: [
          "Guided Tours: Available for approximately LKR 2,000–5,000 (~USD 6–15), providing in-depth knowledge and safe mine visits.",
          "Gem Purchases: Certified gemstones can be purchased directly from miners or authorized dealers."
      ]
    },
    highlights: [
      {
    main: "Traditional Gem Mining",
    subPoints: [
      "Hand-Dug Pits – Mines are typically 10 to 50 meters deep, excavated manually using shovels and bamboo scaffolding.",
      "Mining Process – Soil is extracted and washed in water-filled pits; heavier gems like sapphires and rubies settle at the bottom.",
      "Safety Measures – Mines are reinforced with wooden structures, and water pumps are used to remove excess water."
    ]
  },
  {
    main: " Gem Museums & Markets",
    subPoints: [
      "Sri Lanka Gem Museum – Offers insights into the history and varieties of Sri Lankan gemstones.",
      "Gem Markets – Visit markets like Abagahamula, Clock Tower, and Demuwamawatha to observe gem trading activities.",
      "Gem Treatment Centers – See how raw gems are cleaned, cut, and polished for sale."
    ]
  },
  {
    main: "Scenic Surroundings",
    subPoints: [
      "Waterfalls – Explore nearby waterfalls such as Bopath Ella, known for its unique leaf-shaped cascade.",
      "Tea Plantations – Visit surrounding tea estates for a glimpse into Sri Lanka's tea industry.",
      "Nature Trails – Enjoy walks through lush forests and paddy fields."
    ]
  }
      
    ],
    
    visitingHours: {
      daily:
        "7:00 AM – 6:00 PM",
      bestTime: [
        "Pilgrimage Season: December–May attracts many visitors due to favorable weather and cultural events.",
      ]
    },
   gettingThere: {
    fromTown: "20-25 minutes by car or tuk-tuk",
    fromNearby: [
      "From Colombo Fort:",
        "🚗 By Car/Taxi:Approximately 3.5–4 hours (120 km), fare around USD 35–40 (~LKR 12,000–14,000).",
        "🚌 By Public Bus:Regular buses to Ratnapura; then, use local transport to reach specific mines.",
        "🚆 By Train: Train to Ratnapura; then, a short tuk-tuk ride to the mines",

        "From Bandaranaike International Airport (BIA):- By Car/Taxi:~: Approximately 3.5–4 hours (120 km), fare around USD 35–40 (~LKR 12,000–14,000)"

    ], 
    
    },  
    nearbyHotels: [
      "Budget Options: Guesthouses and homestays in Ratnapura town.",
      "Mid-Range: Hotels and eco-lodges offering amenities for travelers.",
      "Luxury: Resorts and boutique hotels in nearby towns like Kalawana and Pelmadulla"
      
    ],
    travelTips: [
      "Safety: Wear sturdy footwear and be cautious around open pits.",
    	"Respect: Be mindful of local customs and the hard work of miners.",
      "Shopping: Purchase gems from reputable dealers; request certification for authenticity.",
      "Hydration: Carry water and sun protection, especially if visiting during midday."

    ],
    didYouKnow: "Ratnapura is not only famous for its gemstones but also for the discovery of Ekanite, a rare radioactive gemstone first identified in Eheliyagoda in 1953. This discovery adds to the region's rich geological significance.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Sinharaja-Forest",
    title: "Sinharaja Forest Reserve – UNESCO World Heritage Rainforest",
    town: "Rathnapura",
    district: "Rathnapura",
    province: "Sabaragamuwa-Province",
    provinceId: "sabaragamuwa",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Sinharaja Forest/Sinharaja-Forest-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Sinharaja Forest/Sinharaja-Forest-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Sinharaja Forest/Sinharaja-Forest-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Sinharaja Forest/Sinharaja-Forest-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Sinharaja Forest/Sinharaja-Forest-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Sabaragamuwa/Rathnapura/Sinharaja Forest/Sinharaja-Forest-5.jpg`,
    ],
    shortDescription:
    "Sinharaja Forest Reserve is a must-visit for eco-tourists, wildlife enthusiasts, and nature photographers.",
    fullDescription:
      "",
    whyVisit: [
      "Biodiversity Hotspot – Home to hundreds of endemic species of flora and fauna, including rare birds, reptiles, and butterflies.",
      "UNESCO World Heritage Site – Recognized for its ecological and conservation significance.",
      "Pristine Tropical Rainforest – Dense jungle, streams, waterfalls, and rich vegetation.",
      "Adventure & Eco-Tourism – Ideal for guided hikes, birdwatching, and nature photography.",
      "Educational Experience – Learn about rainforest ecosystems, conservation, and endemic wildlife."
    ],
    overview:
    "Sinharaja Forest Reserve, located in the Sabaragamuwa and Southern Provinces, is Sri Lanka’s last viable area of primary tropical rainforest. Covering approximately 11,000 hectares, it is a biodiversity hotspot with over 50% of its trees endemic to Sri Lanka. The forest provides a perfect destination for nature enthusiasts, researchers, and eco-tourists.",
    ticketsEntry: {
      residents: "LKR 1,500 (~USD 5) ",
      foreigners: "LKR 1,500 (~USD 5) ",
      children: "LKR 750 (~USD 2.50) ",
      notes: [
          "Guide Fee: LKR 1,500–2,500 (~USD 5–8) per group (mandatory for first-time visitors)",
          "Photography: Allowed; avoid disturbing wildlife"
      ]
    },
    highlights: [
      {
    main: "Flora & Fauna",
    subPoints: [
      "Rich biodiversity: over 200 species of birds, 50+ endemic reptiles, and rare mammals.",
      "Dense canopy, giant trees, medicinal plants, and tropical flowers.",
      "Trails pass streams, waterfalls, and natural pools."
    ]
  },
  {
    main: " Gem Museums & Markets",
    subPoints: [
      "Sri Lanka Gem Museum – Offers insights into the history and varieties of Sri Lankan gemstones.",
      "Gem Markets – Visit markets like Abagahamula, Clock Tower, and Demuwamawatha to observe gem trading activities.",
      "Gem Treatment Centers – See how raw gems are cleaned, cut, and polished for sale."
    ]
  },
  {
    main: "Birdwatching & Wildlife Spotting",
    subPoints: [
      "Endemic species like the Red-faced Malkoha and Sri Lanka Blue Magpie.",
      "Monkeys, giant squirrels, and a variety of amphibians.",
      "Guided tours enhance chances of spotting elusive wildlife."
    ]
  },
  {
    main:"Photography Opportunities",
    subPoints:[
      "Misty forest trails, cascading waterfalls, and endemic wildlife provide stunning shots.",
      "Early morning or late afternoon for the best lighting and less disturbance to wildlife."
    ]
  },
  {
    main:"Hiking & Nature Trails",
    subPoints:[
      "Trails vary from short 2–3 hour walks to full-day hikes.",
      "Popular trails: Morney Trail, Delgoda Trail, and Kudawa Entrance.",
      "Guides available at entrance to explain flora, fauna, and forest ecology."
    ]
  }
      
    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 5:00 PM",
      bestTime: [
        "December–March (dry season) for easier trails and clearer streams",
      ]
    },
   gettingThere: {
    fromTown: "1.4 hours by car or tuk-tuk",
    fromNearby: [
      "From Colombo Fort:",
        "🚗 By Car/Taxi:5–6 hours (170 km), fare USD 50–60 / LKR 18,000–22,000",
        "🚌 By Public Bus:Colombo–Deniyaya or Colombo–Rakwana; tuk-tuk to forest entrance",
        "🚆 By Train: Colombo–Ratnapura or Colombo–Matara line, then taxi/tuk-tuk to Kudawa or other entrances",

        "From Bandaranaike International Airport (BIA):- By Car/Taxi:5.5–6.5 hours (180 km), fare USD 50–60 / LKR 18,000–22,000"

    ], 
    
    },  
    nearbyHotels: [
      "Eco-Lodges & Jungle Retreats: Available near Kudawa and Delgoda entrances.",
      "Budget Guesthouses: In Deniyaya or Rakwana town.",
      "Mid-Range Hotels: Comfortable stays with easy access to forest trails."
      
    ],
    travelTips: [
      "Wear sturdy shoes, insect repellent, and light, breathable clothing.",
      "Carry water and snacks; few facilities inside the reserve.",
      "Use a local guide to maximize wildlife spotting and ensure safety.",
      "Respect wildlife: do not touch or feed animals.",
      "Combine with Bopath Ella Falls, Ratnapura gem mines, or tea plantations for a full-day itinerary."
    ],
    didYouKnow: "Sinharaja Forest Reserve is Sri Lanka’s only rainforest World Heritage Site. Its name means “Lion Forest”, highlighting its historic and cultural significance. The reserve is crucial for biodiversity conservation and climate regulation in the region.",
    nearby: [
      "",
      "-",
      
    ],
  },

//Sothern Province
//Galle District
{
    id: "Galle-Fort",
    title: "Galle Fort",
    town: "Galle",
    district: "Galle",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Galle Fort/Galle-Fort-1.jpg`,
    images: [
       `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Galle Fort/Galle-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Galle Fort/Galle-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Galle Fort/Galle-Fort-1.jpg`,
       `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Galle Fort/Galle-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Galle Fort/Galle-Fort-1.jpg`,
     
    ],
    shortDescription:
    "",
    fullDescription:
      "",
    whyVisit: [
      "Historic Landmark – Built originally by the Portuguese in 1588 and later greatly expanded by the Dutch in the 17th century.",
      "Architectural Beauty – Within its walls you’ll find colonial-era bastions, cobblestone lanes, grand houses, churches and a mix of South Asian and European styles.",
      "Cultural Experience – It remains a living neighbourhood with homes, shops, galleries and cafés inside the fort walls. ",
      "Shopping & Dining Hotspot – Boutique stores, craft shops, fine cafés and seafood by the sea lend the area plenty of charm.", 
      "Scenic Seaside Views – Walk the ramparts overlooking the Indian Ocean at sunset, see the lighthouse and feel the sea breeze." 

    ],
    overview:
    "Galle Fort is located on a peninsula on Sri Lanka’s south-coast in the town of Galle. The fortifications, ramparts and colonial architecture make it one of the best-preserved European‐era forts in South & Southeast Asia. Explore by foot: narrow lanes, colonial houses, ramparts, and sea views combine into a compact but rich historic experience.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
          "The fort’s public areas (streets, ramparts) are free to access.",
          "Some specific houses/museums inside may charge small fees.",
          "Plan as “free entry to fort”, with optional small payments for special attractions"

      ]
    },
    highlights: [
      {
    main: "Architectural Landmarks",
    subPoints: [
      "Outer ramparts & bastions - walk along the sea-facing walls for sweeping ocean views.",
      "The lighthouse near the fort walls - a signature photo spot.",
      "Dutch Reformed Church (built 1755) and many colonial houses inside the fort precinct.",
      "The living urban fabric - homes, galleries, cafés in the old buildings."

    ]
  },
  {
    main: " Shopping & Food",
    subPoints: [
      "Boutique stores and craft galleries inside and near the fort. ",
      "Cafés and seaside eateries for fresh seafood and local cuisine.",
      "Hidden alleys and small squares make exploring fun."

    ]
  },
  {
    main: "Scenic Walks",
    subPoints: [
     "Walk the outer wall of the fort at late afternoon for the golden-hour light.",
     "Wander the narrow streets inside the fort, peek into little courtyards, enjoy colonial facades.",

    ]
  }, 
    ],
    
    visitingHours: {
      daily:
        "Public outdoor areas: essentially open all day.",
      bestTime: [
        "mornings (8-10 AM) or late afternoon (~4-6 PM) to avoid midday heat and to capture the view at golden-hour.",
      ]
    },
   gettingThere: {
    fromTown: "5 minite by car or tuk-tuk",
    fromNearby: [
      "From Colombo Fort:",
        "🚗 By Car/Taxi:: approx. 2 to 2.5 hours (depending on traffic) via the southern expressway.",
        "🚌 By Public Bus:Frequent departures. For example: route number EX001 runs between Colombo (Makumbura) and Galle Central Bus Station. Sample timetable: buses depart early (from ~05:00) every ~20 minutes, last buses ~20:40. Also there are regular coach departures: e.g., 09:00 from Colombo arriving ~10:20 into Galle.",
        "🚆 By Train: The scenic coastal train from Colombo Fort station to Galle station is also very popular. ",

    ], 
    
    },  
    nearbyHotels: [
     "Galle Fort Hotel – A boutique heritage hotel inside the fort walls. ",
      "Amangalla – Luxury hotel set in a historic building within the fort precinct.",
      "Le Grand Galle – Another highly rated hotel near the fort, good for upscale stays." 

      
    ],
    travelTips: [
      "Wear light, breathable clothing and comfortable shoes — stone pavements inside the fort can be uneven.",
      "Use a tuk-tuk or taxi for bag transport if you’re staying in older lanes inside the fort.",
      "Bring cash (Sri Lankan rupees) for smaller cafés, crafts and market shops.",
      "Arrive late afternoon for the ramparts walk— the sea breeze and sunset make it memorable.",
      "Treat the fort neighbourhood respectfully — locals live and work inside its walls.",

    ],
    didYouKnow: "Galle Fort is a living World Heritage site: the ramparts and fortifications were added in the Dutch colonial period, and the fort’s layout retains the original defensive design. It is still inhabited and functional as a neighbourhood.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Hikkaduwa-Beach",
    title: "Hikkaduwa Beach – Vibrant Coastal Escape & Snorkeling Hub",
    town: "Hikkaduwa",
    district: "Galle",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Hikkaduwa Beach/Hikkaduwa-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Hikkaduwa Beach/Hikkaduwa-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Hikkaduwa Beach/Hikkaduwa-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Hikkaduwa Beach/Hikkaduwa-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Hikkaduwa Beach/Hikkaduwa-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Hikkaduwa Beach/Hikkaduwa-Beach-5.jpg`,
    ],
    shortDescription:
    "Dive into clear waters, relax on golden sands, and explore marine wonders at Hikkaduwa Beach, a perfect blend of adventure and coastal beauty.",
    fullDescription:
      "",
    whyVisit: [
      "Snorkeling & Diving Paradise – Coral reefs and abundant marine life just offshore.",
      "Surfing & Water Sports – Waves suitable for beginners and intermediate surfers.",
      "Scenic Views – Golden sands, coconut palms, and turquoise waters ideal for photography.",
      "Vibrant Beach Life – Lively atmosphere with beachfront bars, cafés, and local vendors.",
      "Marine Exploration – Opportunities to see tropical fish, sea turtles, and colorful corals."

    ],
    overview:
    "Hikkaduwa Beach, located on Sri Lanka’s southern coast in Galle District, is a lively seaside town famous for its coral sanctuary, vibrant nightlife, and water sports. The shallow reef area makes it perfect for snorkeling, while the outer beach offers gentle surf for learners. The town itself has a mix of local markets, restaurants, and beachfront bars, creating a dynamic blend of relaxation and entertainment.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
          "Snorkeling/Diving: ~USD 15–30 per session (prices may fluctuate)",
          "Equipment Rentals: ~USD 5–10",

      ]
    },
    highlights: [
      "Snorkeling & Coral Reefs – Explore shallow waters filled with tropical fish and corals.",
      "Surfing & Water Sports – Surf schools and rentals for all skill levels.",
      "Scenic Beach Views – Coconut-fringed beaches with turquoise waters.",
      "Beachfront Cafés & Bars – Enjoy local seafood, tropical drinks, and live music.",
      "Marine Wildlife Spotting – Chance to see sea turtles and colorful reef fish."

    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        " Morning for snorkeling; late afternoon for sunset and photography",
      ]
    },
   gettingThere: {
    fromTown: "~45 minutes by car (~35 km)",
    fromNearby: [
      "From Colombo Fort:~5–6 hours by car (~140 km) via Southern Expressway",
        "By Public Bus:Regular buses from Colombo, Galle, or Matara; tuk-tuks available locally,",

    ], 
    parking: "Available near main beach areas",
    
    },  
    nearbyHotels: [
      "Hikka Tranz by Cinnamon – Luxury beachfront resort",
      "Coral Sands Hotel – Mid-range stay with direct beach access",
      "Local Guesthouses & Hostels – Budget-friendly accommodations in Hikkaduwa town"
    ],
    travelTips: [
      "Swimwear recommended; modest clothing when leaving beach areas.",
      "Bring sunscreen, hat, and water for daytime visits.",
      "Respect coral reefs and marine life; avoid stepping on corals.",
      "Early morning for calm waters; late afternoon for photography and fewer crowds.",
      "Snorkeling tours often available via local operators — confirm prices in advance."

    ],
    didYouKnow: "Hikkaduwa Beach is home to the Hikkaduwa Coral Sanctuary, one of Sri Lanka’s first marine protected areas, preserving vibrant coral reefs and diverse marine life. It’s a hotspot for snorkelers, divers, and beach lovers alike.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Japanese-Peace",
    title: "Japanese Peace Pagoda – Tranquil Buddhist Monument & Panoramic Views",
    town: "Galle",
    district: "Galle",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/japanese peace pagoda/Japanese-Peace-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/japanese peace pagoda/Japanese-Peace-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/japanese peace pagoda/Japanese-Peace-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/japanese peace pagoda/Japanese-Peace-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/japanese peace pagoda/Japanese-Peace-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/japanese peace pagoda/Japanese-Peace-5.jpg`,
    ],
    shortDescription:
    "Experience calm, stunning vistas, and spiritual reflection at the Japanese Peace Pagoda, a beacon of peace atop Rumassala Hill.",
    fullDescription:
      "",
    whyVisit: [
        "Symbol of Peace – Built to promote harmony and unity, a serene spiritual landmark.",
        "Panoramic Views – Overlooks Unawatuna, Galle, and the southern coastline from the hilltop.",
        "Peaceful Atmosphere – Quiet meditation spaces surrounded by tropical greenery.",
        "Architectural Beauty – White stupa with golden spire, typical Japanese Buddhist design.",
        "Leisurely Walks – Trails through lush forest and landscaped gardens enhance the serene experience."
    ],
    overview:
    "Located on Rumassala Hill near Unawatuna, the Japanese Peace Pagoda is a striking white stupa built by Japanese Buddhist monks in 2005. It is part of a global network of Peace Pagodas promoting world peace. The site combines spiritual reflection with stunning views of the southern coast of Sri Lanka. Visitors can explore the pagoda, meditate in the tranquil surroundings, and enjoy scenic walks along the hill.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
      ]
    },
    highlights: [
      "Peaceful Stupa & Meditation Area – Ideal for quiet contemplation.",
      "Scenic Views – Capture Galle, Unawatuna, and the turquoise Indian Ocean.",
      "Tropical Trails – Walk through forested paths and gardens leading to the stupa.",
      "Japanese Architecture – Minimalist white stupa with golden finial; serene and photogenic.",
      "Sunrise & Sunset Spot – Panoramic hilltop views perfect for photography."

    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning for sunrise; late afternoon for sunset and cooler weather",
      ]
    },
   gettingThere: {
    fromTown: "~25–30 minutes by car (~12 km)",
    fromNearby: [
      "From Colombo :5–6 hours by car (~150 km) via Southern Expressway",
      "From Unawatuna: ~5–10 minutes by car (~2 km)",
      "Public Transport:Tuk-tuks available from Unawatuna or Galle town",

    ], 
    parking: "Small local parking at base of the hill",
    
    },  
    nearbyHotels: [
      "Thaproban Beach House – Boutique stay near Unawatuna Beach",
      "Mirissa Hills Villa – Mid-range resort with ocean views nearby",
      "Local Guesthouses & Homestays – Affordable accommodations in Unawatuna and Galle"

    ],
    travelTips: [
      "Dress modestly (shoulders and knees covered) when entering temple grounds.",
      "Wear comfortable shoes for walking up the hill.",
      "Bring water, sunscreen, and a hat.",
      "Maintain silence and respect the meditation areas.",
      "Early mornings or late afternoons are best for fewer crowds and ideal photography."

    ],
    didYouKnow: "The Japanese Peace Pagoda is one of several Peace Pagodas worldwide, built to inspire peace, non-violence, and harmony. Its hilltop location offers one of the most serene and scenic viewpoints along Sri Lanka’s southern coast.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Jungle-Beach",
    title: "Jungle Beach – Hidden Tropical Paradise",
    town: "Galle",
    district: "Galle",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Jungle Beach/Jungle-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Jungle Beach/Jungle-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Jungle Beach/Jungle-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Jungle Beach/Jungle-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Jungle Beach/Jungle-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Jungle Beach/Jungle-Beach-5.jpg`,
    ],
    shortDescription:
    "Escape the crowds and immerse yourself in nature’s tranquility at Jungle Beach, a serene haven along Sri Lanka’s southern coast.",
    fullDescription:
      "",
    whyVisit: [
        "Secluded & Tranquil – Small, quiet beach surrounded by tropical forest.",
        "Clear Waters & Safe Swimming – Calm waters perfect for relaxing and snorkeling.",
        "Scenic & Photogenic – Golden sand, turquoise waters, and jungle backdrop.",
        "Nature Immersion – Walk through forest paths to reach the beach, feeling fully immersed in nature.",
        "Snorkeling & Marine Life – Shallow waters with colorful fish and occasional sea turtles."

    ],
    overview:
    "Located near Unawatuna in southern Sri Lanka, Jungle Beach is a hidden gem tucked away behind lush greenery and small cliffs. Accessible via a short walk from the main road or Unawatuna Beach, it offers a peaceful escape from crowded tourist spots. The beach is ideal for swimming, sunbathing, and snorkeling, making it a favorite among travelers looking for privacy and natural beauty.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
      ]
    },
    highlights: [
      "Safe Swimming & Snorkeling – Gentle waves and shallow waters perfect for families and beginners.",
      "Tropical Surroundings – Beach flanked by jungle, cliffs, and coconut palms.",
      "Photography Opportunities – Capture pristine sands, clear water, and the surrounding greenery.",
      "Secluded Walks – Short jungle trails lead to the beach, adding adventure to the visit.",
      "Marine Exploration – Observe colorful reef fish and occasionally small sea turtles."

    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for tranquility; late afternoon for sunset photography",
      ]
    },
   gettingThere: {
    fromTown: "25–30 minutes by car (~12 km)",
    fromNearby: [
      "From Colombo :~5–6 hours by car (~150 km) via Southern Expressway",
      "From Unawatuna: ~5–10 minutes by car (~2 km)) + short walk to beach",
      "Public Transport:Tuk-tuks available from Unawatuna or Galle town",

    ], 
    parking: "Limited local parking; walking path from main road",
    
    },  
    nearbyHotels: [
      "Thaproban Beach House – Boutique stay near Unawatuna Beach",
      "Mirissa Hills Villa – Mid-range resort with ocean views nearby",
      "Local Guesthouses & Homestays – Affordable accommodations in Unawatuna and Galle"

    ],
    travelTips: [
      "Swimwear recommended; modest clothing when walking through jungle paths.",
      "Bring sunscreen, hat, and water.",
      "Wear comfortable shoes for the short hike to the beach.",
      "Early mornings or late afternoons are best for fewer crowds and optimal lighting.",
      "Avoid leaving trash; maintain the natural beauty of the secluded beach.",

    ],
    didYouKnow: "Jungle Beach is a hidden coastal gem often overlooked by tourists, offering both serenity and adventure in a naturally lush setting. Its seclusion makes it feel like a private tropical escape.",
    nearby: [
      "",
      "-",
      
    ],
  },
   {
    id: "Rumassala",
    title: "Rumassala – Scenic Hill & Coastal Haven",
    town: "Galle",
    district: "Galle",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Rumassala/Rumassala-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Rumassala/Rumassala-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Rumassala/Rumassala-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Rumassala/Rumassala-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Rumassala/Rumassala-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Rumassala/Rumassala-5.jpg`,
    ],
    shortDescription:
    "Explore Rumassala for breathtaking views, tranquil forest trails, and a touch of legendary mystique along Sri Lanka’s southern shores.",
    fullDescription:
      "",
    whyVisit: [
      "Panoramic Views – Offers breathtaking views of the Indian Ocean, Unawatuna Beach, and surrounding coastline.",
      "Lush Greenery – Covered with tropical forest, coconut palms, and herbal plants.",
      "Photography Spot – Perfect for sunset, coastal, and nature photography.",
      "Cultural Significance – Home to ancient legends and believed to be part of Ravana’s mystical route from the Ramayana epic.",
      "Leisurely Walks – Gentle trails through forested hills ideal for nature walks and birdwatching."
    ],
    overview:
    "Rumassala, located near Unawatuna in the southern coast of Sri Lanka, is a small hill overlooking the turquoise waters of the Indian Ocean. The area is rich in tropical flora and fauna, with trails that lead to viewpoints offering spectacular scenery. Rumassala is steeped in local legends, including its connection to Ravana from the Ramayana, adding a layer of cultural mystique to its natural beauty. Visitors can explore the hill, enjoy peaceful walks, and admire the panoramic coastal views.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
      ]
    },
    highlights: [
      "Panoramic Coastal Views – Stunning vistas of Unawatuna Beach, jungle-clad hills, and the ocean.",
      "Tropical Forest Trails – Walk through coconut palms, medicinal plants, and dense greenery.",
      "Photography Opportunities – Capture dramatic sunsets and scenic landscapes.",
      "Cultural & Legendary Sites – Believed to be linked to Ravana’s legends; spiritual aura in certain areas.",
      "Peaceful Nature Escape – Less crowded than nearby beaches, ideal for tranquility and relaxation."
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for sunrise, late afternoon for sunset views",
      ]
    },
   gettingThere: {
    fromTown: "30 minutes by car (~15 km))",
    fromNearby: [
      "From Colombo :~5–6 hours by car (~150 km) via Southern Expressway",
      "From Unawatuna: ~5–10 minutes by car (~2 km)",
      "Public Transport:Tuk-tuks or motorbike rentals from Unawatuna or Galle town",

    ], 
    parking: "Limited local parking",
    
    },  
    nearbyHotels: [
      "Thaproban Beach House – Boutique resort near Unawatuna Beach",
        "Mirissa Hills Villa – Mid-range stay with ocean views nearby",
        "Local Guesthouses & Homestays – Affordable stays in Unawatuna and Rumassala area"
    ],
    travelTips: [
      "Wear comfortable walking shoes for forest trails.",
      "Bring sunscreen, hat, and water, especially during midday.",
      "Swimwear optional if combining with nearby beaches; modest clothing recommended for walking through forested areas.",
      "Early mornings or late afternoons are best to avoid heat and capture the best light for photos.",
      "Respect nature and local legends; avoid littering and disturbing wildlife."
    ],
    didYouKnow: "Rumassala Hill is often associated with the Ramayana legends, believed to be a resting point for Ravana during his journeys. Its combination of natural beauty, history, and legends makes it a unique destination on Sri Lanka’s southern coast.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Stilt-Fishermen",
    title: "Stilt Fishermen – Iconic Coastal Tradition of Sri Lanka",
    town: "Galle",
    district: "Galle",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Stilt Fishermen/Fishermen-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Stilt Fishermen/Fishermen-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Stilt Fishermen/Fishermen-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Stilt Fishermen/Fishermen-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Stilt Fishermen/Fishermen-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Stilt Fishermen/Fishermen-5.jpg`,
    ],
    shortDescription:
    "Witness the remarkable skill and timeless beauty of Sri Lanka’s stilt fishermen, a living symbol of tradition and coastal ingenuity.",
    fullDescription:
      "",
    whyVisit: [
        "Unique Fishing Method – Fishermen perch on stilts planted in shallow water to catch fish.",
        "Photography & Culture – A visually striking and photogenic scene, especially at sunrise or sunset.",
        "Cultural Experience – Witness a centuries-old tradition that showcases Sri Lankan ingenuity and coastal life.",
        "Scenic Coastal Views – Often set against golden sands, palm trees, and turquoise waters.",
        "Local Interaction – Opportunity to meet fishermen and learn about their daily routines and challenges."

    ],
    overview:
    "Stilt fishing, or “ritipanna”, is a traditional fishing practice unique to Sri Lanka’s southern coast, particularly near Weligama, Koggala, and Ahangama. Fishermen sit on crossbars tied to vertical poles planted in shallow waters, sometimes just meters from the shore, using rods to catch small fish like spotted herrings and mackerels. This method allows them to fish without disturbing the water and has become an iconic symbol of Sri Lanka’s coastal culture.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Photography/Interaction: Free; small donations appreciated"
      ]
    },
    highlights: [
        "Traditional Fishing Technique – Fishermen balance on stilts for hours, demonstrating remarkable skill.",
        "Photography Opportunities – Iconic shots with fishermen silhouetted against sunrise or sunset.",
        "Cultural Insight – Learn about the challenges and history of stilt fishing in Sri Lanka.",
        "Scenic Beach & Water Views – Often photographed with coconut palms, golden sands, and calm waters.",
        "Observation & Interaction – Some locations allow safe distances for tourists to observe and interact respectfully."

    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for fishing activities and softer lighting; late afternoon for sunset shots",
      ]
    },
   gettingThere: {
    fromTown: "30 minutes by car (~15 km))",
    fromNearby: [
      "From Colombo :~5–6 hours by car (~160 km) via Southern Expressway",
      "From Mirissa: ~10–15 minutes by car to Weligama",
      "Public Transport: Frequent buses from Matara, Galle, or Colombo; tuk-tuks locally available",

    ], 
    parking: "Limited local parking near main stilt fishing spots",
    
    },  
    nearbyHotels: [
      "Weligama Bay Resort – Luxury beachfront stay near stilt fishing area",
      "Surf & Stay Guesthouses – Mid-range accommodations catering to tourists and surfers",
      "Local Homestays – Budget-friendly options in Weligama and surrounding villages"

    ],
    travelTips: [
      "Wear light, casual clothing suitable for beach or coastal areas.",
      "Bring sunscreen, hat, and water, especially for morning visits.",
      "Respect the fishermen’s space; avoid disturbing their balance or equipment.",
      "Use a zoom lens or camera from a safe distance for photography.",
      "Early morning or late afternoon is ideal for both fishing activity and photo opportunities"

    ],
    didYouKnow: "Stilt fishing originated as a clever solution to shallow coastal waters, allowing fishermen to fish without anchoring boats. Today, it’s not only a livelihood but also one of Sri Lanka’s most iconic and picturesque coastal traditions",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Talpe-Beach",
    title: "Talpe Beach – Serene Coastal Escape",
    town: "Galle",
    district: "Galle",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Thalpe/Talpe-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Thalpe/Talpe-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Thalpe/Talpe-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Thalpe/Talpe-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Thalpe/Talpe-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/Thalpe/Talpe-Beach-5.jpg`,
    ],
    shortDescription:
    "Escape the crowds and soak in the serene beauty of Talpe Beach, a quiet haven on Sri Lanka’s southern coast.",
    fullDescription:
      "",
    whyVisit: [
        "Tranquil Atmosphere – Less crowded than nearby beaches, perfect for relaxation.",
        "Gentle Waves & Safe Swimming – Ideal for families and casual swimmers.",
        "Scenic Views – Palm-fringed coastline, golden sands, and stunning sunsets.",
        "Laid-back Vibe – Small beachfront cafés and local fishing boats create a peaceful setting.",
        "Beach Walks & Leisure – Long stretch of sand perfect for strolling or jogging.",

    ],
    overview:
    "Located just north of Mirissa in the southern coast of Sri Lanka, Talpe Beach is a quiet, picturesque beach known for its golden sands and calm waters. Unlike Mirissa Beach, Talpe offers a more relaxed atmosphere, making it a great spot for travelers seeking peace and solitude. Visitors can enjoy leisurely walks, sunbathing, and exploring nearby small cafés serving fresh seafood and tropical drinks.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Photography/Interaction: Free; small donations appreciated"
      ]
    },
    highlights: [
        "Safe Swimming & Sunbathing – Calm waters suitable for families.",
        "Photography Opportunities – Capture serene sunsets, coconut palms, and fishing boats.",
        "Peaceful Beach Strolls – Less crowded, ideal for walking or jogging along the shore.",
        "Nearby Activities – Surfing, snorkeling, and whale watching are accessible from nearby Mirissa Beach",
        "Beachfront Eateries – Small local cafés offering fresh seafood and tropical drinks"

    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "EEarly morning for sunrise; late afternoon for sunset",
      ]
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
      "From Colombo :~5–6 hours by car (~160 km) via Southern Expressway",
      "From Mirissa:  ~5–10 minutes by car (~2 km)",
      "Public Transport: Tuk-tuks or motorbike rentals from Mirissa",

    ], 
    parking: "Small local parking available near the beach",
    
    },  
    nearbyHotels: [
      "Mandara Resort Mirissa – Mid-range beachfront resort",
      "Casa Colombo Mirissa – Luxury boutique stay nearby",
      "Local Guesthouses – Budget-friendly options within walking distance",
      "Sri Sharavi Beach Villas – Private villas near the beach"

    ],
    travelTips: [
      "Swimwear recommended; modest clothing when leaving the beach.",
      "Bring sunscreen, hat, and water for daytime visits.",
      "Early mornings or late afternoons are best for fewer crowds and optimal lighting.",
      "Respect local vendors and avoid littering on the beach."
    ],
    didYouKnow: "Talpe Beach is often overshadowed by nearby Mirissa Beach, but its tranquil setting and natural beauty make it a hidden gem for those seeking peace, relaxation, and pristine coastal views.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Unawatuna-Beach",
    title: "Unawatuna Beach – Lively Coastal Paradise & Snorkeling Haven",
    town: "Unawatuna",
    district: "Galle",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/unawatuna/Unawatuna-Beach-1.jpg`,
    images: [
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/unawatuna/Unawatuna-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/unawatuna/Unawatuna-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/unawatuna/Unawatuna-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/unawatuna/Unawatuna-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Galle/unawatuna/Unawatuna-Beach-5.jpg`,
    ],
    shortDescription:
    "Escape the crowds and soak in the serene beauty of Talpe Beach, a quiet haven on Sri Lanka’s southern coast.",
    fullDescription:
      "",
    whyVisit: [
        "Golden Sands & Turquoise Waters – Classic tropical beach ideal for sunbathing and swimming.",
        "Snorkeling & Marine Life – Explore coral reefs and colorful tropical fish close to shore.",
        "Scenic & Photogenic – Coconut palms, gentle waves, and vibrant sunsets perfect for photos.",
        "Vibrant Beach Life – Lively atmosphere with beachfront cafés, bars, and local markets.",
        "Water Sports & Activities – Surfing, diving, and boat rides readily available."

    ],
    overview:
    "Unawatuna Beach, located near Galle on Sri Lanka’s southern coast, is one of the most popular beach destinations in the country. Known for its golden sands, clear shallow waters, and coral reefs, it attracts families, travelers, and adventure enthusiasts alike. The area has a lively town with restaurants, bars, and shops, making it ideal for both relaxation and active beach adventures. Snorkeling, diving, and boat trips to nearby coves are highlights for visitors.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Snorkeling/Diving: ~USD 15–30 per session (prices may fluctuate)",
        "Equipment Rentals: ~USD 5–10"
      ]
    },
    highlights: [
        "Swimming & Sunbathing – Safe, shallow waters ideal for all ages.",
        "Snorkeling & Coral Reefs – Spot tropical fish, sea turtles, and colorful corals.",
        "Photography Opportunities – Capture sunsets, palm-fringed beaches, and lively beach life.",
        "Beachfront Cafés & Nightlife – Sample local seafood, tropical drinks, and enjoy live music.",
        "Water Sports & Boat Tours – Surfing, diving, and excursions to nearby hidden beaches"

    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Morning for calm waters and snorkeling; late afternoon for sunset photography",
      ]
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
      "From Colombo :~5–6 hours by car (~150 km) via Southern Expressway",
      "From Galle: ~15–20 minutes by car (~5 km)",
      "Public Transport: Buses from Colombo, Galle, or Matara; tuk-tuks available locally",

    ], 
    parking: "Limited parking near main beach entrances",
    
    },  
    nearbyHotels: [
        "Coco Bay Unawatuna – Mid-range beachfront resort",
        "Thaproban Beach House – Boutique stay close to the beach",
        "Local Guesthouses & Hostels – Budget-friendly options within walking distance"

    ],
    travelTips: [
      "Swimwear recommended; modest clothing when leaving the beach.",
      "Bring sunscreen, hat, and water.",
      "Early morning is ideal for tranquility and clear snorkeling waters.",
      "Late afternoon for sunset photography and fewer crowds.",
      "Respect coral reefs and marine life; avoid stepping on corals."

    ],
    didYouKnow: "Unawatuna Beach is home to a small coral sanctuary, protecting diverse marine life. It has transformed from a sleepy fishing village into a bustling coastal hotspot, yet retains its natural beauty and charm",
    nearby: [
      "",
      "-",
      
    ],
  },

  // Hambantota District 
   {
    id: "Hambantota-Port",
    title: "Hambantota Port – Gateway to Southern Sri Lanka",
    town: "Hambantota",
    district: "Hambantota",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Commercial",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Hambanthota port/Hambantota-Port-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Hambanthota port/Hambantota-Port-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Hambanthota port/Hambantota-Port-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Hambanthota port/Hambantota-Port-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Hambanthota port/Hambantota-Port-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Hambanthota port/Hambantota-Port-5.jpg`,
    ],
    shortDescription:
        "Experience the blend of modern engineering and coastal scenery at Hambantota Port, where trade, strategy, and nature converge.",
    fullDescription:
      "",
    whyVisit: [
      "Modern Engineering Marvel – One of the newest deep-sea ports in Sri Lanka, designed for international trade.",
      "Maritime Hub – Offers views of cargo ships, fishing boats, and container terminals.",
      "Scenic Coastal Views – Panoramic vistas of the Indian Ocean and harbor activities.",
      "Nearby Attractions – Close to natural sites like Bundala National Park and Mulkirigala Rock Temple.",
      "Economic Significance – A key player in Sri Lanka’s southern development and international trade."

    ],
    overview:
    "Hambantota Port, located in Hambantota District on the southern coast of Sri Lanka, is a strategically important deep-water port constructed to handle cargo and shipping operations. Completed in 2010, it has been developed with Chinese collaboration and serves as a modern maritime hub. While it primarily functions as a commercial port, visitors can admire the expansive harbor, watch port operations, and explore nearby attractions.",
    ticketsEntry: {
      residents: "Free to general port perimeter and public areas",
      foreigners: "Free to general port perimeter and public areas",
      children: "Free to general port perimeter and public areas",
      notes: [
        "Access to operational areas may be restricted for safety and security"
      ]
    },
    highlights: [
        "Port Operations – Observe container handling, cargo ships docking, and maritime logistics.",
        "Ocean Views – Panoramic views of the Indian Ocean from the port perimeter.",
        "Photography – Capture ships, cranes, and coastal landscapes; early morning or late afternoon is ideal.",
        "Proximity to Nature & Culture – Short drive to Bundala National Park, Mulkirigala Temple, and beaches.",
        "Sunset Spot – The port’s coastline provides a quiet place to watch the sun dip into the ocean."
    ],
    
    visitingHours: {
      daily:
        "Open 24 hourshours (public viewing areas accessible during daylight)",
      bestTime: [
        "Early morning for quieter views or late afternoon for sunset photography",
      ]
    },
   gettingThere: {
    fromTown: "~20 minutes by car (~15 km)",
    fromNearby: [
      "From Colombo :~5–6 hours by car (~230 km) via Southern Expressway",
      "Public Transport: Buses run from Hambantota town; tuk-tuks available locally",

    ], 
    parking: "Available near designated viewing areas",
    
    },  
    nearbyHotels: [
        "Amaya Lake Hambantota – Lakeside resort, ~25 minutes away",
        "Jetwing Yala – Luxury eco-lodge, ~1 hour drive",
        "Cinnamon Wild Yala – Mid-range resort near wildlife areas",
        "Local Guesthouses – Budget-friendly stays in Hambantota town"


    ],
    travelTips: [
      "Light, comfortable clothing for coastal weather; hat and sunscreen recommended.",
      "Binoculars are useful for watching port activity and ships.",
      "Follow all safety and restricted area signs.",
      "Bring water and snacks if planning a long stay for photography.",
      "Combine your visit with nearby natural attractions for a full-day itinerary."


    ],
    didYouKnow: "Hambantota Port was constructed as part of Sri Lanka’s southern development plan and is considered one of the most modern deep-sea ports in South Asia. While primarily commercial, its strategic location makes it a point of interest for visitors observing maritime operations",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Kataragama-Temple",
    title: "Kataragama Temple – Sacred Shrine of Devotion and Harmony",
    town: "Hambantota",
    district: "Hambantota",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Katharagama temple/Kataragama-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Katharagama temple/Kataragama-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Katharagama temple/Kataragama-Temple-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Katharagama temple/Kataragama-Temple-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Katharagama temple/Kataragama-Temple-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Katharagama temple/Kataragama-Temple-5.jpg`,
    ],
    shortDescription:
        "Experience devotion, culture, and harmony at Kataragama Temple, a sacred crossroads of faith and tradition in southern Sri Lanka.",
    fullDescription:
      "",
    whyVisit: [
        "Multifaith Sanctuary – Revered by Buddhists, Hindus, and Muslims; a symbol of Sri Lanka’s religious harmony.",
        "Spiritual Pilgrimage – Attracts thousands of devotees annually, especially during the Esala Festival.",
        "Historic & Cultural Significance – Believed to date back over 2,000 years; associated with Lord Skanda/Murugan.",
        "Scenic Surroundings – Set near the Menik River with nearby forests and sacred ponds.",
        "Vibrant Festivals – Experience traditional rituals, fire-walking ceremonies, and processions."

    ],
    overview:
    "Located in southeastern Sri Lanka, Kataragama Temple is one of the island’s most famous pilgrimage sites. The temple complex includes shrines dedicated to Lord Skanda, along with nearby Hindu, Buddhist, and Muslim sacred sites. Pilgrims from across Sri Lanka and India visit throughout the year, with peak activity during the Esala Festival (July–August), where devotees engage in traditional rituals, including fire walking and kavadi dances. The temple exemplifies cultural coexistence and deep spirituality.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "Free ",
      notes: [
        "Special Rituals/Offerings: Optional donations; prices can fluctuate depending on ceremonies"
      ]
    },
    highlights: [
        "Main Shrine – Dedicated to Lord Skanda/Murugan, adorned with colorful decorations and ceremonial offerings.",
        "Sacred Ponds & River – Devotees bathe here as part of purification rituals.",
        "Esala Festival – 10–12 day festival featuring processions, music, and fire-walking ceremonies.",
        "Multifaith Harmony – Presence of Buddhist shrines and Muslim sacred sites alongside the main Hindu temple.",
        "Photography & Culture – Capture rituals, ceremonies, and the vibrant colors of the temple complex."    
    ],
    
    visitingHours: {
      daily:
        "5:00 AM – 8:00 PM",
      bestTime: [
        "Early morning for a peaceful experience; festival season for cultural immersion",
      ]
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
      "From Colombo :6–7 hours by car (~250 km) via Southern Expressway and A2 road",
      "From Tissamaharama: ~1 hour by car (~30 km)",
      "Public Transport: Buses from Colombo, Hambantota, and nearby towns; tuk-tuks available locally ",

    ], 
    parking: "Available near temple entrance",
    
    },  
    nearbyHotels: [
      "Jetwing Yala – Luxury eco-lodge ~1 hour away",
      "Cinnamon Wild Yala – Mid-range stay near wildlife and temple areas",
      "Kataragama Rest House – Budget-friendly, simple accommodation near the temple",
      "Local Guesthouses – Available in Kataragama town"


    ],
    travelTips: [
      "Dress modestly (shoulders and knees covered); remove footwear before entering shrines.",
      "Carry offerings like flowers, fruits, or coconuts if participating in rituals.",
      "Respect all religious practices and avoid photography during ceremonies unless permitted.",
      "Bring water, sunscreen, and hats for comfort, especially during festival days.",
      "Visit early to avoid large crowds or plan to experience festival days for cultural immersion."

    ],
    didYouKnow: "Kataragama Temple is considered a unique pilgrimage site where Buddhists, Hindus, and Muslims worship side by side. The temple’s fire-walking ceremony, a highlight of the Esala Festival, is a centuries-old tradition symbolizing devotion and spiritual cleansing.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Kirinda-Beach",
    title: "Kirinda Beach – Serene Coastal Escape",
    town: "Hambantota",
    district: "Hambantota",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Kirinda Beach/Kirinda-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Kirinda Beach/Kirinda-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Kirinda Beach/Kirinda-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Kirinda Beach/Kirinda-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Kirinda Beach/Kirinda-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Kirinda Beach/Kirinda-Beach-5.jpg`,
    ],
    shortDescription:
        "Relax, swim, and soak in the tranquility of Kirinda Beach, a hidden gem along Sri Lanka’s southern coast.",
    fullDescription:
      "",
    whyVisit: [
      "Peaceful Shores – Less crowded than southern beaches, offering calm and solitude.",
      "Clear Waters – Ideal for swimming, sunbathing, and relaxing by the sea.",
      "Scenic Views – Golden sands with lush greenery and rocky coastline make it a photographer’s delight.",
      "Nearby Attractions – Close to Kirinda Temple and Yala East National Park.",
      "Relaxation & Recreation – Perfect for picnics, beach walks, and watching stunning sunsets"

    ],
    overview:
    "Kirinda Beach, located in Hambantota District, is a quiet and scenic beach on Sri Lanka’s southern coast. Known for its tranquility and natural beauty, the beach is perfect for travelers seeking a relaxing escape from busier tourist spots. Its proximity to Kirinda Temple adds cultural interest, while the nearby Yala East National Park provides wildlife adventure opportunities.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "Free ",
      notes: [
        "Parking: Free at designated areas"
      ]
    },
    highlights: [
        "Swimming & Sunbathing – Calm waters make it safe and enjoyable.",
        "Photography – Capture sunrises, sunsets, and picturesque coastal landscapes.",
        "Nature & Relaxation – Surrounded by coconut palms and coastal vegetation.",
        "Nearby Cultural Sites – Kirinda Temple and small fishing villages nearby.",
        "Beach Walks – Ideal for leisurely strolls along golden sands."

    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for calm waters; late afternoon for sunset photography",
      ]
    },
   gettingThere: {
    fromTown: "1 hour by car (~45 km)",
    fromNearby: [
      "From Colombo :~6–7 hours by car (~240 km) via Southern Expressway",
      "Public Transport: Buses from Hambantota or Tangalle to Kirinda; tuk-tuks available locallyy ",

    ], 
    parking: "Available near the beach",
    
    },  
    nearbyHotels: [
    "Amaya Lake Hambantota – Luxury lakeside resort ~45 min away",
    "Cinnamon Wild Yala – Mid-range eco-lodge near wildlife areas",
    "Local Guesthouses – Simple stays in Kirinda village for budget travelers"

    ],
    travelTips: [
      "Swimwear recommended; modest clothing when leaving the beach.",
      "Bring sunscreen, hats, and water.",
      "Avoid littering and respect local fishing activities.",
      "Early morning or late afternoon visits are best for a peaceful experience.",
      "Combine with a visit to Yala East National Park or Kirinda Temple for a full-day trip.",


    ],
    didYouKnow: "Kirinda Beach is not only a peaceful seaside retreat but also a nesting site for Loggerhead and Green turtles at certain times of the year. Its calm and scenic environment makes it a favorite for photographers and nature lovers.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Ridiyagama-Safari-Park",
    title: "Ridiyagama Safari Park – Sri Lanka’s Wild Adventure",
    town: "Hambantota",
    district: "Hambantota",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Ridiyagama Safari Park/Ridiyagama-Safari-Park-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Ridiyagama Safari Park/Ridiyagama-Safari-Park-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Ridiyagama Safari Park/Ridiyagama-Safari-Park-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Ridiyagama Safari Park/Ridiyagama-Safari-Park-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Ridiyagama Safari Park/Ridiyagama-Safari-Park-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Ridiyagama Safari Park/Ridiyagama-Safari-Park-5.jpg`,
    ],
    shortDescription:
        "Get ready for a day of adventure and wildlife encounters at Ridiyagama Safari Park, where the wild comes alive just a short drive from southern Sri Lanka..",
    fullDescription:
      "",
    whyVisit: [
      ",Wildlife Adventure – Spot lions, tigers, elephants, zebras, giraffes, and other exotic and native animals in a safari-style park.",
      "Open Habitat Experience – Animals roam in large, naturalistic enclosures for a more authentic wildlife encounter.",
      "Photography Paradise – Capture stunning close-up shots of wildlife against lush landscapes.",
      "Family-Friendly – Ideal for all ages, offering interactive experiences and educational ",
      "Convenient Access – Easily accessible from major southern cities, perfect for a day trip."

    ],
    overview:
    "Located near Ridiyagama in Hambantota District, Ridiyagama Safari Park is Sri Lanka’s largest safari park. Designed to replicate the natural habitats of its animals, it provides an immersive experience for visitors to witness wildlife up close while ensuring safety and conservation. The park emphasizes education, recreation, and wildlife preservation, making it a must-visit for families, wildlife enthusiasts, and photographers.",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "USD 10–15",
      children: ", USD 5–10",
      notes: [
        "Safari Jeep Ride: Additional USD 5–10 per person",
        "Note: Prices can change depending on season, special events, or park policies."
      ]
    },
    highlights: [
        "Swimming & Sunbathing – Calm waters make it safe and enjoyable.",
        "Photography – Capture sunrises, sunsets, and picturesque coastal landscapes.",
        "Nature & Relaxation – Surrounded by coconut palms and coastal vegetation.",
        "Nearby Cultural Sites – Kirinda Temple and small fishing villages nearby.",
        "Beach Walks – Ideal for leisurely strolls along golden sands."

    ],
    
    visitingHours: {
      daily:
        ": 8:00 AM – 5:00 P.M",
      bestTime: [
        "Morning or late afternoon for active wildlife sightings",
      ]
    },
   gettingThere: {
    fromTown: "30 minutes by car (~18 km)",
    fromNearby: [
      "From Colombo :~5–6 hours by car (~240 km) via Southern Expressway",
      "Public Transport: Buses to Hambantota or Ridiyagama, followed by tuk-tuk or taxi to park",

    ], 
    parking: "Available at the park entrance",
    
    },  
    nearbyHotels: [
    "Jetwing Yala – Luxury stay ~1 hour away",
    "Cinnamon Wild Yala – Mid-range eco-lodge with safari packages",
    "Amaya Lake – Boutique resort near Bundala and Yala wildlife areas",
    "Local Guesthouses – Budget-friendly options in Hambantota town"


    ],
    travelTips: [
        "Wear light, breathable clothing and comfortable shoes.",
        "Bring sunscreen, a hat, and water for daytime visits.",
        "Binoculars and a zoom lens are recommended for photography.",
        "Follow park rules; do not feed or disturb the animals.",
        "Morning visits are best for active wildlife and softer lighting for photos.",
    ],
    didYouKnow: "Ridiyagama Safari Park spans over 500 acres and was designed to replicate Africa-style safari parks, making it one of Sri Lanka’s most immersive wildlife experiences. The park combines conservation, education, and tourism in a single destination.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Yala-National-Park",
    title: "Yala National Park – The Wild Heart of Sri Lanka",
    town: "Hambantota",
    district: "Hambantota",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Yala national park/Yala-National-Park-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Yala national park/Yala-National-Park-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Yala national park/Yala-National-Park-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Yala national park/Yala-National-Park-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Yala national park/Yala-National-Park-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Hambantota/Yala national park/Yala-National-Park-5.jpg`,
    ],
    shortDescription:
        "Experience the wild spirit of Yala National Park, where Sri Lanka’s raw wilderness, ancient heritage, and breathtaking landscapes come together in perfect harmony.",
    fullDescription:
      "",
    whyVisit: [
      "Leopard Territory – Home to one of the world’s highest densities of leopards, making it a premier wildlife destination.",
      "Elephants & Exotic Wildlife – Spot majestic elephants, sloth bears, crocodiles, deer, and countless bird species.",
      "Diverse Landscapes – A stunning mix of forests, grasslands, lagoons, and beaches.",
      "Safari Adventure – Thrilling jeep safaris offering unforgettable encounters with nature.",
      "Sunrise & Sunset Views – Golden light illuminating the wild plains and coastal stretches"


    ],
    overview:
    "Located in Sri Lanka’s southeast, Yala National Park is the country’s most famous wildlife reserve and second-largest national park. Covering nearly 1,000 square kilometers across the Hambantota and Monaragala districts, it offers an extraordinary blend of biodiversity and scenic beauty. The park is divided into five blocks, with Block 1 being the most visited and known for its frequent leopard sightings.Yala’s ecosystems range from dense jungles to open savannas and coastal lagoons bordering the Indian Ocean. It’s not just about animals, the park also shelters ancient ruins, Buddhist stupas, and serene temples hidden amidst the wilderness.",
    ticketsEntry: {
      residents: "LKR 150–500 per adult.",
      foreigners: "LKR 6,000–8,000 (~USD 18–25) per adult (including park fee and taxes).",
      children: "",
      notes: [
        "Safari Jeep Rental: LKR 10,000–15,000 (~USD 30–45) per half-day (shared or private).",
        "Note: Prices may change; please check locally before your visit."
      ]
    },
    highlights: [
        "Leopard Safaris – Spot the elusive Sri Lankan leopard basking on rocky outcrops.",
        "Elephant Herds – Observe families of elephants at watering holes and forest edges.",
        "Birdlife Paradise – Home to over 200 bird species, including peacocks, painted storks, and eagles.",
        "Beach & Nature – Visit the untouched Yala coastline where the jungle meets the sea.",
        "Cultural Sites – Explore Sithulpawwa Temple, an ancient Buddhist monastery within the park."

    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "February to July (dry season – best wildlife sightings).",
      ]
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
      "From Colombo :6 hours by car (300 km).",
      "From Tissamaharama: ~30 minutes to the main entrance (Palatupana Gate).",
      "Nearest Airport: Mattala Rajapaksa International Airport (~1.5 hours).",
      "Public Transport: Buses to Tissamaharama; jeeps available for hire near entrances.",

    ], 
    parking: "Available at park gates.",
    
    },  
    nearbyHotels: [
      "Jetwing Yala – Luxury eco-resort bordering the park.",
      "Cinnamon Wild Yala – Mid-range resort offering safari-style cabins.",
      "Wild Trails Yala – Budget-friendly safari camps and lodges."
    ],
    travelTips: [
        "Wear neutral-colored clothing for safaris.",
        "Bring binoculars, camera, sunscreen, and insect repellent.",
        "Stay quiet and follow your guide’s instructions to not disturb wildlife.",
        "Book safaris early morning or late afternoon for the best animal activity."

    ],
    didYouKnow: "Yala National Park is not only a wildlife haven — it’s also a historical treasure. The Sithulpawwa Rock Temple, dating back over 2,000 years, once housed thousands of Buddhist monks. The park’s coastline was severely affected by the 2004 tsunami, yet it has since rebounded, symbolizing nature’s resilience and beauty.",
    nearby: [
      "",
      "-",
      
    ],
  },
  //Mathara District
  {
    id: "Coconut-Tree-Hill",
    title: "Coconut Tree Hill – Iconic Coastal Viewpoint & Photo Spot",
    town: "Mirissa",
    district: "Matara",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Coconut Tree Hill/Coconut-Tree-Hill-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Coconut Tree Hill/Coconut-Tree-Hill-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Coconut Tree Hill/Coconut-Tree-Hill-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Coconut Tree Hill/Coconut-Tree-Hill-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Coconut Tree Hill/Coconut-Tree-Hill-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Coconut Tree Hill/Coconut-Tree-Hill-5.jpg`,
    ],
    shortDescription:
        "Experience serene views, iconic scenery, and breathtaking ocean panoramas at Coconut Tree Hill, a must-visit natural gem in southern Sri Lanka.",
    fullDescription:
      "",
    whyVisit: [
    "Picturesque Landscape – A hill dotted with tall coconut palms overlooking the turquoise sea.",
    "Photography Hotspot – Famous for sunrise, sunset, and social media-worthy shots.",
    "Panoramic Ocean Views – Stunning vistas of the southern coastline, beaches, and palm-fringed cliffs.",
    "Peaceful Escape – Less crowded than nearby beaches, offering a serene environment.",
    "Scenic Walks – Enjoy gentle strolls along the hilltop with breezy coastal air."

    ],
    overview:
    "Located in Mirissa, Coconut Tree Hill is a natural viewpoint rising above the southern coastline of Sri Lanka. The hill is covered with a cluster of tall coconut palms and offers panoramic views of the Indian Ocean and surrounding beaches. It’s a favorite for photographers, travelers, and anyone looking for a peaceful spot to enjoy the sun, sea, and wind. Its iconic scenery has made it one of the most recognized landscapes in southern Sri Lanka.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Safari Jeep Rental: LKR 10,000–15,000 (~USD 30–45) per half-day (shared or private).",
        "Note: Prices may change; please check locally before your visit."
      ]
    },
    highlights: [
      "Coconut Trees & Hilltop Views – Iconic palm trees create perfect photography frames.",
      "Sunrise & Sunset Photography – Capture the sky lighting up over the ocean.",
      "Scenic Coastal Vistas – Overlook Mirissa Beach and surrounding cliffs.",
      "Nature Walks – Short trails along the hill for leisurely strolls.",
      "Nearby Beaches & Activities – Combine with Mirissa Beach for swimming, snorkeling, or whale watching."


    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for sunrise; late afternoon for sunset photography",
      ]
    },
   gettingThere: {
    fromTown: ": ~10–15 minutes by car (~2 km)",
    fromNearby: [
      "From Colombo :5–6 hours by car (~160 km) via Southern Expressway",
      "Public Transport: Tuk-tuks or motorbike rentals from Mirissa town",

    ], 
    parking: "Small local parking near the hill",
    
    },  
    nearbyHotels: [
     "Casa Colombo Mirissa – Luxury beachfront resort ~5 min away",
     "Mandara Resort Mirissa – Mid-range stay close to beach and hill",
    "Mirissa Beach Guesthouses – Budget-friendly homestays within walking distance"

    ],
    travelTips: [
          "Comfortable walking shoes recommended; hill can be uneven.",
          "Bring a hat, sunscreen, and water.",
          "Ideal for casual attire; swimwear if combining with a beach visit.",
          "Visit early morning or late afternoon for fewer crowds and best lighting.",
          "Respect the environment; avoid damaging palms or littering."
    ],
    didYouKnow: "Coconut Tree Hill has become one of Sri Lanka’s most photographed natural landmarks, appearing in countless travel blogs, social media posts, and travel magazines. Its simple yet iconic silhouette of palms against the ocean makes it a symbol of the country’s southern coast.",
    nearby: [
      "",
      "-",
      
    ],
  },
  //Matara District
  {
    id: "Lighthouse",
    title: "Dondra Head Lighthouse – Beacon at Sri Lanka’s Southern Tip",
    town: "Matara",
    district: "Matara",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Dondra Head Lighthouse/Lighthouse-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Dondra Head Lighthouse/Lighthouse-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Dondra Head Lighthouse/Lighthouse-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Dondra Head Lighthouse/Lighthouse-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Dondra Head Lighthouse/Lighthouse-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Dondra Head Lighthouse/Lighthouse-5.jpg`,
    ],
    shortDescription:
        "Experience history, scenic ocean vistas, and the southernmost charm of Sri Lanka at Dondra Head Lighthouse, where the land meets the sea in spectacular fashion.",
    fullDescription:
      "",
    whyVisit: [
    "Historic Maritime Landmark – One of the tallest lighthouses in South Asia, guiding ships since the early 20th century.",
    "Panoramic Coastal Views – Offers sweeping views of the Indian Ocean and nearby cliffs.",
    "Scenic Surroundings – Surrounded by rocky shores, coconut groves, and the southern coastline.",
    "Photography & Exploration – Ideal spot for landscape, seascape, and sunset photography.",
    "Cultural & Historical Significance – Nearby temples and the southernmost point of Sri Lanka add cultural interest."


    ],
    overview:
    "Located at Dondra Head in Matara District, this iconic lighthouse was first built by the British in 1889 and reconstructed in 1939. Standing over 49 meters tall, it serves as a guiding light for ships navigating the southern coast of Sri Lanka. Visitors can enjoy the surrounding rocky shoreline, panoramic ocean views, and a glimpse of maritime history. The lighthouse and its surroundings offer a peaceful retreat for travelers exploring the southern coastline.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Lighthouse Tower Access: Limited; check local regulations (may have a small fee)",
        "Parking: Free at the lighthouse vicinity"
      ]
    },
    highlights: [
      "Architectural Marvel – Tall, white cylindrical tower with a functioning beacon.",
      "Coastal Views – Overlook the Indian Ocean and rugged cliffs, perfect for photography.",
      "Sunset & Sunrise Spot – Ideal times for stunning seascape photos.",
      "Nearby Temples – Small Hindu and Buddhist shrines located near Dondra Head.",
      "Walk & Explore – Stroll along the rocky coastline and enjoy serene ocean breezes"

    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning for sunrise or late afternoon for sunset",
      ]
    },
   gettingThere: {
    fromTown: ": ~15–20 minutes by car (~12 km)",
    fromNearby: [
      "From Colombo :~5–6 hours by car (~160 km) via Southern Expressway",
      "Public Transport: Buses to Dondra or Matara, followed by tuk-tuk to lighthouse",

    ], 
    parking: "Available near the lighthouse",
    
    },  
    nearbyHotels: [
        "Jetwing Lighthouse – Luxury coastal resort in Dondra area",
        "Calamansi Cove by Jetwing – Mid-range boutique resort near Matara",
        "Local Guesthouses – Budget-friendly stays in Dondra or Matara town",
    ],
    travelTips: [
        "Comfortable walking shoes recommended for rocky areas.",
        "Sunscreen, hats, and water for daytime visits.",
        "Bring a camera with wide-angle lens for panoramic shots.",
        "Respect any restricted areas; do not climb the lighthouse unless permitted.",
        "Combine with nearby beaches and Matara city tour for a full-day trip."

    ],
    didYouKnow: "Dondra Head Lighthouse is the southernmost point of Sri Lanka and has guided maritime traffic along the coast for over a century. Its strategic location and height make it one of the most important navigational aids in the Indian Ocean.",
    nearby: [
      "",
      "-",
      
    ],
  },
   {
    id: "Matara-Fort",
    title: "Matara Fort – A Blend of Colonial Legacy & Coastal Charm",
    town: "Matara",
    district: "Matara",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Mathara Fort/Matara-Fort-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Mathara Fort/Matara-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Mathara Fort/Matara-Fort-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Mathara Fort/Matara-Fort-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Mathara Fort/Matara-Fort-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Mathara Fort/Matara-Fort-5.jpg`,
    ],
    shortDescription:
        "Matara Fort is where history meets the sea, a place to stroll through centuries of colonial legacy while enjoying the charm of Sri Lanka’s southern coast.",
    fullDescription:
      "",
    whyVisit: [
        "Colonial Heritage – Built by the Portuguese in the 16th century and later fortified by the Dutch.",
        "Oceanfront Location – Overlooks the Indian Ocean with scenic views and sea breezes.",
        "Architectural Beauty – A mix of European and Sri Lankan styles with bastions, walls, and old buildings.",
        "Cultural & Historical Hub – Houses government offices, the Dutch Reformed Church, and colonial-era homes.",
        "Leisurely Exploration – Peaceful to walk around, with cafes and local shops within the fort walls."
    ],
    overview:
    "Located in the heart of Matara, along Sri Lanka’s southern coastline, Matara Fort stands as a testament to the island’s colonial past. Originally built by the Portuguese in 1560 and later expanded by the Dutch in 1640, the fort was strategically positioned to protect the port and control trade routes. Today, it offers visitors a charming blend of history, architecture, and coastal scenery. Within the fort, you’ll find the Old Dutch Church, the Star Fort, colonial-era residences, and small boutiques that preserve the town’s character.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Star Fort (separate entry): LKR 300 (~USD 1)",
      
      ]
    },
    highlights: [
        "Old Dutch Church (1767) – Beautifully preserved church with stone carvings and old tombstones.",
        "Star Fort – A small star-shaped outpost across the river, built for extra defense.",
        "Ocean Views – Stunning panoramic views of the coastline and Matara Beach.",
        "Colonial Architecture – Whitewashed walls, Dutch-style gables, and old administrative buildings.",
        "Cafes & Shops – Quaint cafés, art galleries, and souvenir shops add a modern charm to the historic site."

    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Morning or late afternoon for pleasant temperatures and good lighting",
      ]
    },
   gettingThere: {
    fromTown: "",
    fromNearby: [
      "From Colombo: ~3–4 hours by car (~160 km) via Southern Expressway",
      "From Galle: ~1 hour (~50 km) by car or train",
      "Public Transport: Frequent trains and buses to Matara; fort is within walking distance from Matara Railway Station",

    ], 
    parking: "Available near the main entrance",
    
    },  
    nearbyHotels: [
        "Amaloh by Jetwing – Boutique colonial-style hotel near the fort",
        "Mandara Resort Mirissa – Luxury resort 15 min away",
        "Fort Sea View Guesthouse – Budget-friendly stay inside the fort",
        "Eliya Villa Matara – Tranquil mid-range guesthouse close to the beach"

    ],
    travelTips: [
        "Wear light, breathable clothing suitable for tropical weather.",
        "Bring a hat, sunscreen, and water during the day.",
        "Explore both Matara Fort and nearby Star Fort for a complete experience.",
        "Early mornings and sunsets offer great photography opportunities.",
        "Respect heritage buildings and avoid littering inside the premises."


    ],
    didYouKnow: "The fort once housed Dutch officials and soldiers, and after independence, many colonial buildings were repurposed into government offices, some still in use today. The fort also withstood several tsunamis and still stands strong as a symbol of resilience and history.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Polhena-Beach",
    title: "Polhena Beach – Tranquil Lagoon & Snorkeling Paradise",
    town: "Matara",
    district: "Matara",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Polhena Beach/Polhena-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Polhena Beach/Polhena-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Polhena Beach/Polhena-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Polhena Beach/Polhena-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Polhena Beach/Polhena-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Polhena Beach/Polhena-Beach-5.jpg`,
    ],
    shortDescription:
        "Dive, relax, and explore the calm waters of Polhena Beach, a southern Sri Lankan gem for families, snorkelers, and nature lovers alike.",
    fullDescription:
      "",
    whyVisit: [
        "Calm Lagoon Waters – Safe for swimming and ideal for families with children.",
        "Snorkeling Haven – Coral reefs just off the shore provide an opportunity to see tropical fish and marine life.",
        "Scenic Sunsets – Golden sands and calm waters create perfect sunset photography spots.",
        "Relaxing Atmosphere – Less crowded than neighboring beaches, offering peace and solitude.",
        "Nearby Attractions – Close to Unawatuna Beach, Japanese Peace Pagoda, and local temples."

    ],
    overview:
    "Polhena Beach, located near Matara in southern Sri Lanka, is famous for its natural lagoon and safe swimming conditions. The beach is popular among locals and tourists for snorkeling due to its protected coral reefs and calm waters. With soft sands, shallow waters, and a quiet environment, Polhena is a perfect spot for families, snorkelers, and anyone seeking a peaceful beach experience.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "Snorkeling Equipment: Rentals available at local shops (~USD 5–10)",
        
      
      ]
    },
    highlights: [
        "Safe Swimming – Calm, shallow waters ideal for children and beginners.",
        "Coral Reefs & Snorkeling – Explore colorful marine life close to the shore.",
        "Sunset Photography – Stunning views over the lagoon and surrounding coastline",
        "Peaceful Beach Walks – Relaxing strolls along soft sandy shores.",
        "Nearby Attractions – Unawatuna, Japanese Peace Pagoda, and local fishing villages."

    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for quiet and wildlife; late afternoon for sunsets",
      ]
    },
   gettingThere: {
    fromTown: "15 minutes by car (~8 km)",
    fromNearby: [
      "From Colombo: ~5–6 hours by car (~160 km) via Southern Expressway",
      "Public Transport: Buses from Matara or Galle; tuk-tuks available locally",

    ], 
    parking: "Available near the beach",
    
    },  
    nearbyHotels: [
        "Turyaa Kalutara – Luxury stay ~30 min away",
        "Calamansi Cove by Jetwing – Boutique resort in Unawatuna",
        "Local Guesthouses – Budget-friendly homestays near Matara and Polhena"

    ],
    travelTips: [
      "Swimwear recommended; modest clothing when leaving the beach.",
      "Bring snorkeling gear or rent locally.",
      "Sunscreen, hats, and water are essential for daytime visits.",
      "Respect coral reefs and avoid stepping on them.",
      "Early mornings and late afternoons offer the best experiences for swimming and photography"

    ],
    didYouKnow: "Polhena Beach is not only a popular swimming spot but also home to a small population of sea turtles and diverse coral reef ecosystems, making it a favorite among snorkelers and nature enthusiasts.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Weherahena-Temple",
    title: "Weherahena Temple – Spiritual Haven & Cultural Treasure",
    town: "Matara",
    district: "Matara",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weherahena Temple/Weherahena-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weherahena Temple/Weherahena-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weherahena Temple/Weherahena-Temple-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weherahena Temple/Weherahena-Temple-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weherahena Temple/Weherahena-Temple-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weherahena Temple/Weherahena-Temple-5.jpg`,
    ],
    shortDescription:
        "Experience tranquility, awe-inspiring art, and spiritual serenity at Weherahena Temple, a beacon of faith and culture in southern Sri Lanka.",
    fullDescription:
      "",
    whyVisit: [
        "Massive Buddha Statue – Features a 38-meter-high seated Buddha, one of the tallest in Sri Lanka.",
        "Spiritual Experience – Peaceful meditation halls and serene surroundings for reflection.",
        "Buddhist Museum – Displays artifacts, paintings, and relics representing Sri Lanka’s Buddhist heritage.",
        "Photography & Architecture – Intricate murals, statues, and serene gardens offer perfect photo opportunities.",
        "Cultural Immersion – Learn about Buddhist teachings, rituals, and Sri Lankan religious traditions."

    ],
    overview:
    "Located in Matara, the Weherahena Temple is a major Buddhist pilgrimage site featuring a colossal seated Buddha statue that dominates the temple grounds. The temple complex includes meditation halls, shrines, and a museum show casing Buddhist artifacts, statues, and paintings. Visitors can explore both the spiritual and cultural aspects of Sri Lankan Buddhism in a serene and welcoming environment.",
    ticketsEntry: {
      residents: "Free(donations appreciated)",
      foreigners: "Free(donations appreciated)",
      children: "Free",
      notes: [
        "Museum Access: Free or minimal donation",
        
      
      ]
    },
    highlights: [
        "Colossal Buddha Statue – 38 meters tall, visible from a distance and impressive up close.",
        "Meditation & Prayer Halls – Peaceful spaces for reflection and prayer.",
        "Buddhist Museum – Exhibits ancient statues, religious artifacts, and murals.",
        "Photography Opportunities – Capture intricate carvings, statues, and landscaped gardens.",
        "Scenic Surroundings – Temple located on a gentle hill with views of Matara town and coastline."

    ],
    
    visitingHours: {
      daily:
        "Daily: 6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning or late afternoon for a tranquil experience",
      ]
    },
   gettingThere: {
    fromTown: "10–15 minutes by car (~5 km)",
    fromNearby: [
      "From Colombo: ~5–6 hours by car (~160 km) via Southern Expressway",
      "Public Transport: tuk-tuks available from town",

    ], 
    parking: "Available on-site",
    
    },  
    nearbyHotels: [
        "Mandara Resort Mirissa – Mid-range stay ~20 min away",
        "Casa Colombo Mirissa – Luxury boutique resort nearby",
        "Local Guesthouses – Budget-friendly stays within Matara"


    ],
    travelTips: [
      "Dress modestly (shoulders and knees covered); remove shoes before entering temple areas.",
      "Maintain silence and respect rituals and meditation spaces.",
      "Carry water and wear comfortable shoes for walking around the temple grounds.",
      "Photography is allowed, but avoid sacred rituals",


    ],
    didYouKnow: "The Weherahena Temple’s giant Buddha statue was built in 1992 and is considered one of the tallest seated Buddha statues in Sri Lanka. Its combination of religious, cultural, and artistic significance makes it a must-visit for travelers interested in Buddhism and Sri Lankan heritage",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Weligame-Bay",
    title: "Weligama Bay – Surfing & Coastal Charm",
    town: "Matara",
    district: "Matara",
    province: "Southern-Province",
    provinceId: "southern",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weligama Bay/Weligame-Bay-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weligama Bay/Weligame-Bay-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weligama Bay/Weligame-Bay-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weligama Bay/Weligame-Bay-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weligama Bay/Weligame-Bay-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Southern/Matara/Weligama Bay/Weligame-Bay-5.jpg`,
    ],
    shortDescription:
        "Catch waves, relax on the golden sands, and experience the authentic coastal charm of Weligama Bay, a paradise for surfers and beach lovers alike.",
    fullDescription:
      "",
    whyVisit: [
        "Massive Buddha Statue – Features a 38-meter-high seated Buddha, one of the tallest in Sri Lanka.",
        "Spiritual Experience – Peaceful meditation halls and serene surroundings for reflection.",
        "Buddhist Museum – Displays artifacts, paintings, and relics representing Sri Lanka’s Buddhist heritage.",
        "Photography & Architecture – Intricate murals, statues, and serene gardens offer perfect photo opportunities.",
        "Cultural Immersion – Learn about Buddhist teachings, rituals, and Sri Lankan religious traditions."

    ],
    overview:
    "Weligama Bay, located on Sri Lanka’s southern coast in Matara District, is a long crescent-shaped bay known for its soft sandy beach, calm waters, and vibrant surfing culture. It has become a hub for beginner surfers, offering multiple surf schools along the shore. The bay is also famous for stilt fishermen, traditional boats, and easy access to nearby attractions like Mirissa Beach and Coconut Tree Hill.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
        "USD 15–30 per hour (prices may fluctuate)",
        "Equipment Rentals: ~USD 5–10"
        
      
      ]
    },
    highlights: [
        "Surfing Schools & Rentals – Learn to surf or rent boards for all skill levels.",
        "Safe Swimming & Sunbathing – Calm waters make it ideal for families.",
        "Scenic Photography – Capture traditional stilt fishermen, palm-lined beaches, and colorful boats.",
        "Beachfront Cafés – Relax with local seafood, tropical drinks, and snacks.",
        "Nearby Whale Watching – Short drive to Mirissa for seasonal whale tours (Nov–Apr).",

    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Morning for surfing; late afternoon for sunset photography",
      ]
    },
   gettingThere: {
    fromTown: "40 minutes by car (~25 km)",
    fromNearby: [
      "From Colombo: ~5–6 hours by car (~160 km) via Southern Expressway",
      "Public Transport: Frequent buses from Matara, Galle, or Colombo; tuk-tuks locally"
    ], 
    parking: "Available near the beach",
    
    },  
    nearbyHotels: [
        "Weligama Bay Resort – Luxury beachfront resort",
        "Surf & Stay Guesthouses – Mid-range options catering to surfers",
        "Budget Guesthouses – Affordable homestays within walking distance of the beach"

    ],
    travelTips: [
      "Swimwear recommended; modest clothing when leaving the beach.",
      "Bring sunscreen, hats, and water.",
      "Book surf lessons in advance during peak season (Nov–Apr).",
      "Respect local fishing activities and avoid stepping on coral or marine life.",
      "Early mornings are best for fewer crowds and ideal surf conditions."


    ],
    didYouKnow: "Weligama Bay is famous for its stilt fishermen, a traditional fishing method unique to southern Sri Lanka. The calm bay and gentle waves make it one of the best coastal spots for learning to surf while enjoying local culture.",
    nearby: [
      "",
      "-",
      
    ],
  },

  //Uwa province
  //badulla District
   {
    id: "Demodara-Loop",
    title: "Demodara Loop – Ella",
    town: "Ella",
    district: "Badulla",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Demodara loop/Demodara-Loop-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Demodara loop/Demodara-Loop-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Demodara loop/Demodara-Loop-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Demodara loop/Demodara-Loop-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Demodara loop/Demodara-Loop-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Demodara loop/Demodara-Loop-5.jpg`,
    ],
    shortDescription:
        "Demodara Loop is perfect for railway enthusiasts, photographers, and curious travelers.Experience the marvel of colonial engineering, surrounded by tea-covered hills and the timeless rhythm of Sri Lanka’s hill-country trains.",
    fullDescription:
      "",
    whyVisit: [
        "Unique Railway Engineering – A one-of-a-kind spiral rail loop where the track passes under itself.",
        "Scenic Wonder – Surrounded by misty hills, tea plantations, and lush greenery.",
        "Perfect Photo Spot – Capture trains curving dramatically around the hillside loop.",
        "Historical Landmark – Built during the British colonial era, showcasing century-old ingenuity.",
        "Iconic Train Experience – Watch or ride one of the world’s most scenic train routes."

    ],
    overview:
    "The Demodara Loop, located about 6 km from Ella town, is one of Sri Lanka’s greatest railway engineering marvels. Constructed during the British colonial period (early 20th century), this spiral loop allows the train track to pass around a hill and then directly beneath itself through a tunnel before continuing forward. The design was developed to manage steep elevation changes without using zigzags or switchbacks. It’s part of the legendary Colombo–Badulla railway line, renowned as one of the most scenic in the world.",
    ticketsEntry: {
      residents: "Free (public viewing area)",
      foreigners: "Free (public viewing area)",
      children: "Free (public viewing area)",
      notes: [
        "Guided Tours: Optional (~LKR 1,000–1,500 / USD 3–5) including historical background",
        "Photography: Allowed; bring a zoom lens for capturing trains from a distance."
        
      
      ]
    },
    highlights: [
      {
    main: "Ingenious Engineering Design",
    subPoints: [
      "The railway line loops around a hill and passes under itself through a tunnel.",
      "Built in 1921, it showcases incredible colonial-era engineering using minimal machinery.",
      "Known locally as “Loop de Loop” or “Demodara Tunnel."

    ]
  },
  {
    main: " Scenic Landscape",
    subPoints: [
      "Surrounded by rolling tea plantations and misty mountains.",
      "A peaceful area offering great views of rural life in Sri Lanka’s hill country"

    ]
  },
  {
    main: "Train Spotting",
    subPoints: [
      "Watch trains slowly wind around the full loop before entering the tunnel.",
      "Best viewing times coincide with morning and afternoon train schedules."

    ]
  },
  {
    main:"Photography Hotspot",
    subPoints:[
      "Ideal for drone shots or panoramic photos from nearby viewpoints.",
      "Early morning mist adds a magical touch to your photos."

    ]
  },   
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        " 8:00 AM – 11:00 AM or 3:00 PM – 5:00 PM (for train crossings)",
      ]
    },
   gettingThere: {
    fromTown: [
      "By Car/Tuk-Tuk: 10–15 minutes (6 km) via Demodara Road – tuk-tuk fare ~LKR 500–700",
      " By Foot: Scenic walk along railway tracks (~1 hour each way from Ella Station).",
      "By Train: Take a short ride from Ella to Demodara Station (~10 min), and walk to the viewpoint."
    ],
    fromNearby: [
      "From Colombo:",
      "By Train: Take the Colombo–Badulla route (9–10 hours) – stop at Demodara Station.",
      "By Car/Taxi: ~6–7 hours (200 km), fare ~LKR 22,000–25,000 (~USD 60–70)."

    ], 
    
    },  
    nearbyHotels: [
        "Morning Dew Hotel – Comfortable stay close to Ella town.",
        "Zion View Ella Green Retreat – Mid-range with panoramic hill views.",
        "98 Acres Resort & Spa – Luxury option with direct access to Ella’s main attractions.",
        "Mountain Heavens Ella – Boutique hotel with views of the railway and Ella Gap."
    ],
    travelTips: [
      "Arrive 30–45 minutes before the train arrives to secure a good viewing spot.",
      "Bring a camera or drone for aerial shots of the full loop.",
      "Be cautious near railway tracks, stay on safe viewing points.",
      "Combine with Nine Arches Bridge or Ella Rock for a full scenic day.",
      "Check train timetables in advance for the best viewing experience."

    ],
    didYouKnow: "The Demodara Loop is often said to resemble a giant spiral bun, earning it the nickname “Demodara Loop Bun.” According to local legend, a railway engineer got the idea by watching a worker twist his turban inspiring this creative solution to manage the steep terrain!.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Dunhinda-Falls",
    title: "Dunhinda Falls – Badulla",
    town: "Badulla",
    district: "Badulla",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Dunhinda Falls/Dunhinda-Falls-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Dunhinda Falls/Dunhinda-Falls-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Dunhinda Falls/Dunhinda-Falls-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Dunhinda Falls/Dunhinda-Falls-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Dunhinda Falls/Dunhinda-Falls-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Dunhinda Falls/Dunhinda-Falls-5.jpg`,
    ],
    shortDescription:
        "Dunhinda Falls is perfect for nature enthusiasts, photographers, and those seeking serenity.Walk through forested trails, listen to the sound of rushing water, and witness one of Sri Lanka’s most enchanting natural wonders",
    fullDescription:
      "",
    whyVisit: [
      "Majestic Waterfall – One of Sri Lanka’s most beautiful and iconic waterfalls, cascading 64 meters into a misty basin.",
      "Lush Jungle Surroundings – Enclosed by thick forest, vines, and vibrant birdlife.",
      "Perfect for Photography – Capture the dramatic “smoky” mist that gives the fall its name (Dunhinda means “smoky waterfall” in Sinhala).",
      "Peaceful Natural Escape – Ideal for nature walks, meditation, and quiet reflection.",
      "Adventure Walk – A scenic 1.5 km trail through the forest leads to the viewing point."

    ],
    overview:
    "Dunhinda Falls, located about 5 km north of Badulla, is considered one of the most picturesque waterfalls in Sri Lanka. The waterfall drops from a height of 64 meters and is famous for the fine mist that rises from the base, giving it a dreamy, smoky appearance. The trail leading to the falls is an experience in itself — passing through lush jungle paths, streams, and lookout points. Dunhinda is a must-see for nature lovers and photographers exploring Sri Lanka’s hill country",
    ticketsEntry: {
      residents: "LKR 200 ",
      foreigners: "LKR 500 (~USD 2) ",
      children: "",
      notes: [
        "Guided Tours: Optional (~LKR 1,000–1,500 / USD 3–5) from Badulla town.",
        "Photography: Allowed (drones permitted with caution and local approval)."
        
      
      ]
    },
    highlights: [
      {
    main: "Natural Wonder",
    subPoints: [
      "64-meter-high single-drop waterfall on the Badulu Oya River.",
      "Best viewed during the rainy season (May–September) when water flow is strong."


    ]
  },
  {
    main: " Scenic Jungle Trail",
    subPoints: [
      "1.5 km walk from the entrance gate to the main viewing platform.",
      "The trail features smaller cascades and rich flora and fauna along the way."

    ]
  },
  {
    main: "Photography Hotspot",
    subPoints: [
      "Capture the waterfall framed by rocks and greenery.",
      "Early morning and late afternoon offer the best light for photos.",


    ]
  },
  {
    main:"Relaxation & Nature",
    subPoints:[
     "Serene environment perfect for unwinding.",
      "Rest areas and small tea stalls along the walking path."


    ]
  },   
    ],
    
    visitingHours: {
      daily:
        "7:00 AM – 5:30 PM",
      bestTime: [
        " May–September (rainy season) for strongest waterfall flow.",
      ]
    },
   gettingThere: {
    fromTown: [
      "By Car/Tuk-Tuk: ~15–20 minutes (5 km) to the Dunhinda Falls entrance; tuk-tuk fare ~LKR 500–700.",
      "By Foot: 1.5 km forest trail (20–30 minutes) from the entrance to the viewing platform",

    ],
    fromNearby: [
      "From Ella:",
      "By Car: ~1 hour (35 km) via Ella–Badulla Road.",
      "By Train: Take the Ella–Badulla train (about 45 minutes), then tuk-tuk to the entrance.",


      "From Colombo:",
      "By Car/Taxi: ~6–7 hours (220 km), fare ~LKR 22,000–25,000 (~USD 60–70).",
      "By Train: Colombo–Badulla scenic route (~9–10 hours), then tuk-tuk to Dunhinda."


    ], 
    
    },  
    nearbyHotels: [
        "The Heritage Badulla – Comfortable hotel with modern amenities.",
        "Dunhinda Falls Inn – Budget guesthouse near the entrance.",
        "Hotel Vantage – Mid-range stay in Badulla town.",
        "Ella Flower Garden Resort – Popular choice if you’re traveling from Ella."
    ],
    travelTips: [
      "Wear comfortable shoes, the trail can be slippery after rain.",
      "Carry drinking water and insect repellent.",
      "Avoid swimming; the water currents near the base are strong.",
      "Visit early morning for cool weather and fewer crowds.",
      "Combine your visit with Ella Rock, Diyaluma Falls, or Lipton’s Seat for a full-day nature tour."


    ],
    didYouKnow: "The name “Dunhinda” comes from the Sinhala words “Dun” (smoke) and “Hinda” (evaporate), referring to the smoky mist created as the waterfall crashes into the pool below. Locals call it the “Smoky Cascade of Badulla,” and it’s featured in many legends and poems celebrating Sri Lanka’s natural beauty.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Ella-Rock",
    title: "Ella Rock – Ella",
    town: "Ella",
    district: "Badulla",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ella Rock/Ella-Rock-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ella Rock/Ella-Rock-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ella Rock/Ella-Rock-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ella Rock/Ella-Rock-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ella Rock/Ella-Rock-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ella Rock/Ella-Rock-5.jpg`,
    ],
    shortDescription:
        "Ella Rock is perfect for hikers, photographers, and adventure seekers.Take the journey through misty trails, breathe in the mountain air, and witness one of Sri Lanka’s most breathtaking views from the top.",
    fullDescription:
      "",
    whyVisit: [
      "Ultimate Hiking Experience – A moderately challenging trail offering rewarding panoramic views.",
      "Breathtaking Summit Views – Overlooks Little Adam’s Peak, Ella Gap, and rolling tea hills.",
      "Rich Biodiversity – Encounter native flora, butterflies, and birds along the way.",
      "Photographer’s Paradise – Capture sunrise or mist-covered valley landscapes.",
      "Peaceful Escape – Ideal for travelers seeking adventure and solitude amidst nature"


    ],
    overview:
    "Ella Rock is one of Sri Lanka’s most popular and scenic hikes, located just outside Ella town. Rising over 1,000 meters above sea level, this hike takes around 2–3 hours each way through tea plantations, forest paths, and along railway lines. Though moderately challenging, the view from the summit, with clouds drifting through valleys and endless mountains stretching to the horizon is truly unforgettable. It’s a perfect experience for adventure lovers exploring Sri Lanka’s hill country",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free ",
      children: "Free",
      notes: [
        "Guided Tours:Optional local guides available (~LKR 2,000–3,000 / USD 6–10).",
        "Photography:Allowed anywhere along the route."
        
      ]
    },
    highlights: [
      {
    main: "Scenic Hiking Trail",
    subPoints: [
    	"Total distance: ~8 km (round trip 4–5 hours).",
      "Trail passes through railway tracks, tea fields, and dense forest.",
      "Moderate difficulty with some steep climbs near the top."

    ]
  },
  {
    main: " Panoramic Views",
    subPoints: [
      "Stunning views of Ella Gap, Ravana Falls, and Little Adam’s Peak.",
      "Ideal for sunrise or morning hikes before the mist rolls in."

    ]
  },
  {
    main: "Nature & Serenity",
    subPoints: [
      "Walk through calm tea estates with chirping birds and rustling leaves.",
      "Stop at small viewpoints or meditation spots along the way."

    ]
  },
  {
    main:"Photography Hotspot",
    subPoints:[
     "Capture breathtaking landscapes and morning light over the valley.",
      "Best photos are taken facing east during sunrise."

    ]
  },   
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours daily",
      bestTime: [
        " 6:00 AM – 9:00 AM for sunrise views",
        "Avoid midday heat; afternoons can bring mist or rain"
      ]
    },
   gettingThere: {
    fromTown: [
      "By Foot: The trail starts near Ella Railway Station – follow the track toward Kithal Ella for about 2 km.",
      "By Tuk-Tuk: LKR 300–500 to the starting point near Kithal Ella Station.",
      "By Train: Disembark at Ella or Kithal Ella Station and walk to the trailhead (10–15 min).",

    ],
    fromNearby: [
      "From Ella:",
      "By Foot: The trail starts near Ella Railway Station – follow the track toward Kithal Ella for about 2 km.",
      "By Tuk-Tuk: LKR 300–500 to the starting point near Kithal Ella Station.",
      "By Train: Disembark at Ella or Kithal Ella Station and walk to the trailhead (10–15 min).",


      "From Colombo:",
      "By Car/Taxi: ~6–7 hours (200 km), fare LKR 22,000–25,000 (~USD 60–70).",
       "By Train: Colombo–Ella scenic train (~9 hours).",
      "By Bus: Colombo–Badulla line; alight at Ella junction."



    ], 
    
    },  
    nearbyHotels: [
        "Zion View Ella Green Retreat – Mid-range stay with sunrise views.",
        "Mountain Heavens Ella – Boutique hotel overlooking Ella Gap.",
        "EKHO Ella – Luxury hotel near the town center.",
        "Hangover Hostels Ella – Budget-friendly and ideal for hikers."

    ],
    travelTips: [
        "Start early for the best visibility and to avoid heat.",
        "Carry at least 1 liter of water and light snacks.",
        "Wear sturdy shoes with good grip for rocky sections.",
        "Check weather forecasts — trails can get slippery after rain.",
        "Be cautious of unmarked trails; consider hiring a local guide for your first visit.",
        "Combine your hike with Nine Arches Bridge or Little Adam’s Peak for a full adventure day."

    ],
    didYouKnow: "Ella Rock is often called the “Guardian of Ella” because it overlooks the entire valley. The locals believe the mountain protects the town and its people, a spiritual belief that adds charm to this already magical spot.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Adam's-Peak",
    title: "Little Adam’s Peak – Ella",
    town: "Ella",
    district: "Badulla",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Little Adam’s Peak/Little Adam's-Peak-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Little Adam’s Peak/Little Adam's-Peak-1.jpg`,
       `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Little Adam’s Peak/Little Adam's-Peak-2.jpg`,
       `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Little Adam’s Peak/Little Adam's-Peak-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Little Adam’s Peak/Little Adam's-Peak-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Little Adam’s Peak/Little Adam's-Peak-5.jpg`,
    ],
    shortDescription:
        "Little Adam’s Peak is a must-visit for hikers, photographers, and nature lovers.Take in panoramic views, enjoy a peaceful trek through tea plantations, and experience the charm of Sri Lanka’s hill country at its finest.",
    fullDescription:
      "",
    whyVisit: [
        "Scenic Hike for Everyone – An easy and accessible trail suitable for all fitness levels.",
        "Panoramic Highland Views – Breathtaking vistas of Ella Rock, tea estates, and valleys.",
        "Tea Plantation Trails – Walk through lush green estates and meet friendly tea pickers.",
        "Perfect Photo Spot – Capture stunning sunrise and sunset views over the misty hills.",
        "Peaceful Ambiance – A serene escape from city life, surrounded by nature and fresh mountain air."

    ],
    overview:
    "Little Adam’s Peak, also known as Punchi Sri Pada, is one of Ella’s most popular attractions. Standing at 1,141 meters, it offers a shorter and easier climb compared to the sacred Adam’s Peak in central Sri Lanka. The well-marked trail passes through picturesque tea plantations and rewards visitors with sweeping views of the southern highlands. Its accessibility and beauty make it a must-do experience for every traveler visiting Ella.",
    ticketsEntry: {
      residents: "Free",
      foreigners: "Free ",
      children: "Free",
      notes: [
        "Guided Tours:Optional local guides available (~LKR 2,000–3,000 / USD 6–10).",
        "Photography:Allowed; best during golden hours (6:00–8:00 AM or 4:00–6:00 PM)"
        
      ]
    },
    highlights: [
      {
    main: "Easy Hiking Trail",
    subPoints: [
    	"Takes about 30–45 minutes from the trailhead.",
      "Well-marked paths and steps make it beginner-friendly."

    ]
  },
  {
    main: "Stunning Views",
    subPoints: [
      "Panoramic views of Ella Rock, Ravana Falls, and the valley.",
      "Ideal for sunrise and sunset photography"

    ]
  },
  {
    main: "Nature & Relaxation",
    subPoints: [
        "Experience tranquil surroundings and fresh mountain air.",
        "Spot birds and butterflies along the trail.",
    ]
  },
  {
    main:"Photography Hotspot",
    subPoints:[
     "Capture tea fields, hikers, and mist-covered peaks.",
      "Drone-friendly area (check local regulations)."
    ]
  },   
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours daily",
      bestTime: [
        " Early morning or late afternoon",

      ]
    },
   gettingThere: {
    fromTown: ["",

    ],
    fromNearby: [
      "From Colombo:",
      "By Car/Taxi: ~6 hours (200 km) via A4 Highway – approx. LKR 22,000–25,000 (~USD 60–70)",
      "By Train: Scenic route from Colombo to Ella (~9 hours); tuk-tuk from station to trailhead (~LKR 300–500).",
      "By Bus: Colombo to Ella (change at Badulla), then tuk-tuk to 98 Acres Resort (trail start point).",

      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~6–7 hours (220 km) – approx. LKR 25,000 (~USD 70)"
    ], 
    
    },  
    nearbyHotels: [
        "98 Acres Resort & Spa – Luxury stay with direct access to the trail.",
        "Zion View Ella Green Retreat – Mid-range with panoramic views.",
      	"Hangover Hostels Ella – Budget option close to Ella town.",
        "Ella Flower Garden Resort – Comfortable mid-range with scenic balconies."
    ],
    travelTips: [
          "Carry water, sunscreen, and a hat for sun protection.",
          "Wear comfortable walking shoes with good grip.",
          "Visit during sunrise for the best lighting and fewer tourists.",
          "Be respectful to locals working in the tea estates when taking photos.",
          "Combine with Nine Arches Bridge or Ravana Falls for a perfect day itinerary"
    ],
    didYouKnow: "Little Adam’s Peak is named after the sacred Adam’s Peak (Sri Pada) due to its similar shape. Locals say the hill symbolizes a smaller version of the iconic pilgrimage mountain making it a “mini spiritual journey” without the strenuous climb.",
    nearby: [
      "",
      "-",
      
    ],
  },
   {
    id: "Nine-Arches-Bridge",
    title: "Nine Arches Bridge – Iconic Colonial-Era Railway Bridge",
    town: "Ella",
    district: "Badulla",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Nine Arches/Nine-Arches-Bridge-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Nine Arches/Nine-Arches-Bridge-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Nine Arches/Nine-Arches-Bridge-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Nine Arches/Nine-Arches-Bridge-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Nine Arches/Nine-Arches-Bridge-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Nine Arches/Nine-Arches-Bridge-5.jpg`,
    ],
    shortDescription:
        "Nine Arches Bridge is perfect for history buffs, photographers, and nature lovers.Witness trains crossing the iconic viaduct, explore the surrounding tea estates, and capture Sri Lanka’s central highlands beauty. ",
    fullDescription:
      "",
    whyVisit: [
        "Architectural Marvel – Stunning colonial-era railway bridge built entirely from stone and brick.",
        "Scenic Location – Surrounded by lush tea plantations, tropical forests, and rolling hills.",
        "Photography Hotspot – Ideal for capturing trains crossing the bridge and scenic landscapes.",
      	"Historic Significance – Built during British colonial times (1921–1922), showcasing engineering heritage.",
        "Peaceful Escape – Less crowded than city attractions, perfect for a quiet nature stroll."
    ],
    overview:
    "Nine Arches Bridge, also known as Demodara Bridge, is located between Ella and Demodara in Sri Lanka’s Central Highlands. It is a 9-arch viaduct made entirely from stone, brick, and cement without using steel. Surrounded by tea estates, this bridge has become one of Sri Lanka’s most photographed landmarks, attracting tourists, hikers, and railway enthusiasts.",
    ticketsEntry: {
      residents: "Free(public area)",
      foreigners: "Free(public area) ",
      children: "Free(public area)",
      notes: [
        "Guided Tours:Optional local guides available ~LKR 1,000–1,500 (~USD 3–5)",
        "Photography:Allowed; best time is during train crossings"
        
      ]
    },
    highlights: [
      {
    main: "Engineering & Architecture",
    subPoints: [
    	"Built entirely from stone bricks and cement, without steel.",
      "Nine arches each spanning 28 meters; total length ~91 meters, height ~24 meters.",
      "Example of British colonial engineering ingenuity in tropical terrain."
    ]
  },
  {
    main: "Scenic Views & Nature",
    subPoints: [
      "Surrounded by green tea plantations, forested hills, and waterfalls.",
      "Trails along the railway offer panoramic views of the bridge and valley."
    ]
  },
  {
    main: "Train Spotting",
    subPoints: [
        "Trains from Ella to Demodara cross the bridge several times daily.",
        "Best viewed from the hillside or the nearby lookout point."
    ]

  },
  {
    main:"Photography Opportunities",
    subPoints:[
      "Capture the bridge with a train passing over, especially during morning or late afternoon.",
      "Lush greenery and misty hills provide dramatic backdrops."

    ]
  },   
    ],
    
    visitingHours: {
      daily:
        ": Open 24 hours, best daylight hours for visiting and photography",
      bestTime: [
        " Morning or late afternoon for softer light and fewer crowds",

      ]
    },
   gettingThere: {
    fromTown: ["",

    ],
    fromNearby: [
      "From Colombo:",
      "By Car/Taxi: ~6–7 hours (200–220 km), fare USD 60–70 / LKR 22,000–25,000",
      "By Public Bus: Colombo–Badulla or Colombo–Ella buses; then short walk to the bridge",
      "By Train: Colombo–Badulla train (scenic route); get off at Demodara or Ella, then walk 15–20 min to bridge",


      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~6–7 hours (200–220 km), fare USD 60–70 / LKR 22,000–25,000"
    ], 
    
    },  
    nearbyHotels: [
      "Ella Town Guesthouses & Hotels – Budget and mid-range options within walking distance.",
      "Eco-Lodges & Boutique Hotels – Surrounded by tea estates for a scenic stay.",
      "Luxury Stays: Upscale resorts in Ella with panoramic views of the highlands."

    ],
    travelTips: [
          "Wear comfortable walking shoes; the trail has uneven surfaces.",
          "Bring a camera and zoom lens for train photography.",
          "Visit early morning for fewer crowds and misty mountain views.",
          "Stay on marked paths; avoid walking on active railway tracks.",
          "Combine with Ella Rock hike, Diyaluma Falls, or Ravana Falls for a full-day highlands itinerary."

    ],
    didYouKnow: "Nine Arches Bridge is also called the “Bridge in the Sky” because of its dramatic appearance amidst the highlands. The construction relied on local labor and materials, making it a testament to both engineering and community effort during the British era.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Ravana-Falls",
    title: "Ravana Falls – Ella",
    town: "Ella",
    district: "Badulla",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ravana Falls/Ravana-Falls-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ravana Falls/Ravana-Falls-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ravana Falls/Ravana-Falls-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ravana Falls/Ravana-Falls-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ravana Falls/Ravana-Falls-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Badulla/Ravana Falls/Ravana-Falls-5.jpg`,
    ],
    shortDescription:
        "Ravana Falls is perfect for nature enthusiasts, photographers, and culture lovers.Stand before this roaring cascade, feel the mist on your face, and experience one of Sri Lanka’s most legendary natural wonders.",
    fullDescription:
      "",
    whyVisit: [
        "Majestic Waterfall – One of Sri Lanka’s widest waterfalls, cascading from 82 feet (25 meters).",
        "Scenic Beauty – Surrounded by lush greenery and misty mountains of Ella.",
        "Mythical Legend – Tied to the Ramayana epic; said to be where King Ravana hid Princess Sita.",
        "Perfect for Photography – A popular stop for capturing the power and beauty of Sri Lankan nature.",
        "Easy Access – Convenient roadside location, just a short drive from Ella town."

    ],
    overview:
    "Ravana Falls is one of Sri Lanka’s most iconic natural attractions, located about 6 km from Ella town on the Ella, Wellawaya Road. The waterfall drops from an impressive height amidst rocky cliffs and dense forest, creating a breathtaking view, especially during the rainy season. The falls are part of the Ravana Ella Wildlife Sanctuary and are steeped in local legend who believed to be the hiding place of Princess Sita by King Ravana in the ancient Ramayana story.",
    ticketsEntry: {
      residents: "Free(public area)",
      foreigners: "Free(public area) ",
      children: "Free(public area)",
      notes: [
        "Guided Tours:: Available from Ella (~LKR 2,000–3,000 / USD 6–10) including nearby attractions.",
        "Photography:Allowed; best from roadside or safe lower ledges."
        
      ]
    },
    highlights: [
      {
    main: "Natural Wonder",
    subPoints: [
    	"Cascades down a series of rock faces forming shallow natural pools.",
      "Most spectacular during the monsoon season (April–September)."

    ]
  },
  {
    main: "Cultural & Historical Significance",
    subPoints: [
        "Associated with the ancient Ramayana legend of King Ravana.",
	      "A small cave (Ravana Cave) nearby is said to have been his secret hideout."

    ]
  },
  {
    main: "Relaxation & Nature",
    subPoints: [
        "Refreshing natural pools (swimming not recommended during heavy rains).",
        "Surrounded by lush jungle sounds and cool mountain air."

    ]

  },
  {
    main:"Photography Hotpot",
    subPoints:[
       " Capture dramatic water flow, rainbows, and misty surroundings.",
      	"Morning light provides the best conditions for photos."
    ]
  },   
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours daily",
      bestTime: [
        " Open 24 hours daily",
        "Ideal Season: April–September for full water flow"

      ]
    },
   gettingThere: {
    fromTown: [
      "By Car/Tuk-Tuk: ~15–20 minutes drive (6 km); tuk-tuk fare ~LKR 500–800.",
      "By Foot: Possible for hikers – a scenic downhill walk (around 1 hour each way).",

    ],
    fromNearby: [
      "From Colombo:",
      "By Car/Taxi: ~6–7 hours (200 km) – fare LKR 22,000–25,000 (~USD 60–70).",
      "By Train: Colombo to Ella scenic route, then tuk-tuk from Ella Station.",
      "By Bus: Colombo–Badulla or Ella-bound buses stop near the waterfall entrance."

    ], 
    
    },  
    nearbyHotels: [
      "98 Acres Resort & Spa – Luxury option with panoramic views of Ella Gap.",
      "Ravana Heights – Mid-range hotel overlooking the falls.",
      "Ella Flower Garden Resort – Comfortable stay near the main road.",
      "The Hideaway Ella – Boutique-style lodging with a tranquil setting."


    ],
    travelTips: [
          "Visit early morning to avoid crowds and capture clear photos.",
          "Avoid swimming during the monsoon due to strong currents.",
          "Wear non-slip shoes if walking near wet rocks.",
          "Bring insect repellent and water.",
          "Combine your visit with Little Adam’s Peak or Nine Arches Bridge for a complete Ella experience."
    ],
    didYouKnow: "Ravana Falls is believed to be linked to Ravana Cave, located about 2 km away, where King Ravana is said to have hidden Princess Sita after kidnapping her from India. These two sites are key stops on Sri Lanka’s Ramayana Trail, blending myth, nature, and history",
    nearby: [
      "",
      "-",
      
    ],
  },

  //Monaragala District
  {
    id: "Buduruwagala-Temple",
    title: "Buduruwagala Temple – Wellawaya",
    town: "Wellawaya",
    district: "Monaragala",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Buduruwagala Temple/Buduruwagala-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Buduruwagala Temple/Buduruwagala-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Buduruwagala Temple/Buduruwagala-Temple-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Buduruwagala Temple/Buduruwagala-Temple-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Buduruwagala Temple/Buduruwagala-Temple-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Buduruwagala Temple/Buduruwagala-Temple-5.jpg`,
    ],
    shortDescription:
        "Buduruwagala Temple is a mystical fusion of art, history, and devotion — a quiet reminder of Sri Lanka’s deep spiritual roots. Whether you’re an art lover, history enthusiast, or simply seeking peace, this hidden gem near Wellawaya offers an unforgettable experience.",
    fullDescription:
      "",
    whyVisit: [
        "Ancient Rock Sculptures – Home to the tallest standing Buddha statue in Sri Lanka, carved into a rock face over 1,000 years ago.",
        "Spiritual Serenity – A peaceful Buddhist site surrounded by forest and calm waters.",
        "Historical Significance – Dating back to the 10th century, this site showcases Mahayana Buddhist influences during the late Anuradhapura period.",
        "Unique Photo Spot – The dark-grey rock and delicate carvings create a stunning contrast in photos, especially during golden hour."

    ],
    overview:
    "Buduruwagala Temple lies about 5 km from Wellawaya in the Monaragala District. The name “Buduruwagala” translates to “the rock with Buddha images.” The temple’s main feature is a colossal 15-meter-high standing Buddha figure carved into a cliff, surrounded by six smaller statues forming a spiritual ensemble. The central Buddha is believed to represent the Mahā Vairocana Buddha, while the surrounding figures depict Bodhisattvas and disciples, symbolizing deep Mahayana roots in Sri Lankan Buddhist history.",
    ticketsEntry: {
      residents: ": LKR 300 ",
      foreigners: "LKR 1,000 (~USD 3)  ",
      children: "",
      notes: [
        "Guide Fee (optional): LKR 500–800 (~USD 2–3).",
        "Photography: Allowed; drones may require local temple permission."
        
      ]
    },
    highlights: [
      {
    main: "The Main Buddha Statue",
    subPoints: [
    	"The tallest ancient standing Buddha in Sri Lanka, measuring 15.5 meters (51 ft).",
      "Carved into a single granite rock, it retains traces of ancient orange and white stucco paint, suggesting the robe’s original colors",
    ]
  },
  {
    main: "The Ensemble of Seven Figures",
    subPoints: [
        "The main figure is flanked by three smaller statues on each side, representing Avalokiteshvara, Tara, and Mahayana Bodhisattvas.",
        "A carved flame-shaped halo behind the head symbolizes enlightenment."
    ]
  },
  {
    main: "Natural Setting",
    subPoints: [
        "The site is set within a tranquil forest reserve with a small man-made lake nearby.",
        "Known for its serene atmosphere — a perfect spot for quiet reflection."

    ]

  },
  {
    main:"Photography Tips",
    subPoints:[
       "Visit early morning or late afternoon for soft light and fewer visitors.",
        "Capture the full cliff with surrounding greenery for dramatic effect."

    ]
  },   
    ],
    
    visitingHours: {
      daily:
        "7:00 AM – 6:00 PM",
      bestTime: [
        "Early morning (before 9:00 AM) or evening (after 4:00 PM)."

      ]
    },
   gettingThere: {
    fromTown: [
      "By Car/Tuk-Tuk: 10 minutes (5 km) via Buduruwagala Road. Tuk-tuk fare ~LKR 500.",
      "By Foot/Bike: Possible for those staying nearby; scenic countryside route."


    ],
    fromNearby: [
      "From Ella:",
      "By Car: ~1 hour (40 km) via Wellawaya–Ella Road (A23).",
      "By Bus: Ella to Wellawaya (~LKR 150), then tuk-tuk to the site.",

      "From Colombo:",
      "By Car: ~6 hours (240 km) via the Southern Expressway (E01) and A2/A23 roads.",

    ], 
    
    },  
    nearbyHotels: [
      "Jetwing Kaduruketha – Luxury eco-lodge in Wellawaya’s lush surroundings.",
      "Mount View Hotel – Mid-range stay close to the site.",
      "Siyanbola Holiday Resort – Budget accommodation with local charm.",
      "EKHO Ella – Great option if you’re traveling from Ella."

    ],
    travelTips: [
          "Wear modest clothing (shoulders and knees covered) – this is a sacred site.",
          "Bring water and a hat; the area can get quite hot during midday.",
          "Avoid touching carvings or statues.",
          "Visit early to experience the peaceful forest ambiance and bird sounds.",
          "Combine your visit with Diyaluma Falls or Ravana Falls for a full cultural day trip."

    ],
    didYouKnow: "Legend says that Buduruwagala was once a thriving Buddhist monastery, and a flame of oil miraculously appeared from a crack in the rock near the main statue, believed to symbolize the “light of the Buddha.” Even today, a dark oil stain can be seen below the carved flame on the rock surface.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Gal-Oya",
    title: "Gal Oya National Park – Monaragala District",
    town: "Monaragala",
    district: "Monaragala",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Gal oya/Gal-Oya-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Gal oya/Gal-Oya-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Gal oya/Gal-Oya-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Gal oya/Gal-Oya-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Gal oya/Gal-Oya-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Gal oya/Gal-Oya-5.jpg`,
    ],
    shortDescription:
        "Gal Oya National Park offers an unforgettable mix of tranquility, wildlife, and scenic beauty, a perfect retreat for eco-travelers and nature enthusiasts seeking Sri Lanka’s untouched wilderness",
    fullDescription:
      "",
    whyVisit: [
      "Unique Boat Safaris – The only place in Sri Lanka offering wildlife safaris by boat across Senanayake Samudraya.",
      "Elephant Swimming Experience – Witness elephants swimming between islands — a rare and magical sight.",
      "Biodiversity Haven – Home to elephants, leopards, sloth bears, deer, and over 150 bird species.",
      "Scenic Islands & Landscapes – Picturesque mix of lakes, forests, and mountain ranges.",
      "Peaceful Wilderness – Undisturbed by mass tourism, ideal for solitude and true nature exploration."


    ],
    overview:
    "Established in 1954, Gal Oya National Park spans 25,900 hectares in southeastern Sri Lanka. The park centers around the Senanayake Samudraya, the country’s largest man-made lake, created to support the Gal Oya irrigation scheme. What makes Gal Oya unique is its boat safaris, allowing visitors to cruise among tiny forested islands and watch elephants swimming, an unforgettable wildlife experience.The park’s rich ecosystem includes evergreen forests, savannas, and grasslands, hosting over 30 mammal species and 150 bird species, including rare endemics. It’s managed by the Department of Wildlife Conservation and remains one of the least-crowded parks in the country, offering an intimate connection with nature",
    ticketsEntry: {
      residents: "LKR 150 ",
      foreigners: "LKR 3,000 ",
      children: "LKR 1,500",
      notes: [
        "Safari Jeep (if used): LKR 8,000–10,000 per vehicle",
        "Boat Safari Fee: LKR 8,000–12,000 (shared boats available",
        "Guides: Required; available at the park entrance (LKR 1,000–2,000)."
        
      ]
    },
    highlights: [
      {
    main: "Boat Safaris on Senanayake Samudraya",
    subPoints: [
    	"Observe elephants swimming between islands.",
      "Spot crocodiles basking, eagles soaring, and herds drinking by the lake.",
      "Early morning safaris offer the best light and calm waters."

    ]
  },
  {
    main: "Wildlife Watching",
    subPoints: [
        "Home to elephants, leopards, sloth bears, deer, water buffalo, and langurs.",
        "Birdwatching paradise: see grey-headed fish eagles, pelicans, and kingfishers."

    ]
  },
  {
    main: "Scenic Views & Trekking",
    subPoints: [
        "Trek to Danigala Rock, known as the “Alien Rock,” with panoramic lake views.",
        "Explore forest trails with local guides to experience untouched wilderness."


    ]

  },
  {
    main:"Photography Opportunities",
    subPoints:[
       "Capture elephants swimming or birds in flight over tranquil waters.",
        "Sunrise and sunset over the reservoir create breathtaking reflections."


    ]
  },   
    ],
    
    visitingHours: {
      daily:
        "7:00 AM – 6:00 PM",
      bestTime: [
        "May – September (dry season, best for animal sightings)",
        "Ideal Safari Time: Early morning or late afternoon"

      ]
    },
   gettingThere: {
    fromTown: [
      
    ],
    fromNearby: [
      "Nearest Town: Inginiyagala (~20 km from park entrance).",
      "Nearest Train Station: Badulla (~100 km away, then taxi or bus).",

      "From Colombo:",
      "By Car: ~6 hours (315 km) via A4 Highway (Colombo → Ratnapura → Monaragala → Inginiyagala).",
      "By Bus: Colombo → Ampara or Monaragala, then tuk-tuk to the park."


    ], 
    
    },  
    nearbyHotels: [
      	"Gal Oya Lodge – Eco-luxury lodge offering guided safaris and nature treks.",
        "Wild Glamping Gal Oya – Immersive glamping surrounded by nature.",
        "Senanayake Rest House – Budget stay near the dam.",
        "Saraii Village (Tissamaharama) – Ideal for travelers combining southern routes."


    ],
    travelTips: [
          "Book boat safaris in advance (limited daily slots).",
          "Carry binoculars, insect repellent, and sunscreen.",
          "Wear light, earth-toned clothing and sturdy shoes.",
          "Avoid loud noises — wildlife here is sensitive to human presence.",
          "Combine your visit with Lahugala or Maduru Oya National Park for an extended eco-trip.",

    ],
    didYouKnow: "Gal Oya is the only national park in Sri Lanka where visitors can watch elephants swimming between islands, an extraordinary phenomenon seen nowhere else in Asia! The lake and park were named after the Gal Oya River, which feeds the Senanayake Samudraya, built in honor of the country’s first Prime Minister, D. S. Senanayake",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Maligawila-Buddha-Statue",
    title: "Maligawila Buddha Statue – Monaragala District",
    town: "Monaragala",
    district: "Monaragala",
    province: "Uva-Province",
    provinceId: "uva",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Maligawila Buddha Statue/Maligawila-Buddha-Statue-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Maligawila Buddha Statue/Maligawila-Buddha-Statue-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Maligawila Buddha Statue/Maligawila-Buddha-Statue-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Maligawila Buddha Statue/Maligawila-Buddha-Statue-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Maligawila Buddha Statue/Maligawila-Buddha-Statue-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Uva/Monaragala/Maligawila Buddha Statue/Maligawila-Buddha-Statue-5.jpg`,
    ],
    shortDescription:
        "Maligawila Buddha Statue stands as a testament to Sri Lanka’s ancient artistry, spirituality, and resilience, an awe-inspiring encounter with the island’s cultural soul.",
    fullDescription:
      "",
    whyVisit: [
      "Largest Free-Standing Buddha in Sri Lanka – A towering masterpiece carved from a single limestone block.",
      "Historical and Cultural Landmark – Built in the 7th century during the reign of King Aggabodhi IV.",
      "Symbol of Peace and Faith – Represents Sri Lanka’s rich Buddhist heritage and artistic craftsmanship.",
      "Serene Surroundings – Located amidst tranquil forests, perfect for quiet reflection and meditation.",
      "Majestic Photography Spot – Ideal for capturing the grandeur of ancient stone art in a natural setting."
    ],
    overview:
    "The Maligawila Buddha Statue, situated near Buttala in the Monaragala District, is one of Sri Lanka’s most awe-inspiring archaeological wonders. Carved entirely from a single limestone rock, this 12-meter (39-foot) statue represents the standing Buddha in the “Abhaya Mudra”, the gesture of fearlessness and protection. The statue dates back to the 7th century and was commissioned by King Aggabodhi IV (665–683 AD).Rediscovered and restored in 1951 after centuries of neglect, the statue now stands proudly once again, symbolizing resilience and devotion. Nearby, visitors can also find the Dambegoda Avalokiteshvara Statue, believed to represent the Bodhisattva of Compassion",
    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free",
      children: "Free",
      notes: [
      	"Guides: Optional, ~LKR 500–1,000 for local guide insights,",
        "Photography: Allowed freely"
      ]
    },
    highlights: [
      {
    main: "Impressive Craftsmanship",
    subPoints: [
      "Carved from a single block of limestone – a remarkable engineering feat for its time.",
      "Stands 12.3 meters (40 ft) tall, making it the tallest ancient free-standing Buddha statue in Sri Lanka.",
      "Depicts the Buddha in Abhaya Mudra, symbolizing protection and peace."

    ]
  },
  {
    main: "Ancient Heritage",
    subPoints: [
        "Constructed under King Aggabodhi IV’s patronage to promote Buddhism in southern Sri Lanka.",
        "Part of a larger monastic complex once active in the Ruhuna region."
    ]
  },
  {
    main: "Scenic Setting",
    subPoints: [
        "Surrounded by dense vegetation, small lakes, and paddy fields.",
        "The calm, unspoiled environment enhances the sacred experience.",

    ]

  },
  {
    main:"Photography Tips",
    subPoints:[
       "Capture the statue from a low angle to emphasize its towering presence.",
      	"Morning light provides beautiful shadows on the limestone surface."

    ]
  },   
    ],
    
    visitingHours: {
      daily:
        "7:00 AM – 6:00 PM",
      bestTime: [
        "Morning or late afternoon for cooler temperatures and softer light",

      ]
    },
   gettingThere: {
    fromTown: [
      
    ],
    fromNearby: [
      "From Buttala:",
      "By Car/Tuk-Tuk: ~30 minutes (15 km) south via Maligawila Road. Tuk-tuk fare ~LKR 700–1,000.",

      "From Ella:",
      "By Car: ~1.5 hours (60 km) via Wellawaya–Buttala Road (A23).",
      "By Bus: Ella → Wellawaya → Buttala, then tuk-tuk to the site.",



      "From Colombo:",
      "By Car: ~6–7 hours (290 km) via Southern Expressway (E01) and A2/A4 routes."


    ], 
    
    },  
    nearbyHotels: [
      	"Jetwing Kaduruketha – Luxury eco-lodge surrounded by nature in Wellawaya.",
        "Hotel Centauria Wild – Located near Monaragala, offering comfort and convenience.",
        "Mount View Guest Inn – Budget option for backpackers.",
        "Saraii Village – Eco-stay near Tissamaharama, ideal for combining southern cultural tours."


    ],
    travelTips: [
          "Dress modestly (cover shoulders and knees) when visiting.",
          "Bring water, sunscreen, and a hat — the area can be hot during midday.",
          "Combine with a visit to Dambegoda Bodhisattva Statue nearby.",
          "Morning hours offer the best light for photography and the most peaceful atmosphere.",
          "Respect the sacred nature of the site. Avoid loud noises and remove footwear near the base."


    ],
    didYouKnow: "The Maligawila Buddha Statue was found broken into several pieces when rediscovered in the 1950s. In a monumental restoration project, it was carefully reassembled using traditional stone-lifting techniques without modern machinery, a symbolic act of reviving Sri Lanka’s Buddhist legacy.",
    nearby: [
      "",
      "-",
      
    ],
  },

  //Western Province
  //colombo district
   {
    id: "Beira-Lake",
    title: "Beira Lake – Colombo’s Serene Urban Oasis",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Bere lake/Beira-Lake-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Bere lake/Beira-Lake-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Bere lake/Beira-Lake-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Bere lake/Beira-Lake-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Bere lake/Beira-Lake-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Bere lake/Beira-Lake-5.jpg`,
    ],
    shortDescription:
        "Beira Lake offers a peaceful retreat and a glimpse into Colombo’s history.Stroll its scenic paths, enjoy the reflections of temples, and experience the calm in the heart of the city. ",
    fullDescription:
      "",
    whyVisit: [
        "Peaceful Escape – A calm, scenic lake in the heart of bustling Colombo.",
        "Historic Significance – Built by the Portuguese in the 16th century as a defensive and trade waterway.",
        "Picturesque Walks – Paths around the lake are perfect for photography and evening strolls.",
        "Cultural Landmarks – Home to Gangaramaya Temple, Seema Malakaya, and historic bridges.",
        "Birdwatching & Nature – Spot local birds, lotus flowers, and serene reflections on the water."

    ],
    overview:
    "Beira Lake, located near Colombo Fort, is one of the city’s oldest man-made lakes. Originally built during Portuguese rule in the 1500s, it has historically served as a trade and defense hub. Today, the lake is a peaceful green-blue retreat surrounded by temples, gardens, and walking paths, making it an ideal spot for relaxation, photography, and cultural exploration.",
    ticketsEntry: {
      residents: "Free to public areas and walking paths. ",
      foreigners: "Free to public areas and walking paths.",
      children: "Free to public areas and walking paths.",
      notes: [
      	"Temple Visits: Gangaramaya Temple and Seema Malakaya have small entry fees (LKR 400–600 / USD 1–2).",
        "Photography: Allowed in most areas; be respectful near worshippers."
      ]
    },
    highlights: [
      {
    main: "Temples & Cultural Sites",
    subPoints: [
      "Gangaramaya Temple: One of Colombo’s most iconic Buddhist temples, right on the lake’s edge.",
      "Seema Malakaya: A floating meditation hall designed by Geoffrey Bawa, offering a serene spiritual experience."


    ]
  },
  {
    main: "Nature & Scenic Walks",
    subPoints: [
        "Stroll along the lakeside paths lined with lotus plants and local birds.",
      	"Watch local fishermen and occasional small boats on the water.",
        "Peaceful spot for meditation, yoga, or photography.",

    ]
  },
  {
    main: "Photography Opportunities",
    subPoints: [
        "Stunning reflections of temples and cityscapes on the water.",
        "Sunrise and sunset provide soft, golden lighting for dramatic shots."
    ]

  },  
    ],
    
    visitingHours: {
      daily:
        "5:30 AM – 7:00 PM",
      bestTime: [
        "Early morning for calm reflections and cool temperatures, or late afternoon for sunset views.",

      ]
    },
   gettingThere: {
    fromTown: [
      "By Tuk-Tuk: 5–10 minutes, fare LKR 300–500.",
      "By Car: 5 minutes, via Sir James Peiris Mawatha.",
      "By Bus: Buses heading toward Slave Island or Cinnamon Gardens stop nearby."

      
    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Taxi/Car: ~40 minutes (35 km), cost USD 15–20 / LKR 5,000–7,000."
    ], 
    
    },  
    nearbyHotels: [
      	"Cinnamon Grand Colombo (5★) – 5 minutes away, luxury stay near the lake.",
        "Taj Samudra Colombo (5★) – Oceanfront comfort, 10 minutes drive.",
      	"CityRest Fort (Budget) – Affordable and central.",
        "Cinnamon Red Colombo (4★) – Modern hotel with rooftop bar, 10–15 minutes away."
    ],
    travelTips: [
        "Combine your visit with Gangaramaya Temple or Seema Malakaya for a full cultural experience.",
        "Bring a water bottle and sunscreen for walking around the lake.",
        "Morning visits are best for birdwatching and photography.",
        "Respect temple rules and avoid loud noise near religious sites.",
        "Ideal for casual strolls, meditation, or short boat rides if available."

    ],
    didYouKnow: "Beira Lake was originally created by the Portuguese in the 16th century and later expanded by the Dutch. It served as a key trade and defense route for Colombo before becoming the serene urban oasis it is today.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Colombo-Fort",
    title: "Colombo Fort – Where History Meets the Heart of the City",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Commercial",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Colombo Fort/Colombo-Fort-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Colombo Fort/Colombo-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Colombo Fort/Colombo-Fort-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Colombo Fort/Colombo-Fort-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Colombo Fort/Colombo-Fort-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Colombo Fort/Colombo-Fort-5.jpg`,
    ],
    shortDescription:
        "Colombo Fort is where Sri Lanka’s history, culture, and modern city life come together.From colonial architecture to ocean sunsets, it’s a must-visit stop on your journey through Colombo. ",
    fullDescription:
      "",
    whyVisit: [
        "Historic Landmark – Once a fortified colonial stronghold shaped by the Portuguese, Dutch, and British.",
      	"Architectural Beauty – Home to grand colonial buildings like the Old Parliament and Cargills, mixed with modern city life.",
        "Cultural Experience – Discover Sri Lanka’s blend of history, religion, food, and urban energy in one compact area.",
        "Shopping & Dining Hotspot – From luxury restaurants at the Dutch Hospital to local bites at Pettah Market.",
        "Scenic Seaside Views – Walk to Galle Face Green for ocean sunsets and vibrant street food stalls."
    ],
    overview:
    "Colombo Fort is the historic and commercial heart of Sri Lanka’s capital city. A walk through its streets reveals centuries of history and a mix of old-world architecture and contemporary life. It’s where government buildings, financial centers, colonial monuments, and local markets meet the sea, making it the ideal place to begin exploring Colombo.",
    ticketsEntry: {
      residents: "Free to public areas ",
      foreigners: "Free to public areas",
      children: "Free to public areas ",
      notes: [
      	"Guided Tours: Available for USD 10–20 (LKR 3,000–6,000) per person",
        "Photography: Allowed in most areas; restricted near government buildings",
        "Prices may change; please check locally before your visit."
      ]
    },
    highlights: [
      {
    main: "Architectural Landmarks",
    subPoints: [
      "Old Parliament Building – A colonial icon now housing the Presidential Secretariat.",
      "Cargills (Ceylon) Building – Classic red-brick structure from the 19th century.",
      "Old Dutch Hospital – A heritage building turned into a lifestyle and dining complex.",
      "Grand Oriental Hotel – Historic hotel offering sweeping views of Colombo Harbor."

    ]
  },
  {
    main: "Shopping & Food",
    subPoints: [
        "Souvenir shops and cafés inside the Dutch Hospital precinct.",
        "Famous restaurants like Ministry of Crab, Nihonbashi, and Sugar Bistro.",
        "Pettah Market nearby for spices, fabrics, and local snacks."
    ]
  },
  {
    main: "Scenic Walks",
    subPoints: [
        "alle Face Green – Colombo’s oceanfront promenade, ideal for sunsets and street food.",
        "Lighthouse Clock Tower – A signature landmark overlooking the Indian Ocean."
    ]

  }, 
   
    ],
    
    visitingHours: {
      daily:
        "Open all day.(Shops & Restaurants: 10:00 AM – 10:00 PM.)",
      bestTime: [
        "December–April for clear skies and cooler weather. Early mornings or evenings are best for walking tours and photography.",

      ]
    },
   gettingThere: {
    fromTown: [
    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: 35–40 minutes (35 km), around USD 15–20 / LKR 5,000–7,000.",
      "By Train: Take any major train to Colombo Fort Railway Station.",
      "By Bus: Frequent airport–city buses, approx. USD 2 / LKR 600.",
      "By Tuk-Tuk: Common around Colombo; confirm fare or use a metered ride."

    ], 
    
    },  
    nearbyHotels: [
      	"The Galle Face Hotel (5★) – Historic oceanfront luxury.",
        "Mövenpick Hotel Colombo (5★) – Modern hotel with rooftop views.",
        "Cinnamon Lakeside (5★) – Elegant lakeside stay.",
        "CityRest Fort (Budget) – Affordable and central."

    ],
    travelTips: [
        "Wear light, breathable clothes for walking tours.",
        "Use PickMe or Uber for convenient rides.",
        "Carry small notes for local transactions.",
        "Stay alert around busy traffic areas.",
        "Visit at sunset to see Galle Face Green at its liveliest.",


    ],
    didYouKnow: "Colombo Fort was once completely surrounded by ramparts and a moat. Though the walls no longer stand, the streets still trace the original outline of the old fortress.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Galle-Face",
    title: "Galle Face Green – Colombo’s Oceanfront Escape",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Commercial",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Galle face Green/Galle-Face-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Galle face Green/Galle-Face-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Galle face Green/Galle-Face-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Galle face Green/Galle-Face-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Galle face Green/Galle-Face-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Galle face Green/Galle-Face-5.jpg`,
    ],
    shortDescription:
        "From golden sunsets to sizzling street food, Galle Face Green captures the soul of Colombo.Relax, unwind, and soak up the ocean breeze at the city’s most iconic seaside promenade",
    fullDescription:
      "",
    whyVisit: [
          "Iconic Landmark – A 5-hectare urban park stretching along the Indian Ocean, symbolizing Colombo’s coastal charm.",
        	"Sunset Paradise – Watch the sky turn golden as the sun dips over the sea, a must-see moment in Colombo.",
          "Local Life Experience – Mingle with families, couples, and street vendors for an authentic Sri Lankan vibe.",
          "Street Food Heaven – Taste isso wade (prawn fritters), kottu, and fresh fruit juices while strolling by the waves.",
          "Historic Significance – Created in 1859 by British Governor Sir Henry Ward for horse racing and seaside recreation."

    ],
    overview:
    "Located just minutes away from Colombo Fort, Galle Face Green is one of the city’s most popular and beloved open spaces. The long stretch of lawn borders the ocean, offering a refreshing breeze and a front-row seat to spectacular sunsets. It’s a meeting point for locals and visitors alike, a place to relax, fly kites, snack on street food, and experience the pulse of Colombo.",
    ticketsEntry: {
      residents: "Free for all visitors",
      foreigners: "Free for all visitors",
      children: "",
      notes: [
          "Kite-flying: Available for around LKR 300–600 per kite.",
          "Horse rides (on weekends): Approx. LKR 1,000–2,000 per short ride.",
          "Street food: Most snacks cost LKR 200–800 each."

      ]
    },
    highlights: [
      {
    main: "Sunset Strolls",
    subPoints: [
      "The most famous sunset spot in Colombo — perfect for evening walks and photos.",
      "Locals gather daily to relax, play cricket, or enjoy street performances."

    ]
  },
  {
    main: "Street Food Delights",
    subPoints: [
        "Try popular Sri Lankan snacks like isso wade, cuttlefish rolls, and spicy mango.",
        "Vendors set up from late afternoon until night, creating a lively food market atmosphere."

    ]
  },
    ],
    
    visitingHours: {
      daily:
        "24 hours (most lively from 4:00 PM – 9:00 PM).",
      bestTime: [
        "Evening hours, especially 5:30 PM – 6:30 PM for sunset views and cooler weather.",

      ]
    },
   gettingThere: {
    fromTown: [
      "By Foot: About 15 minutes’ walk (1 km).",
      "By Tuk-Tuk: 5 minutes, fare LKR 300–600.",
      "By Car/Taxi: Easily accessible via Galle Road.",
      "By Bus: Many city buses stop along Galle Face Road."

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Taxi/Car: Around 40–45 minutes, cost USD 15–20 / LKR 5,000–7,000."
    ], 
    
    },  
    nearbyHotels: [
      	"The Galle Face Hotel (5★) – Overlooking the Green and ocean; iconic colonial charm.",
        "Taj Samudra Colombo (5★) – Luxurious stay with sea views and fine dining.",
        "Mövenpick Hotel Colombo (5★) – Modern comfort and rooftop sunsets.",
        "Colombo City Hotel (3★) – Convenient and budget-friendly option."

    ],
    travelTips: [
        "Bring a picnic mat or buy snacks from local vendors for the full experience.",
        "Dress casually but modestly; it’s a family-friendly public space.",
        "Be cautious with valuables after dark; the area can get crowded.",
        "Visit on weekends for the liveliest atmosphere, or weekdays for a calmer experience.",
        "Carry small change (LKR 100–500) for street vendors."



    ],
    didYouKnow: "Galle Face Green was originally created for horse racing and military parades during the British colonial era. Today, it remains one of the largest and oldest open spaces in Sri Lanka’s capital, and a favorite gathering spot for both locals and visitors",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Gangaramaya-Temple",
    title: "Gangaramaya Temple – Colombo’s Spiritual & Cultural Landmark",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Religious",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Gangaramaya/Gangaramaya-Temple-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Gangaramaya/Gangaramaya-Temple-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Gangaramaya/Gangaramaya-Temple-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Gangaramaya/Gangaramaya-Temple-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Gangaramaya/Gangaramaya-Temple-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Gangaramaya/Gangaramaya-Temple-5.jpg`,
    ],
    shortDescription:
        "Gangaramaya Temple is more than a place of worship — it’s a living museum of faith, art, and community.",
    fullDescription:
      "",
    whyVisit: [
          "Architectural Marvel – A stunning blend of Sri Lankan, Thai, Indian, and Chinese styles.",
        	"Cultural Experience – Explore centuries of Buddhist art, relics, and statues in one sacred space.",
          "Serene Atmosphere – A peaceful escape from the bustle of Colombo city.",
          "Museum of Treasures – Home to rare artifacts, ancient coins, ivory carvings, and gifts from across Asia.",
          "Vesak Celebrations – One of the best places in Sri Lanka to witness Colombo’s colorful Buddhist festival."
    ],
    overview:
    "Located near Beira Lake in central Colombo, Gangaramaya Temple is both a place of worship and a center for learning, charity, and culture. Founded in the late 19th century, it’s one of Sri Lanka’s most important Buddhist temples, revered not only for its spiritual value but also for its incredible architectural diversity and extensive museum collection. The temple complex seamlessly combines traditional and modern design, reflecting Sri Lanka’s openness and cultural richness.",
    ticketsEntry: {
      residents: "Free for all visitors",
      foreigners: "Free for all visitors",
      children: "",
      notes: [
          "Dress Code: Modest attire required — shoulders and knees should be covered.",
          "Photography: Allowed inside; avoid flash and respect worshippers.",
          "Donations: Optional but appreciated; funds support the temple’s social programs."
      ]
    },
    highlights: [
      {
    main: "Sunset Strolls",
    subPoints: [
      "The most famous sunset spot in Colombo — perfect for evening walks and photos.",
      "Locals gather daily to relax, play cricket, or enjoy street performances."

    ]
  },
  {
    main: "Street Food Delights",
    subPoints: [
        "Try popular Sri Lankan snacks like isso wade, cuttlefish rolls, and spicy mango.",
        "Vendors set up from late afternoon until night, creating a lively food market atmosphere."

    ]
  },
    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 9:00 PM..",
      bestTime: [
        "Early morning (6:00–8:00 AM) or evening (after 5:00 PM) for a quiet spiritual experience and soft natural light for photos.",

      ]
    },
   gettingThere: {
    fromTown: [
      "By Tuk-Tuk: 10 minutes, fare LKR 400–700.",
      "By Car: Around 10–15 minutes via Sir James Peiris Mawatha.",
      "By Bus: Frequent buses from Pettah and Kollupitiya stop nearby (ask for Slave Island junction)."
    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Taxi/Car:Around 45 minutes (35 km), cost USD 15–20 / LKR 5,000–7,000."
    ], 
    
    },  
    nearbyHotels: [
      	"Cinnamon Lakeside (5★) – Tranquil luxury with a lakeside view.",
        "Cinnamon Grand Colombo (5★) – Ideal for visitors seeking comfort near key attractions.",
        "Taj Samudra (5★) – Overlooks the Indian Ocean, 10 minutes away.",
        "CityRest Fort (Budget) – Great value and central location"


    ],
    travelTips: [
      "Remove shoes and hats before entering the main temple.",
      "Bring or buy a sarong/shawl at the entrance if your outfit isn’t temple-appropriate.",
      "Visit Seema Malakaya on the same trip, entrance is free and photography-friendly.",
      "Respect the monks and worshippers; avoid turning your back to Buddha statues when taking photos.",
      "Visit during Vesak (May) or Poson (June) for spectacular lantern displays and processions"

    ],
    didYouKnow: "Gangaramaya Temple not only serves as a religious site but also funds schools, orphanages, and vocational training centers across Sri Lanka — making it one of the most socially active temples in the country.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Independence-Square",
    title: "Independence Square – Colombo’s Symbol of Freedom",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Independence Arcade/Independence-Square-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Independence Arcade/Independence-Square-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Independence Arcade/Independence-Square-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Independence Arcade/Independence-Square-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Independence Arcade/Independence-Square-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Independence Arcade/Independence-Square-5.jpg`,
    ],
    shortDescription:
        "Independence Square is a living testament to Sri Lanka’s history and pride.Walk its grand halls, admire the statues of national heroes, and soak in the peaceful gardens — all while connecting with the story of the nation. ",
    fullDescription:
      "",
    whyVisit: [
        "Historic Landmark – Commemorates Sri Lanka’s independence from British rule in 1948.",
        "Architectural Elegance – Features grand colonnades, statues, and landscaped gardens.",
        "Cultural Hub – Home to art exhibitions, cultural performances, and public events.",
        "Photo Opportunities – The iconic stone pillars and wide steps make for stunning shots.",
        "Peaceful Retreat – A calm space in the heart of Colombo, ideal for walks and reflection."

    ],
    overview:
    "Independence Square (also called Independence Memorial Hall) is located in Cinnamon Gardens, central Colombo. Built in 1948, it celebrates Sri Lanka’s freedom from colonial rule. The site blends classical Sri Lankan architecture with serene gardens, making it a popular destination for history enthusiasts, photographers, and anyone seeking a peaceful escape from city bustle.",
    ticketsEntry: {
      residents: "Free for all visitors ",
      foreigners: "Free for all visitors",
      children: "Free for all visitors.",
      notes: [
      	"Guided Tours: Available upon request; typically arranged via local guides or travel agencies.",
        "Photography: Allowed freely; perfect for history or architecture enthusiasts."

      ]
    },
    highlights: [
      {
    main: "Main Hall & Statues",
    subPoints: [
      "Independence Memorial Hall: Majestic hall with white stone pillars and a roof inspired by ancient Sri Lankan architecture.",
      "Founding Leaders’ Statues: Statues of national heroes who contributed to Sri Lanka’s independence.",
      "Open Courtyards: Spacious areas for photography, strolls, or cultural performances."

    ]
  },
  {
    main: "Gardens & Surroundings",
    subPoints: [
        "Manicured lawns and flowering trees provide a peaceful environment.",
        "Jogging and walking paths attract locals in the mornings and evenings.",
        "Nearby Cinnamon Gardens neighborhood offers cafés and boutique shops."
    ]
  },
  {
    main: "Cultural Activities",
    subPoints: [
        "Occasionally hosts art exhibitions, music performances, and national events.",
        "A great spot for school or university trips focused on history and culture.",

    ]

  },  
    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning (6:00–8:00 AM) or late afternoon for soft lighting and cooler weather.",

      ]
    },
   gettingThere: {
    fromTown: [
      "Car/Taxi: ~15 minutes (7 km), fare ~USD 5 / LKR 1,500–2,000.",
      "By Tuk-Tuk: 10–15 minutes, fare LKR 500–700.",
      "By Bus: Buses heading to Borella, Cinnamon Gardens, or Hulftsdorp stop nearby."

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~45 minutes (35 km), cost USD 15–20 / LKR 5,000–7,000."
    ], 
    
    },  
    nearbyHotels: [
      "Cinnamon Grand Colombo (5★) – Luxury stay, 10–15 minutes away.",
      "Taj Samudra (5★) – Oceanfront elegance, ~10 minutes drive.",
      "Colombo City Hotel (Budget) – Affordable option near the city center.",
      "Cinnamon Red Colombo (4★) – Modern hotel with rooftop bar, ~15 minutes away."

    ],
    travelTips: [
        "Visit in the early morning to enjoy quiet gardens and fewer crowds.",
        "Wear comfortable shoes for walking around the wide courtyards.",
        "Respect the memorials and avoid climbing on statues or pillars.",
        "Combine your visit with Gangaramaya Temple or Viharamahadevi Park for a full cultural tour.",
        "Bring water, sunscreen, and a hat if visiting midday."

    ],
    didYouKnow: "Independence Square was originally built on the site of a former British colonial prison. Its transformation into a national monument symbolizes Sri Lanka’s journey from colonization to freedom.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Lotus-Tower",
    title: "Lotus Tower – Colombo’s Iconic Landmark",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Commercial",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Lotus Tower/Lotus-Tower-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Lotus Tower/Lotus-Tower-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Lotus Tower/Lotus-Tower-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Lotus Tower/Lotus-Tower-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Lotus Tower/Lotus-Tower-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Lotus Tower/Lotus-Tower-5.jpg`,
    ],
    shortDescription:
        "Lotus Tower offers a breathtaking perspective of Colombo and a mix of culture, dining, and entertainment.See the city from above, snap stunning photos, and experience one of Sri Lanka’s most iconic landmarks. ",
    fullDescription:
      "",
    whyVisit: [
        	"Tallest Structure in Sri Lanka – Standing at 356 meters, it dominates Colombo’s skyline.",
        	"Panoramic Views – Observation decks offer 360° views of Colombo city, Beira Lake, and the Indian Ocean.",
          "Modern Architecture – Iconic lotus-inspired design blending culture with modern engineering.",
          "Entertainment & Dining – Restaurants, cafés, and event spaces located within the tower.",
          "Photography Heaven – A must-visit for cityscape shots, especially at sunset or night.",


    ],
    overview:
    "Lotus Tower, also known as the Colombo Lotus Tower, is Sri Lanka’s tallest freestanding structure. Located in Colombo’s waterfront district, it was completed in 2019 and serves as a telecommunications, observation, and entertainment hub. The tower’s lotus-shaped design represents purity, resilience, and Sri Lankan culture, making it both an engineering marvel and a national symbol.",

    ticketsEntry: {
      residents: "LKR 500 ",
      foreigners: "USD 20 ",
      children: "under 10 free",
      notes: [
          "Dining: Buffet ~LKR 9,000 per adult; half price for children 5–11, free under 5.",
          "Photography: Allowed; tripod may need prior permission.",
            "Prices may change; please check locally before your visit."


      ]
    },
    highlights: [
      {
    main: "Observation Decks",
    subPoints: [
      "Enjoy panoramic views of Colombo city, Beira Lake, and the coastline.",
      "See Colombo’s famous landmarks, including Galle Face Green, Pettah Market, and Mount Lavinia Beach from above."


    ]
  },
  {
    main: "Dining & Entertainment",
    subPoints: [
       "Revolving restaurants at the top serve local and international cuisine.",
        "Cafés and lounges at mid-levels for relaxing with a view.",
        "Event spaces host exhibitions, shows, and corporate functions."



    ]
  },
  {
    main: "Photography Opportunities",
    subPoints: [
        "Sunset and night views provide dramatic cityscape photography.",
        "The lotus-shaped tower itself is a prime subject for architectural photos."


    ]
  },
    ],
    
    visitingHours: {
      daily:
        "9:00 AM – 10:00 PM.",
      bestTime: [
        ": Late afternoon for sunset views or evening for the illuminated cityscape."
      ]
    },
   gettingThere: {
    fromTown: [
        "By Car/Taxi: ~15–20 minutes (8 km), fare ~USD 5–7 / LKR 1,500–2,500",
        "By Tuk-Tuk: 15–20 minutes, fare LKR 500–800",
        "By Bus: Buses toward Talangama or Dematagoda pass nearby"


    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Taxi/Car: ~45 minutes (35 km), cost USD 15–20 / LKR 5,000–7,000",
    ], 
    
    },  
    nearbyHotels: [
      	"Cinnamon Grand Colombo (5★) – 10 minutes drive, central luxury.",
        "Taj Samudra Colombo (5★) – 10 minutes away, oceanfront comfort.",
        "Galle Face Hotel (5★) – 15 minutes, beachfront luxury.",
        "Budget Guesthouses – Multiple options in Colombo 01 and 03, near the lake"


    ],
    travelTips: [
    " Book tickets online for the observation deck to avoid queues.",
    "Combine with nearby Beira Lake, Gangaramaya Temple, or Pettah Market for a full day itinerary.",
    "Bring a camera with zoom for distant city views.",
    "Evening visits provide illuminated cityscape photography opportunities.",
    "Check the restaurant schedule if you plan to dine at the top."



    ],
    didYouKnow: "Lotus Tower is not just a tourist attraction — it also functions as a telecommunication hub and a cultural landmark, symbolizing Sri Lanka’s progress and modern identity while honoring its traditional lotus symbolism",
    nearby: [
      "",
      "-",
      
    ],
  },
   {
    id: "Mount-Lavinia",
    title: "Mount Lavinia Beach – Colombo’s Coastal Escape",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Mout Lavania Beach/Mount-Lavinia-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Mout Lavania Beach/Mount-Lavinia-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Mout Lavania Beach/Mount-Lavinia-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Mout Lavania Beach/Mount-Lavinia-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Mout Lavania Beach/Mount-Lavinia-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Mout Lavania Beach/Mount-Lavinia-5.jpg`,
    ],
    shortDescription:
        "",
    fullDescription:
      "Mount Lavinia Beach is the perfect mix of history, sun, and sea.Relax on golden sands, enjoy ocean views, and capture unforgettable sunsets just minutes from Colombo. ",
    whyVisit: [
        	"Sun, Sand & Sea – A long golden beach ideal for swimming, sunbathing, and beach activities.",
          "Scenic Views – Stunning sunset vistas over the Indian Ocean.",
        	"Historic Landmark – Home to the famous Mount Lavinia Hotel, built during the British colonial era.",
          "Vibrant Atmosphere – Mix of locals, tourists, and beachside cafés creating a lively yet relaxed vibe.",
          "Water Sports & Activities – Options for swimming, paddleboarding, and occasional surfing."



    ],
    overview:
    "Mount Lavinia Beach, located just south of Colombo, is one of Sri Lanka’s most popular urban beaches. Its soft golden sand, calm waters, and lively beach culture make it a favorite spot for both locals and tourists. The beach is famous for the Mount Lavinia Hotel, a historic colonial-era landmark that overlooks the ocean, adding a touch of elegance to your seaside visit. ",

    ticketsEntry: {
      residents: "Free to access the beach ",
      foreigners: "Free to access the beach",
      children: "Free to access the beach",
      notes: [
          "Paddleboards/kayaks: LKR 1,500–3,000 (~USD 5–10).",
        	"Beach umbrellas/chairs: LKR 300–600 (~USD 1–2).",
          "Photography: Allowed everywhere; ideal for sunset and landscape photography",
          "Prices may change; please check locally before your visit."


      ]
    },
    highlights: [
      {
    main: "Sunsets & Scenic Walks",
    subPoints: [
      "Walk along the 5 km stretch of golden sand and enjoy the vibrant sunsets.",
      "Capture stunning photos with the Mount Lavinia Hotel in the background."
    ]
  },
  {
    main: "Food & Dining",
    subPoints: [
       "Beachside restaurants and cafés serving fresh seafood, local dishes, and tropical drinks.",
        "Try street food snacks like isso wade (prawn fritters) or fresh fruit juices."
    ]
  },
  {
    main: "Activities & Leisure",
    subPoints: [
        
      "Swimming and sunbathing in safe, designated areas.",
      "Occasional water sports like paddleboarding and kayaking.",
      "Beach cricket and volleyball with locals on weekends."
    ]
  },
  {
    main: "Nearby Landmark",
    subPoints: [
        "Mount Lavinia Hotel (5★) – Colonial-era luxury hotel with ocean views, fine dining, and rooftop bars."
    ]
  },
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours, busiest and most scenic in late afternoon (4:00 PM – 6:30 PM).",
      bestTime: [
        "Late afternoon to sunset for the perfect golden-hour experience"
      ]
    },
   gettingThere: {
    fromTown: [
      "By Car/Taxi: ~30 minutes (12 km), fare ~USD 8 / LKR 2,500–3,500.",
      "By Tuk-Tuk: 30–40 minutes, fare LKR 1,200–1,500.",
      "By Bus: Frequent buses to Mount Lavinia from Colombo Fort (ask for Mount Lavinia stop).",
     "By Train: Coastal railway from Colombo Fort to Mount Lavinia Railway Station (~30 minutes)."

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Taxi/Car: ~45 minutes (35 km), cost USD 15–20 / LKR 5,000–7,000",
    ], 
    
    },  
    nearbyHotels: [
      	"Mount Lavinia Hotel (5★) – Iconic colonial-era luxury, directly on the beach.",
        "Galle Face Hotel (5★) – 20–25 minutes drive north, oceanfront comfort.",
        "Cinnamon Red Colombo (4★) – Modern city stay, 25 minutes away.",
        "Budget Guesthouses & Beachside Hostels – Many along the coastline, offering affordable stays with sea views."



    ],
    travelTips: [
    "Bring sunscreen, a hat, and swimwear for beach activities.",
    "Arrive early evening to enjoy cooler temperatures and sunset views.",
    "Watch for local vendors selling snacks and crafts; small purchases support the local community.",
    "Avoid leaving valuables unattended while swimming.",
    "Combine with a visit to Dutch Hospital or Mount Lavinia town for dining and shopping"

    ],
    didYouKnow: "Mount Lavinia Beach was named after Lovina Aponsuwa, the daughter of a local chief, who was associated with Governor Sir Thomas Maitland during the British colonial period. The Mount Lavinia Hotel started as the governor’s seaside residence in the 1800s and remains an iconic landmark today.",
    nearby: [
      "",
      "-",
      
    ],
  },
  
  {
    id: "Museum ",
    title: "National Museum of Colombo – Sri Lanka’s Cultural Treasure",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/National Museum/Museum-1.jpg`,
    images: [
    `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/National Museum/Museum-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/National Museum/Museum-2.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/National Museum/Museum-3.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/National Museum/Museum-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/National Museum/Museum-5.jpg`,
    ],
    shortDescription:
        "The National Museum of Colombo is a window into Sri Lanka’s past, from royal regalia to ancient art.Explore centuries of history, culture, and heritage under one roof — a must-visit for history lovers and travelers alike. ",
    fullDescription:
      "",
    whyVisit: [
        "Largest Museum in Sri Lanka – Home to over 400,000 artifacts spanning history, art, and culture.",   
        "Royal Collections – Explore regalia, crowns, and thrones of ancient Sri Lankan kings.",
        "Buddhist Art & Relics – See rare statues, paintings, and relics from across the country.",
        "Educational Experience – Learn about Sri Lanka’s rich heritage, from prehistoric times to modern history.",
        "Photography & History Combined – Beautiful galleries and intricate displays perfect for history enthusiasts and photographers"

    ],
    overview:
    "The Colombo National Museum, located in Cinnamon Gardens, is the largest and oldest museum in Sri Lanka, established in 1877 by Sir William Henry Gregory. Its grand colonial architecture houses royal artifacts, ancient art, weapons, manuscripts, and traditional crafts, offering visitors a comprehensive overview of Sri Lanka’s rich cultural and historical heritage",

    ticketsEntry: {
      residents: "LKR 1,500 (~USD 5)",
      foreigners: " LKR 2,500 (~USD 8)",
      children: "LKR 500 (~USD 2)",
      notes: [
          "Guided Tours: Available; cost LKR 2,000–3,000 (~USD 6–10) per group.",
          "Photography: Allowed in most areas; avoid flash in certain galleries.",
          "Prices may change; please check locally before your visit."


      ]
    },
    highlights: [
      {
    main: "Key Exhibits",
    subPoints: [
      "Royal Regalia – Crowns, swords, and thrones of Sinhalese kings.",
      "Buddhist Art – Ancient statues, relics, and sacred artifacts.",
      "Archaeology & History – Prehistoric tools, coins, and manuscripts tracing Sri Lanka’s development.",
      "Ethnology – Traditional crafts, masks, and musical instruments representing local culture"

    ]
  },
  {
    main: "Special Galleries",
    subPoints: [
        "Temporary exhibitions showcasing contemporary art, photography, and cultural projects.",
        "Interactive displays explaining historical events, costumes, and ancient scripts."
    ]
  },
  {
    main: "Architecture & Photography",
    subPoints: [
        "Colonial-era building with spacious halls, high ceilings, and intricate woodwork.",
      	"Indoor and outdoor photography allowed (without flash in certain galleries)."

    ]
  }
    ],
    
    visitingHours: {
      daily:
        "9:00 AM – 5:00 PM",
      bestTime: [
        "Morning hours (9:00–11:30 AM) to avoid crowds and enjoy natural light in the galleries."
      ]
    },
   gettingThere: {
    fromTown: [
      	" By Car/Taxi: 15–20 minutes (6 km), fare ~USD 5 / LKR 1,500–2,000",
        "By Tuk-Tuk: 10–15 minutes, fare LKR 400–600",
        "By Bus: Buses heading toward Cinnamon Gardens or Bullers Road stop nearby,",


    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Taxi/Car: ~45 minutes (35 km), cost USD 15–20 / LKR 5,000–7,000",
    ], 
    
    },  
    nearbyHotels: [
      	"Cinnamon Grand Colombo (5★) – Luxury stay, 10–15 minutes away.",
        "Taj Samudra Colombo (5★) – Oceanfront comfort, 10 minutes away.",
        "Colombo City Hotel (Budget) – Central and affordable.",
        "Cinnamon Red Colombo (4★) – Modern hotel with rooftop bar."

    ],
    travelTips: [
      "Wear comfortable shoes; the museum is large with multiple galleries.",
      "Bring water, especially if combining the visit with nearby attractions.",
      "Hire a guide for deeper insight into artifacts and historical context.",
      "Avoid flash photography in sensitive exhibits.",
      "Combine your visit with Viharamahadevi Park or Independence Square, which are nearby."



    ],
    didYouKnow: "The National Museum’s main building was designed by James George Smither, the first Government Architect of Ceylon, and its architecture was inspired by the British Museum in London.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Pettah",
    title: "Pettah Market – Colombo’s Bustling Bazaar",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Commercial",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Petta/Pettah-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Petta/Pettah-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Petta/Pettah-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Petta/Pettah-3.jpg`,
     `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Petta/Pettah-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Petta/Pettah-5.jpg`,
    ],
    shortDescription:
        "Pettah Market is the heartbeat of Colombo’s local life.Shop, explore, and taste your way through one of Sri Lanka’s most vibrant and authentic market experiences.",
    fullDescription:
      "",
    whyVisit: [
        "Authentic Local Experience – Immerse yourself in the sights, sounds, and smells of a traditional Sri Lankan market.",
        "Shopping Paradise – From textiles and electronics to spices and jewelry, the market has it all.",
        "Cultural Vibe – A melting pot of cultures with Hindu, Muslim, and Buddhist traders side by side.",
        "Street Food Heaven – Try local snacks like isso wade, kottu, and fresh tropical fruits.",
        "Photography Opportunities – Colorful shops, crowded streets, and vibrant street life make it a photographer’s dream."

    ],
    overview:
    "Pettah Market, located near Colombo Fort, is the city’s most famous open-air bazaar. Known for its lively streets, colorful stalls, and maze-like layout, Pettah offers a unique shopping and cultural experience. It’s the ideal spot to buy souvenirs, experience local life, and enjoy authentic street food, all while navigating the energetic chaos that defines this historic market district.",

    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free",
      notes: [
          "Shopping Costs: Vary widely; small souvenirs start at LKR 50 (~USD 0.20), spices LKR 200–500 (~USD 1–2), clothing LKR 500–2,000 (~USD 2–8).",
          "Photography: Allowed; be mindful of vendors and shoppers."



      ]
    },
    highlights: [
      {
    main: "Markets & Shopping",
    subPoints: [
      "Main Street Shops: Electronics, clothing, accessories, and household items.",
      "Spice Market: Fresh spices, dried herbs, and tea — perfect for souvenirs.",
      "Textile Rows: Colorful saris, fabrics, and tailoring services.",
      "Jewelry & Gold: Traditional goldsmiths and costume jewelry stalls."


    ]
  },
  {
    main: "Street Food & Snacks",
    subPoints: [
      "Local favorites like isso wade (prawn fritters), kottu roti, and fresh coconut water.",
      "Small cafés and tea stalls offering authentic Sri Lankan snacks and beverages."

    ]
  },
  {
    main: "Photography & Vibe",
    subPoints: [
        "Capture the vibrant streets filled with rickshaws, traders, and bustling crowds.",
        "Morning visits provide softer light and slightly calmer conditions for photos."

    ]
  }
    ],
    
    visitingHours: {
      daily:
        "7:00 AM – 8:00 PM",
      bestTime: [
        "Morning hours (9:00–11:30 AM) to avoid crowds and enjoy natural light in the galleries."
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Foot: 5–10 minutes walk; very close to the train station",
        "By Tuk-Tuk: 5 minutes, fare LKR 300–500.",
        "By Bus: Many city buses stop along Main Street and Gasworks Junction.,",


    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Taxi/Car: ~45 minutes (35 km), cost USD 15–20 / LKR 5,000–7,000",
    ], 
    
    },  
    nearbyHotels: [
      	"Cinnamon Grand Colombo (5★) – Luxury stay, 10–15 minutes away.",
        "Taj Samudra Colombo (5★) – Oceanfront comfort, 10 minutes away.",
        "Colombo City Hotel (Budget) – Central and affordable.",
        "Cinnamon Red Colombo (4★) – Modern hotel with rooftop bar."

    ],
    travelTips: [
      "Carry small change for easy purchases and bargaining.",
      "Keep valuables close; the market is crowded and busy.",
      "Bargaining is expected — start at 20–30% lower than the asking price.",
      "Visit with a guide if you want help navigating the maze-like streets.",
      "Try local street food, but only from clean, busy stalls."



    ],
    didYouKnow: "Pettah is one of Colombo’s oldest trading districts, with roots dating back to the Dutch and Portuguese periods. Its name comes from the Tamil word “Pettai”, meaning a marketplace or town outside the fort.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Viharamahadevi-Park",
    title: "Viharamahadevi Park – Colombo’s Largest Urban Green Space",
    town: "Colombo",
    district: "Colombo",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Vihara maha devi park/Viharamahadevi-Park-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Vihara maha devi park/Viharamahadevi-Park-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Vihara maha devi park/Viharamahadevi-Park-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Vihara maha devi park/Viharamahadevi-Park-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Vihara maha devi park/Viharamahadevi-Park-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Colombo/Vihara maha devi park/Viharamahadevi-Park-5.jpg`,
    ],
    shortDescription:
        "Viharamahadevi Park is the perfect blend of nature, culture, and recreation in the heart of Colombo.Stroll, relax, or play — and experience one of the city’s most cherished green spaces",
    fullDescription:
      "",
    whyVisit: [
        	"City Oasis – The largest and oldest park in Colombo, perfect for relaxation amidst greenery.",
          "Family-Friendly – Play areas, ponds, and open lawns make it ideal for children and picnics.",
          "Scenic Beauty – Beautiful flower gardens, shady trees, and statues throughout the park.",
          "Cultural Monuments – Houses the iconic Statue of Queen Viharamahadevi and other historic sculptures.",
          "Recreational Activities – Jogging tracks, outdoor exercise areas, and paddle boating on the park’s lake."

    ],
    overview:
    "Viharamahadevi Park, located next to Independence Square in central Colombo, is the city’s largest green space. Originally named Victoria Park during the British era, it was renamed in honor of Queen Viharamahadevi, mother of King Dutugemunu. The park combines natural beauty, recreational facilities, and cultural landmarks, making it a favorite destination for families, fitness enthusiasts, and tourists seeking a peaceful urban escape. ",

    ticketsEntry: {
      residents: " Free ",
      foreigners: " Free",
      children: "Free ",
      notes: [
          "Paddle boats: LKR 300–500 per ride.",
          "Playgrounds: Free."

      ]
    },
    highlights: [
      {
    main: "Gardens & Walking Paths",
    subPoints: [
      "Lush lawns, flowerbeds, and mature trees create a peaceful atmosphere.",
      "Shaded walking and jogging paths ideal for exercise or a casual stroll."

    ]
  },
  {
    main: "Monuments & Statues",
    subPoints: [
        "Queen Viharamahadevi Statue – Central landmark commemorating Sri Lanka’s historic queen.",
        "Other sculptures depict local heritage, animals, and important national figures."


    ]
  },
  {
    main: "Recreational Activities",
    subPoints: [
       "Paddle boating available on the park’s small lake.",
      "Outdoor gym equipment and exercise areas for public use.",
      "Children’s playgrounds with swings and slides."


    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "5:30 AM – 7:00 PM.",
      bestTime: [
        "Early morning for jogging and cool temperatures, or late afternoon to enjoy the sunset over the park."
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Tuk-Tuk: ~10 minutes, fare LKR 400–600.",
        "By Car: 5–10 minutes, via Ananda Coomaraswamy Mawatha.",
        "By Bus: Multiple buses stop nearby along Flower Road and Cotta Road."



    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~45 minutes (35 km), cost USD 15–20 / LKR 5,000–7,000.",
    ], 
    
    },  
    nearbyHotels: [
      	"Cinnamon Grand Colombo (5★) – 5–10 minutes drive, luxury stay with city views.",
        "Taj Samudra Colombo (5★) – Close to the ocean, ~10 minutes away.",
        "CityRest Fort (Budget) – Affordable central option, ~15 minutes away.",
        "Cinnamon Red Colombo (4★) – Modern stay with rooftop bar."


    ],
    travelTips: [
        "Bring water and sunscreen for daytime visits.",
        "Ideal for picnics, morning jogs, or reading under the trees.",
        "Combine with a visit to Independence Square or Gangaramaya Temple nearby.",
        "Watch for local pigeons and squirrels; feeding them is allowed but keep it safe.",
        "Visit during weekends for lively local activity or weekdays for a quieter experience."

    ],
    didYouKnow: "Viharamahadevi Park was initially created by the British in 1930 as Victoria Park, and it was the first large public park in Colombo. Renaming it honored Queen Viharamahadevi, whose legendary bravery is celebrated in Sri Lankan history.",
    nearby: [
      "",
      "-",
      
    ],
  },

  //Gampaha district
  {
    id: "Dutch-Fort",
    title: "Dutch Fort – Negombo’s Historic Landmark",
    town: "Negombo",
    district: "Gampaha",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Historical",
   image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Dutch Fort – Negombo/Dutch-Fort-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Dutch Fort – Negombo/Dutch-Fort-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Dutch Fort – Negombo/Dutch-Fort-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Dutch Fort – Negombo/Dutch-Fort-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Dutch Fort – Negombo/Dutch-Fort-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Dutch Fort – Negombo/Dutch-Fort-5.jpg`,
    ],
    shortDescription:
        "Dutch Fort Negombo is a must-visit for history lovers and photographers.Explore the remnants, learn about colonial Sri Lanka, and combine with nearby beaches and markets for a full cultural experience.",
    fullDescription:
      "",
    whyVisit: [
        	"Historic Architecture – Built by the Dutch in the 17th century, showcasing colonial military design.",
          "Cultural Significance – Part of Negombo’s rich Portuguese, Dutch, and British colonial history.",
          "Photogenic Spot – Ancient walls, cannons, and gates make for great photography.",
          "Central Location – Easily accessible in Negombo town, near the lagoon and main streets.",
          "Nearby Attractions – Combine your visit with Negombo Beach or the Negombo Fish Market."
    ],
    overview:
    "The Dutch Fort Negombo is a historic fortification built by the Dutch in 1672 during their colonial rule of Sri Lanka. Although only remnants remain today, including cannons, ramparts, and walls, it offers a glimpse into Negombo’s past as a strategic trading and defense hub. Its location in the center of Negombo makes it an easy stop for history enthusiasts and photographers.",

    ticketsEntry: {
      residents: " Free ",
      foreigners: " Free ",
      children: "Free ",
      notes: [
          "Guided Tours: Available with local guides; prices LKR 500–1,500 (~USD 1–5) per group.",
        	"Photography: Allowed everywhere; best in morning or late afternoon for soft lighting."

      ]
    },
    highlights: [
      {
    main: "Fort Remnants & Architecture",
    subPoints: [
      "Old ramparts, bastions, and cannons that show Dutch military engineering.",
      "Partial fort walls still visible, providing a sense of the original structure."
    ]
  },
  {
    main: "Photography & Exploration",
    subPoints: [
        "Great spot for taking historic and scenic photos.",
        "Explore the surrounding streets lined with colonial-style buildings."
    ]
  },
  {
    main: "Nearby Cultural Spots",
    subPoints: [
      "Close to St. Mary’s Church, one of the largest churches in Sri Lanka.",
      "Negombo lagoon and canals are nearby, ideal for a combined visit."

    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "7:00 AM – 6:00 PM.",
      bestTime: [
        "Morning for cooler temperatures and fewer crowds, or late afternoon for sunset photography.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~10 minutes, fare LKR 400–600",
        "By Tuk-Tuk: ~10 minutes, fare LKR 300–500",
        "By Bus: Short ride from Negombo town center"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: 15–20 minutes (7 km), cost USD 5–7 / LKR 1,500–2,500",
    ], 
    
    },  
    nearbyHotels: [
      	"Jetwing Blue (5★) – Beachfront luxury, 10–15 minutes away.",
        "Heritance Negombo (5★) – 10–15 minutes drive.",
        "Club Hotel Dolphin (4★) – Mid-range option near the beach.",
        "Budget Guesthouses – Located in Negombo town center, close to the fort."

    ],
    travelTips: [
        "Wear comfortable shoes for walking around the fort remnants.",
        "Combine your visit with a stroll along Negombo Beach or the fish market.",
        "Bring water and sun protection, as shade is limited.",
        "Hire a local guide to learn more about the fort’s history and Negombo’s colonial past.",
        "Morning or late afternoon visits are ideal for photography."

    ],
    didYouKnow: "The Dutch Fort in Negombo was originally built on a site used by the Portuguese before them. It was part of the Dutch colonial defense system protecting the cinnamon trade and coastal settlements. Today, it stands as a reminder of Sri Lanka’s rich colonial history.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Hamilton-Canal",
    title: "Hamilton Canal – Negombo’s Historic Waterway",
    town: "Negombo",
    district: "Gampaha",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Hamilton Canal/Hamilton-Canal-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Hamilton Canal/Hamilton-Canal-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Hamilton Canal/Hamilton-Canal-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Hamilton Canal/Hamilton-Canal-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Hamilton Canal/Hamilton-Canal-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Hamilton Canal/Hamilton-Canal-5.jpg`,
    ],
    shortDescription:
      "Hamilton Canal offers a scenic, historic, and peaceful experience near Negombo.Take a boat ride, explore mangroves, and enjoy one of Sri Lanka’s most picturesque waterways. ",
    fullDescription:
      "",
    whyVisit: [
  
          	"Historic Significance – Built by the Dutch in the 17th century for trade and transportation.",
          	"Scenic Boat Rides – Navigate calm waters lined with mangroves, coconut palms, and local villages.",
          	"Wildlife & Nature – Spot birds, fish, and occasional monitor lizards along the canal.",
          	"Photography Opportunities – Ideal for landscape, village, and wildlife photography.",
          	"Relaxing Escape – Enjoy a peaceful break from the bustling Negombo town and beaches."

    ],
    overview:
    "Hamilton Canal, originally constructed during Dutch colonial times, connects Negombo Lagoon to Colombo and other inland waterways. Stretching over 14 km, it was historically used for transporting cinnamon and other goods. Today, it is a serene waterway popular for eco-tours, boat rides, and photography, offering travelers a glimpse into Sri Lanka’s colonial and natural heritage.",

    ticketsEntry: {
      residents: " Free for walking along accessible banks",
      foreigners: " Free for walking along accessible banks ",
      children: "Free for walking along accessible banks ",
      notes: [
            "Boat Ride Fee: LKR 2,000–5,000 (~USD 6–15) depending on duration and private vs shared boat.",
            "Guided Tours: Recommended to fully explore the canal and surrounding wetlands."
      ]
    },
    highlights: [
      {
    main: "Boat Tours & Eco-Exploration",
    subPoints: [
      "Guided boat tours along the canal, exploring local villages, mangroves, and wetlands.",
        "Experience traditional fishing and see stilted houses and small bridges along the canal.",
    ]
  },
  {
    main: " Flora & Fauna",
    subPoints: [
        "Mangrove trees, coconut palms, and lush wetlands dominate the scenery.",
        "Birdwatching opportunities: kingfishers, egrets, herons, and migratory species.",
        "Spot monitor lizards and other small wildlife along the canal banks."

    ]
  },
  {
    main: "Photography & Scenic Views",
    subPoints: [
      "Capture reflections on the calm water, traditional boats, and village life.",
      "Sunrise and sunset provide excellent lighting for landscapes."
    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 5:00 PM",
      bestTime: [
        "Early morning for calm waters and active wildlife, or late afternoon for sunset photography.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~15 minutes (7 km), fare LKR 400–600",
        "By Tuk-Tuk: ~15–20 minutes, fare LKR 300–500"
    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~20 minutes (10 km), cost USD 5–7 / LKR 1,500–2,500",

       "From Colombo Fort:",
      "By Car/Taxi: ~1–1.5 hours (38 km), fare USD 20–25 / LKR 7,000–9,000"
    ], 
    
    },  
    nearbyHotels: [
      	"Jetwing Lagoon (5★) – Luxury eco-friendly stay near the canal.",
        "Heritance Negombo (5★) – 10–15 minutes drive.",
        "Club Hotel Dolphin (4★) – Popular mid-range option near the beach.",
        "Budget Guesthouses – Many along Negombo town and lagoon areas."

    ],
    travelTips: [
        "Bring a hat, sunscreen, and mosquito repellent for boat tours.",
        "Early morning tours offer the best chance for wildlife spotting.",
        "Hire a local guide or operator to fully appreciate the canal’s history.",
        "Keep noise low to avoid disturbing birds and aquatic wildlife.",
        "Combine with a visit to Muthurajawela Wetlands or Negombo Lagoon for a full eco-tour."
    ],
    didYouKnow: "Hamilton Canal was built by the Dutch to transport cinnamon and other trade goods from inland plantations to the coast. It remains an important historical and ecological landmark, showcasing Sri Lanka’s colonial engineering and natural beauty.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Henerathgoda-Garden",
    title: "Henerathgoda Botanical Gardens – Gampaha’s Green Treasure",
    town: "Henerathgoda",
    district: "Gampaha",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Henerathgoda Botanical Garden/Henerathgoda-Garden-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Henerathgoda Botanical Garden/Henerathgoda-Garden-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Henerathgoda Botanical Garden/Henerathgoda-Garden-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Henerathgoda Botanical Garden/Henerathgoda-Garden-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Henerathgoda Botanical Garden/Henerathgoda-Garden-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Henerathgoda Botanical Garden/Henerathgoda-Garden-5.jpg`,
    ],
    shortDescription:
      "Henerathgoda Botanical Gardens is a serene and educational escape near Colombo.Explore lush greenery, learn about Sri Lankan plants, and enjoy peaceful nature walks",
    fullDescription:
      "",
    whyVisit: [
        "Historic Garden – Established in 1876 as Sri Lanka’s first Cinnamon Research Center.",
      	"Diverse Flora – Over 2,000 plant species, including medicinal plants, ornamentals, and exotic trees.",
        "Scenic & Peaceful – Perfect for nature walks, picnics, and relaxation.",
        "Photography Opportunities – Lush greenery, tropical flowers, and tree-lined paths make for stunning shots.",
        "Educational Experience – Learn about medicinal plants, spices, and Sri Lankan botanical heritage."

    ],
    overview:
    "Henerathgoda Botanical Gardens, located in Gampaha District, is a historic botanical garden known for its research on cinnamon and other economically important plants. The garden features tropical flora, medicinal plants, fruit trees, and flowering species, making it a haven for botanists, nature lovers, and families seeking a peaceful retreat.",

    ticketsEntry: {
      residents: " LKR 200 (~USD 1",
      foreigners: " LKR 200 (~USD 1",
      children: "LKR 100 (~USD 0.50)",
      notes: [
            "Guided Tours: Optional, LKR 500–1,000 (~USD 2–5) depending on group size",
            "Photography: Allowed throughout the gardens"

      ]
    },
    highlights: [
      {
    main: "Flora & Gardens",
    subPoints: [
      "Cinnamon trees and other spice plants with educational signage.",
      "Ornamental plants, flowering trees, and rare tropical species.",
      "Medicinal plants section showcasing traditional Sri Lankan remedies."

    ]
  },
  {
    main: "Walking Trails & Relaxation",
    subPoints: [
        "Tree-lined paths ideal for walking or jogging.",
        "Open lawns perfect for picnics, yoga, or quiet reflection"

    ]
  },
  {
    main: "Photography & Nature",
    subPoints: [
        "Capture tropical flowers, grand trees, and shaded walkways.",
        "Ideal morning or late afternoon visit for soft, natural lighting."

    ]
  },
   {
    main: "Family & Educational Visits",
    subPoints: [
      "Informative plant labels and guided tours available for students and tourists.",
      "Interactive opportunities for children to learn about plants and ecology."

    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "8:00 AM – 5:00 PM",
      bestTime: [
        "Early morning or late afternoon for cooler temperatures and best lighting.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5 minutes (2.2 km)",
    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: 30 minutes (20 km), cost USD 5–7 / LKR 1,500–2,500",

       "From Colombo Fort:",
      "By Car/Taxi: ~1–1.5 hours (35 km), fare USD 15–20 / LKR 5,000–6,500",
      "By Tuk-Tuk: ~1.5–2 hours, fare LKR 3,000–4,000 (long ride, not ideal)",
      "By Bus: Buses to Gampaha; short tuk-tuk from town to the garden"

    ], 
    
    },  
    nearbyHotels: [
      	"Jetwing Lagoon (5★) – ~45 minutes away, near Negombo.",
        "Heritance Negombo (5★) – ~40–45 minutes drive.",
        "Budget Guesthouses – Available in Gampaha town and surrounding villages"


    ],
    travelTips: [
        "Wear comfortable walking shoes for exploring trails.",
        "Bring insect repellent and sunscreen.",
        "Carry water and light snacks; picnic areas are available.",
        "Morning visits are best for cooler temperatures and active birdlife.",
        "Combine with a trip to Negombo Lagoon or Dutch Fort for a full day tour."
    ],
    didYouKnow: "Henerathgoda Botanical Gardens was originally established as a cinnamon experimental station by the British in 1876 and is considered the first botanical research garden in Sri Lanka. It has since expanded to include tropical ornamental and medicinal plants, contributing to both research and tourism.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Muthurajawela-Wetlands",
    title: "Muthurajawela Wetlands – Colombo’s Natural Sanctuary",
    town: "Muthurajawela",
    district: "Gampaha",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Muthurjawela/Muthurajawela-Wetland-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Muthurjawela/Muthurajawela-Wetland-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Muthurjawela/Muthurajawela-Wetland-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Muthurjawela/Muthurajawela-Wetland-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Muthurjawela/Muthurajawela-Wetland-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Muthurjawela/Muthurajawela-Wetland-5.jpg`,
    ],
    shortDescription:
      "Muthurajawela Wetlands are a peaceful retreat and a wildlife haven near Colombo.Explore the mangroves, spot birds and reptiles, and experience one of Sri Lanka’s most important natural ecosystems.",
    fullDescription:
      "",
    whyVisit: [
      "Biodiversity Hotspot – Home to over 180 bird species, monitor lizards, mud crabs, and various fish.",
      "Mangrove Ecosystem – Explore one of Sri Lanka’s last remaining coastal marshlands with mangrove forests.",
      "Eco-Tours & Boat Safaris – Navigate serene waterways and observe wildlife up close.",
      "Photography Paradise – Capture unique flora, fauna, and tranquil water scenes.",
      "Educational Experience – Learn about wetlands’ role in flood control, biodiversity, and the environment."

    ],
    overview:
    "Muthurajawela Wetlands, located about 25 km north of Colombo, is Sri Lanka’s largest wetland and a critical ecological zone. Established as a protected wetland sanctuary, it covers over 3,000 hectares of marshes, mangroves, and lagoons. The area is ideal for eco-tourism, birdwatching, and experiencing a peaceful natural environment just outside the city.",

    ticketsEntry: {
      residents: "LKR 500–1,500 (~USD 2–5)",
      foreigners: "LKR 500–1,500 (~USD 2–5)",
      children: "",
      notes: [
              "Boat Safari Fee: LKR 2,000–5,000 (~USD 6–15) for a private boat tour (1–2 hours).",
              "Guided Tours: Recommended for wildlife spotting and eco-education",
              "Entry Fee for per person depending on tour."

      ]
    },
    highlights: [
      {
    main: "Boat Safaris & Eco-Tours",
    subPoints: [
      "Cinnamon trees and other spice plants with educational signage.",
      "Ornamental plants, flowering trees, and rare tropical species.",
      "Medicinal plants section showcasing traditional Sri Lankan remedies."

    ]
  },
  {
    main: "Flora & Fauna",
    subPoints: [
        "Mangroves, reeds, and tropical plants dominate the landscape.",
        "Birds include kingfishers, egrets, herons, and migratory species.",
        "Spot monitor lizards, mud crabs, and small fish along waterways."
    ]
  },
  {
    main: "Photography Opportunities",
    subPoints: [
        "Scenic waterways, lush mangroves, and wildlife provide endless photo opportunities.",
        "Early morning tours offer the best lighting and more active wildlife."
    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "8:00 AM – 5:00 PM",
      bestTime: [
        "Early morning (6:00–9:00 AM) for birdwatching and calm waters; late afternoon is also pleasant.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~35 minutes (18.2 km)",
    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~20 minutes (15 km), cost USD 5–7 / LKR 1,500–2,500",

       "From Colombo Fort:",
      "By Car/Taxi: ~45 minutes (25 km), fare USD 15–20 / LKR 5,000–6,500",
      "By Tuk-Tuk: ~1–1.5 hours, fare LKR 2,500–3,500 (long ride, not ideal)",
      "By Bus: Buses toward Muthurajawela or Ja-Ela with drop-off near the wetlands, then a short walk to the boat launch."

    ], 
    
    },  
    nearbyHotels: [
      	"Jetwing Lagoon (5★) – Luxury stay near wetlands, eco-friendly property.",
        "Negombo Beach Hotels – 20–30 minutes drive south; convenient for combining beach and wetland visits.",
      	"Budget Guesthouses & Eco-Lodges – Available near Ja-Ela and Muthurajawela."

    ],
    travelTips: [
        "Wear light, breathable clothing and mosquito repellent.",
        "Bring binoculars and a camera with zoom lens for birdwatching.",
        "Book guided tours in advance for better wildlife spotting.",
        "Carry water and snacks, as facilities are limited within the wetlands.",
        "Respect nature: avoid littering and keep noise to a minimum",

    ],
    didYouKnow: "Muthurajawela Wetlands act as a natural flood control system for Colombo and Negombo. Its mangroves and marshes filter water, protect against erosion, and provide vital habitat for many species, making it an ecological treasure for the region.",
    nearby: [
      "",
      "-",
      
    ],
  },
  {
    id: "Negombo-Beach",
    title: "Negombo Beach – Sri Lanka’s Charming Coastal Retreat",
    town: "Negombo",
    district: "Gampaha",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Negombo Beach/Negombo-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Negombo Beach/Negombo-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Negombo Beach/Negombo-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Negombo Beach/Negombo-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Negombo Beach/Negombo-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/Negombo Beach/Negombo-Beach-5.jpg`,
    ],
    shortDescription:
      "Negombo Beach is the perfect blend of sun, sand, culture, and local flavor.Relax on golden shores, enjoy fresh seafood, and watch the sun set over the Indian Ocean.",
    fullDescription:
      "",
    whyVisit: [
      "Golden Sandy Shores – A long stretch of soft sand ideal for sunbathing and leisurely walks.",
      "Vibrant Fishing Town – Experience local life with colorful fishing boats and the famous Negombo fish market.",
      "Sunset Views – Stunning Indian Ocean sunsets perfect for photography and relaxation.",
      "Water Activities – Safe swimming, snorkeling, and occasional surfing for adventure seekers.",
      "Beachside Dining – Numerous seafood restaurants and cafés along the coast."
    ],
    overview:
    "Negombo Beach, located north of Colombo near Bandaranaike International Airport (BIA), is one of Sri Lanka’s most popular coastal destinations. Known for its fishing heritage, golden sands, and tranquil waters, it provides a perfect mix of relaxation, local culture, and beachside adventure. The nearby lagoon and canals also offer unique opportunities for boat rides and birdwatching.",

    ticketsEntry: {
      residents: "Free for the beach.",
      foreigners: "Free for the beach.",
      children: "Free for the beach.",
      notes: [
              "Boat rides: LKR 1,500–3,000 (~USD 5–10)",
              "Umbrella & sunbed rentals: LKR 300–600 (~USD 1–2)",
              "Photography: Allowed everywhere; ideal for sunset, landscape, and local culture shots."
      ]
    },
    highlights: [
      {
    main: "Scenic Walks & Sunsets",
    subPoints: [
      "Walk along the golden sand for calm mornings or lively sunsets.",
      "Capture photos of fishing boats lined along the shore."
    ]
  },
  {
    main: "Fishing & Local Culture",
    subPoints: [
        "Negombo’s historic fishing community uses traditional catamarans and stilt fishing.",
        "Visit the Negombo Fish Market to see fresh catches and interact with locals"

    ]
  },
  {
    main: "Food & Dining",
    subPoints: [
        "Beachside cafés and restaurants serving freshly caught seafood.",
        "Local specialties include prawn curry, crab, and seafood rice dishes"

    ]
  },
  {
    main: "Water & Leisure Activities",
    subPoints: [
        "Swimming in designated safe areas.",
        "Snorkeling and occasional surfing depending on weather.",
        "Relaxing on loungers or taking short boat trips on the lagoon."

    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours; busiest in the late afternoon for sunsets.",
      bestTime: [
        "Early morning for calm waters or late afternoon for sunset views.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~35 minutes (18.2 km)",
    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~20 minutes (15 km), cost USD 5–7 / LKR 1,500–2,500",

       "From Colombo Fort:",
      "By Car/Taxi: ~1–1.5 hours (38 km), fare USD 20–25 / LKR 7,000–9,000",
      "By Tuk-Tuk: ~1.5–2 hours, fare LKR 3,000–4,000 (not ideal for long trips)",
      "By Bus: Frequent buses from Colombo Fort to Negombo town (~1.5 hours)",
      "By Train: Colombo–Negombo train via Ragama station (~1 hour)"

    ], 
    
    },  
    nearbyHotels: [
      	"Jetwing Blue (5★) – Beachfront luxury with ocean views.",
        "Heritance Negombo (5★) – Modern comfort by the beach.",
        "Club Hotel Dolphin (4★) – Popular mid-range option with private beach.",
        "Budget Guesthouses – Many small, cozy options in Negombo town."


    ],
    travelTips: [
        "Bring sunscreen, a hat, and swimwear for beach activities.",
        "Early morning walks provide cooler temperatures and peaceful vibes.",
        "Explore the Negombo lagoon or take a short boat ride for a unique perspective.",
        "Enjoy seafood at local restaurants, but check freshness and hygiene.",
        "Combine your visit with a short trip to Dutch Fort Negombo for history"
    ],
    didYouKnow: "Negombo Beach was historically a Portuguese, Dutch, and British settlement, with the town serving as a major trading port. Its lagoon system and fishing traditions are centuries old, making it both a cultural and scenic gem.",
    nearby: [
      "",
      "-",
      
    ],
  },
   {
    id: "Marys-Church",
    title: "St. Mary’s Church – Negombo’s Colonial Gem",
    town: "Negombo",
    district: "Gampaha",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Religous",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/St. Mary’s Church – Negombo/Marys-Church-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/St. Mary’s Church – Negombo/Marys-Church-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/St. Mary’s Church – Negombo/Marys-Church-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/St. Mary’s Church – Negombo/Marys-Church-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/St. Mary’s Church – Negombo/Marys-Church-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Gampaha/St. Mary’s Church – Negombo/Marys-Church-5.jpg`,
    ],
    shortDescription:
      "St. Mary’s Church is a must-visit for history buffs, architecture lovers, and cultural explorers.Admire its grand interiors, learn about Negombo’s Catholic heritage, and capture beautiful photos",
    fullDescription:
      "",
    whyVisit: [
      "Historic Architecture – Built during the Portuguese era, later rebuilt by the Dutch and British; features a mix of Gothic and Baroque styles.",
      "Largest Church in Negombo – One of Sri Lanka’s biggest Catholic churches, with a capacity of thousands.",
      "Stunning Interior – Beautiful murals, statues, and stained glass windows depicting biblical scenes.",
      "Cultural Insight – Learn about the strong Catholic heritage of Negombo, known as “Little Rome.",
      "Photography Opportunities – Grand altar, colorful frescoes, and architectural details ideal for photos."

    ],
    overview:
    "St. Mary’s Church, located in Negombo town, is a major landmark reflecting the city’s Portuguese and Dutch colonial history. The church is renowned for its elaborate interior, towering structure, and religious significance. Negombo has a strong Catholic population, and the church serves as a center for worship and community events",

    ticketsEntry: {
      residents: "Free for visitors.",
      foreigners: "Free for visitors.",
      children: "Free for visitors.",
      notes: [
              "Guided Tours: Optional with local guides, LKR 500–1,000 (~USD 2–5).",
              "Photography: Allowed; avoid flash during mass or ceremonies."

      ]
    },
    highlights: [
      {
    main: "Architecture & Interior",
    subPoints: [
      "Gothic and Baroque influences visible in arches, columns, and ceiling designs.",
      "Elaborate altar with statues of Mary and various saints.",
      "Large stained glass windows depicting biblical stories."

    ]
  },
  {
    main: "Artwork & Murals",
    subPoints: [
        "Murals painted in vibrant colors cover ceilings and walls.",
        "Historical and religious significance depicted in each painting."

    ]
  },
  {
    main: "Photography & Exploration",
    subPoints: [
        "Excellent for interior and architectural photography.",
        "Capture details of the altar, ceiling frescoes, and grand arches."

    ]
  },
  {
    main: "Religious & Cultural Significance",
    subPoints: [
        "Active parish with regular services.",
        "Central location in Negombo town, often visited during religious festivals like Christmas and Easter."

    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Morning or late afternoon for quieter times and better natural light for photography.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5–10 minutes, fare LKR 400–600",
        "By Tuk-Tuk: ~5–10 minutes, fare LKR 300–500"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~15 minutes (7 km), cost USD 5–7 / LKR 1,500–2,500",

       "From Colombo Fort:",
      "By Car/Taxi: ~1–1.5 hours (38 km), fare USD 20–25 / LKR 7,000–9,000",
    ], 
    
    },  
    nearbyHotels: [
      	"Jetwing Blue (5★) – 10–15 minutes drive.",
        "Heritance Negombo (5★) – 10–15 minutes.",
        "Club Hotel Dolphin (4★) – Popular mid-range option near the beach.",
      	"Budget Guesthouses – Located in Negombo town, close to the church."
    ],
    travelTips: [
        "Dress modestly when entering the church (cover shoulders and knees).",
        "Visit outside of mass times for a quiet experience.",
        "Combine your visit with Negombo Beach, Dutch Fort, or Hamilton Canal for a full-day itinerary.",
        "Respect local worshippers during services.",
        "Bring a camera for the impressive interior architecture and murals."

    ],
    didYouKnow: "St. Mary’s Church is also known as “Our Lady of Anguruwatota” and is considered the spiritual heart of Negombo’s Catholic community. The church’s ornate design and size reflect the city’s strong colonial and religious history, earning Negombo the nickname “Little Rome”.",
    nearby: [
      "",
      "-",
      
    ],
  },


//  Kaluthara District


  {
    id: "Bentota-Beach",
    title: "Bentota Beach-Adventure & Relaxation by the Ocean",
    town: "Bentota",
    district: "Kaluthara",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Bentota-Beach/Bentota-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Bentota-Beach/Bentota-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Bentota-Beach/Bentota-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Bentota-Beach/Bentota-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Bentota-Beach/Bentota-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Bentota-Beach/Bentota-Beach-5.jpg`,
    ],
    shortDescription:
      "Bentota Beach is perfect for water sports, sunbathing, and a family-friendly beach experience.Combine it with nearby gardens, river tours, and turtle hatcheries for a full-day coastal adventure",
    fullDescription:
      "",
    whyVisit: [
      "Golden Sands & Turquoise Waters – Long stretches of clean sand and calm waters ideal for swimming and sunbathing.",
      "Water Sports Hub – Jet skiing, windsurfing, banana boats, snorkeling, and diving opportunities.",
      "Family-Friendly & Safe – Supervised swimming zones suitable for all ages.",
      "Scenic & Photogenic – Stunning coastline, palm trees, and sunsets perfect for photography.",
      "Proximity to Attractions – Close to Bentota River, Brief Garden, and Kosgoda Turtle Hatchery."

    ],
    overview:
    "Bentota Beach, located in the Galle District along Sri Lanka’s western coast, is a popular destination for both leisure and adventure. It is part of a coastal area known for resorts, water sports, and scenic beauty. Its calm waters and long sandy shoreline make it a favorite among families, couples, and adventure seekers.",

    ticketsEntry: {
      residents: "Free for visitors.",
      foreigners: "Free for visitors.",
      children: "Free for visitors.",
      notes: [
              "Water Sports: Prices vary, e.g., jet skiing ~LKR 2,000–5,000 (~USD 6–15) per hour",
              "Photography: Allowed everywhere"

      ]
    },
    highlights: [
      {
    main: "Water Sports & Activities",
    subPoints: [
      "Jet skiing, windsurfing, and banana boats for thrill-seekers.",
      "Snorkeling and diving trips to explore marine life.",
      "Boat tours on Bentota River and estuary excursions."


    ]
  },
  {
    main: "Scenic Views & Photography",
    subPoints: [
        "Murals painted in vibrant colors cover ceilings and walls.",
        "Historical and religious significance depicted in each painting."

    ]
  },
  {
    main: "Photography & Exploration",
    subPoints: [
        "Panoramic beach views and sunsets over the Indian Ocean.",
        "Ideal for photography in early morning or late afternoon for natural lighting."
    ]
  },
  {
    main: "Relaxation & Leisure",
    subPoints: [
        "Sunbathing, beach volleyball, and long walks along the golden sand.",
        "Family-friendly with calm waters and safety zones for children.",
    ]
  },
  {
    main: "Nearby Attractions",
    subPoints: [
        "Brief Garden – Tropical garden and art gallery by Bevis Bawa (~10–15 min drive).",
        "Kosgoda Turtle Hatchery – Learn about sea turtle conservation and hatchling releases.",
        "Local markets and restaurants offering seafood and Sri Lankan cuisine."

    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for calm waves and sunrise; late afternoon for sunset photography",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5–10 minutes, fare LKR 400–600",
        "By Tuk-Tuk: ~5–10 minutes, fare LKR 300–500"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~2 hours (90 km), cost USD 20–25 / LKR 7,000–9,000",

       "From Colombo Fort:",
      "By Car/Taxi: ~1.5–2 hours (95 km), fare USD 25–30 / LKR 8,000–10,000", 
      "By Public Bus: Colombo–Bentota buses available; short walk to the beach (~5 minutes)",
      "By Train: Colombo–Bentota line (~2 hours), then walk or tuk-tuk to the beach"  

    ], 
    
    },  
    nearbyHotels: [
      	"Avani Bentota Resort (5★) – Beachfront luxury",
        "Centara Ceysands Resort & Spa (5★) – Beach access and water sports",
        "Budget Guesthouses & Local Lodges – Available in Bentota town and along the coast"

    ],
    travelTips: [
       "Bring sunscreen, hats, swimwear, and water shoes.",
       "Early morning or late afternoon visits for cooler temperatures and better photography.",
       "Combine your visit with Brief Garden, Bentota River, or Kosgoda Turtle Hatchery.",
       "Stay hydrated; carry water and snacks if spending the full day.",
       "Respect local fishermen and avoid restricted areas near boats and nets."

    ],
    didYouKnow: "Bentota Beach is part of the western coastal belt of Sri Lanka known for calm waters and sandy shores. It’s not only a popular tourist beach but also a hub for water sports and eco-tourism, with nearby river and conservation areas adding to its appeal.",
    nearby: [
      "",
      "-",
      
    ],
  },

  {
    id: "Beruwela-Beach",
    title: "Beruwela Beach – Family-Friendly Coastal Retreat",
    town: "Beruwela",
    district: "Kaluthara",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Beruwela-Beach/Beruwela-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Beruwela-Beach/Beruwela-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Beruwela-Beach/Beruwela-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Beruwela-Beach/Beruwela-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Beruwela-Beach/Beruwela-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Beruwela-Beach/Beruwela-Beach-5.jpg`,
    ],
    shortDescription:
      " Beruwela Beach is perfect for a calm, family-friendly beach day.Swim, relax, or explore nearby attractions for a complete coastal experience.",
    fullDescription:
      "",
    whyVisit: [
      "Golden Sandy Shoreline – Long stretches of soft sand ideal for swimming, sunbathing, and beach walks.",
      "Calm Waters – Gentle waves make it safe for families and beginner swimmers.",
      "Water Sports & Activities – Snorkeling, jet skiing, and occasional surfing opportunities.",
      "Tranquil Atmosphere – Less crowded than nearby Bentota, offering a relaxing coastal escape.",
      "Proximity to Attractions – Close to Bentota River, Kosgoda Turtle Hatchery, and local fishing villages."

    ],
    overview:
    "Beruwela Beach, located in Kalutara District, is a serene and family-friendly destination along Sri Lanka’s western coast. Known for its calm waters and peaceful environment, it’s perfect for swimming, water sports, and relaxation. The area is also home to local fishing communities, offering cultural insight and fresh seafood experiences.",

    ticketsEntry: {
      residents: "Free ",
      foreigners: "Free ",
      children: "Free ",
      notes: [
              "Water Sports & Activities: Prices vary; jet skiing ~LKR 2,000–5,000 (~USD 6–15) per session",
              "Photography: Allowed everywhere"


      ]
    },
    highlights: [
      {
    main: "Swimming & Beach Activities",
    subPoints: [
      "Safe swimming zones; calm waves suitable for all ages.",
      "Water sports options include snorkeling, jet skiing, and occasional surfing."
    ]
  },
  {
    main: "Scenic Views & Photography",
    subPoints: [
        "Panoramic ocean views, sandy shores, and coastal sunsets.",
        "Early morning or late afternoon provides the best lighting for photos."


    ]
  },
  {
    main: " Relaxation & Leisure",
    subPoints: [
        "Quiet beaches perfect for picnics, yoga, or sunbathing.",
        "Walk along the shore to explore nearby fishing villages and coconut groves."

    ]
  },
  {
    main: "Nearby Attractions",
    subPoints: [
        "Kosgoda Turtle Hatchery – Learn about sea turtle conservation and hatchling releases.",
        "Bentota River & Water Sports – Boat rides and river tours.",
        "Local markets and eateries offering fresh seafood and snacks."


    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for sunrise or late afternoon for sunsets and calmer waters",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5–10 minutes, fare LKR 400–600",
        "By Tuk-Tuk: ~5–10 minutes, fare LKR 300–500"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: 2 hours (90 km), cost USD 20–25 / LKR 7,000–9,000",

       "From Colombo Fort:",
      "By Car/Taxi: ~2–2.5 hours (95 km), fare USD 25–30 / LKR 8,000–10,000", 
      "By Public Bus: Colombo–Beruwela buses available; short walk to the beach (~5–10 minutes)",
      "By Train: Colombo–Beruwela line (~2 hours), then walk or tuk-tuk to the beach"  

    ], 
    
    },  
    nearbyHotels: [
      	"Avani Bentota Resort (5★) – ~15–20 minutes drive",
        "Centara Ceysands Resort & Spa (5★) – ~20 minutes",
	      "Budget Guesthouses & Local Lodges – Available in Beruwela town and along the coast"


    ],
    travelTips: [
       "Bring sunscreen, hats, and swimwear for beach activities.",
        "Early morning visits for peaceful walks and sunrise photography.",
        "Combine with Kosgoda Turtle Hatchery or Bentota River tours for a full-day itinerary.",
        "Stay hydrated and carry water during midday visits.",
        "Respect local fishing activities and avoid disturbing nets or boats."

    ],
    didYouKnow: "Beruwela Beach is part of Sri Lanka’s western coastal belt, known for calm waters and family-friendly beaches. Its proximity to conservation areas like Kosgoda Turtle Hatchery makes it ideal for combining leisure with eco-tourism experiences.",
    nearby: [
      "",
      "-",
      
    ],
  },

  {
    id: "Brief-Garden",
    title: "Brief Garden – Tropical Oasis & Artistic Haven",
    town: "Bentota",
    district: "Kaluthara",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Brief-Garden/Brief-Garden-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Brief-Garden/Brief-Garden-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Brief-Garden/Brief-Garden-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Brief-Garden/Brief-Garden-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Brief-Garden/Brief-Garden-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Brief-Garden/Brief-Garden-5.jpg`,
    ],
    shortDescription:
      "Brief Garden is a must-visit for art lovers, nature enthusiasts, and photographers.Stroll through its lush gardens, admire sculptures, and enjoy a peaceful tropical escape.",
    fullDescription:
      "",
    whyVisit: [
    "Architectural & Artistic Charm – Designed by renowned Sri Lankan architect Bevis Bawa, blending nature and art.",
    "Lush Tropical Gardens – Exotic plants, vibrant flowers, and serene walking paths.",
    "Historic Estate – Former estate of Bevis Bawa, famous for his landscaping and design.",
    "Sculptures & Art Installations – Features whimsical sculptures, statues, and creative garden elements.",
    "Photography Heaven – Perfect for capturing vibrant flora, art, and scenic views of Bentota."

    ],
    overview:
    "Brief Garden, located in Bentota, was the home and tropical masterpiece of Bevis Bawa, one of Sri Lanka’s most celebrated landscape architects. The estate combines art, architecture, and horticulture in a unique way, creating a tranquil and visually stunning environment for visitors. It is a must-visit for nature lovers, artists, and photographers.",

    ticketsEntry: {
      residents: "LKR 1,500 (~USD 5)",
      foreigners: "LKR 1,500 (~USD 5)",
      children: "LKR 750 (~USD 2.50) ",
      notes: [
             "Guided Tours: Available on request (~LKR 500–1,000 / USD 2–5)",
            "Photography: Allowed; perfect for casual or professional photography"
      ]
    },
    highlights: [
      {
    main: "Gardens & Flora",
    subPoints: [
      "Walk through manicured gardens filled with exotic flowers, palms, and trees.",
      "Hidden corners with tropical plants and decorative elements.",
      "Peaceful paths perfect for reflection and leisure walks"
    ]
  },
  {
    main: "Art & Architecture",
    subPoints: [
        "Statues, sculptures, and fountains integrated into the natural landscape.",
        "Creative use of space, terraces, and stone pathways blending with nature.",
        "Insights into Bevis Bawa’s vision and design philosophy"


    ]
  },
  {
    main: "Photography Opportunities",
    subPoints: [
        "Vibrant flora and garden vistas for landscape photography.",
        "Sculptures and artistic elements provide unique photo subjects.",
        "Early morning or late afternoon visits recommended for soft natural light."


    ]
  },
  {
    main: "Cultural & Historical Significance",
    subPoints: [
       "Estate reflects Sri Lankan colonial-era architecture with a personal artistic twist.",
       "Offers a glimpse into the life and creativity of Bevis Bawa, a pioneer in tropical landscaping."
    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "9:00 AM – 5:00 PM",
      bestTime: [
        "Morning or late afternoon for cooler weather and optimal photography lighting",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5–10 minutes, fare LKR 400–600",
        "By Tuk-Tuk: ~5–10 minutes, fare LKR 300–500"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~2 hours (90 km), cost USD 20–25 / LKR 7,000–9,000",

       "From Colombo Fort:",
      "By Car/Taxi: ~2–2.5 hours (95 km), fare USD 25–30 / LKR 8,000–10,000", 
      "By Public Bus: Colombo–Beruwela buses available; short walk to the beach (~5–10 minutes)",
      "By Train: Colombo–Beruwela line (~2 hours), then walk or tuk-tuk to the beach"  

    ], 
    
    },  
    nearbyHotels: [
      	"Avani Bentota Resort (5★) – ~15–20 minutes drive",
        "Centara Ceysands Resort & Spa (5★) – 10–15 minutes drive",
	      "Budget Guesthouses & Local Lodges – Available in Beruwela town and along the coast"


    ],
    travelTips: [
       "Wear comfortable walking shoes for exploring gardens and paths.",
        "Morning visits recommended for cooler temperatures and fewer crowds.",
        "Combine with Bentota Beach, Beruwela Beach, or Kosgoda Turtle Hatchery for a full-day itinerary.",
        "Carry water and sun protection.",
        "Respect garden areas and art installations; avoid touching sculptures unless permitted."

    ],
    didYouKnow: "Brief Garden was originally a coconut plantation before being transformed by Bevis Bawa into a tropical artistic haven. Its creative integration of architecture, art, and landscaping makes it one of Sri Lanka’s most unique garden estates.",
    nearby: [
      "",
      "-",
      
    ],
  },

  {
    id: "Kalutara-Beach",
    title: "Kalutara Beach – Tranquil Coastal Escape",
    town: "Kalutara",
    district: "Kaluthara",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Beach/Kalutara-Beach-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Beach/Kalutara-Beach-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Beach/Kalutara-Beach-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Beach/Kalutara-Beach-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Beach/Kalutara-Beach-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Beach/Kalutara-Beach-5.jpg`,
    ],
    shortDescription:
      " Kalutara Beach is perfect for relaxation, scenic walks, and sunset views.Combine it with nearby cultural and historic attractions for a complete coastal experience. ",
    fullDescription:
      "",
    whyVisit: [
    "Golden Sandy Beach – Long stretches of clean sand ideal for walking, sunbathing, and relaxation.",
    "Scenic Views – Overlooks the Indian Ocean and nearby Kalu Ganga (Black River) estuary.",
    "Water Sports & Activities – Opportunities for swimming, surfing, and local fishing experiences.",
    "Peaceful Atmosphere – Less crowded than Colombo and Negombo beaches, perfect for a quiet retreat.",
    "Sunset Photography – Stunning sunsets over the ocean and river mouth, ideal for photographers."


    ],
    overview:
    "Kalutara Beach, located in Kalutara town, is a serene coastal destination known for its golden sands and calm atmosphere. The beach lies at the mouth of Kalu Ganga, where the river meets the Indian Ocean, creating picturesque views. It’s ideal for families, couples, and travelers seeking relaxation away from city crowds.",

    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free ",
      notes: [
             "Water Sports & Activities: Prices vary; surfing lessons ~LKR 1,500–3,000 (~USD 5–10) per hour",
             "Photography: Allowed everywhere"

      ]
    },
    highlights: [
      {
    main: "Swimming & Beach Activities",
    subPoints: [
      "Safe zones for swimming; moderate waves suitable for beginners.",
      "Local vendors offer beachside snacks, drinks, and refreshments."

    ]
  },
  {
    main: "Scenic Photography",
    subPoints: [
        "Panoramic views of the ocean and river estuary.",
        "Stunning sunrise and sunset opportunities."
    ]
  },
  {
    main: "Relaxation & Picnics",
    subPoints: [
       "Wide sandy areas for sunbathing, jogging, or family picnics.",
       "Calm environment compared to busier western beaches like Negombo."
    ]
  },
  {
    main: "Nearby Attractions",
    subPoints: [
       "Close to Kalutara Bodhiya and Richmond Castle, allowing for a combined heritage and beach trip.",
       "Local fishing communities provide cultural insight and fresh seafood experiences"
    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "Open 24 hours",
      bestTime: [
        "Early morning for sunrise or late afternoon for sunset and cooler temperatures",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5–10 minutes, fare LKR 300–500",
        "By Tuk-Tuk: ~5–10 minutes, fare LKR 250–400"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      "By Car/Taxi: ~1.5 hours (60 km), cost USD 20–25 / LKR 7,000–9,000",

       "From Colombo Fort:",
      "By Car/Taxi: ~1.5–2 hours (70 km), fare USD 20–25 / LKR 7,000–9,000", 
      "By Bus: Buses from Colombo to Kalutara; short walk to the beach (~5–10 minutes)",
      "By Train: Colombo–Kalutara South line (~1.5–2 hours), then walk or tuk-tuk to the beach"  

    ], 
    
    },  
    nearbyHotels: [
      	"AVANI Kalutara Resort (5★) – Beachfront luxury, walking distance from the shore",
        "Hikka Tranz by Cinnamon (5★) – ~10–15 minutes drive",
        "Budget Guesthouses & Eco-Lodges – Located in Kalutara town and near the river"
    ],
    travelTips: [
      "Bring sunscreen, hats, and swimwear.",
      "Early morning visits are ideal for quiet walks and photography.",
      "Combine your visit with Kalutara Bodhiya or Richmond Castle for a full-day itinerary.",
      "Respect local customs, especially near religious or residential areas.",
      "Stay hydrated and carry water, especially during midday visits."


    ],
    didYouKnow: "Kalutara Beach is located at the mouth of the Kalu Ganga, one of Sri Lanka’s major rivers. Its calm waves and scenic surroundings make it popular with locals and tourists alike while maintaining a peaceful environment away from busier beaches.",
    nearby: [
      "",
      "-",
      
    ],
  },

  {
    id: "Kalutara-Bodhiya",
    title: "Kalutara Bodhiya – Sacred Buddhist Landmark",
    town: "Kalutara",
    district: "Kaluthara",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Bodhiya/Kalutara-Bodhiya-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Bodhiya/Kalutara-Bodhiya-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Bodhiya/Kalutara-Bodhiya-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Bodhiya/Kalutara-Bodhiya-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Bodhiya/Kalutara-Bodhiya-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Kalutara-Bodhiya/Kalutara-Bodhiya-5.jpg`,
    ],
    shortDescription:
      " Kalutara Bodhiya offers a spiritual and serene escape.Experience Sri Lanka’s rich Buddhist heritage, admire unique architecture, and enjoy the tranquil riverfront setting. ",
    fullDescription:
      "",
    whyVisit: [
    "Sacred Significance – One of Sri Lanka’s most revered Buddhist sites, housing a sacred Bo tree believed to be a sapling from the original Jaya Sri Maha Bodhi in Anuradhapura.",
    "Unique Architecture – Features a striking hollow stupa (dagoba) allowing visitors to walk through its interior.",
    "Peaceful Environment – Tranquil surroundings ideal for meditation, reflection, and spiritual visits.",
    "Cultural Insight – Learn about Buddhist traditions, rituals, and Sri Lankan religious practices.",
    "Photography Opportunities – Beautiful stupa, gardens, and serene riverfront for scenic shots."

    ],
    overview:
    "Kalutara Bodhiya, located in Kalutara town on the western coast, is a sacred Buddhist site and a major pilgrimage destination. The highlight is the hollow stupa, unique in the world, which houses relics and allows visitors to walk inside. The site is situated along the Kalu Ganga (Black River), adding to its serene and picturesque atmosphere.",

    ticketsEntry: {
      residents: "Free",
      foreigners: "Free",
      children: "Free ",
      notes: [
            "Guided Tours: Available on request; typically LKR 500–1,500 (~USD 2–5)",
            "Photography: Allowed; respect worshippers during rituals."
      ]
    },
    highlights: [
      {
    main: "Hollow Stupa (Dagoba)",
    subPoints: [
      "Walk inside the stupa to see Buddha relics and murals depicting Buddhist teachings.",
      "A rare architectural feature, attracting both pilgrims and tourists"


    ]
  },
  {
    main: "Sacred Bo Tree & Gardens",
    subPoints: [
        "Ancient Bo tree planted from a cutting of the original Jaya Sri Maha Bodhi.",
        "Peaceful gardens and riverfront areas perfect for reflection and photography"

    ]
  },
  {
    main: "Spiritual & Cultural Experience",
    subPoints: [
       "Witness traditional Buddhist rituals, offerings, and meditation practices.",
       "Learn about the history of Buddhism in Kalutara and Sri Lanka."

    ]
  },
  {
    main: "Photography Opportunities",
    subPoints: [
      "Capture the hollow stupa, sacred tree, and calm river views.",
      "Best lighting during early morning or late afternoon."

    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "6:00 AM – 6:00 PM",
      bestTime: [
        "Early morning for peaceful experience and cooler temperatures; late afternoon for photography.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5–10 minutes, fare LKR 300–500",
        "By Tuk-Tuk: ~5–10 minutes, fare LKR 250–400"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      " By Car/Taxi: ~1.5 hours (70 km), cost USD 20–25 / LKR 7,000–9,000",

       "From Colombo Fort:",
      "By Car/Taxi: ~1.5–2 hours (60 km), fare USD 20–25 / LKR 7,000–9,000", 
      "By Public Bus: Buses from Colombo to Kalutara; then a short tuk-tuk ride to the Bodhiya (~15 minutes)",
      "By Train: Colombo–Kalutara South line (~1.5–2 hours), then walk or tuk-tuk to the beach"  

    ], 
    
    },  
    nearbyHotels: [
      	"AVANI Kalutara Resort (5★) – Beachfront luxury, 10–15 minutes away",
      "Hikka Tranz by Cinnamon (5★) – ~20–25 minutes drive",
      "Budget Guesthouses & Eco-Lodges – Located in Kalutara town and near the river"

    ],
    travelTips: [
      "Dress modestly: cover shoulders and knees when entering the stupa.",
      "Remove footwear before entering sacred areas.",
      "Visit early morning to avoid crowds and enjoy tranquility.",
      "Combine your visit with a trip to Kalutara beach or Richmond Castle for a full-day itinerary.",
      "Carry water and sunscreen, as shaded areas are limited outside the stupa and gardens"


    ],
    didYouKnow: "Kalutara Bodhiya is one of only a few hollow dagobas in the world. It was built to allow devotees to walk through the stupa while paying respects to Buddha relics. The site also serves as a center for Buddhist education and cultural preservation",
    nearby: [
      "",
      "-",
      
    ],
  },
{
    id: "Richmond-Castle",
    title: "Richmond Castle – Kalutara’s Colonial Jewel",
    town: "Kalutara",
    district: "Kaluthara",
    province: "Western-Province",
    provinceId: "western",
    rating: 4.7,
    reviews: 2891,
    category: "Nature",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Richmond-Castle/Richmond-Castle-1.jpg`,
    images: [
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Richmond-Castle/Richmond-Castle-1.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Richmond-Castle/Richmond-Castle-2.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Richmond-Castle/Richmond-Castle-3.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Richmond-Castle/Richmond-Castle-4.jpg`,
      `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/attractions/Western/Kaluthara/Richmond-Castle/Richmond-Castle-5.jpg`,
    ],
    shortDescription:
      "Richmond Castle offers a colonial charm and historical significance. Explore the ruins of this 19th-century British fort, surrounded by lush gardens and scenic views.",
    fullDescription:
      "",
    whyVisit: [
    "Historic Architecture – A grand mansion built in the early 20th century, showcasing colonial and European architectural styles.",
    "Cultural Insight – Learn about Sri Lanka’s aristocratic history and local elites during the British era.",
    "Beautiful Interiors & Gardens – Stunning antique furniture, crystal chandeliers, and landscaped gardens.",
    "Photography Opportunities – Elegant architecture, gardens, and interior décor perfect for photos.",
    "Riverside Location – Situated along the Kalu Ganga, offering scenic views and a peaceful atmosphere.",


    ],
    overview:
    "Richmond Castle, located in Kalutara town, was built by Don Arthur de Silva, a wealthy aristocrat, in 1904. This mansion is famous for its European-style architecture, lavish interiors, and well-maintained gardens. It remains a popular tourist attraction for those interested in colonial history, architecture, and heritage estates.",

    ticketsEntry: {
      residents: "LKR 500 (~USD 2–3) per adult",
      foreigners: "LKR 500 (~USD 2–3) per adult",
      children: "",
      notes: [
            "Guided Tours: Optional, LKR 1,000–1,500 (~USD 5)",
            "Photography: Allowed; interior and garden photography encouraged"
      ]

    },
    highlights: [
      {
    main: "Architecture & Interiors",
    subPoints: [
      "Grand wooden staircases, European-style ceilings, and intricate woodwork.",
      "Antique furniture and crystal chandeliers preserved from the colonial era.",
      "Beautiful tiled floors and ornate balconies overlooking the river."
    ]
  },
  {
    main: "Gardens & Riverside Views",
    subPoints: [
       "Landscaped gardens with flowering plants and tropical trees.",
       "Riverside view of Kalu Ganga, perfect for photography and relaxation.",
    ]
  },
  {
    main: "Photography & Exploration",
    subPoints: [
       "Exterior and interior architecture provide excellent photo opportunities.",
        "Gardens and riverbank ideal for scenic shots and leisurely walks.",
    ]
  },
  {
    main: "Cultural & Historical Insight",
    subPoints: [
      "Learn about the life of Sri Lankan aristocracy in the early 20th century.",
      "Understand colonial influences on architecture and lifestyle in Kalutara.",
    ]
  },
  
    ],
    
    visitingHours: {
      daily:
        "8:00 AM – 5:00 PM",
      bestTime: [
        "Morning for cooler temperatures and fewer crowds; late afternoon for scenic river photography.",
      ]
    },
   gettingThere: {
    fromTown: [
      	"By Car/Taxi: ~5–10 minutes, fare LKR 300–500",
        "By Tuk-Tuk: ~5–10 minutes, fare LKR 250–400"

    ],
    fromNearby: [
      "From Bandaranaike International Airport (BIA):",
      " By Car/Taxi: ~1.5 hours (60 km), cost USD 20–25 / LKR 7,000–9,000",

       "From Colombo Fort:",
      "By Car/Taxi: ~1.5–2 hours (70 km), fare USD 20–25 / LKR 7,000–9,000",
      "By Public Bus: Buses from Colombo to Kalutara; short tuk-tuk ride to Richmond Castle (~10 minutes)",
      "By Train: Colombo–Kalutara South line (~1.5–2 hours), then tuk-tuk to the castle"


    ], 
    
    },  
    nearbyHotels: [
      	"AVANI Kalutara Resort (5★) – ~10–15 minutes drive, beachfront luxury",
          "Hikka Tranz by Cinnamon (5★) – ~20–25 minutes",
          "Budget Guesthouses & Eco-Lodges – Located in Kalutara town and near the river"


    ],
    travelTips: [
      "Wear comfortable shoes for exploring the mansion and gardens.",
      "Hire a local guide to fully appreciate the history and architecture.",
      "Combine your visit with Kalutara Bodhiya and Kalutara Beach for a full-day tour.",
      "Carry water and sun protection, especially during midday visits.",
      "Morning visits provide cooler temperatures and better photography conditions."



    ],
    didYouKnow: "Richmond Castle was constructed by Don Arthur de Silva, a wealthy Sri Lankan aristocrat, who left the mansion to the nation as a heritage property. Its European-style architecture and riverside gardens make it one of Kalutara’s most iconic historical landmarks.",
    nearby: [
      "",
      "-",
      
    ],
  },

  
  
];

// Helper functions remain the same
export const getProvinces = (): string[] => {
  return Array.from(new Set(attractions.map(a => a.province))).sort();
};

export const getAttractionsByProvince = (provinceId: string): Attraction[] => {
  return attractions.filter(a => a.provinceId === provinceId);
};

export const getDistrictsByProvince = (provinceId: string): string[] => {
  const provinceAttractions = getAttractionsByProvince(provinceId);
  return Array.from(new Set(provinceAttractions.map(a => a.district))).sort();
};

export const getAttractionsByDistrict = (district: string): Attraction[] => {
  return attractions.filter(a => a.district.toLowerCase() === district.toLowerCase());
};

export const getTowns = (): string[] => {
  return Array.from(new Set(attractions.map(a => a.town))).sort();
};

export const getAttractionsByTown = (town: string): Attraction[] => {
  return attractions.filter(a => a.town.toLowerCase() === town.toLowerCase());
};

export const getAttractionById = (id: string): Attraction | undefined => {
  return attractions.find(a => a.id === id);
};

export const searchAttractions = (query: string, provinceId?: string): Attraction[] => {
  let result = provinceId ? getAttractionsByProvince(provinceId) : attractions;
  
  if (query) {
    const lowerQuery = query.toLowerCase();
    result = result.filter(attraction =>
      attraction.title.toLowerCase().includes(lowerQuery) ||
      attraction.town.toLowerCase().includes(lowerQuery) ||
      attraction.district.toLowerCase().includes(lowerQuery) ||
      attraction.shortDescription.toLowerCase().includes(lowerQuery) ||
      attraction.category.toLowerCase().includes(lowerQuery)
    );
  }
  
  return result;
};